import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import BuildingManagersDialog from '../components/BuildingManagersDialog';
import EditRepairDistributionDialog from '../components/EditRepairDistributionDialog';
import BuildingOwnerHistoryTab from '../components/BuildingOwnerHistoryTab';
import ChangeOwnerDialog from '../components/ChangeOwnerDialog';
import ApartmentFormBody from '../components/ApartmentFormBody';
import SupplierCreditNotesTab from '../components/SupplierCreditNotesTab';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Plus,
  CurrencyEur,
  ChartBar,
  CaretDown,
  CaretUp,
  CaretRight,
  Trash,
  PencilSimple,
  ArrowsLeftRight,
  CheckCircle,
  ShieldCheck,
  ShieldWarning,
  Warning,
  FileText,
  ArrowsClockwise,
  Eye,
  CreditCard,
  Buildings,
  List,
  SquaresFour,
  CalendarBlank,
  ArrowUp,
  ArrowDown,
  House,
  Wallet,
  Receipt,
  Elevator,
  Shield,
  Note,
  UsersThree,
  Calculator,
  Notebook,
  ArrowCounterClockwise,
  ArrowRight,
  DownloadSimple,
  Printer,
  Prohibit,
  FilePdf,
  Funnel,
  ClipboardText,
  Check,
  Copy,
  ClockClockwise,
  Info,
  FloppyDisk,
  Paperclip,
  MagnifyingGlass,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import MeetingsTab from './MeetingsTab';
import { TopicCategoriesContent } from './TopicCategories';
import { printPdf, downloadPdf as downloadPdfUtil, openPdfInBrowser, getPdfBlobUrl } from '../utils/pdfUtils';
import SendEmailDialog from '../components/SendEmailDialog';
import { formatDateTimeDMY } from '../utils/dateUtils';
import InvoicesTab from './InvoicesTab';
import PaymentsTab from './PaymentsTab';
import DatePickerDMY from '../components/DatePickerDMY';
import InvoiceStatusBadge from '../components/InvoiceStatusBadge';
import CollectionsPanel from '../components/CollectionsPanel';
import CommunicationsPanel from '../components/CommunicationsPanel';
import OwnerAdvancesPanel from '../components/OwnerAdvancesPanel';
import ExpenseFormFields from '../components/ExpenseFormFields';
import ExpenseAttachmentsInline from '../components/ExpenseAttachmentsInline';
import BuildingEmailsTab from '../components/BuildingEmailsTab';
import FireCertificationTab from '../components/FireCertificationTab';
import OwnerFeedbackTab from '../components/ownerFeedback/OwnerFeedbackTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHLY_CATEGORIES = ['201', '202', '203', '204', '205', '206'];
const CAPITAL_CATEGORIES = ['301', '302', '303', '304', '305', '306', '307', '308', '309', '310'];
const CATEGORY_TO_TYPE = {
  '201': 'other', '202': 'other', '203': 'other',
  '204': 'electricity', '205': 'other', '206': 'bank_fees',
  '301': 'other', '302': 'other', '303': 'other',
  '304': 'electrical_work', '305': 'other', '306': 'other',
  '307': 'other', '308': 'other', '309': 'bank_fees', '310': 'other',
};

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};
const round2 = (n) => Math.round(n * 100) / 100;

const getCatLabel = (cat, t, categories) => {
  if (!cat || cat === 'uncategorized') return t('uncategorized');
  const key = `cat_${cat}`;
  const label = t(key);
  if (label !== key) return label;
  // Phase 501 — Predefined categories (201-206, 301-310) carry their
  // localized label in the translations file ("301 Elevator Works").
  // User-defined categories (311+, 207+, etc.) only live in the DB; we
  // must look them up in the `customCategories` collection so the
  // dropdown never shows just a bare numeric code. The `name` field on
  // custom categories already contains "311 Fire Extinguishers" form, so
  // we return it as-is when present.
  if (Array.isArray(categories)) {
    const found = categories.find(c => String(c.category_code) === String(cat));
    if (found && found.name) return found.name;
  }
  return cat;
};

const getCategoryColor = (cat) => {
  if (!cat || cat === 'uncategorized') return 'bg-zinc-50 text-zinc-700 border-zinc-200';
  const code = parseInt(cat);
  if (code >= 200 && code <= 206) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (code >= 300 && code <= 310) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-zinc-50 text-zinc-700 border-zinc-200';
};

// Phase 348 — `categories` removed from TABS (moved to Settings → Expense
// Categories). The tab render block remains as dead-code in the file but
// is no longer reachable from the UI.
// Phase 349 — `emailTemplates` replaced by `emails`. Templates are now
// managed globally at Settings → Email Templates; the per-building tab is
// purely a SEND interface that selects a template and dispatches.
const TABS = ['overview', 'apartments', 'invoices', 'payments', 'creditNotes', 'expenses', 'repairs', 'cashReceipts', 'bankReceipts', 'bankDetails', 'annualExpenses', 'elevator', 'reserveFundTab', 'meetings', 'topicCategories', 'insuranceSection', 'statements', 'buildingNotes', 'elevatorCertification', 'fireCertification', 'ownerHistory', 'companySettings', 'emails', 'communications', 'collections', 'ownerAdvances', 'ownerFeedback'];

const TAB_ICONS = {
  overview: House,
  apartments: UsersThree,
  invoices: Receipt,
  payments: CreditCard,
  expenses: Receipt,
  repairs: ArrowsClockwise,
  cashReceipts: CurrencyEur,
  bankReceipts: CreditCard,
  bankDetails: CreditCard,
  annualExpenses: Calculator,
  elevator: Elevator,
  reserveFundTab: Shield,
  meetings: Notebook,
  insuranceSection: ShieldCheck,
  statements: FileText,
  buildingNotes: Note,
  elevatorCertification: ShieldCheck,
};

// Phase 153 — compact indicator for the Annual Expenses auto-save status.
// Presents a click-to-save fallback when the debounced timer is still pending.
function AnnualSavePill({ status, t, onManualSave }) {
  const map = {
    idle:     { bg: 'bg-zinc-50',     border: 'border-zinc-200',    text: 'text-zinc-400', label: t('saved'),         dot: 'bg-zinc-300' },
    saved:    { bg: 'bg-emerald-50',  border: 'border-emerald-200', text: 'text-emerald-700', label: t('saved'),      dot: 'bg-emerald-500' },
    unsaved:  { bg: 'bg-amber-50',    border: 'border-amber-200',   text: 'text-amber-700',  label: t('unsavedChanges'), dot: 'bg-amber-500' },
    saving:   { bg: 'bg-blue-50',     border: 'border-blue-200',    text: 'text-blue-700',   label: t('saving'),       dot: 'bg-blue-500 animate-pulse' },
    error:    { bg: 'bg-red-50',      border: 'border-red-200',     text: 'text-red-700',    label: t('saveError'),    dot: 'bg-red-500' },
  };
  const s = map[status] || map.idle;
  const clickable = status === 'unsaved' || status === 'error';
  return (
    <button
      type="button"
      onClick={clickable ? onManualSave : undefined}
      disabled={!clickable}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 border ${s.bg} ${s.border} ${s.text} text-[11px] uppercase tracking-wider font-semibold transition-colors ${clickable ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}`}
      data-testid="annual-save-status-pill"
      title={clickable ? t('saveNow') : ''}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
      {clickable && <span className="ml-1 normal-case text-[10px] opacity-70">({t('saveNow')})</span>}
    </button>
  );
}

// Phase 168 — Reusable per-repair attachments section.
function RepairAttachmentsSection({ repairId, attachments, pending, onUpload, onDelete, onOpen, t }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const fmtSize = (n) => {
    if (!n) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };
  const handlePick = () => fileInputRef.current?.click();
  const handleInput = (e) => {
    const list = e.target.files;
    if (list && list.length) onUpload(list);
    e.target.value = '';
  };
  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) onUpload(e.dataTransfer.files);
  };
  return (
    <div className="border border-zinc-200 p-3 space-y-2" data-testid={`repair-attach-${repairId}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">📎 {t('repairAttachments')}</p>
        <button
          type="button"
          onClick={handlePick}
          disabled={pending}
          className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
          data-testid={`repair-attach-upload-${repairId}`}
        >➕ {pending ? t('saving') : t('uploadFile')}</button>
        <input ref={fileInputRef} type="file" hidden multiple onChange={handleInput} accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv" />
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`text-[11px] text-zinc-400 border border-dashed ${dragOver ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200'} px-3 py-2`}
      >
        {t('repairAttachmentsHint')}
      </div>
      {(!attachments || attachments.length === 0) ? (
        <p className="text-[11px] text-zinc-400 italic">{t('noAttachments')}</p>
      ) : (
        <ul className="space-y-1">
          {attachments.map(a => (
            <li key={a.file_id} className="flex items-center justify-between gap-2 px-2 py-1 border border-zinc-100 hover:bg-zinc-50" data-testid={`repair-attach-item-${a.file_id}`}>
              <button type="button" onClick={() => onOpen(a.file_id, false)} className="flex items-center gap-2 min-w-0 flex-1 text-left">
                <span className="text-zinc-500">{(a.content_type || '').startsWith('image/') ? '🖼' : '📄'}</span>
                <span className="truncate text-xs font-medium">{a.filename}</span>
                <span className="text-[10px] text-zinc-400 font-mono whitespace-nowrap">{fmtSize(a.size)}</span>
              </button>
              <button type="button" onClick={() => onOpen(a.file_id, true)} className="text-[10px] px-1.5 py-0.5 border border-zinc-200 hover:bg-zinc-100" data-testid={`repair-attach-download-${a.file_id}`}>↓</button>
              <button type="button" onClick={() => onDelete(a.file_id)} className="text-[10px] px-1.5 py-0.5 border border-red-200 text-red-600 hover:bg-red-50" data-testid={`repair-attach-delete-${a.file_id}`}>✕</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function BuildingDetails() {
  const { buildingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();

  const [building, setBuilding] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [aptSummary, setAptSummary] = useState(null);
  // Phase 239 — Payment Reminder panel state.
  const [reminderCandidates, setReminderCandidates] = useState([]);
  const [reminderSending, setReminderSending] = useState({});  // { [apt_id]: bool }
  const [reminderPanelOpen, setReminderPanelOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expSummary, setExpSummary] = useState(null);
  const [expArchiveFilter, setExpArchiveFilter] = useState('active');
  const [aptArchiveFilter, setAptArchiveFilter] = useState('active');
  const [expExportOpen, setExpExportOpen] = useState(false);
  const [expExportForm, setExpExportForm] = useState({ format: 'excel', dateFrom: '', dateTo: '', categoryGroup: '' });
  const [elevExportOpen, setElevExportOpen] = useState(false);
  const [elevExportKind, setElevExportKind] = useState('invoices'); // 'invoices' | 'payments'
  const [elevExportForm, setElevExportForm] = useState({ format: 'excel', dateFrom: '', dateTo: '' });
  const [overviewDash, setOverviewDash] = useState(null);
  // Phase 207j — Active committee derived from latest meeting
  const [activeCommittee, setActiveCommittee] = useState({ members: [], meeting_date: '' });
  const [elevExpenses, setElevExpenses] = useState([]);
  const [elevSubTab, setElevSubTab] = useState('invoices');
  const [customCategories, setCustomCategories] = useState([]);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // {category_code, name, group, usage_count, isNew}
  const [catForm, setCatForm] = useState({ name: '', group: 'monthly' });
  const [catDeleteOpen, setCatDeleteOpen] = useState(false);
  const [catToDelete, setCatToDelete] = useState(null);
  const [repSubTab, setRepSubTab] = useState('invoices');
  const [repPaymentPickerOpen, setRepPaymentPickerOpen] = useState(false);
  const [repPaymentPickerRepairId, setRepPaymentPickerRepairId] = useState('');
  const [elevContracts, setElevContracts] = useState([]);
  const [elevSummary, setElevSummary] = useState(null);
  const [elevLedger, setElevLedger] = useState(null);
  // Phase 379 — Invoice / Opening Balance payment history modal
  const [elevInvHistoryOpen, setElevInvHistoryOpen] = useState(false);
  const [elevInvHistory, setElevInvHistory] = useState(null);
  const [elevInvHistoryLoading, setElevInvHistoryLoading] = useState(false);
  // Phase 382 — Attachments manager. `target` shape:
  //   { kind: 'inv'|'pay', id: string, label: string, attachments: array }
  const [attManagerOpen, setAttManagerOpen] = useState(false);
  const [attManagerTarget, setAttManagerTarget] = useState(null);
  const [attUploading, setAttUploading] = useState(false);
  // Phase 351 — Statement date filter (period). Drives both the in-app
  // ledger view and the Download PDF action so the user sees the matching
  // numbers in both places.
  const [elevStmtFrom, setElevStmtFrom] = useState('');
  const [elevStmtTo, setElevStmtTo] = useState('');
  // Phase 375 — Invoice + Payment tab date filters (client-side). Empty
  // strings mean no filter applied.
  const [elevInvFrom, setElevInvFrom] = useState('');
  const [elevInvTo, setElevInvTo] = useState('');
  const [elevPayFrom, setElevPayFrom] = useState('');
  const [elevPayTo, setElevPayTo] = useState('');
  // Phase 661 — Free-text search boxes for Elevator Invoices / Payments
  // tabs (and Repairs equivalents). Search is purely client-side and
  // filters across the most useful fields (number / date / description /
  // supplier / reference / source).
  const [elevInvSearch, setElevInvSearch] = useState('');
  const [elevPaySearch, setElevPaySearch] = useState('');
  const [repInvSearch, setRepInvSearch] = useState('');
  const [repPaySearch, setRepPaySearch] = useState('');
  // Phase 351 — Refetch the elevator ledger when the user changes the
  // statement period filter so the table & totals always reflect the
  // selected window (Balance Brought Forward + running balance both
  // recompute on the server).
  useEffect(() => {
    if (!buildingId) return;
    const params = new URLSearchParams({ building_id: buildingId });
    if (elevStmtFrom) params.set('date_from', elevStmtFrom);
    if (elevStmtTo) params.set('date_to', elevStmtTo);
    axios.get(`${API}/elevator/expenses/ledger?${params.toString()}`)
      .then(r => setElevLedger(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elevStmtFrom, elevStmtTo, buildingId]);
  const [expandedGrandExtra, setExpandedGrandExtra] = useState(false);
  const [expandedGrandMonthly, setExpandedGrandMonthly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const urlTab = new URLSearchParams(window.location.search).get('tab');
    return TABS.includes(urlTab) ? urlTab : 'overview';
  });

  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams]);
  const [notes, setNotes] = useState([]);
  const [buildingTasks, setBuildingTasks] = useState([]);
  // Phase 65: in-page Add Task dialog (keeps building context)
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    type: 'building',  // 'building' | 'apartment' (Phase 67)
    apartment_number: '',
    start_date: new Date().toISOString().split('T')[0],
    reminder_date: '',
    is_important: false,
    notes: '',
  });
  const [taskSaving, setTaskSaving] = useState(false);
  // Phase 71 — Task Details dialog state
  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
  const [taskDetailsData, setTaskDetailsData] = useState(null);
  const [taskNoteDraft, setTaskNoteDraft] = useState('');
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);
  // Phase 598 — Show / hide completed tasks list (collapsed by default
  // so the active workload stays in focus). Click the count header to
  // expand or collapse.
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);

  // Phase 65: Create task in-context (keeps user on the building page)
  const refetchBuildingTasks = async () => {
    try {
      const tRes = await axios.get(`${API}/tasks?building_id=${buildingId}`);
      setBuildingTasks(tRes.data || []);
    } catch { /* ignore */ }
  };
  // Phase 71 — Task Details: open, add note, mark completed
  const openTaskDetails = async (task) => {
    setTaskDetailsOpen(true);
    setTaskDetailsLoading(true);
    setTaskNoteDraft('');
    // Optimistic show from the list data; then refresh with canonical data+history
    setTaskDetailsData({ ...task, history: task.history || [] });
    try {
      const res = await axios.get(`${API}/tasks/${task.task_id}`);
      setTaskDetailsData(res.data);
    } catch { /* keep optimistic */ }
    finally { setTaskDetailsLoading(false); }
  };
  const reloadTaskDetails = async () => {
    if (!taskDetailsData?.task_id) return;
    try {
      const res = await axios.get(`${API}/tasks/${taskDetailsData.task_id}`);
      setTaskDetailsData(res.data);
      // Also keep list view in sync
      refetchBuildingTasks();
    } catch { /* ignore */ }
  };
  const handleAddTaskNote = async (e) => {
    e.preventDefault();
    const note = (taskNoteDraft || '').trim();
    if (!note || !taskDetailsData?.task_id) return;
    try {
      await axios.post(`${API}/tasks/${taskDetailsData.task_id}/notes`, { note });
      setTaskNoteDraft('');
      reloadTaskDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };
  const handleMarkTaskCompleted = async () => {
    if (!taskDetailsData?.task_id) return;
    try {
      await axios.put(`${API}/tasks/${taskDetailsData.task_id}`, { status: 'completed' });
      toast.success(t('savedSuccess'));
      reloadTaskDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleCreateBuildingTask = async (e) => {
    e.preventDefault();
    if (!taskForm.start_date) { toast.error(t('errorOccurred')); return; }
    if (taskForm.type === 'apartment' && !(taskForm.apartment_number || '').trim()) {
      toast.error(t('errorOccurred'));
      return;
    }
    setTaskSaving(true);
    // Phase 69: Optimistic local-state add — task appears in the UI immediately,
    // before the server round-trip completes. Replaced with the server's canonical
    // copy once the POST returns.
    const tempId = `temp_${Date.now()}`;
    const optimisticTask = {
      task_id: tempId,
      building_id: buildingId,
      type: taskForm.type,
      apartment_number: taskForm.type === 'apartment' ? (taskForm.apartment_number || '').trim() : '',
      start_date: taskForm.start_date,
      reminder_date: taskForm.reminder_date || null,
      is_important: !!taskForm.is_important,
      status: 'pending',
      notes: taskForm.notes || '',
      title: taskForm.notes || t('task'),
      _optimistic: true,
    };
    setBuildingTasks(prev => [optimisticTask, ...prev]);
    try {
      const res = await axios.post(`${API}/tasks`, {
        building_id: buildingId,
        type: taskForm.type,
        apartment_number: taskForm.type === 'apartment' ? (taskForm.apartment_number || '').trim() : '',
        start_date: taskForm.start_date,
        reminder_date: taskForm.reminder_date || null,
        is_important: !!taskForm.is_important,
        status: 'pending',
        notes: taskForm.notes || '',
      });
      toast.success(t('savedSuccess'));
      setAddTaskDialogOpen(false);
      setTaskForm({
        type: 'building',
        apartment_number: '',
        start_date: new Date().toISOString().split('T')[0],
        reminder_date: '',
        is_important: false,
        notes: '',
      });
      // Replace the optimistic row with the real server data
      await refetchBuildingTasks();
      // Phase 69: auto-scroll to the Tasks card so user sees the new row immediately
      setTimeout(() => {
        const el = document.querySelector('[data-testid="ov-tasks-section"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      void res;
    } catch (err) {
      // Roll back the optimistic add on failure
      setBuildingTasks(prev => prev.filter(t => t.task_id !== tempId));
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setTaskSaving(false);
    }
  };

  // Dialogs
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  // paymentDialogOpen removed — replaced by invoice-based flow via addPayDialogOpen
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [deletingNote, setDeletingNote] = useState(null);
  // payingApt removed — invoice-based flow selects apartment inside the dialog

  // Insurance state
  const [insuranceDialogOpen, setInsuranceDialogOpen] = useState(false);
  const [deleteInsuranceDialogOpen, setDeleteInsuranceDialogOpen] = useState(false);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [insuranceForm, setInsuranceForm] = useState({
    company_name: '', insurance_company_id: '', policy_number: '',
    policy_start_date: '', policy_end_date: '', notes: '',
    total_premium: '', num_installments: '1',
    insured_building: '', insured_common_areas: '', insured_outdoor: '',
    insured_civil_liability: '', insured_excess: '',
  });
  const [editingInsurance, setEditingInsurance] = useState(false);
  const [insuranceHistory, setInsuranceHistory] = useState([]);
  const [renewingInsuranceId, setRenewingInsuranceId] = useState(null);
  const [expandedInsuranceId, setExpandedInsuranceId] = useState(null);
  const [insuranceStatements, setInsuranceStatements] = useState({});
  const [cancelInsuranceDialogOpen, setCancelInsuranceDialogOpen] = useState(false);
  const [cancellingInsuranceId, setCancellingInsuranceId] = useState(null);
  const [editingInsuranceId, setEditingInsuranceId] = useState(null);
  const [deletingInsurance, setDeletingInsurance] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    building_id: '', expense_type: 'other', category: '204', amount: 0,
    date: new Date().toISOString().split('T')[0], status: 'paid', notes: '',
    vendor_id: '', reference_number: '',
    billing_period_start: '', billing_period_end: '',
    paid_from_reserve_fund: false,
    insurance_id: '', installment_number: '', pay_full: false,
    is_recurring: false, recurring_day: '1',
    // Phase 309 — Default Cash. "Unpaid" no longer exists; every expense
    // is automatically paid via Cash or Bank when saved.
    payment_source: 'cash',
  });
  const [paidInstallments, setPaidInstallments] = useState([]);
  const [insuranceStatement, setInsuranceStatement] = useState(null);
  const [expPaymentDialog, setExpPaymentDialog] = useState({ open: false, expense_id: '', expense_notes: '', expense_amount: 0, remaining: 0 });
  const [expPaymentForm, setExpPaymentForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], notes: '', payment_source: 'cash', paid_from_reserve_fund: false });
  // payForm removed — replaced by invoice-based receiptForm flow

  // Expense view mode (grouped by category = default)
  const [expViewMode, setExpViewMode] = useState('group');
  const [expandedExpCats, setExpandedExpCats] = useState({});
  const [selectedExpCategory, setSelectedExpCategory] = useState('all');

  // Recurring expenses management
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [recurringForm, setRecurringForm] = useState({ category: '204', amount: 0, recurring_day: '1', vendor_id: '', notes: '', reference_number: '' });
  const [expTypeFilter, setExpTypeFilter] = useState('all'); // 'all' | 'monthly' | 'capital'
  const [expDateFrom, setExpDateFrom] = useState('');
  const [expDateTo, setExpDateTo] = useState('');
  const [expCategoryFilter, setExpCategoryFilter] = useState('all');
  const [expStatusFilter, setExpStatusFilter] = useState('all');
  const [vendorList, setVendorList] = useState([]);
  const [deleteExpDialogOpen, setDeleteExpDialogOpen] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState(null);
  // Phase 820.5 — Delete confirmation state for Management Company Advances /
  // Repayments and Apartment Owner Credits / Reimbursements. When non-null the
  // corresponding AlertDialog is open.
  const [mcaDeleteId, setMcaDeleteId] = useState(null);
  const [aocDeleteId, setAocDeleteId] = useState(null);
  const [editExpDialogOpen, setEditExpDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editExpForm, setEditExpForm] = useState({
    amount: 0, date: '', category: '', notes: '',
    vendor_id: '', reference_number: '',
    billing_period_start: '', billing_period_end: '',
    // Phase 309 — Edit form mirrors Add. Default Cash.
    paid_from_reserve_fund: false,
    insurance_id: '', installment_number: '', pay_full: false,
    payment_source: 'cash',
  });
  // Phase 308 — Expense attachments state (shared by Add + Edit dialogs).
  const [expenseAttachments, setExpenseAttachments] = useState([]); // server-side (Edit)
  const [pendingExpenseFiles, setPendingExpenseFiles] = useState([]); // queued (Add)
  const [quickVendorOpen, setQuickVendorOpen] = useState(false);
  const [quickVendorForm, setQuickVendorForm] = useState({ name: '', phone: '', notes: '' });
  const [quickVendorTarget, setQuickVendorTarget] = useState('add'); // 'add' or 'edit'
  const [monthlySidebarOpen, setMonthlySidebarOpen] = useState(true);
  const [capitalSidebarOpen, setCapitalSidebarOpen] = useState(true);
  const [expandedExpPayments, setExpandedExpPayments] = useState({});

  // Building Company Settings
  const [bldgBankForm, setBldgBankForm] = useState({
    bank_name: '', bank_iban: '', bank_account_name: '',
    bank_account_number: '', bank_swift: '', bank_phone: '',
  });
  const [bldgSettingsLoading, setBldgSettingsLoading] = useState(false);
  const [bldgSettingsSaving, setBldgSettingsSaving] = useState(false);

  // Apartment CRUD dialogs
  const [aptDialogOpen, setAptDialogOpen] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  // Phase 290 — ChangeOwnerDialog state (opens from inside the Edit Apartment form)
  const [changeOwnerOpen, setChangeOwnerOpen] = useState(false);
  const [deleteAptDialogOpen, setDeleteAptDialogOpen] = useState(false);
  const [deletingApt, setDeletingApt] = useState(null);
  const [aptForm, setAptForm] = useState({
    apartment_number: '', owner_name: '', phone: '', email: '',
    share_percentage: 0, notes: '', monthly_fee: 0,
    opening_balance: 0, opening_balance_date: '', opening_balance_notes: '',
    reserve_fund: 0, repairs_fund: 0,
    tenant_name: '', tenant_phone: '', tenant_email: '',
    payment_responsibility: 'owner',
    extra_emails: [],
    contact_name: '', contact_phone: '', contact_email: '',
    email_recipients: { owner: true, contact: false, extras: [] },
    // Phase 490 — Apartment Details metadata
    floor: '', apt_type: '', share_units: 0, voting_rights: 0,
  });

  // Expandable payment history
  const [historyOpen, setHistoryOpen] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Cash/Bank Opening Balance dialog
  const [cashBankObDialogOpen, setCashBankObDialogOpen] = useState(false);
  const [cashBankObSource, setCashBankObSource] = useState('cash');
  const [cashBankObAmount, setCashBankObAmount] = useState('');
  const [cashBankObDate, setCashBankObDate] = useState('');
  // Phase 249 — Free-text note describing the opening balance (e.g.
  // "Outstanding Jan + Feb 2025"). Stored per building+source on the
  // building doc; surfaced in Cash Receipts ledger, Apartment Statement
  // (when relevant), and Annual Financial Statement.
  const [cashBankObNotes, setCashBankObNotes] = useState('');

  // Elevator invoice CRUD dialogs
  const [elevExpenseDialogOpen, setElevExpenseDialogOpen] = useState(false);
  const [editingElevExpense, setEditingElevExpense] = useState(null);
  const [deleteElevExpenseDialogOpen, setDeleteElevExpenseDialogOpen] = useState(false);
  const [deletingElevExpense, setDeletingElevExpense] = useState(null);
  const [elevExpenseForm, setElevExpenseForm] = useState({
    company: '', date: new Date().toISOString().split('T')[0],
    description: '', invoice_number: '', invoice_amount: 0, maintenance_notes: '',
    extra_charges: [], contract_id: '', vat_rate: 19,
    discount_type: 'amount', discount_value: 0,
    // Phase 408 — separate maintenance line item, distinct from extra_charges.
    // Description is auto-generated from maintenance_month + maintenance_year.
    maintenance_line: { quantity: 1, price: 0, vat_rate: 19, category: '202' },
  });

  // Inline add company
  const [addingNewCompany, setAddingNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  // Elevator invoice payment dialogs
  const [elevPaymentDialogOpen, setElevPaymentDialogOpen] = useState(false);
  const [elevViewPaymentsDialogOpen, setElevViewPaymentsDialogOpen] = useState(false);
  const [selectedElevExpense, setSelectedElevExpense] = useState(null);
  const [elevPaymentForm, setElevPaymentForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], notes: '', payment_source: 'cash', reference: '', allocations: {}, alloc_reserve: {}, payment_number: '' });
  // Phase 306 — Canonical list of items that can receive an allocation
  // (Opening Balance + unallocated credits + unpaid expenses). Loaded
  // from GET /api/elevator/payable-items when the dialog opens.
  const [elevPayableItems, setElevPayableItems] = useState([]);
  const [elevExpensePayments, setElevExpensePayments] = useState([]);
  const [loadingElevPayments, setLoadingElevPayments] = useState(false);
  const [deleteElevPaymentDialogOpen, setDeleteElevPaymentDialogOpen] = useState(false);
  const [deletingElevPayment, setDeletingElevPayment] = useState(null);
  const [editingElevPayment, setEditingElevPayment] = useState(null);

  // Elevator companies
  const [elevatorCompanies, setElevatorCompanies] = useState([]);

  // Last cleaning by type
  const [lastCleaningByType, setLastCleaningByType] = useState({});
  const [elevCompanyDialogOpen, setElevCompanyDialogOpen] = useState(false);
  const [editingElevCompany, setEditingElevCompany] = useState(null);
  const [elevCompanyForm, setElevCompanyForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [deleteElevCompanyDialogOpen, setDeleteElevCompanyDialogOpen] = useState(false);
  const [deletingElevCompany, setDeletingElevCompany] = useState(null);

  // Elevator Certifications (building-level)
  const [elevCerts, setElevCerts] = useState([]);
  const [elevCertCompanies, setElevCertCompanies] = useState([]);
  const [failureReasonsList, setFailureReasonsList] = useState([]);
  const [elevCertDialogOpen, setElevCertDialogOpen] = useState(false);
  const [editingElevCert, setEditingElevCert] = useState(null);
  const [elevCertForm, setElevCertForm] = useState({
    company_name: '', certificate_number: '', inspection_date: '', certification_date: '', next_inspection_date: '',
    inspector_name: '', result: 'passed', failure_reasons: [], notes: '',
    net_cost: 0, vat_rate: 19, vat_amount: 0, total_cost: 0,
    followup_of: null, supersedes: null, repair_date: '',
  });
  const [deleteElevCertDialogOpen, setDeleteElevCertDialogOpen] = useState(false);
  const [deletingElevCert, setDeletingElevCert] = useState(null);
  const [newFailureReason, setNewFailureReason] = useState('');
  const [elevCertHistoryOpen, setElevCertHistoryOpen] = useState(false);
  const [failureReasonsPopoverOpen, setFailureReasonsPopoverOpen] = useState(false);
  // Phase 704 — Inspection History entry edit / delete
  const [editingHistEntry, setEditingHistEntry] = useState(null); // { cert_id, snapshot }
  const [editHistForm, setEditHistForm] = useState({ result: '', certificate_number: '', inspector_name: '', company_name: '', next_inspection_date: '', failure_reasons: [], notes: '' });
  const [deletingHistEntry, setDeletingHistEntry] = useState(null); // { cert_id, snapshot }

  // Opening balance
  const [obDialogOpen, setObDialogOpen] = useState(false);
  const [obForm, setObForm] = useState({ amount: '', date: '' });
  const [obHistory, setObHistory] = useState([]);

  // Elevator Opening Balance
  const [elevObDialogOpen, setElevObDialogOpen] = useState(false);
  const [elevObForm, setElevObForm] = useState({ amount: '', date: '' });

  // Close Building
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ close_date: new Date().toISOString().split('T')[0], close_reason: '' });

  // Reserve Fund
  const [reserveFundInput, setReserveFundInput] = useState('');
  const [reserveFundMonthlyInput, setReserveFundMonthlyInput] = useState('');
  // Phase 817 — Manual RF opening balance seed inputs.
  const [rfSeedAmount, setRfSeedAmount] = useState('');
  const [rfSeedDate, setRfSeedDate] = useState('');
  const [rfSeedNotes, setRfSeedNotes] = useState('');
  const [editingReserveFund, setEditingReserveFund] = useState(null); // apartment_id being edited
  const [reserveFundEditValue, setReserveFundEditValue] = useState('');
  const [reserveFundHistory, setReserveFundHistory] = useState([]);
  const [showReserveFundHistory, setShowReserveFundHistory] = useState(false);
  const [rfCollected, setRfCollected] = useState(null);
  const [rfSpentOpen, setRfSpentOpen] = useState(false);
  const [extraReserveMonthly, setExtraReserveMonthly] = useState('');
  const [extraReserveMode, setExtraReserveMode] = useState('share');
  const [extraReserveFixed, setExtraReserveFixed] = useState('');
  const [extraReservePeriod, setExtraReservePeriod] = useState(new Date().toISOString().slice(0, 7));
  const [repairsFundInput, setRepairsFundInput] = useState('');
  const [editingRepairsFund, setEditingRepairsFund] = useState(null);
  const [repairsFundEditValue, setRepairsFundEditValue] = useState('');

  // Repairs History
  const [repairsList, setRepairsList] = useState([]);
  const [repairsStatement, setRepairsStatement] = useState(null);
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [editingBankDetails, setEditingBankDetails] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_account_name: '', bank_account_number: '', bank_iban: '', bank_swift: '', bank_name: '', bank_phone: '' });
  const [committeeDialogOpen, setCommitteeDialogOpen] = useState(false);
  const [committeeForm, setCommitteeForm] = useState({
    chairman: { name: '', apartment_id: '', is_authorized: false, authorized_by: '', notes: '' },
    treasurer: { name: '', apartment_id: '', is_authorized: false, authorized_by: '', notes: '' },
    secretary: { name: '', apartment_id: '', is_authorized: false, authorized_by: '', notes: '' },
  });
  // Phase 165 — `payment_source` is the FIRST choice ("building_fund" or
  // "distribution"). UI adapts conditionally below.
  const [repairForm, setRepairForm] = useState({
    description: '', amount: '',
    from_date: new Date().toISOString().split('T')[0], due_date: '',
    generate_invoices: false, payment_type: 'shared',
    payment_source: 'distribution', payment_method: 'cash',
    // Phase 259 — Distribution method when payment_source === 'distribution'.
    // 'share' → by share % (default), 'equal' → equally per apartment.
    distribution_method: 'share',
    building_fund_amount: '',
    // Phase 920.5 — Optional advancing apartment (Owner Advance).
    advancing_apartment_id: '',
  });
  const [repairFundBalances, setRepairFundBalances] = useState({ cash: 0, bank: 0, reserve_fund: 0 });
  // Phase 168 — per-repair attachments (uploaded via the Edit Repair modal or
  // the expanded row). `repairAttachments` is a per-repair cache keyed by
  // `repair_id` so we can refresh one repair without refetching others.
  const [repairAttachments, setRepairAttachments] = useState({}); // { repair_id: [files...] }
  const [repairUploadPending, setRepairUploadPending] = useState({}); // { repair_id: bool }
  // Fund-payment dialog state (building_fund repairs only)
  const [repairPaymentDialogOpen, setRepairPaymentDialogOpen] = useState(false);
  const [activePaymentRepair, setActivePaymentRepair] = useState(null);
  const [editingRepairPayment, setEditingRepairPayment] = useState(null);
  const [repairPaymentForm, setRepairPaymentForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', notes: '', payment_source: 'cash', paid_from_reserve_fund: false, category: '', vendor_id: '' });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [deleteRepairDialogOpen, setDeleteRepairDialogOpen] = useState(false);
  const [deletingRepair, setDeletingRepair] = useState(null);
  // Phase 279 — Edit Repair Distribution dialog
  const [editDistOpen, setEditDistOpen] = useState(false);
  const [editDistRepair, setEditDistRepair] = useState(null);
  const [expandedRepair, setExpandedRepair] = useState(null);

  // Fund Balances
  const [fundBalances, setFundBalances] = useState({ cash: 0, bank: 0, reserve_fund: 0 });
  // Source Balances — receipts-based (matches Finance → Cash/Bank Receipts tabs)
  // Shape: { cash: {received, spent, available}, bank: {...}, reserve_fund: {total, spent, available} }
  const [sourceBalances, setSourceBalances] = useState({ cash: { received: 0, spent: 0, available: 0 }, bank: { received: 0, spent: 0, available: 0 }, reserve_fund: { total: 0, spent: 0, available: 0 } });
  const [fundTransactions, setFundTransactions] = useState([]);
  const [showFundTransactions, setShowFundTransactions] = useState(false);
  const [showSetBalances, setShowSetBalances] = useState(false);
  const [balanceForm, setBalanceForm] = useState({ cash: '', bank: '', reserve_fund: '' });
  const [cashLedger, setCashLedger] = useState(null);
  const [bankLedger, setBankLedger] = useState(null);
  // Phase 820.3 — Management Company Advances/Repayments summary + dialog state.
  // Summary shape: { bank/cash/total: { advances, repayments, outstanding } }
  const [mcaSummary, setMcaSummary] = useState({
    bank:  { advances: 0, repayments: 0, outstanding: 0 },
    cash:  { advances: 0, repayments: 0, outstanding: 0 },
    total: { advances: 0, repayments: 0, outstanding: 0 },
  });
  // Dialog form. When `entry_id` is set → edit mode; otherwise create.
  const [mcaDialog, setMcaDialog] = useState(null);
  // Phase 820.4 — Apartment Owner Credits / Reimbursements. Same shape,
  // different keys (credits/reimbursements) and adds a by_apartment list.
  const [aocSummary, setAocSummary] = useState({
    bank:  { credits: 0, reimbursements: 0, outstanding: 0 },
    cash:  { credits: 0, reimbursements: 0, outstanding: 0 },
    total: { credits: 0, reimbursements: 0, outstanding: 0 },
    by_apartment: [],
  });
  const [aocDialog, setAocDialog] = useState(null);
  // Phase 735.28 — Financial-year filter for Cash Receipts and Bank Receipts.
  // 'all' shows every entry. A specific year shows only that year's rows,
  // prefixed with a synthetic "Balance Brought Forward from {prev year}"
  // row whose value is the closing balance of the previous year.
  const [cashYear, setCashYear] = useState('all');
  const [bankYear, setBankYear] = useState('all');
  // Phase 735.35 — Outstanding balances modal state.
  const [outstandingModalOpen, setOutstandingModalOpen] = useState(false);
  const [outstandingData, setOutstandingData] = useState(null); // { total_outstanding, apartments: [...] }
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const loadOutstandingBalances = React.useCallback(async () => {
    if (!buildingId) return;
    setOutstandingLoading(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/outstanding-balances`);
      setOutstandingData(res.data);
    } catch (err) {
      console.error('[loadOutstandingBalances]', err);
      toast.error(err?.response?.data?.detail || 'Failed to load outstanding balances');
    } finally {
      setOutstandingLoading(false);
    }
  }, [buildingId]);

  // Build the list of available years for a ledger (years that have at
  // least one entry). Sorted ascending. Always includes the current calendar
  // year so users can pre-select a fresh year even before any transactions
  // land there.
  const _yearOptions = (ledger) => {
    if (!ledger || !ledger.entries) return [new Date().getFullYear()];
    const yrs = new Set();
    for (const e of ledger.entries) {
      const d = e.date || '';
      const y = parseInt(d.slice(0, 4), 10);
      if (Number.isFinite(y)) yrs.add(y);
    }
    yrs.add(new Date().getFullYear());
    return Array.from(yrs).sort((a, b) => a - b);
  };

  // Filter the ledger to a single financial year, prepending a synthetic
  // "Balance Brought Forward from {prev year}" row when the selected year
  // is NOT the earliest one containing entries. Preserves the running
  // `balance` field on each surviving row (already cumulative since the
  // very first ledger entry, so the running column remains correct).
  const _filterLedgerByYear = (ledger, year) => {
    if (!ledger) return null;
    if (year === 'all' || year == null) return ledger;
    const y = Number(year);
    const chronological = [...(ledger.entries || [])].sort((a, b) => {
      // Opening balance is chronologically first even if its `date` is
      // later than a real transaction (defensive — matches backend).
      if (a.is_opening_balance && !b.is_opening_balance) return -1;
      if (b.is_opening_balance && !a.is_opening_balance) return 1;
      const da = a.date || '', db = b.date || '';
      const cmp = da.localeCompare(db);
      if (cmp !== 0) return cmp;
      return (a.seq || 0) - (b.seq || 0);
    });
    const priorEntries = chronological.filter(e => {
      const ey = parseInt((e.date || '').slice(0, 4), 10);
      return Number.isFinite(ey) && ey < y;
    });
    const yearEntries = chronological.filter(e => {
      const ey = parseInt((e.date || '').slice(0, 4), 10);
      return Number.isFinite(ey) && ey === y;
    });
    let entries = yearEntries;
    // Only insert Balance Brought Forward when there ARE prior-year rows;
    // otherwise the actual Opening Balance row (which lives in yearEntries
    // if its date lands in the selected year) is the correct starting row.
    if (priorEntries.length > 0) {
      const lastPrior = priorEntries[priorEntries.length - 1];
      const bfBalance = Number(lastPrior.balance) || 0;
      const bfRow = {
        date: `${y - 1}-12-31`,
        description: `${t('balanceBroughtForward') || 'Balance Brought Forward'} (${t('from') || 'from'} ${y - 1})`,
        amount_in: 0,
        amount_out: 0,
        balance: bfBalance,
        is_brought_forward: true,
        is_opening_balance: false,
        seq: -1,
      };
      entries = [bfRow, ...yearEntries];
    }
    // Recompute totals for the selected year only.
    const total_in = yearEntries.reduce((acc, e) => acc + (Number(e.amount_in) || 0), 0);
    const total_out = yearEntries.reduce((acc, e) => acc + (Number(e.amount_out) || 0), 0);
    // Closing balance for the year: running balance of the last surviving row.
    const balance = entries.length > 0
      ? Number(entries[entries.length - 1].balance) || 0
      : (priorEntries.length > 0 ? Number(priorEntries[priorEntries.length - 1].balance) || 0 : 0);
    return { ...ledger, entries, total_in, total_out, balance };
  };

  const cashYearOptions = useMemo(() => _yearOptions(cashLedger), [cashLedger]);
  const bankYearOptions = useMemo(() => _yearOptions(bankLedger), [bankLedger]);
  const cashLedgerView = useMemo(() => _filterLedgerByYear(cashLedger, cashYear), [cashLedger, cashYear, t]); // eslint-disable-line react-hooks/exhaustive-deps
  const bankLedgerView = useMemo(() => _filterLedgerByYear(bankLedger, bankYear), [bankLedger, bankYear, t]); // eslint-disable-line react-hooks/exhaustive-deps
  // Phase 718 — Cash ↔ Bank Internal Transfer state. Single dialog shared
  // between the Cash Receipts and Bank Receipts toolbars. When opened
  // from "Cash Receipts" we pre-select from=cash/to=bank; from "Bank
  // Receipts" we flip to from=bank/to=cash. The user can swap freely.
  const [xferOpen, setXferOpen] = useState(false);
  const [xferFrom, setXferFrom] = useState('cash');
  const [xferTo, setXferTo] = useState('bank');
  const [xferAmount, setXferAmount] = useState('');
  const [xferDate, setXferDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [xferReference, setXferReference] = useState('');
  const [xferNotes, setXferNotes] = useState('');
  const [xferSaving, setXferSaving] = useState(false);
  const [aptPeriodFilter, setAptPeriodFilter] = useState('all'); // all, month, year, custom
  const [aptPeriodMonth, setAptPeriodMonth] = useState(new Date().getMonth() + 1);
  const [aptPeriodYear, setAptPeriodYear] = useState(new Date().getFullYear());
  const [aptPeriodFrom, setAptPeriodFrom] = useState('');
  const [aptPeriodTo, setAptPeriodTo] = useState('');
  const [filteredAptSummary, setFilteredAptSummary] = useState(null);

  // Phase 331 — Apartments PDF export: customizable column selection dialog.
  const APT_EXPORT_COLS = ['apartment', 'owner', 'total_due', 'total_paid', 'remaining_balance'];
  const APT_EXPORT_LS_KEY = `apt-export-cols::${buildingId}`;
  const loadAptExportCols = () => {
    try {
      const raw = localStorage.getItem(APT_EXPORT_LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const sane = parsed.filter(c => APT_EXPORT_COLS.includes(c));
          if (sane.length > 0) return sane;
        }
      }
    } catch { /* noop */ }
    return [...APT_EXPORT_COLS];
  };
  const [aptExportDialogOpen, setAptExportDialogOpen] = useState(false);
  const [aptExportCols, setAptExportCols] = useState(loadAptExportCols);
  useEffect(() => { setAptExportCols(loadAptExportCols()); }, [buildingId]); // eslint-disable-line

  // Phase 337 — Repairs PDF: one-click "View PDF" reads stored column
  // selection (defaults to all 8) and opens the preview modal directly.
  const REPAIR_EXPORT_COLS = ['apartment', 'owner', 'invoice', 'share', 'allocated', 'paid', 'remaining', 'status'];
  const REPAIR_EXPORT_LS_KEY = `repair-export-cols::${buildingId}`;
  const loadRepairExportCols = () => {
    try {
      const raw = localStorage.getItem(REPAIR_EXPORT_LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const sane = parsed.filter(c => REPAIR_EXPORT_COLS.includes(c));
          if (sane.length > 0) return sane;
        }
      }
    } catch { /* noop */ }
    return [...REPAIR_EXPORT_COLS];
  };
  const [repairExportTarget, setRepairExportTarget] = useState(null); // repair object
  const [repairPreviewUrl, setRepairPreviewUrl] = useState('');
  const [repairPreviewLoading, setRepairPreviewLoading] = useState(false);
  const [repairPreviewOpen, setRepairPreviewOpen] = useState(false);

  // Phase 337 — Generate Email dialog removed in Phase 347; the Email
  // Templates tab is now the single entry point.
  // (state removed)

  // Add Payment + Receipt
  const [addPayDialogOpen, setAddPayDialogOpen] = useState(false);
  const [payStep, setPayStep] = useState('select'); // 'select' | 'common_expense' | 'building_expense'
  const [receiptForm, setReceiptForm] = useState({
    apartment_id: '', amount: '', payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash', notes: '', expense_id: '', supplier: '',
    // Phase 241 — Optional manual receipt number (empty → auto-generated).
    receipt_number: '',
  });
  const [lastReceipt, setLastReceipt] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [showReceiptHistory, setShowReceiptHistory] = useState(false);
  const [aptInvoices, setAptInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState({}); // {invoice_id: {selected: bool, amount: number}}

  // Period filter state
  const [periodMode, setPeriodMode] = useState('thisMonth'); // 'thisMonth' | 'lastMonth' | 'custom'
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [periodSummary, setPeriodSummary] = useState(null);
  const [prevPeriodSummary, setPrevPeriodSummary] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);

  // Annual Expenses
  const [annualPlan, setAnnualPlan] = useState(null);
  // Phase 148 — Multiple named plans per {building, year}. Selected plan drives every
  // read/write. `activePlanId` is the one currently synced to apartment monthly_fee.
  const [plansList, setPlansList] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  // Phase 255 — All plans across every year for the building, used to power
  // the Annual Expenses "Plans" dropdown for one-click year switching.
  const [allPlansList, setAllPlansList] = useState([]);
  // Phase 161 — replace the unreliable window.confirm() (silently blocked in
  // iframe/preview contexts) with an AlertDialog so deletion of inactive plans
  // always proceeds.
  const [planDeleteDialogOpen, setPlanDeleteDialogOpen] = useState(false);
  const [planDeleteTarget, setPlanDeleteTarget] = useState(null);
  const [planDialogMode, setPlanDialogMode] = useState(null); // 'new' | 'duplicate' | 'rename' | null
  const [planDialogName, setPlanDialogName] = useState('');
  const [planDialogKeepRounded, setPlanDialogKeepRounded] = useState(true);
  const [planDialogTargetId, setPlanDialogTargetId] = useState(null);
  // Phase 169 — Plan Start/End Date Versioning. Activation requires a start_date
  // via a dialog; a separate dialog lets admins edit start/end with overlap checks.
  const [planActivateDialogOpen, setPlanActivateDialogOpen] = useState(false);
  const [planActivateTarget, setPlanActivateTarget] = useState(null);
  const [planActivateStartDate, setPlanActivateStartDate] = useState('');
  const [planDatesDialogOpen, setPlanDatesDialogOpen] = useState(false);
  const [planDatesTarget, setPlanDatesTarget] = useState(null);
  const [planDatesStart, setPlanDatesStart] = useState('');
  const [planDatesEnd, setPlanDatesEnd] = useState('');
  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [annualLoading, setAnnualLoading] = useState(false);
  const [annualEditing, setAnnualEditing] = useState(false);
  const [annualCatValues, setAnnualCatValues] = useState({});
  // Phase 153 — auto-save for category value edits. Prevents data loss when the
  // user types in the "Actual (€)" inputs and navigates away without clicking Save.
  // `annualCatBaseline` snapshots the last persisted state so we can detect "dirty".
  const [annualCatBaseline, setAnnualCatBaseline] = useState({});
  const [annualSaveStatus, setAnnualSaveStatus] = useState('idle'); // idle | unsaved | saving | saved | error
  const annualSaveTimerRef = useRef(null);
  const [annualCustomCats, setAnnualCustomCats] = useState([]);
  const [addAnnualCatDialogOpen, setAddAnnualCatDialogOpen] = useState(false);
  const [newAnnualCatName, setNewAnnualCatName] = useState('');
  const [newAnnualCatType, setNewAnnualCatType] = useState('operational');
  const [editingAnnualApt, setEditingAnnualApt] = useState(null);
  const [annualAptEditValue, setAnnualAptEditValue] = useState('');
  const [monthlyOverride, setMonthlyOverride] = useState(null);
  const [monthlyEditValue, setMonthlyEditValue] = useState('');
  const [loadingFromExpenses, setLoadingFromExpenses] = useState(false);
  const [editingMonthlyApt, setEditingMonthlyApt] = useState(null);
  const [monthlyAptEditValue, setMonthlyAptEditValue] = useState('');
  // Phase 139 — Annual Expenses audit history modal.
  const [annualHistoryOpen, setAnnualHistoryOpen] = useState(false);
  const [annualHistoryLoading, setAnnualHistoryLoading] = useState(false);
  const [annualHistoryRows, setAnnualHistoryRows] = useState([]);
  const [annualHistoryCategory, setAnnualHistoryCategory] = useState({ code: '', label: '' });

  const openAnnualHistory = async (code, label) => {
    setAnnualHistoryCategory({ code, label });
    setAnnualHistoryOpen(true);
    setAnnualHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/annual-expenses/history`, { params: { year: annualYear, category_code: code } });
      setAnnualHistoryRows(res.data?.history || []);
    } catch { toast.error(t('errorOccurred')); setAnnualHistoryRows([]); }
    finally { setAnnualHistoryLoading(false); }
  };

  // Phase 142 — Consistent monetary formatting. On blur, normalize any "95" / "95.5" / ""
  // into strictly 2-decimal strings ("95.00", "95.50") so every cell reads the same way.
  // Empty values stay empty (meaning "no override, use original").
  const formatMoneyBlur = (v) => {
    if (v === undefined || v === null || v === '') return '';
    const n = parseFloat(v);
    if (Number.isNaN(n)) return '';
    return n.toFixed(2);
  };
  // Distribution controls (Phase 57)
  const [distributionSource, setDistributionSource] = useState('actual'); // 'actual' | 'annual'
  const [includeInDist, setIncludeInDist] = useState({}); // { [categoryKey]: true/false }

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogProps, setEmailDialogProps] = useState({ pdfUrl: '', filename: '', defaultSubject: '', defaultEmail: '' });

  // Edit Building dialog
  const [editBldgDialogOpen, setEditBldgDialogOpen] = useState(false);
  // Phase 207i — Standalone Managers dialog (decoupled from building info)
  const [managersDialogOpen, setManagersDialogOpen] = useState(false);
  const [editBldgForm, setEditBldgForm] = useState({ name: '', street_address: '', zip_code: '', city: '', country: '', building_type: 'managed', management_fee: 0, management_vat_rate: 19, cleaning_fee: 0, cleaning_vat_rate: 19, managers: [], notes: '', reminder_enabled: false, reminder_first_days: 5, reminder_second_days: 15, reminder_final_days: 30, manager_name: '', committee_name: '' });

  // Collapsible overview sections (all hidden by default)
  const [ovCommitteeOpen, setOvCommitteeOpen] = useState(false);
  const [ovBankOpen, setOvBankOpen] = useState(false);
  const [ovRecentPayOpen, setOvRecentPayOpen] = useState(false);
  const [ovRecentExpOpen, setOvRecentExpOpen] = useState(false);

  useEffect(() => { fetchAll(); }, [buildingId]);
  // Phase 239 — Pre-load reminder candidates with the rest of building data.
  useEffect(() => { if (buildingId) fetchReminderCandidates(); }, [buildingId]); // eslint-disable-line
  useEffect(() => { if (building?.insurance_id) fetchInsuranceStatement(); }, [building?.insurance_id]); // eslint-disable-line
  useEffect(() => { fetchInsuranceHistory(); }, [buildingId]); // eslint-disable-line

  const fetchAll = async () => {
    try {
      const [bRes, aptRes, aptSum, expRes, expSum, ecRes, eeRes, eSum, notesRes, icRes, elevCoRes, cleanRes, rcpRes, obhRes, ledgerRes, dashRes] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}`),
        axios.get(`${API}/buildings/${buildingId}/apartments`),
        axios.get(`${API}/buildings/${buildingId}/apartments/summary`),
        axios.get(`${API}/expenses?building_id=${buildingId}`),
        axios.get(`${API}/expenses/summary?building_id=${buildingId}`),
        axios.get(`${API}/elevator/contracts?building_id=${buildingId}`),
        axios.get(`${API}/elevator/expenses?building_id=${buildingId}`),
        axios.get(`${API}/elevator/expenses/summary?building_id=${buildingId}`),
        axios.get(`${API}/buildings/${buildingId}/notes`),
        axios.get(`${API}/insurance-companies`),
        axios.get(`${API}/elevator/companies`),
        axios.get(`${API}/buildings/${buildingId}/last-cleaning-by-type`),
        axios.get(`${API}/receipts?building_id=${buildingId}`),
        axios.get(`${API}/buildings/${buildingId}/opening-balance/history`),
        axios.get(`${API}/elevator/expenses/ledger?building_id=${buildingId}`),
        axios.get(`${API}/buildings/${buildingId}/overview-dashboard`),
      ]);
      setBuilding(bRes.data);
      // Phase 817 — Hydrate the Manual RF Opening Balance form inputs
      // from the fetched building. Keep them empty when the field is
      // unset so the form doesn't display a stray "0".
      setRfSeedAmount(bRes.data?.opening_reserve_fund_balance != null
        ? String(bRes.data.opening_reserve_fund_balance)
        : '');
      setRfSeedDate(bRes.data?.opening_reserve_fund_balance_date || '');
      setRfSeedNotes(bRes.data?.opening_reserve_fund_balance_notes || '');
      // Phase 207j — Fire-and-forget fetch of active committee (depends on latest meeting)
      axios.get(`${API}/buildings/${buildingId}/active-committee`)
        .then(r => setActiveCommittee(r.data || { members: [], meeting_date: '' }))
        .catch(() => {});
      
      // Redirect cleaning_only buildings to statement page
      if (bRes.data.building_type === 'cleaning_only') {
        navigate(`/buildings/${buildingId}/statement`, { replace: true });
        return;
      }
      
      setApartments(aptRes.data);
      setAptSummary(aptSum.data);
      setExpenses(expRes.data);
      setExpSummary(expSum.data);
      setElevContracts(ecRes.data);
      setElevExpenses(eeRes.data);
      setElevSummary(eSum.data);
      setNotes(notesRes.data);
      setInsuranceCompanies(icRes.data);
      setElevatorCompanies(elevCoRes.data);
      setLastCleaningByType(cleanRes.data);
      setReceipts(rcpRes.data || []);
      setObHistory(obhRes.data || []);
      setElevLedger(ledgerRes.data);
      setOverviewDash(dashRes.data);
      
      // Fetch fund balances
      try {
        const fbRes = await axios.get(`${API}/buildings/${buildingId}/fund-balances`);
        setFundBalances(fbRes.data);
        setBalanceForm({ cash: String(fbRes.data.cash || 0), bank: String(fbRes.data.bank || 0), reserve_fund: String(fbRes.data.reserve_fund || 0) });
      } catch { /* ignore if not set yet */ }
      // Fetch receipts-based source balances (for Payment Source dropdowns)
      try {
        const sbRes = await axios.get(`${API}/buildings/${buildingId}/source-balances`);
        setSourceBalances(sbRes.data);
      } catch { /* ignore */ }
      // Fetch cash ledger
      try {
        const clRes = await axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=cash`);
        setCashLedger(clRes.data);
      } catch { /* ignore */ }
      // Fetch bank ledger
      try {
        const blRes = await axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=bank`);
        setBankLedger(blRes.data);
      } catch { /* ignore */ }
      // Phase 820.3 — Management Company Advances summary
      try {
        const mcaRes = await axios.get(`${API}/buildings/${buildingId}/management-advances/summary`);
        setMcaSummary(mcaRes.data);
      } catch { /* ignore */ }
      // Phase 820.4 — Apartment Owner Credits summary
      try {
        const aocRes = await axios.get(`${API}/buildings/${buildingId}/apartment-owner-credits/summary`);
        setAocSummary(aocRes.data);
      } catch { /* ignore */ }
      // Fetch vendors
      try {
        const vnRes = await axios.get(`${API}/vendors`);
        setVendorList(vnRes.data);
      } catch { /* ignore */ }
      // Fetch custom expense categories (for Repair/Elevator payment dialogs)
      try {
        const ccRes = await axios.get(`${API}/expense-categories`);
        setCustomCategories(ccRes.data || []);
      } catch { /* ignore */ }
      // Fetch building tasks
      try {
        const tRes = await axios.get(`${API}/tasks?building_id=${buildingId}`);
        setBuildingTasks(tRes.data || []);
      } catch { /* ignore */ }
      // Fetch elevator certifications
      try {
        const ecertRes = await axios.get(`${API}/buildings/${buildingId}/elevator-certifications`);
        setElevCerts(ecertRes.data);
      } catch { /* ignore */ }
      // Fetch elevator certification companies (for dropdown)
      try {
        const ecertCoRes = await axios.get(`${API}/elevator-certification-companies`);
        setElevCertCompanies(ecertCoRes.data);
      } catch { /* ignore */ }
      // Fetch failure reasons (for dropdown)
      try {
        const frRes = await axios.get(`${API}/failure-reasons`);
        setFailureReasonsList(frRes.data);
      } catch { /* ignore */ }
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async (archFilter) => {
    try {
      const res = await axios.get(`${API}/expenses?building_id=${buildingId}&archive_filter=${archFilter || expArchiveFilter}`);
      setExpenses(res.data);
    } catch { /* ignore */ }
  };

  const fetchApartments = async (archFilter) => {
    try {
      const [aptRes, aptSum] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}/apartments?archive_filter=${archFilter || aptArchiveFilter}`),
        axios.get(`${API}/buildings/${buildingId}/apartments/summary`),
      ]);
      setApartments(aptRes.data);
      setAptSummary(aptSum.data);
    } catch { /* ignore */ }
  };

  const handleExpArchiveFilter = (val) => { setExpArchiveFilter(val); fetchExpenses(val); };
  const handleAptArchiveFilter = (val) => { setAptArchiveFilter(val); fetchApartments(val); };

  // Phase 1010 — Live refresh when a payment (or edit/reverse) is saved
  // anywhere in the app for an apartment of THIS building. Listens for
  // the `cleanpro:apartment-payment-saved` CustomEvent emitted by
  // RecordPaymentDialog. Re-fetches apartments + summary WITHOUT
  // reloading the page or losing the user's selection.
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (!detail.building_id || detail.building_id === buildingId) {
        fetchApartments();
      }
    };
    window.addEventListener('cleanpro:apartment-payment-saved', handler);
    window.addEventListener('cleanpro:apartment-payment-updated', handler);
    window.addEventListener('cleanpro:apartment-payment-deleted', handler);
    return () => {
      window.removeEventListener('cleanpro:apartment-payment-saved', handler);
      window.removeEventListener('cleanpro:apartment-payment-updated', handler);
      window.removeEventListener('cleanpro:apartment-payment-deleted', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // Phase 239 — Reminder candidates loader + send action.
  const fetchReminderCandidates = async () => {
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}/reminder-candidates`);
      setReminderCandidates(r.data?.candidates || []);
    } catch { setReminderCandidates([]); }
  };
  const sendReminder = async (aptId, method = 'email') => {
    setReminderSending(s => ({ ...s, [aptId]: true }));
    try {
      const r = await axios.post(`${API}/apartments/${aptId}/reminders/send`, { method });
      const status = r.data?.status || 'queued';
      const labelMap = { sent: t('reminderSent') || 'Reminder sent', queued: t('reminderQueued') || 'Reminder queued', logged: t('reminderLogged') || 'Reminder logged', failed: t('reminderFailed') || 'Reminder failed' };
      const label = labelMap[status] || status;
      if (status === 'sent' || status === 'logged') toast.success(label);
      else if (status === 'queued') toast.success(label);
      else toast.error(label + (r.data?.error ? `: ${r.data.error}` : ''));
      await fetchReminderCandidates();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setReminderSending(s => ({ ...s, [aptId]: false }));
    }
  };

  const handleExpenseExport = () => {
    const token = localStorage.getItem('cleanpro_token');
    const params = new URLSearchParams();
    params.append('building_id', buildingId);
    params.append('token', token);
    if (expExportForm.dateFrom) params.append('date_from', expExportForm.dateFrom);
    if (expExportForm.dateTo) params.append('date_to', expExportForm.dateTo);
    if (expExportForm.categoryGroup && expExportForm.categoryGroup !== 'all') params.append('category_group', expExportForm.categoryGroup);
    const fmt = expExportForm.format === 'pdf' ? 'pdf' : 'excel';
    const url = `${API}/expenses/export/${fmt}?${params.toString()}`;
    // Iframe download: invisible, never blocked, works everywhere
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 60000);
    setExpExportOpen(false);
    toast.success(fmt === 'pdf' ? t('pdfExported') : t('exportExcel'));
  };

  const handleElevExport = () => {
    const token = localStorage.getItem('cleanpro_token');
    const params = new URLSearchParams();
    params.append('building_id', buildingId);
    params.append('token', token);
    params.append('format', elevExportForm.format === 'pdf' ? 'pdf' : 'excel');
    if (elevExportForm.dateFrom) params.append('date_from', elevExportForm.dateFrom);
    if (elevExportForm.dateTo) params.append('date_to', elevExportForm.dateTo);
    const endpoint = elevExportKind === 'payments' ? '/elevator/payments/export' : '/elevator/invoices/export';
    const url = `${API}${endpoint}?${params.toString()}`;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 60000);
    setElevExportOpen(false);
    toast.success(elevExportForm.format === 'pdf' ? t('pdfExported') : t('exportExcel'));
  };


  // Period date helpers
  const getMonthRange = (offset = 0) => {
    const d = new Date();
    d.setMonth(d.getMonth() + offset);
    const y = d.getFullYear();
    const m = d.getMonth();
    const first = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const last = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { from: first, to: last };
  };

  // Annual Expenses fetch
  // `opts.preserveCatValues` — when true (used by silent auto-save refetch), we
  // skip overwriting the user's `annualCatValues` so concurrent keystrokes are
  // not lost. Derived data (apartments, monthly totals) is still refreshed.
  const fetchAnnualPlan = async (year, planId = null, opts = {}) => {
    const preserveCatValues = !!opts.preserveCatValues;
    setAnnualLoading(true);
    try {
      const pid = planId || currentPlanId;
      const qs = pid ? `?year=${year}&plan_id=${encodeURIComponent(pid)}` : `?year=${year}`;
      const res = await axios.get(`${API}/buildings/${buildingId}/annual-expenses${qs}`);
      setAnnualPlan(res.data);
      setCurrentPlanId(res.data?.plan_id || null);
      const catView = res.data.category_view || [];
      const rfView = res.data.reserve_fund_view || { budget: 0, actual: 0, included: true };
      const vals = {};
      const incMap = {};
      catView.forEach(c => {
        // Phase 62 / 142 — normalize budget override to 2dp string; empty = no override.
        // Phase 163 — when no override exists but an "Original" actual amount is
        // available (e.g. €50 from bookkeeping), auto-populate the input once so
        // the user sees a sensible default. We also rebase the baseline to the
        // same value below, so the auto-save effect will NOT fire spuriously —
        // the prefilled value is treated as "in sync with the server" until the
        // user actively changes it.
        let prefill = '';
        if (c.budget > 0) {
          prefill = (Math.round(c.budget * 100) / 100).toFixed(2);
        } else if ((c.actual || 0) > 0) {
          prefill = (Math.round(c.actual * 100) / 100).toFixed(2);
        }
        vals[c.code] = prefill;
        incMap[c.code] = c.include !== false;
      });
      vals['__rf'] = (Math.round((rfView.budget || 0) * 100) / 100).toFixed(2);
      incMap['__rf'] = true;
      if (!preserveCatValues) {
        setAnnualCatValues(vals);
        setAnnualCatBaseline(vals);
        setAnnualSaveStatus('idle');
      } else {
        // Only rebase baseline so `dirty` reflects reality against the latest
        // server state, but keep user's in-flight edits intact.
        setAnnualCatBaseline(vals);
      }
      setIncludeInDist(incMap);
      setDistributionSource(res.data.distribution_source || 'actual');
      setAnnualCustomCats(res.data.custom_categories || []);
      setMonthlyOverride(res.data.monthly_override != null ? String(res.data.monthly_override) : null);
      setMonthlyEditValue(res.data.monthly_override != null ? String(res.data.monthly_override) : '');
    } catch { toast.error(t('errorOccurred')); }
    finally { setAnnualLoading(false); }
  };

  // Phase 148 — Plan CRUD helpers.
  const fetchPlansList = async (year) => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/annual-expenses/plans?year=${year}`);
      const plans = res.data?.plans || [];
      setPlansList(plans);
      // Default selection: the active plan, else the first plan, else null.
      if (!currentPlanId) {
        const act = plans.find(p => p.is_active) || plans[0] || null;
        if (act) setCurrentPlanId(act.plan_id);
      }
      return plans;
    } catch { return []; }
  };

  // Phase 255 — Fetch ALL plans across every year for the building so the
  // Annual Expenses dropdown can switch between years with a single click.
  const fetchAllPlans = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/annual-expenses/plans?all_years=true`);
      const plans = res.data?.plans || [];
      setAllPlansList(plans);
      return plans;
    } catch { return []; }
  };

  const openPlanDialog = (mode, targetId = null, prefill = '') => {
    setPlanDialogMode(mode);
    setPlanDialogTargetId(targetId);
    setPlanDialogName(prefill);
    setPlanDialogKeepRounded(true);
  };

  const submitPlanDialog = async () => {
    const name = planDialogName.trim();
    if (!name) { toast.error(t('planNameRequired')); return; }
    try {
      if (planDialogMode === 'new') {
        const res = await axios.post(`${API}/buildings/${buildingId}/annual-expenses/plans`, { year: annualYear, name });
        toast.success(t('planCreated'));
        setPlanDialogMode(null);
        setCurrentPlanId(res.data.plan_id);
        await fetchPlansList(annualYear);
        await fetchAllPlans();
        await fetchAnnualPlan(annualYear, res.data.plan_id);
      } else if (planDialogMode === 'duplicate') {
        const res = await axios.post(`${API}/buildings/${buildingId}/annual-expenses/plans`, {
          year: annualYear, name, duplicate_of: planDialogTargetId, keep_rounded: planDialogKeepRounded,
        });
        toast.success(t('planCreated'));
        setPlanDialogMode(null);
        setCurrentPlanId(res.data.plan_id);
        await fetchPlansList(annualYear);
        await fetchAllPlans();
        await fetchAnnualPlan(annualYear, res.data.plan_id);
      } else if (planDialogMode === 'rename') {
        await axios.put(`${API}/buildings/${buildingId}/annual-expenses/plans/${planDialogTargetId}`, { year: annualYear, name });
        toast.success(t('planRenamed'));
        setPlanDialogMode(null);
        await fetchPlansList(annualYear);
        await fetchAllPlans();
        if (planDialogTargetId === currentPlanId) await fetchAnnualPlan(annualYear, currentPlanId);
      }
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // Phase 281 — Annual Expenses are record-keeping only now. Activation is
  // a simple "mark this plan as the current reference" with no apartment
  // sync, no start_date prompt, no overlap math.
  const activatePlan = async (planId) => {
    if (!planId) return;
    try {
      await axios.post(`${API}/buildings/${buildingId}/annual-expenses/plans/${planId}/activate`,
        { year: annualYear });
      toast.success(t('planActivated'));
      await fetchPlansList(annualYear);
      await fetchAllPlans();
      await fetchAnnualPlan(annualYear, planId);
      setCurrentPlanId(planId);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  // Phase 281 — Kept to satisfy old refs in the codebase but no longer used
  // by the simplified UI. Activation no longer needs a start_date.
  const confirmActivatePlan = async () => {
    if (!planActivateTarget) return;
    await activatePlan(planActivateTarget.plan_id);
    setPlanActivateDialogOpen(false);
    setPlanActivateTarget(null);
  };

  const openEditPlanDatesDialog = (plan) => {
    setPlanDatesTarget(plan);
    setPlanDatesStart(plan.start_date || '');
    setPlanDatesEnd(plan.end_date || '');
    setPlanDatesDialogOpen(true);
  };

  const saveEditPlanDates = async () => {
    if (!planDatesTarget) return;
    try {
      await axios.put(
        `${API}/buildings/${buildingId}/annual-expenses/plans/${planDatesTarget.plan_id}/dates`,
        { start_date: planDatesStart || null, end_date: planDatesEnd || null }
      );
      toast.success(t('planDatesUpdated'));
      setPlanDatesDialogOpen(false);
      setPlanDatesTarget(null);
      await fetchPlansList(annualYear);
      await fetchAllPlans();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const deletePlan = async (planId) => {
    const plan = plansList.find(p => p.plan_id === planId);
    if (!plan) return;
    // Phase 151 — clearer feedback. If trying to delete the Active plan, explain the required step
    // instead of silently doing nothing.
    if (plan.is_active) {
      const other = plansList.find(p => p.plan_id !== planId);
      if (other) {
        toast.error(t('cannotDeleteActivePlanHint').replace('{name}', other.name));
      } else {
        toast.error(t('cannotDeleteOnlyPlan'));
      }
      return;
    }
    // Phase 161 — open the AlertDialog instead of using window.confirm which
    // is silently blocked in embedded/iframe preview contexts.
    setPlanDeleteTarget(plan);
    setPlanDeleteDialogOpen(true);
  };

  const confirmDeletePlan = async () => {
    const plan = planDeleteTarget;
    if (!plan) { setPlanDeleteDialogOpen(false); return; }
    const planId = plan.plan_id;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/annual-expenses/plans/${planId}?year=${annualYear}`);
      toast.success(t('planDeleted'));
      const remaining = plansList.filter(p => p.plan_id !== planId);
      const next = remaining.find(p => p.is_active) || remaining[0] || null;
      const nextId = next ? next.plan_id : null;
      setCurrentPlanId(nextId);
      await fetchPlansList(annualYear);
      await fetchAllPlans();
      if (nextId) await fetchAnnualPlan(annualYear, nextId);
      else setAnnualPlan(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setPlanDeleteDialogOpen(false);
      setPlanDeleteTarget(null);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab !== 'annualExpenses') return;
    let cancelled = false;
    (async () => {
      // Phase 735.38 — Deterministic first-load sequence: resolve the
      // Active Plan explicitly and pass its plan_id to fetchAnnualPlan.
      // Previously we fired `fetchPlansList` + `fetchAnnualPlan(year)` as
      // parallel fire-and-forget calls, letting the backend default the
      // plan. That left `currentPlanId` unset for one render and starved
      // downstream memoized calculations (Final Monthly, distributions)
      // of the active plan_id — until the user manually re-selected the
      // same plan. Now we await the plans list, pick the active plan,
      // and drive one consistent fetch.
      const [plans, allPlans] = await Promise.all([fetchPlansList(annualYear), fetchAllPlans()]);
      if (cancelled) return;
      // Prefer the active plan in the current annualYear; otherwise fall
      // back to the active plan in ANY year (Annual Expenses can switch
      // years). If none, use whatever plan the backend picks (planId=null).
      const activeInYear = (plans || []).find(p => p.is_active);
      const activeAnywhere = activeInYear || (allPlans || []).find(p => p.is_active) || null;
      if (activeAnywhere && !activeInYear) {
        const targetYear = parseInt(activeAnywhere.year || annualYear, 10);
        if (targetYear !== annualYear) {
          // Year switch will re-run this effect; skip the fetch here.
          setCurrentPlanId(activeAnywhere.plan_id);
          setAnnualYear(targetYear);
          return;
        }
      }
      const pid = (activeInYear && activeInYear.plan_id) || null;
      if (pid) setCurrentPlanId(pid);
      await fetchAnnualPlan(annualYear, pid);
    })();
    return () => { cancelled = true; };
  }, [activeTab, annualYear]);
  // Phase 166 — also fetch plan data when on the Reserve Fund tab so the
  // read-only Reserve Fund Distribution there stays in sync with Annual Expenses.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab !== 'reserveFundTab') return;
    let cancelled = false;
    (async () => {
      // Phase 735.38 — Same deterministic Active-Plan resolution as the
      // Annual Expenses effect above. The RF read-only distribution
      // reads `annualCatValues` + `includeInDist` + `annualPlan.apartments`
      // and computes Final Monthly. Firing plan-list and plan-fetch in
      // parallel produced a first-load race where `currentPlanId` was
      // null and the Final Monthly values were derived from a partially
      // hydrated state — the user then had to re-select the active plan
      // manually to trigger a clean refetch. By awaiting the plans list
      // and passing the resolved plan_id up-front, the first render on
      // this tab matches exactly what happens after a manual re-select.
      const [plans, allPlans] = await Promise.all([fetchPlansList(annualYear), fetchAllPlans()]);
      if (cancelled) return;
      const activeInYear = (plans || []).find(p => p.is_active);
      const activeAnywhere = activeInYear || (allPlans || []).find(p => p.is_active) || null;
      if (activeAnywhere && !activeInYear) {
        const targetYear = parseInt(activeAnywhere.year || annualYear, 10);
        if (targetYear !== annualYear) {
          setCurrentPlanId(activeAnywhere.plan_id);
          setAnnualYear(targetYear);
          return;
        }
      }
      const pid = (activeInYear && activeInYear.plan_id) || null;
      if (pid) setCurrentPlanId(pid);
      await fetchAnnualPlan(annualYear, pid);
    })();
    return () => { cancelled = true; };
  }, [activeTab, annualYear]);

  // Phase 150 — Also pull the plans list when the Apartments tab is active so the
  // Edit Apartment modal can reliably know whether an Active plan governs monthly_fee.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'apartments') fetchPlansList(annualYear); }, [activeTab, annualYear]);

  // Building Company Settings fetch
  const fetchBldgSettings = async () => {
    setBldgSettingsLoading(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/company-settings`);
      setBldgBankForm({
        bank_name: res.data.bank_name || '',
        bank_iban: res.data.bank_iban || '',
        bank_account_name: res.data.bank_account_name || '',
        bank_account_number: res.data.bank_account_number || '',
        bank_swift: res.data.bank_swift || '',
        bank_phone: res.data.bank_phone || '',
      });
    } catch (err) { console.error(err); }
    finally { setBldgSettingsLoading(false); }
  };
  useEffect(() => { if (activeTab === 'companySettings') fetchBldgSettings(); }, [activeTab, buildingId]); // eslint-disable-line

  const handleSaveBldgSettings = async (e) => {
    e.preventDefault();
    setBldgSettingsSaving(true);
    try {
      await axios.put(`${API}/buildings/${buildingId}/company-settings`, bldgBankForm);
      toast.success(t('changesSaved'));
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
    finally { setBldgSettingsSaving(false); }
  };

  // ---- Phase 60: Build payload from code-keyed state ----
  // `annualCatValues` is keyed by category_code + '__rf' for Reserve Fund.
  // `includeInDist` is keyed the same way.
  const buildCodePayload = () => {
    const view = (annualPlan && annualPlan.category_view) || [];
    const budgets = {};
    const includedCodes = [];
    view.forEach(c => {
      // Phase 62: only persist rounded_actual when the user has explicitly
      // entered a value. Empty string means "use original actual".
      const raw = annualCatValues[c.code];
      if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
        const num = parseFloat(raw);
        if (!isNaN(num)) budgets[c.code] = num;
      }
      if (includeInDist[c.code] !== false) includedCodes.push(c.code);
    });
    const rfBudget = parseFloat(annualCatValues['__rf']) || 0;
    return { budgets, includedCodes, rfBudget };
  };

  const handleSaveAnnualPlan = async () => {
    const { budgets, includedCodes, rfBudget } = buildCodePayload();
    try {
      await axios.put(`${API}/buildings/${buildingId}/annual-expenses`, {
        year: annualYear,
        plan_id: currentPlanId,
        category_budgets: budgets,
        included_codes: includedCodes,
        reserve_fund_budget: rfBudget,
        distribution_source: distributionSource,
        monthly_override: monthlyOverride !== null && monthlyOverride !== '' ? parseFloat(monthlyOverride) : null,
      });
      toast.success(t('savedSuccess'));
      setAnnualEditing(false);
      fetchAnnualPlan(annualYear);
    } catch { toast.error(t('errorOccurred')); }
  };

  // Save just the distribution source (fires on toggle click)
  const handleSaveDistributionSource = async (newSource) => {
    setDistributionSource(newSource);
    try {
      const { budgets, includedCodes, rfBudget } = buildCodePayload();
      await axios.put(`${API}/buildings/${buildingId}/annual-expenses`, {
        year: annualYear,
        plan_id: currentPlanId,
        category_budgets: budgets,
        included_codes: includedCodes,
        reserve_fund_budget: rfBudget,
        distribution_source: newSource,
        monthly_override: annualPlan?.monthly_override,
      });
      fetchAnnualPlan(annualYear);
    } catch { toast.error(t('errorOccurred')); }
  };

  // Phase 153 — Auto-save for the "Actual (€)" category override inputs.
  // Previously these inputs only updated local state, so users who typed a
  // value and navigated away lost their work. Now every change marks the form
  // as "unsaved" and triggers a debounced PUT after 900ms of inactivity.
  const annualCatValuesDirty = React.useMemo(() => {
    const keys = new Set([...Object.keys(annualCatValues || {}), ...Object.keys(annualCatBaseline || {})]);
    for (const k of keys) {
      const a = (annualCatValues?.[k] ?? '').toString().trim();
      const b = (annualCatBaseline?.[k] ?? '').toString().trim();
      if (a !== b) return true;
    }
    return false;
  }, [annualCatValues, annualCatBaseline]);

  // Phase 253 — Manual-save model. Editing is purely local; nothing is
  // persisted while the user types. The pill shows "Unsaved changes" the
  // moment any input differs from the baseline, and the user must click
  // "Save Now" (or the dedicated Save button) to commit.
  useEffect(() => {
    if (!annualPlan || annualLoading) return;
    if (annualCatValuesDirty) {
      setAnnualSaveStatus('unsaved');
    } else {
      // Only revert to "saved" when the values match baseline AND the
      // status was previously "unsaved" (don't override a fresh "saved"
      // pulse from a successful manual save).
      setAnnualSaveStatus(prev => prev === 'unsaved' ? 'saved' : prev);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annualCatValues]);

  // Warn on unload if there are unsaved annual edits.
  useEffect(() => {
    const handler = (e) => {
      if (annualSaveStatus === 'unsaved' || annualSaveStatus === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [annualSaveStatus]);

  // Debounced save when category selections change (includeInDist)
  useEffect(() => {
    if (!annualPlan || annualLoading) return;
    const handle = setTimeout(async () => {
      try {
        const { budgets, includedCodes, rfBudget } = buildCodePayload();
        const srvIncluded = new Set(
          (annualPlan.category_view || []).filter(c => c.include).map(c => c.code)
        );
        const curIncluded = new Set(includedCodes);
        const unchanged = srvIncluded.size === curIncluded.size &&
          [...srvIncluded].every(c => curIncluded.has(c));
        if (unchanged) return;
        await axios.put(`${API}/buildings/${buildingId}/annual-expenses`, {
          year: annualYear,
          plan_id: currentPlanId,
          category_budgets: budgets,
          included_codes: includedCodes,
          reserve_fund_budget: rfBudget,
          distribution_source: distributionSource,
          monthly_override: annualPlan.monthly_override,
        });
        fetchAnnualPlan(annualYear);
      } catch { /* silent */ }
    }, 700);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInDist]);

  const handleAnnualAptManual = async (aptId) => {
    const val = parseFloat(annualAptEditValue) || 0;
    try {
      await axios.put(`${API}/buildings/${buildingId}/annual-expenses/apartment/${aptId}`, {
        year: annualYear, total: val,
      });
      toast.success(t('savedSuccess'));
      setEditingAnnualApt(null);
      fetchAnnualPlan(annualYear);
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleAnnualAptReset = async (aptId) => {
    try {
      await axios.put(`${API}/buildings/${buildingId}/annual-expenses/apartment/${aptId}/reset`, { year: annualYear });
      toast.success(t('savedSuccess'));
      fetchAnnualPlan(annualYear);
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleAptMonthlyOverride = async (aptId) => {
    const val = parseFloat(monthlyAptEditValue);
    if (isNaN(val) || val < 0) { toast.error(t('invalidAmount')); return; }
    try {
      await axios.put(`${API}/buildings/${buildingId}/annual-expenses/apartment/${aptId}/monthly`, {
        year: annualYear, plan_id: currentPlanId, monthly_final_override: val,
      });
      toast.success(t('savedSuccess'));
      // Phase 145 — Optimistic in-place update. We patch the single apartment in local
      // state instead of re-fetching so the table never unmounts → scroll position and
      // focus chain are preserved. This is critical for rapid keyboard entry.
      const nextState = (prev) => prev ? { ...prev, apartments: (prev.apartments || []).map(a => a.apartment_id === aptId ? { ...a, monthly_final_override: val } : a) } : prev;
      setAnnualPlan(nextState);
      // Auto-advance to the next apartment row (same column) so Enter-Enter-Enter entry flows.
      const apts = (annualPlan?.apartments) || [];
      const idx = apts.findIndex(a => a.apartment_id === aptId);
      const next = (idx >= 0 && idx < apts.length - 1) ? apts[idx + 1] : null;
      if (next) {
        const share = next.share_percentage || 0;
        const opTotal = annualPlan?.operational_total || 0;
        const nextDefault = next.monthly_final_override != null
          ? next.monthly_final_override
          : Math.round(((opTotal / 12) * share / 100) * 100) / 100;
        setEditingMonthlyApt(next.apartment_id);
        setMonthlyAptEditValue(String(nextDefault));
      } else {
        setEditingMonthlyApt(null);
      }
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleAptMonthlyReset = async (aptId) => {
    try {
      await axios.put(`${API}/buildings/${buildingId}/annual-expenses/apartment/${aptId}/monthly`, {
        year: annualYear, plan_id: currentPlanId, monthly_final_override: null,
      });
      toast.success(t('savedSuccess'));
      // Phase 145 — Optimistic patch, no refetch → scroll preserved.
      setAnnualPlan(prev => prev ? { ...prev, apartments: (prev.apartments || []).map(a => {
        if (a.apartment_id !== aptId) return a;
        const { monthly_final_override, ...rest } = a; // eslint-disable-line no-unused-vars
        return rest;
      }) } : prev);
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleAddAnnualCategory = async () => {
    if (!newAnnualCatName.trim()) return;
    try {
      await axios.post(`${API}/buildings/${buildingId}/annual-expenses/add-category`, {
        year: annualYear, name_en: newAnnualCatName.trim(), type: newAnnualCatType || 'operational',
      });
      setNewAnnualCatName('');
      setNewAnnualCatType('operational');
      setAddAnnualCatDialogOpen(false);
      fetchAnnualPlan(annualYear);
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleDeleteAnnualCategory = async (key) => {
    try {
      await axios.delete(`${API}/buildings/${buildingId}/annual-expenses/category/${key}?year=${annualYear}`);
      fetchAnnualPlan(annualYear);
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleLoadFromExpenses = async () => {
    setLoadingFromExpenses(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/annual-expenses/from-expenses?year=${annualYear}`);
      const totals = res.data.category_totals || {};
      const newVals = { ...annualCatValues };
      Object.entries(totals).forEach(([key, amount]) => {
        if (amount > 0) newVals[key] = String(amount);
      });
      setAnnualCatValues(newVals);
      toast.success(`${t('loadedFromExpenses')} (${res.data.expense_count} ${t('entries')})`);
    } catch { toast.error(t('errorOccurred')); }
    finally { setLoadingFromExpenses(false); }
  };

  const computePeriodDates = (mode) => {
    if (mode === 'thisMonth') return getMonthRange(0);
    if (mode === 'lastMonth') return getMonthRange(-1);
    return { from: periodFrom, to: periodTo };
  };

  const fetchPeriodSummary = async (from, to) => {
    setPeriodLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.append('date_from', from);
      if (to) params.append('date_to', to);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API}/buildings/${buildingId}/period-summary${qs}`);
      setPeriodSummary(res.data);

      // Fetch previous period for comparison
      if (from && to) {
        try {
          const df = new Date(from);
          const dt = new Date(to);
          const diffMs = dt.getTime() - df.getTime();
          const prevTo = new Date(df.getTime() - 86400000); // day before from
          const prevFrom = new Date(prevTo.getTime() - diffMs);
          const pf = prevFrom.toISOString().split('T')[0];
          const pt = prevTo.toISOString().split('T')[0];
          const prevRes = await axios.get(`${API}/buildings/${buildingId}/period-summary?date_from=${pf}&date_to=${pt}`);
          setPrevPeriodSummary(prevRes.data);
        } catch { setPrevPeriodSummary(null); }
      }
    } catch {
      setPeriodSummary(null);
      setPrevPeriodSummary(null);
    } finally {
      setPeriodLoading(false);
    }
  };

  // Initialize period on mount and when mode changes
  useEffect(() => {
    if (periodMode !== 'custom') {
      const { from, to } = computePeriodDates(periodMode);
      setPeriodFrom(from);
      setPeriodTo(to);
    }
  }, [periodMode]);

  // Fetch when period dates change
  useEffect(() => {
    if (periodFrom && periodTo && buildingId) {
      fetchPeriodSummary(periodFrom, periodTo);
    }
  }, [periodFrom, periodTo, buildingId]);

  // Opening Balance CRUD
  const openObDialog = () => {
    setObForm({
      amount: building?.opening_balance != null ? String(building.opening_balance) : '',
      date: building?.opening_balance_date || '',
    });
    setObDialogOpen(true);
  };
  const handleSaveOb = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/buildings/${buildingId}/opening-balance`, {
        opening_balance: parseFloat(obForm.amount) || 0,
        opening_balance_date: obForm.date,
      });
      toast.success(t('savedSuccess'));
      setObDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };
  const handleClearOb = async () => {
    try {
      await axios.delete(`${API}/buildings/${buildingId}/opening-balance`);
      toast.success(t('deletedSuccess'));
      setObDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // Elevator Opening Balance
  const openElevObDialog = () => {
    setElevObForm({
      amount: elevSummary?.opening_balance != null ? String(elevSummary.opening_balance) : '',
      date: elevSummary?.opening_balance_date || '',
    });
    setElevObDialogOpen(true);
  };
  const handleSaveElevOb = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/buildings/${buildingId}/elevator-opening-balance`, {
        opening_balance: parseFloat(elevObForm.amount) || 0,
        opening_balance_date: elevObForm.date,
      });
      toast.success(t('savedSuccess'));
      setElevObDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // Cash/Bank Opening Balance handlers
  const openCashBankOb = (source) => {
    const field = source === 'cash' ? 'opening_cash_balance' : 'opening_bank_balance';
    const dateField = source === 'cash' ? 'opening_cash_balance_date' : 'opening_bank_balance_date';
    const notesField = source === 'cash' ? 'opening_cash_balance_notes' : 'opening_bank_balance_notes';
    setCashBankObSource(source);
    setCashBankObAmount(building?.[field] != null ? String(building[field]) : '');
    setCashBankObDate(building?.[dateField] || '');
    setCashBankObNotes(building?.[notesField] || '');
    setCashBankObDialogOpen(true);
  };
  const handleSaveCashBankOb = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/buildings/${buildingId}/cash-bank-opening-balance`, {
        source: cashBankObSource,
        amount: parseFloat(cashBankObAmount) || 0,
        date: cashBankObDate,
        notes: (cashBankObNotes || '').trim(),
      });
      toast.success(t('savedSuccess'));
      setCashBankObDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // Phase 718 — Open the Transfer Funds dialog. `from` is the source the
  // user clicked from (Cash Receipts → 'cash', Bank Receipts → 'bank').
  // The opposite source is pre-selected as the destination; both are
  // still editable so the user can swap directions if they pick the
  // wrong button.
  const openTransferDialog = (from) => {
    const f = from === 'bank' ? 'bank' : 'cash';
    setXferFrom(f);
    setXferTo(f === 'cash' ? 'bank' : 'cash');
    setXferAmount('');
    setXferDate(new Date().toISOString().slice(0, 10));
    setXferReference('');
    setXferNotes('');
    setXferOpen(true);
  };

  // Phase 820.3 — Management Company Advances helpers.
  // These project into cash-ledger as ordinary rows; the dialog handles
  // both create and edit modes based on presence of `entry_id` in state.
  const reloadFinancials = async () => {
    try {
      const [clRes, blRes, sumRes, aocRes] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=cash`),
        axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=bank`),
        axios.get(`${API}/buildings/${buildingId}/management-advances/summary`),
        axios.get(`${API}/buildings/${buildingId}/apartment-owner-credits/summary`),
      ]);
      setCashLedger(clRes.data);
      setBankLedger(blRes.data);
      setMcaSummary(sumRes.data);
      setAocSummary(aocRes.data);
    } catch { /* ignore */ }
  };

  const openMcaDialog = (source, entryType = 'advance', existing = null) => {
    if (existing) {
      setMcaDialog({
        entry_id: existing.entry_id,
        entry_type: existing.entry_type,
        source: existing.source,
        date: existing.date || new Date().toISOString().slice(0, 10),
        amount: String(existing.amount || ''),
        reference: existing.reference || '',
        notes: existing.notes || '',
      });
    } else {
      setMcaDialog({
        entry_id: null,
        entry_type: entryType,
        source: source === 'bank' ? 'bank' : 'cash',
        date: new Date().toISOString().slice(0, 10),
        amount: '',
        reference: '',
        notes: '',
      });
    }
  };

  const saveMcaEntry = async () => {
    if (!mcaDialog) return;
    const amt = parseFloat(mcaDialog.amount);
    if (!(amt > 0)) { toast.error(language === 'el' ? 'Ποσό > 0' : 'Amount must be > 0'); return; }
    if (!mcaDialog.date) { toast.error(language === 'el' ? 'Απαιτείται ημερομηνία' : 'Date is required'); return; }
    try {
      const body = {
        entry_type: mcaDialog.entry_type,
        source: mcaDialog.source,
        date: mcaDialog.date,
        amount: amt,
        reference: (mcaDialog.reference || '').trim(),
        notes: (mcaDialog.notes || '').trim(),
      };
      if (mcaDialog.entry_id) {
        await axios.put(`${API}/buildings/${buildingId}/management-advances/${mcaDialog.entry_id}`, body);
        toast.success(language === 'el' ? 'Αποθηκεύτηκε' : 'Saved');
      } else {
        await axios.post(`${API}/buildings/${buildingId}/management-advances`, body);
        toast.success(language === 'el' ? 'Καταχωρήθηκε' : 'Created');
      }
      setMcaDialog(null);
      reloadFinancials();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'el' ? 'Αποτυχία αποθήκευσης' : 'Save failed'));
    }
  };

  // Phase 820.5 — Delete confirmation via AlertDialog (window.confirm is
  // silently blocked by some browsers/webviews, which was making the trash
  // icon appear "dead"). Setting state opens the dialog; the actual DELETE
  // fires only from the "Delete" button inside the dialog below.
  const deleteMcaEntry = (entryId) => {
    if (!entryId) {
      toast.error(language === 'el' ? 'Σφάλμα: χωρίς αναγνωριστικό' : 'Missing entry id');
      return;
    }
    setMcaDeleteId(entryId);
  };

  const confirmDeleteMcaEntry = async () => {
    const entryId = mcaDeleteId;
    if (!entryId) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/management-advances/${entryId}`);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      setMcaDeleteId(null);
      reloadFinancials();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'el' ? 'Αποτυχία διαγραφής' : 'Delete failed'));
      setMcaDeleteId(null);
    }
  };

  // Phase 820.4 — Apartment Owner Credits / Reimbursements helpers.
  // Mirrors MCA exactly; additionally carries `apartment_id` + auto-fills
  // owner_name from the picked apartment.
  const openAocDialog = (source, entryType = 'credit', existing = null) => {
    if (existing) {
      setAocDialog({
        entry_id: existing.entry_id,
        entry_type: existing.entry_type,
        source: existing.source,
        apartment_id: existing.apartment_id || '',
        apartment_number: existing.apartment_number || '',
        owner_name: existing.owner_name || '',
        date: existing.date || new Date().toISOString().slice(0, 10),
        amount: String(existing.amount || ''),
        reference: existing.reference || '',
        notes: existing.notes || '',
      });
    } else {
      setAocDialog({
        entry_id: null,
        entry_type: entryType,
        source: source === 'bank' ? 'bank' : 'cash',
        apartment_id: '',
        apartment_number: '',
        owner_name: '',
        date: new Date().toISOString().slice(0, 10),
        amount: '',
        reference: '',
        notes: '',
      });
    }
  };

  const saveAocEntry = async () => {
    if (!aocDialog) return;
    if (!aocDialog.apartment_id) {
      toast.error(language === 'el' ? 'Επίλεξε διαμέρισμα' : 'Pick an apartment');
      return;
    }
    const amt = parseFloat(aocDialog.amount);
    if (!(amt > 0)) { toast.error(language === 'el' ? 'Ποσό > 0' : 'Amount must be > 0'); return; }
    if (!aocDialog.date) { toast.error(language === 'el' ? 'Απαιτείται ημερομηνία' : 'Date is required'); return; }
    try {
      const body = {
        entry_type: aocDialog.entry_type,
        source: aocDialog.source,
        apartment_id: aocDialog.apartment_id,
        owner_name: (aocDialog.owner_name || '').trim(),
        date: aocDialog.date,
        amount: amt,
        reference: (aocDialog.reference || '').trim(),
        notes: (aocDialog.notes || '').trim(),
      };
      if (aocDialog.entry_id) {
        await axios.put(`${API}/buildings/${buildingId}/apartment-owner-credits/${aocDialog.entry_id}`, body);
        toast.success(language === 'el' ? 'Αποθηκεύτηκε' : 'Saved');
      } else {
        await axios.post(`${API}/buildings/${buildingId}/apartment-owner-credits`, body);
        toast.success(language === 'el' ? 'Καταχωρήθηκε' : 'Created');
      }
      setAocDialog(null);
      reloadFinancials();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'el' ? 'Αποτυχία αποθήκευσης' : 'Save failed'));
    }
  };

  const deleteAocEntry = (entryId) => {
    if (!entryId) {
      toast.error(language === 'el' ? 'Σφάλμα: χωρίς αναγνωριστικό' : 'Missing entry id');
      return;
    }
    setAocDeleteId(entryId);
  };

  const confirmDeleteAocEntry = async () => {
    const entryId = aocDeleteId;
    if (!entryId) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/apartment-owner-credits/${entryId}`);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      setAocDeleteId(null);
      reloadFinancials();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'el' ? 'Αποτυχία διαγραφής' : 'Delete failed'));
      setAocDeleteId(null);
    }
  };


  const handleSaveTransfer = async (e) => {
    if (e) e.preventDefault();
    if (xferFrom === xferTo) {
      toast.error(t('transferSameSourceError') || 'From and To accounts must differ');
      return;
    }
    const amt = parseFloat(xferAmount);
    if (!amt || amt <= 0) {
      toast.error(t('transferAmountError') || 'Amount must be greater than zero');
      return;
    }
    if (!xferDate) {
      toast.error(t('dateRequired') || 'Date is required');
      return;
    }
    try {
      setXferSaving(true);
      await axios.post(`${API}/buildings/${buildingId}/cash-bank-transfers`, {
        from_source: xferFrom,
        to_source: xferTo,
        amount: amt,
        date: xferDate,
        reference: (xferReference || '').trim(),
        notes: (xferNotes || '').trim(),
      });
      toast.success(t('transferCreated') || 'Funds transferred');
      setXferOpen(false);
      // Refresh BOTH ledgers since the transfer updates both balances.
      const [clRes, blRes] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=cash`),
        axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=bank`),
      ]);
      setCashLedger(clRes.data);
      setBankLedger(blRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setXferSaving(false);
    }
  };

  // Close / Reopen Building
  const handleCloseBuilding = async (e) => {
    e.preventDefault();
    if (!closeForm.close_date || !closeForm.close_reason) return;
    try {
      await axios.put(`${API}/buildings/${buildingId}/close`, closeForm);
      toast.success(t('buildingClosed'));
      setCloseDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleReopenBuilding = async () => {
    try {
      await axios.put(`${API}/buildings/${buildingId}/reopen`);
      toast.success(t('buildingReopened'));
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const isInactive = building?.building_status === 'inactive';

  // Reserve Fund handlers
  React.useEffect(() => {
    if (building?.total_reserve_fund !== undefined) {
      const annual = building.total_reserve_fund || 0;
      setReserveFundInput(String(annual || ''));
      setReserveFundMonthlyInput(annual > 0 ? String(round2(annual / 12)) : '');
    }
    if (building) {
      setExtraReserveMonthly(String(building.extra_reserve_monthly || ''));
      setExtraReserveMode(building.extra_reserve_mode || 'share');
      setExtraReserveFixed(String(building.extra_reserve_fixed_amount || ''));
    }
  }, [building?.total_reserve_fund, building?.extra_reserve_monthly, building?.extra_reserve_mode, building?.extra_reserve_fixed_amount]);

  const handleSaveReserveFund = async () => {
    const amount = parseFloat(reserveFundInput) || 0;
    try {
      await axios.put(`${API}/buildings/${buildingId}/reserve-fund`, { total_reserve_fund: amount });
      toast.success(t('reserveFundDistributed'));
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const [extraReserveStartDate, setExtraReserveStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [extraReserveEndDate, setExtraReserveEndDate] = useState('');
  // Phase 1004d — Inline edit state for History end_date (empty string
  // means the row is not being edited; `null`/empty string when editing
  // means the user wants to clear it back to Open).
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [editingEndDate, setEditingEndDate] = useState('');
  // Phase 1004g — Management Company Advance history modal
  const [mcaHistoryOpen, setMcaHistoryOpen] = useState(false);
  const [mcaHistoryData, setMcaHistoryData] = useState(null);
  const [extraReserveHistory, setExtraReserveHistory] = useState([]);

  const fetchExtraReserveHistory = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/extra-reserve-history`);
      setExtraReserveHistory(res.data || []);
    } catch { setExtraReserveHistory([]); }
  };

  const handleSaveExtraReserve = async () => {
    try {
      await axios.put(`${API}/buildings/${buildingId}/extra-reserve-fund`, {
        extra_reserve_monthly: parseFloat(extraReserveMonthly) || 0,
        extra_reserve_mode: extraReserveMode,
        extra_reserve_fixed_amount: parseFloat(extraReserveFixed) || 0,
        start_date: extraReserveStartDate,
        end_date: extraReserveEndDate || null,
      });
      toast.success(t('savedSuccess'));
      // Phase 1004b — Reset the entry form after a successful Save so the
      // top form does not keep displaying the values that were just
      // persisted. The saved schedule stays visible only in the History
      // table below. All fields blank (Phase 1004c — Start Date is also
      // cleared per user request; never auto-populated by today or any
      // derived value). End Date must remain BLANK unless the user
      // enters one.
      setExtraReserveMonthly('');
      setExtraReserveFixed('');
      setExtraReserveMode('share');
      setExtraReserveStartDate('');
      setExtraReserveEndDate('');
      fetchAll();
      fetchExtraReserveHistory();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // Phase 1004d — Edit end_date of an existing history row. When the user
  // clears the field completely, the backend stores null and the row is
  // shown as Open. Amount / mode / fixed_amount / start_date are never
  // touched.
  const handleSaveHistoryEndDate = async (historyId) => {
    try {
      await axios.patch(
        `${API}/buildings/${buildingId}/extra-reserve-history/${historyId}`,
        { end_date: editingEndDate || null },
      );
      toast.success(t('savedSuccess'));
      setEditingHistoryId(null);
      setEditingEndDate('');
      fetchExtraReserveHistory();
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // Phase 1004g — Management Company Advance History dialog + PDF.
  const openMcaHistory = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/management-advances/history`);
      setMcaHistoryData(res.data);
      setMcaHistoryOpen(true);
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };
  const openMcaHistoryPdf = () => {
    printPdf(`/buildings/${buildingId}/management-advances/history/pdf`);
  };

  // Phase 171 — Stop the currently-active Extra Reserve record. No deletion;
  // the record stays in history with an `end_date` so historical invoices
  // retain their amounts.
  const handleStopExtraReserve = async () => {
    if (!window.confirm(t('confirmStopExtraReserve') || 'Stop Extra Monthly Reserve? Historical data remains intact.')) return;
    try {
      const today = new Date().toISOString().slice(0, 10);
      await axios.post(`${API}/buildings/${buildingId}/extra-reserve-fund/stop`, { end_date: today });
      toast.success(t('extraReserveStopped'));
      setExtraReserveMonthly('');
      setExtraReserveFixed('');
      fetchAll();
      fetchExtraReserveHistory();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDeleteExtraReserveHistory = async (historyId) => {
    // First attempt without force — backend returns 409 if used in invoices.
    const tryDelete = async (force) => {
      await axios.delete(
        `${API}/buildings/${buildingId}/extra-reserve-history/${historyId}${force ? '?force=true' : ''}`
      );
    };
    try {
      if (!window.confirm(t('confirmDeleteHistoryEntry'))) return;
      await tryDelete(false);
      toast.success(t('savedSuccess'));
      fetchExtraReserveHistory();
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 409 && detail?.code === 'history_in_use') {
        const msg = (t('confirmDeleteUsedHistory') || '').replace('{count}', detail.usage_count);
        if (!window.confirm(msg)) return;
        try {
          await tryDelete(true);
          toast.success(t('savedSuccess'));
          fetchExtraReserveHistory();
        } catch (err2) {
          toast.error(err2.response?.data?.detail?.message || err2.response?.data?.detail || t('errorOccurred'));
        }
        return;
      }
      toast.error(detail?.message || detail || t('errorOccurred'));
    }
  };

  const handleRecalculateReserve = async () => {
    try {
      await axios.post(`${API}/buildings/${buildingId}/reserve-fund/recalculate`);
      toast.success(t('reserveFundRecalculated'));
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleSaveAptReserveFund = async (aptId) => {
    const amount = parseFloat(reserveFundEditValue) || 0;
    try {
      await axios.put(`${API}/apartments/${aptId}/reserve-fund`, { reserve_fund: amount, manual: true });
      toast.success(t('reserveFundUpdated'));
      setEditingReserveFund(null);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleResetReserveFund = async (aptId) => {
    try {
      await axios.put(`${API}/apartments/${aptId}/reserve-fund/reset`);
      toast.success(t('reserveFundUpdated'));
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const fetchReserveFundHistory = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/reserve-fund/history`);
      setReserveFundHistory(res.data);
      setShowReserveFundHistory(!showReserveFundHistory);
    } catch { setReserveFundHistory([]); }
  };

  const fetchReserveFundCollected = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/reserve-fund-collected`);
      setRfCollected(res.data);
    } catch { setRfCollected(null); }
  };

  useEffect(() => {
    if (activeTab === 'reserveFundTab' && buildingId) {
      fetchReserveFundCollected();
      fetchExtraReserveHistory();
    }
  }, [activeTab, buildingId]); // eslint-disable-line

  // Repairs Fund handlers
  useEffect(() => {
    if (building?.total_repairs_fund !== undefined) setRepairsFundInput(building.total_repairs_fund.toString());
  }, [building?.total_repairs_fund]);

  const handleSaveRepairsFund = async () => {
    const amount = parseFloat(repairsFundInput) || 0;
    try {
      await axios.put(`${API}/buildings/${buildingId}/repairs-fund`, { total_repairs_fund: amount });
      toast.success(t('repairsFundDistributed'));
      fetchAll();
      fetchRepairs();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleRecalculateRepairs = async () => {
    try {
      await axios.post(`${API}/buildings/${buildingId}/repairs-fund/recalculate`);
      toast.success(t('savedSuccess'));
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleSaveAptRepairsFund = async (aptId) => {
    const amount = parseFloat(repairsFundEditValue) || 0;
    try {
      await axios.put(`${API}/apartments/${aptId}/repairs-fund`, { repairs_fund: amount, manual: true });
      toast.success(t('savedSuccess'));
      setEditingRepairsFund(null);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleResetRepairsFund = async (aptId) => {
    try {
      await axios.put(`${API}/apartments/${aptId}/repairs-fund/reset`);
      toast.success(t('savedSuccess'));
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // Repairs CRUD
  const fetchRepairs = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/repairs`);
      setRepairsList(res.data || []);
    } catch { /* ignore */ }
    try {
      const st = await axios.get(`${API}/buildings/${buildingId}/repairs/statement`);
      setRepairsStatement(st.data || null);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (buildingId) fetchRepairs(); }, [buildingId]);

  // Fetch global bank accounts (used when "Bank" source is selected in payment dialog)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/bank-accounts`);
        if (!cancelled) setBankAccounts(res.data || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAddRepair = async () => {
    const amount = parseFloat(repairForm.amount);
    if (!repairForm.description.trim() || !amount || amount <= 0) {
      toast.error(t('fillRequiredFields'));
      return;
    }
    const source = repairForm.payment_source || 'distribution';
    // Phase 956 — For Building Fund repairs, create the repair UNPAID.
    // Payments are added afterwards via the existing "Add Payment" dialog
    // (POST /repairs/{id}/fund-payments) which supports multiple partial
    // payments with per-payment source/date/notes. This lets the user
    // record e.g. €4,000 total and pay in 4 × €1,000 instalments. Legacy
    // behaviour (auto-paying the full amount at creation) is retained
    // ONLY when editing an existing repair to avoid corrupting historic
    // records.
    const rfPortion = 0;

    // Phase 956 — Balance check removed at creation. Availability is
    // re-checked when each individual fund payment is added, which is the
    // correct moment (creation is just recording the invoice).

    try {
      const payload = {
        description: repairForm.description,
        amount,
        from_date: repairForm.from_date,
        due_date: repairForm.due_date,
        building_fund_amount: rfPortion,
        payment_method: source === 'building_fund' ? (repairForm.payment_method || 'cash') : 'cash',
        // Phase 956 — When source is "Building Fund", persist that as the
        // payment_type so the backend does NOT distribute the amount to
        // apartments. Payments are then added incrementally via the
        // existing fund-payments endpoint.
        payment_type: source === 'building_fund' ? 'building_fund' : 'shared',
        // Phase 259 — Persist the distribution method only when actually
        // distributing to apartments.
        distribution_method: source === 'distribution' ? (repairForm.distribution_method || 'share') : 'share',
        // Generate invoices only makes sense when distributing to apartments.
        generate_invoices: source === 'distribution' ? !!repairForm.generate_invoices : false,
        // Phase 920.5 — Optional advancing apartment. When provided, the
        // linked Owner Advance is auto-created and no invoice is generated
        // for that apartment.
        advancing_apartment_id: source === 'distribution' ? (repairForm.advancing_apartment_id || null) : null,
      };
      if (editingRepair) {
        await axios.put(`${API}/buildings/${buildingId}/repairs/${editingRepair.repair_id}`, payload);
      } else {
        await axios.post(`${API}/buildings/${buildingId}/repairs`, payload);
      }
      toast.success(t('savedSuccess'));
      setRepairDialogOpen(false);
      setEditingRepair(null);
      setRepairForm({
        description: '', amount: '',
        from_date: new Date().toISOString().split('T')[0], due_date: '',
        generate_invoices: false, payment_type: 'shared',
        payment_source: 'distribution', payment_method: 'cash',
        distribution_method: 'share',
        building_fund_amount: '',
        advancing_apartment_id: '',
      });
      fetchRepairs();
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // Phase 165 — fetch current cash/bank/reserve balances for the building so the
  // Add Repair modal can show "Available: €X" beside each payment method and
  // block overdraft submissions.
  const fetchRepairFundBalances = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/fund-balances`);
      setRepairFundBalances({
        cash: Number(res.data?.cash || 0),
        bank: Number(res.data?.bank || 0),
        reserve_fund: Number(res.data?.reserve_fund || 0),
      });
    } catch { /* non-fatal */ }
  };
  useEffect(() => {
    if (repairDialogOpen && buildingId) fetchRepairFundBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairDialogOpen, buildingId]);

  // Phase 168 — Repair attachments helpers.
  const fetchRepairAttachments = async (repairId) => {
    if (!repairId) return [];
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/repairs/${repairId}/attachments`);
      const list = res.data?.attachments || [];
      setRepairAttachments(prev => ({ ...prev, [repairId]: list }));
      return list;
    } catch {
      setRepairAttachments(prev => ({ ...prev, [repairId]: [] }));
      return [];
    }
  };

  const uploadRepairAttachments = async (repairId, fileList) => {
    if (!repairId || !fileList || !fileList.length) return;
    setRepairUploadPending(prev => ({ ...prev, [repairId]: true }));
    try {
      const files = Array.from(fileList);
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name}: ${t('repairAttachmentTooLarge') || 'File exceeds 10 MB'}`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        try {
          await axios.post(`${API}/buildings/${buildingId}/repairs/${repairId}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (err) {
          toast.error(err.response?.data?.detail || `${f.name}: ${t('errorOccurred')}`);
        }
      }
      await fetchRepairAttachments(repairId);
      toast.success(t('savedSuccess'));
    } finally {
      setRepairUploadPending(prev => ({ ...prev, [repairId]: false }));
    }
  };

  const deleteRepairAttachment = async (repairId, fileId) => {
    try {
      await axios.delete(`${API}/buildings/${buildingId}/repairs/${repairId}/attachments/${fileId}`);
      await fetchRepairAttachments(repairId);
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const openRepairAttachment = async (repairId, fileId, download = false) => {
    try {
      const path = `/buildings/${buildingId}/repairs/${repairId}/attachments/${fileId}`;
      if (download) {
        await downloadPdfUtil(path, { disposition: 'download' }, 'attachment.pdf');
      } else {
        await openPdfInBrowser(path, { disposition: 'inline' });
      }
    } catch (err) { toast.error(err?.message || t('errorOccurred')); }
  };

  // When the Edit Repair modal opens for an existing repair, prefetch its
  // attachment list so the modal can show them without a second click.
  useEffect(() => {
    if (repairDialogOpen && editingRepair?.repair_id) {
      fetchRepairAttachments(editingRepair.repair_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairDialogOpen, editingRepair]);

  // When a repair row is expanded in the Invoices tab, prefetch its attachments
  // so the Attachments panel can render immediately. We intentionally refetch
  // every expand to keep the list in sync with other users' uploads.
  useEffect(() => {
    if (expandedRepair) {
      fetchRepairAttachments(expandedRepair);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRepair]);

  const handleGenerateRepairInvoices = async (repairId) => {
    try {
      const res = await axios.post(`${API}/buildings/${buildingId}/repairs/${repairId}/generate-invoices`);
      toast.success(t('repairInvoicesCreated'));
      fetchRepairs();
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const openRepairPaymentDialog = (repair, payment = null) => {
    setActivePaymentRepair(repair);
    setEditingRepairPayment(payment);
    setRepairPaymentForm(payment
      ? {
          date: payment.date || '',
          amount: String(payment.amount || ''),
          notes: payment.notes || '',
          payment_source: payment.payment_source || 'cash',
          paid_from_reserve_fund: !!payment.paid_from_reserve_fund,
          category: payment.category || '',
          vendor_id: payment.vendor_id || '',
        }
      : {
          date: new Date().toISOString().split('T')[0],
          amount: '',
          notes: '',
          payment_source: 'cash',
          paid_from_reserve_fund: false,
          category: '',
          vendor_id: '',
        }
    );
    setRepairPaymentDialogOpen(true);
  };

  const handleSaveRepairPayment = async () => {
    const amt = parseFloat(repairPaymentForm.amount);
    if (!amt || amt <= 0) { toast.error(t('enterValidAmount')); return; }
    // Remaining check (frontend)
    if (activePaymentRepair) {
      const already = (activePaymentRepair.fund_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
      const excludeCurrent = editingRepairPayment ? (editingRepairPayment.amount || 0) : 0;
      const remaining = (activePaymentRepair.amount || 0) - (already - excludeCurrent);
      if (amt > remaining + 0.01) {
        toast.error(t('paymentExceedsRemaining') + `: €${remaining.toFixed(2)}`);
        return;
      }
    }
    if (!repairPaymentForm.category) {
      toast.error(t('categoryRequired'));
      return;
    }
    if (!repairPaymentForm.vendor_id) {
      toast.error(t('vendorRequired'));
      return;
    }
    try {
      const payload = {
        date: repairPaymentForm.date,
        amount: amt,
        notes: repairPaymentForm.notes,
        payment_source: repairPaymentForm.payment_source || 'cash',
        paid_from_reserve_fund: !!repairPaymentForm.paid_from_reserve_fund,
        category: repairPaymentForm.category,
        vendor_id: repairPaymentForm.vendor_id,
      };
      if (editingRepairPayment) {
        await axios.put(`${API}/buildings/${buildingId}/repairs/${activePaymentRepair.repair_id}/fund-payments/${editingRepairPayment.payment_id}`, payload);
      } else {
        await axios.post(`${API}/buildings/${buildingId}/repairs/${activePaymentRepair.repair_id}/fund-payments`, payload);
      }
      toast.success(t('savedSuccess'));
      setRepairPaymentDialogOpen(false);
      setEditingRepairPayment(null);
      setActivePaymentRepair(null);
      fetchRepairs();
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDeleteRepairFundPayment = async (repair, payment) => {
    if (!window.confirm(t('confirmDeletePayment'))) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/repairs/${repair.repair_id}/fund-payments/${payment.payment_id}`);
      toast.success(t('deletedSuccess'));
      fetchRepairs();
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleToggleRepairStatus = async (repair) => {
    const newStatus = repair.status === 'active' ? 'closed' : 'active';
    try {
      await axios.put(`${API}/buildings/${buildingId}/repairs/${repair.repair_id}`, { status: newStatus });
      toast.success(t('savedSuccess'));
      fetchRepairs();
      fetchAll();
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleDeleteRepair = async () => {
    if (!deletingRepair) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/repairs/${deletingRepair.repair_id}`);
      toast.success(t('savedSuccess'));
      setDeleteRepairDialogOpen(false);
      setDeletingRepair(null);
      fetchRepairs();
      fetchAll();
    } catch { toast.error(t('errorOccurred')); }
  };


  // Fund balance handlers
  const handleSaveBalances = async () => {
    try {
      const res = await axios.put(`${API}/buildings/${buildingId}/fund-balances`, {
        cash: parseFloat(balanceForm.cash) || 0,
        bank: parseFloat(balanceForm.bank) || 0,
        reserve_fund: parseFloat(balanceForm.reserve_fund) || 0,
      });
      setFundBalances(res.data);
      setShowSetBalances(false);
      toast.success(t('balanceSaved'));
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const fetchFundTransactions = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/fund-transactions`);
      setFundTransactions(res.data);
      setShowFundTransactions(!showFundTransactions);
    } catch { setFundTransactions([]); }
  };

  // Receipt / Payment Handlers
  const openAddPayment = () => {
    setPayStep('select');
    setReceiptForm({ apartment_id: '', amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'cash', notes: '', expense_id: '', supplier: '' });
    setLastReceipt(null);
    setAptInvoices([]);
    setSelectedInvoices({});
    setAddPayDialogOpen(true);
  };
  const fetchAptInvoices = async (aptId) => {
    if (!aptId) { setAptInvoices([]); setSelectedInvoices({}); return; }
    try {
      // Phase 243 — Use the unified payable-items endpoint that includes
      // the apartment's Opening Balance debt as a virtual `OPENING` row.
      const res = await axios.get(`${API}/apartments/${aptId}/payable-items`);
      const items = (res.data || []).filter(it => (it.remaining || 0) > 0.01);
      setAptInvoices(items);
      const sel = {};
      items.forEach(it => { sel[it.invoice_id] = { amount: 0 }; });
      setSelectedInvoices(sel);
    } catch { setAptInvoices([]); }
  };

  const updateInvoiceAllocation = (invoiceId, val) => {
    const inv = aptInvoices.find(i => i.invoice_id === invoiceId);
    const maxVal = inv ? (inv.remaining || 0) : 99999;
    const num = Math.min(parseFloat(val) || 0, maxVal);
    setSelectedInvoices(prev => ({ ...prev, [invoiceId]: { amount: Math.max(0, num) } }));
  };

  // Phase 492 — Quick-allocate helpers used by the per-row buttons.
  // `allocateFull` fills the row with the invoice's full remaining
  // balance (regardless of the payment amount entered above).
  // `allocateRemaining` fills the row with whatever portion of the
  // payment hasn't been allocated yet, capped at the invoice remaining.
  const allocateFull = (invoiceId) => {
    const inv = aptInvoices.find(i => i.invoice_id === invoiceId);
    if (!inv) return;
    setSelectedInvoices(prev => ({
      ...prev,
      [invoiceId]: { amount: Math.round((inv.remaining || 0) * 100) / 100 },
    }));
  };
  const allocateRemainingTo = (invoiceId) => {
    const inv = aptInvoices.find(i => i.invoice_id === invoiceId);
    if (!inv) return;
    // Compute total allocated EXCLUDING this row so we know what's free.
    const usedElsewhere = Object.entries(selectedInvoices).reduce((s, [k, v]) => {
      if (k === invoiceId) return s;
      return s + (parseFloat(v?.amount) || 0);
    }, 0);
    const pay = parseFloat(receiptForm.amount) || 0;
    const free = Math.max(0, pay - usedElsewhere);
    const take = Math.min(free, inv.remaining || 0);
    setSelectedInvoices(prev => ({
      ...prev,
      [invoiceId]: { amount: Math.round(take * 100) / 100 },
    }));
  };

  const totalAllocated = Object.values(selectedInvoices).reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const bldPayTotalAmount = parseFloat(receiptForm.amount) || 0;
  const bldAllocationMatch = bldPayTotalAmount > 0 && Math.abs(totalAllocated - bldPayTotalAmount) < 0.01;
  const bldOverAllocated = totalAllocated > bldPayTotalAmount + 0.01;

  const bldAutoDistribute = () => {
    if (bldPayTotalAmount <= 0) return;
    let remaining = bldPayTotalAmount;
    const newSel = {};
    const sorted = [...aptInvoices].sort((a, b) => (a.period || '').localeCompare(b.period || ''));
    sorted.forEach(inv => {
      const invRemaining = inv.remaining || 0;
      if (remaining > 0 && invRemaining > 0) {
        const allocate = Math.min(remaining, invRemaining);
        newSel[inv.invoice_id] = { amount: Math.round(allocate * 100) / 100 };
        remaining = Math.round((remaining - allocate) * 100) / 100;
      } else {
        newSel[inv.invoice_id] = { amount: 0 };
      }
    });
    setSelectedInvoices(newSel);
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        building_id: buildingId,
        receipt_type: payStep,
        payment_date: receiptForm.payment_date,
        notes: receiptForm.notes,
        // Phase 241 — Optional manual receipt number; trim and only send
        // when non-empty so the backend's auto-generator stays in charge
        // when the field is left blank.
        receipt_number: (receiptForm.receipt_number || '').trim() || undefined,
      };
      if (payStep === 'common_expense') {
        if (!bldAllocationMatch || bldPayTotalAmount <= 0) {
          toast.error(t('allocationMismatch'));
          return;
        }
        payload.apartment_id = receiptForm.apartment_id;
        payload.payment_method = receiptForm.payment_method;
        payload.amount = bldPayTotalAmount;
        payload.invoice_selections = Object.entries(selectedInvoices)
          .filter(([, s]) => (parseFloat(s.amount) || 0) > 0)
          .map(([id, s]) => ({ invoice_id: id, amount: parseFloat(s.amount) || 0 }));
        if (payload.invoice_selections.length === 0) {
          toast.error(t('selectAtLeastOneInvoice'));
          return;
        }
      } else {
        payload.amount = parseFloat(receiptForm.amount) || 0;
        payload.expense_id = receiptForm.expense_id || undefined;
        payload.supplier = receiptForm.supplier;
      }
      const res = await axios.post(`${API}/receipts`, payload);
      setLastReceipt(res.data);
      setPayStep('done');
      toast.success(t('paymentRecorded'));
      fetchAll();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(t('receiptNumberAlreadyExists') || 'Receipt number already exists.');
      } else {
        toast.error(error.response?.data?.detail || t('errorOccurred'));
      }
    }
  };
  const downloadReceipt = (receiptId) => {
    downloadPdfUtil(`/receipts/${receiptId}/pdf`, { lang: document.documentElement.lang || 'en' });
  };
  const printReceipt = (receiptId) => {
    printPdf(`/receipts/${receiptId}/pdf`, { lang: document.documentElement.lang || 'en' });
  };

  // Quick add expense
  const handleQuickVendorCreate = async () => {
    const name = quickVendorForm.name.trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API}/vendors`, {
        name,
        phone: quickVendorForm.phone.trim(),
        notes: quickVendorForm.notes.trim(),
      });
      const newId = res.data.vendor_id;
      // Refresh vendor list
      const vnRes = await axios.get(`${API}/vendors`);
      setVendorList(vnRes.data);
      // Auto-select in the target form
      if (quickVendorTarget === 'edit') {
        setEditExpForm(f => ({ ...f, vendor_id: newId }));
      } else {
        setExpenseForm(f => ({ ...f, vendor_id: newId }));
      }
      setQuickVendorOpen(false);
      setQuickVendorForm({ name: '', phone: '', notes: '' });
      toast.success(t('vendorCreated') || 'Vendor created');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleExpPaymentSubmit = async () => {
    const amt = parseFloat(expPaymentForm.amount);
    if (!amt || amt <= 0) { toast.error(t('amountRequired')); return; }
    try {
      await axios.post(`${API}/expenses/${expPaymentDialog.expense_id}/payments`, {
        amount: amt,
        date: expPaymentForm.date,
        notes: expPaymentForm.notes,
        payment_source: expPaymentForm.payment_source,
        paid_from_reserve_fund: expPaymentForm.paid_from_reserve_fund,
      });
      toast.success(t('saved'));
      setExpPaymentDialog({ open: false, expense_id: '', expense_notes: '', expense_amount: 0, remaining: 0 });
      setExpPaymentForm({ amount: '', date: new Date().toISOString().split('T')[0], notes: '', payment_source: 'cash', paid_from_reserve_fund: false });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const isInsurance = expenseForm.category === '306' && building?.insurance_id;

      // Pay Full flow: use dedicated endpoint
      if (isInsurance && expenseForm.pay_full) {
        await axios.post(`${API}/insurance/${building.insurance_id}/pay-full`, {
          payment_source: expenseForm.payment_source !== 'none' ? expenseForm.payment_source : 'cash',
          date: expenseForm.date,
        });
        toast.success(t('savedSuccess'));
        setExpenseDialogOpen(false);
        fetchAll();
        fetchInsuranceStatement();
        return;
      }

      const payload = {
        building_id: buildingId,
        category: expenseForm.category,
        amount: parseFloat(expenseForm.amount) || 0,
        expense_type: CATEGORY_TO_TYPE[expenseForm.category] || 'other',
        date: expenseForm.date,
        notes: expenseForm.notes || '',
        reference_number: expenseForm.reference_number || '',
        vendor_id: expenseForm.vendor_id || null,
        billing_period_start: isInsurance ? null : (expenseForm.billing_period_start || null),
        billing_period_end: isInsurance ? null : (expenseForm.billing_period_end || null),
        // Phase 309/373.1 — Cash/Bank is the independent base source. Reserve Fund is a separate flag.
        payment_source: (expenseForm.payment_source === 'bank' ? 'bank' : 'cash'),
        paid_from_reserve_fund: !!expenseForm.paid_from_reserve_fund,
        // Phase 708 — Structured Reserve Fund Deduction fields. Forwarded
        // only when the RF flag is on so a stale Reason cannot leak into
        // a non-RF expense. Backend re-validates and clears the fields
        // when the flag is off.
        reserve_fund_deduction_amount: expenseForm.paid_from_reserve_fund
          ? (expenseForm.reserve_fund_deduction_amount === '' || expenseForm.reserve_fund_deduction_amount === null || expenseForm.reserve_fund_deduction_amount === undefined
              ? null
              : parseFloat(expenseForm.reserve_fund_deduction_amount))
          : null,
        reserve_fund_reason: expenseForm.paid_from_reserve_fund ? (expenseForm.reserve_fund_reason || null) : null,
        reserve_fund_description: expenseForm.paid_from_reserve_fund ? (expenseForm.reserve_fund_description || '') : null,
        reserve_fund_reference: expenseForm.paid_from_reserve_fund ? (expenseForm.reserve_fund_reference || '') : null,
      };

      // For insurance, attach insurance_id + installment_number
      if (isInsurance && expenseForm.installment_number) {
        payload.insurance_id = building.insurance_id;
        payload.installment_number = parseInt(expenseForm.installment_number);
      }

      const res = await axios.post(`${API}/expenses`, payload);
      // Phase 308 — Upload queued attachments now that the expense exists.
      const expenseId = res.data?.expense_id;
      if (expenseId && pendingExpenseFiles.length > 0) {
        try {
          const fd = new FormData();
          pendingExpenseFiles.forEach(f => fd.append('files', f));
          await axios.post(`${API}/expenses/${expenseId}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) {
          toast.error(t('attachmentUploadFailed') || 'Some files failed to upload');
        }
      }
      toast.success(t('savedSuccess'));
      setExpenseDialogOpen(false);
      setPendingExpenseFiles([]);
      // Phase 316 — Force-refresh ledgers + source balances before
      // fetchAll fires so the new row appears immediately.
      try {
        const [clRes, blRes, sbRes, fbRes] = await Promise.all([
          axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=cash`),
          axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=bank`),
          axios.get(`${API}/buildings/${buildingId}/source-balances`),
          axios.get(`${API}/buildings/${buildingId}/fund-balances`),
        ]);
        setCashLedger(clRes.data);
        setBankLedger(blRes.data);
        setSourceBalances(sbRes.data);
        setFundBalances(fbRes.data);
      } catch {}
      fetchAll();
      if (isInsurance) fetchInsuranceStatement();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  // Fetch paid installments for insurance dropdown
  const fetchPaidInstallments = async (insuranceId) => {
    if (!insuranceId) { setPaidInstallments([]); return; }
    try {
      const res = await axios.get(`${API}/insurance/${insuranceId}/paid-installments`);
      setPaidInstallments(res.data || []);
    } catch { setPaidInstallments([]); }
  };

  // Recurring Expenses CRUD
  const fetchRecurringExpenses = async () => {
    try {
      const res = await axios.get(`${API}/recurring-expenses?building_id=${buildingId}`);
      setRecurringExpenses(res.data || []);
    } catch { setRecurringExpenses([]); }
  };

  React.useEffect(() => {
    if (activeTab === 'expenses' && buildingId) fetchRecurringExpenses();
  }, [activeTab, buildingId]);

  const openRecurringDialog = (rec = null) => {
    if (rec) {
      setEditingRecurring(rec);
      setRecurringForm({ category: rec.category || '204', amount: rec.amount || 0, recurring_day: String(rec.recurring_day || 1), vendor_id: rec.vendor_id || '', notes: rec.notes || '', reference_number: rec.reference_number || '' });
    } else {
      setEditingRecurring(null);
      setRecurringForm({ category: '204', amount: 0, recurring_day: '1', vendor_id: '', notes: '', reference_number: '' });
    }
    setRecurringDialogOpen(true);
  };

  const handleRecurringSave = async (e) => {
    e.preventDefault();
    try {
      if (editingRecurring) {
        await axios.put(`${API}/recurring-expenses/${editingRecurring.expense_id}`, { ...recurringForm, amount: parseFloat(recurringForm.amount) || 0, recurring_day: parseInt(recurringForm.recurring_day) || 1 });
      } else {
        await axios.post(`${API}/recurring-expenses`, { ...recurringForm, building_id: buildingId, amount: parseFloat(recurringForm.amount) || 0, recurring_day: parseInt(recurringForm.recurring_day) || 1 });
      }
      toast.success(t('savedSuccess'));
      setRecurringDialogOpen(false);
      fetchRecurringExpenses();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleRecurringDelete = async (expId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API}/recurring-expenses/${expId}`);
      toast.success(t('savedSuccess'));
      fetchRecurringExpenses();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // Fetch insurance statement for the Insurance tab
  const fetchInsuranceStatement = async () => {
    if (!building?.insurance_id) { setInsuranceStatement(null); return; }
    try {
      const res = await axios.get(`${API}/insurance/${building.insurance_id}/statement`);
      setInsuranceStatement(res.data);
    } catch { setInsuranceStatement(null); }
  };

  // Fetch insurance history for the building
  const fetchInsuranceHistory = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/insurance-history`);
      setInsuranceHistory(res.data);
    } catch { setInsuranceHistory([]); }
  };

  // Fetch statement for a specific insurance policy (lazy load on expand).
  // Phase 1011 — Accept a `force` flag so callers can bypass the cache
  // after editing the underlying insurance record (edit / cancel /
  // reactivate / installment payment).
  const fetchStatementForPolicy = async (insuranceId, { force = false } = {}) => {
    if (!force && insuranceStatements[insuranceId]) return; // already fetched
    try {
      const res = await axios.get(`${API}/insurance/${insuranceId}/statement`);
      setInsuranceStatements(prev => ({ ...prev, [insuranceId]: res.data }));
    } catch { /* ignore */ }
  };

  // Phase 1011 — Invalidate the whole cache and refetch expanded policy
  // so Installment Tracking always mirrors the latest DB state.
  const invalidateInsuranceStatements = (insuranceId = null) => {
    setInsuranceStatements(prev => {
      if (!insuranceId) return {};
      const { [insuranceId]: _drop, ...rest } = prev;
      return rest;
    });
    if (expandedInsuranceId && (!insuranceId || insuranceId === expandedInsuranceId)) {
      fetchStatementForPolicy(expandedInsuranceId, { force: true });
    }
  };

  const handleExpandInsurance = (insuranceId) => {
    if (expandedInsuranceId === insuranceId) {
      setExpandedInsuranceId(null);
    } else {
      setExpandedInsuranceId(insuranceId);
      fetchStatementForPolicy(insuranceId);
    }
  };

  const handleCancelInsurance = async () => {
    if (!cancellingInsuranceId) return;
    try {
      await axios.post(`${API}/insurance/${cancellingInsuranceId}/cancel`);
      toast.success(t('savedSuccess'));
      setCancelInsuranceDialogOpen(false);
      invalidateInsuranceStatements(cancellingInsuranceId);
      setCancellingInsuranceId(null);
      fetchInsuranceHistory();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleReactivateInsurance = async (insuranceId) => {
    try {
      await axios.post(`${API}/insurance/${insuranceId}/reactivate`);
      toast.success(t('savedSuccess'));
      invalidateInsuranceStatements(insuranceId);
      fetchInsuranceHistory();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // Phase 315 — Delete any row from the Cash / Bank ledger view.
  const handleDeleteLedgerEntry = async (entry) => {
    if (entry.is_opening_balance) {
      toast.error(t('cannotDeleteOpeningBalance') || 'Cannot delete opening balance');
      return;
    }
    const etype = entry.type;
    if (!etype) return;
    const labelByType = {
      apartment_payment: t('apartmentReceipt') || 'apartment receipt',
      expense_payment: t('expensePayment') || 'expense payment',
      elevator_payment: t('elevatorPayment') || 'elevator payment',
      repair_payment: t('repairPayment') || 'repair payment',
    };
    const what = labelByType[etype] || 'entry';
    if (!window.confirm(`${t('confirmDelete') || 'Delete this'} ${what}?\n\n${entry.notes || ''}\n€${(entry.amount_in || entry.amount_out || 0).toFixed(2)}`)) return;
    try {
      const params = new URLSearchParams({ entry_type: etype, ref_id: entry.ref_id || '' });
      if (entry.expense_id) params.append('expense_id', entry.expense_id);
      if (entry.repair_id) params.append('repair_id', entry.repair_id);
      await axios.delete(`${API}/buildings/${buildingId}/ledger-entry?${params.toString()}`);
      toast.success(t('deletedSuccess') || 'Deleted');
      try {
        const [clRes, blRes, sbRes, fbRes] = await Promise.all([
          axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=cash`),
          axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=bank`),
          axios.get(`${API}/buildings/${buildingId}/source-balances`),
          axios.get(`${API}/buildings/${buildingId}/fund-balances`),
        ]);
        setCashLedger(clRes.data);
        setBankLedger(blRes.data);
        setSourceBalances(sbRes.data);
        setFundBalances(fbRes.data);
      } catch {}
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred'));
    }
  };


  const handleDeleteExpense = async () => {
    if (!deletingExpense) return;
    try {
      await axios.delete(`${API}/expenses/${deletingExpense.expense_id}`);
      toast.success(t('deleteSuccess'));
      setDeleteExpDialogOpen(false);
      setDeletingExpense(null);
      // Phase 316 — Force-refresh ledgers + source balances synchronously.
      try {
        const [clRes, blRes, sbRes, fbRes] = await Promise.all([
          axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=cash`),
          axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=bank`),
          axios.get(`${API}/buildings/${buildingId}/source-balances`),
          axios.get(`${API}/buildings/${buildingId}/fund-balances`),
        ]);
        setCashLedger(clRes.data);
        setBankLedger(blRes.data);
        setSourceBalances(sbRes.data);
        setFundBalances(fbRes.data);
      } catch {}
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const openEditExpense = async (exp) => {
    setEditingExpense(exp);
    setEditExpForm({
      amount: exp.amount || 0,
      date: exp.date || '',
      category: exp.category || '',
      notes: exp.notes || '',
      vendor_id: exp.vendor_id || '',
      reference_number: exp.reference_number || '',
      billing_period_start: exp.billing_period_start || '',
      billing_period_end: exp.billing_period_end || '',
      // Phase 309/373.1 — Hydrate from saved expense. Cash/Bank is the
      // independent payment-source field. Reserve Fund is a SEPARATE flag.
      // For legacy `payment_source='reserve_fund'` rows, infer the underlying
      // source: prefer Bank if the existing expense_payment carries source=bank,
      // else default to Cash so the checkbox+dropdown can be edited independently.
      paid_from_reserve_fund: !!exp.paid_from_reserve_fund || (exp.payment_source || '').toLowerCase() === 'reserve_fund',
      insurance_id: exp.insurance_id || '',
      installment_number: exp.installment_number ? String(exp.installment_number) : '',
      pay_full: false,
      payment_source: (() => {
        const ps = (exp.payment_source || '').toLowerCase();
        if (ps === 'bank') return 'bank';
        if (ps === 'cash') return 'cash';
        // Legacy 'reserve_fund' rows: fall back to the underlying source if
        // tracked, else default to cash.
        return (exp.payment_source_underlying || 'cash');
      })(),
      // Phase 708 — Hydrate structured Reserve Fund Deduction fields so the
      // Edit dialog can keep / change the existing Reason/Description/Reference.
      reserve_fund_deduction_amount: exp.reserve_fund_deduction_amount ?? null,
      reserve_fund_reason: exp.reserve_fund_reason || '',
      reserve_fund_description: exp.reserve_fund_description || '',
      reserve_fund_reference: exp.reserve_fund_reference || '',
    });
    setPendingExpenseFiles([]);
    setExpenseAttachments([]);
    // Load saved attachments (best-effort)
    try {
      const r = await axios.get(`${API}/expenses/${exp.expense_id}/attachments`);
      setExpenseAttachments(r.data?.attachments || []);
    } catch {}
    setEditExpDialogOpen(true);
  };

  const handleEditExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!editingExpense) return;
    try {
      await axios.put(`${API}/expenses/${editingExpense.expense_id}`, {
        amount: parseFloat(editExpForm.amount) || 0,
        date: editExpForm.date,
        category: editExpForm.category,
        expense_type: CATEGORY_TO_TYPE[editExpForm.category] || 'other',
        notes: editExpForm.notes,
        vendor_id: editExpForm.vendor_id || null,
        reference_number: editExpForm.reference_number || '',
        billing_period_start: editExpForm.billing_period_start || null,
        billing_period_end: editExpForm.billing_period_end || null,
        // Phase 309/373.1 — Cash/Bank is the independent base source. Reserve Fund is a separate flag.
        payment_source: (editExpForm.payment_source === 'bank' ? 'bank' : 'cash'),
        paid_from_reserve_fund: !!editExpForm.paid_from_reserve_fund,
        // Phase 708 — Structured Reserve Fund Deduction fields (same
        // contract as Add Expense). When the RF flag is off, the
        // backend clears all four fields.
        reserve_fund_deduction_amount: editExpForm.paid_from_reserve_fund
          ? (editExpForm.reserve_fund_deduction_amount === '' || editExpForm.reserve_fund_deduction_amount === null || editExpForm.reserve_fund_deduction_amount === undefined
              ? null
              : parseFloat(editExpForm.reserve_fund_deduction_amount))
          : null,
        reserve_fund_reason: editExpForm.paid_from_reserve_fund ? (editExpForm.reserve_fund_reason || null) : null,
        reserve_fund_description: editExpForm.paid_from_reserve_fund ? (editExpForm.reserve_fund_description || '') : null,
        reserve_fund_reference: editExpForm.paid_from_reserve_fund ? (editExpForm.reserve_fund_reference || '') : null,
      });
      // Phase 308 — Upload any newly attached files.
      if (pendingExpenseFiles.length > 0) {
        try {
          const fd = new FormData();
          pendingExpenseFiles.forEach(f => fd.append('files', f));
          await axios.post(`${API}/expenses/${editingExpense.expense_id}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (uerr) {
          toast.error(t('attachmentUploadFailed') || 'Some files failed to upload');
        }
      }
      toast.success(t('savedSuccess'));
      setEditExpDialogOpen(false);
      setEditingExpense(null);
      setPendingExpenseFiles([]);
      setExpenseAttachments([]);
      // Phase 316 — Force-refresh the Cash + Bank ledgers AND source
      // balances BEFORE fetchAll fires so the UI shows the moved row
      // immediately (no stale state, no race condition).
      try {
        const [clRes, blRes, sbRes, fbRes] = await Promise.all([
          axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=cash`),
          axios.get(`${API}/buildings/${buildingId}/cash-ledger?source=bank`),
          axios.get(`${API}/buildings/${buildingId}/source-balances`),
          axios.get(`${API}/buildings/${buildingId}/fund-balances`),
        ]);
        setCashLedger(clRes.data);
        setBankLedger(blRes.data);
        setSourceBalances(sbRes.data);
        setFundBalances(fbRes.data);
      } catch {}
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // Phase 308 — Attachment helpers shared by Add + Edit Expense dialogs.
  const handlePickExpenseFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setPendingExpenseFiles(prev => [...prev, ...arr]);
  };
  const handleRemovePendingExpenseFile = (idx) => {
    setPendingExpenseFiles(prev => prev.filter((_, i) => i !== idx));
  };
  const handleRemoveSavedExpenseAttachment = async (attachmentId) => {
    if (!editingExpense) return;
    try {
      await axios.delete(`${API}/expenses/${editingExpense.expense_id}/attachments/${attachmentId}`);
      setExpenseAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
      toast.success(t('deletedSuccess'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errorOccurred'));
    }
  };
  const handleDownloadExpenseAttachment = async (attachmentId, originalName) => {
    if (!editingExpense) return;
    try {
      await downloadPdfUtil(
        `/expenses/${editingExpense.expense_id}/attachments/${attachmentId}`,
        { disposition: 'attachment' },
        originalName || 'attachment',
      );
    } catch (e) {
      toast.error(e?.message || t('errorOccurred'));
    }
  };

  // Opens invoice-based payment dialog directly for apartments
  const openAptPaymentDialog = (preselectedAptId) => {
    setPayStep('common_expense');
    setReceiptForm({ apartment_id: preselectedAptId || '', amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'cash', notes: '', expense_id: '', supplier: '' });
    setLastReceipt(null);
    setAptInvoices([]);
    setSelectedInvoices({});
    if (preselectedAptId) fetchAptInvoices(preselectedAptId);
    setAddPayDialogOpen(true);
  };

  const toggleHistory = async (aptId) => {
    if (historyOpen === aptId) { setHistoryOpen(null); return; }
    try {
      const res = await axios.get(`${API}/apartments/${aptId}/payments`);
      setPaymentHistory(res.data);
      setHistoryOpen(aptId);
    } catch (error) { toast.error(t('errorOccurred')); }
  };

  // ====== APARTMENT CRUD HANDLERS ======
  const openAddApt = () => {
    setEditingApt(null);
    setAptForm({
      apartment_number: '', owner_name: '', phone: '', email: '',
      share_percentage: 0, notes: '', monthly_fee: 0,
      opening_balance: 0, opening_balance_date: '', opening_balance_notes: '',
      reserve_fund: 0, repairs_fund: 0,
      // Phase 257 — Full parity with the Edit dialog: tenant fields,
      // payment responsibility, and CC email list must be present on
      // create too so the form is identical across all buildings.
      tenant_name: '', tenant_phone: '', tenant_email: '',
      payment_responsibility: 'owner',
      extra_emails: [],
      // Phase 294 — Contact Person (e.g. secretary, accountant, family
      // member who handles communication for the owner) + per-recipient
      // toggles deciding who actually receives invoices/statements/etc.
      contact_name: '', contact_phone: '', contact_email: '',
      email_recipients: { owner: true, contact: false, extras: [] },
    });
    setAptDialogOpen(true);
  };

  const openEditApt = (apt) => {
    setEditingApt(apt);
    setAptForm({
      apartment_number: apt.apartment_number || '',
      owner_name: apt.owner_name || '',
      phone: apt.phone || '',
      email: apt.email || '',
      share_percentage: apt.share_percentage || 0,
      notes: apt.notes || '',
      monthly_fee: apt.monthly_fee || 0,
      opening_balance: apt.opening_balance || 0,
      opening_balance_date: apt.opening_balance_date || '',
      opening_balance_notes: apt.opening_balance_notes || '',
      reserve_fund: apt.reserve_fund || 0,
      repairs_fund: apt.repairs_fund || 0,
      tenant_name: apt.tenant_name || '',
      tenant_phone: apt.tenant_phone || '',
      tenant_email: apt.tenant_email || '',
      payment_responsibility: apt.payment_responsibility || 'owner',
      extra_emails: apt.extra_emails || [],
      // Phase 294 — load Contact Person + per-recipient toggles, with
      // sensible defaults that don't change behaviour for legacy apartments.
      contact_name: apt.contact_name || '',
      contact_phone: apt.contact_phone || '',
      contact_email: apt.contact_email || '',
      email_recipients: apt.email_recipients || {
        owner: true,
        contact: !!apt.contact_email,
        extras: (apt.extra_emails || []).map(() => true),
      },
    });
    setAptDialogOpen(true);
  };

  const handleAptSubmit = async (e) => {
    e.preventDefault();
    // Phase 150 — build payload with all fields. When an Active plan governs monthly_fee,
    // we DON'T send monthly_fee so the value stays authoritative from the plan sync.
    const hasActivePlan = plansList.some(p => p.is_active);
    const payload = {
      apartment_number: aptForm.apartment_number,
      owner_name: aptForm.owner_name,
      phone: aptForm.phone,
      email: aptForm.email,
      share_percentage: parseFloat(aptForm.share_percentage) || 0,
      notes: aptForm.notes,
      opening_balance: parseFloat(aptForm.opening_balance) || 0,
      opening_balance_date: aptForm.opening_balance_date || null,
      // Phase 236 — Free-text note describing the opening balance origin.
      opening_balance_notes: aptForm.opening_balance_notes || '',
      reserve_fund: parseFloat(aptForm.reserve_fund) || 0,
      repairs_fund: parseFloat(aptForm.repairs_fund) || 0,
      // Phase 257 — Tenant + payment responsibility + CC emails must be
      // persisted on BOTH create and edit paths so the form renders
      // identical values when reopened.
      tenant_name: aptForm.tenant_name || '',
      tenant_phone: aptForm.tenant_phone || '',
      tenant_email: aptForm.tenant_email || '',
      payment_responsibility: aptForm.payment_responsibility || 'owner',
      extra_emails: (aptForm.extra_emails || []).filter(em => (em || '').trim()),
      // Phase 294 — Contact Person + per-recipient toggles
      contact_name: aptForm.contact_name || '',
      contact_phone: aptForm.contact_phone || '',
      contact_email: aptForm.contact_email || '',
      email_recipients: aptForm.email_recipients || { owner: true, contact: false, extras: [] },
    };
    if (!hasActivePlan) payload.monthly_fee = parseFloat(aptForm.monthly_fee) || 0;
    try {
      if (editingApt) {
        await axios.put(`${API}/apartments/${editingApt.apartment_id}`, payload);
        toast.success(t('savedSuccess'));
      } else {
        await axios.post(`${API}/buildings/${buildingId}/apartments`, { building_id: buildingId, ...payload, monthly_fee: payload.monthly_fee ?? 0 });
        toast.success(t('savedSuccess'));
      }
      setAptDialogOpen(false);
      setEditingApt(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDeleteApt = async () => {
    if (!deletingApt) return;
    try {
      await axios.delete(`${API}/apartments/${deletingApt.apartment_id}`);
      toast.success(t('deleteSuccess'));
      setDeleteAptDialogOpen(false);
      setDeletingApt(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // Fetch period-filtered apartment summary
  const fetchFilteredAptSummary = async () => {
    let dateFrom = '', dateTo = '';
    if (aptPeriodFilter === 'month') {
      const y = aptPeriodYear;
      const m = String(aptPeriodMonth).padStart(2, '0');
      const lastDay = new Date(y, aptPeriodMonth, 0).getDate();
      dateFrom = `${y}-${m}-01`;
      dateTo = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
    } else if (aptPeriodFilter === 'year') {
      dateFrom = `${aptPeriodYear}-01-01`;
      dateTo = `${aptPeriodYear}-12-31`;
    } else if (aptPeriodFilter === 'custom' && aptPeriodFrom && aptPeriodTo) {
      dateFrom = aptPeriodFrom;
      dateTo = aptPeriodTo;
    }
    if (aptPeriodFilter === 'all' || !dateFrom) {
      setFilteredAptSummary(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/period-summary?date_from=${dateFrom}&date_to=${dateTo}`);
      setFilteredAptSummary(res.data);
    } catch { setFilteredAptSummary(null); }
  };

  useEffect(() => { fetchFilteredAptSummary(); }, [aptPeriodFilter, aptPeriodMonth, aptPeriodYear, aptPeriodFrom, aptPeriodTo, buildingId]); // eslint-disable-line

  // Phase 331 — Click "Export PDF" → open column-selection dialog first.
  const openApartmentsExportDialog = () => {
    setAptExportCols(loadAptExportCols());
    setAptExportDialogOpen(true);
  };

  // Download apartments PDF (called after user confirms column selection)
  const downloadApartmentsPdf = async () => {
    if (!aptExportCols || aptExportCols.length === 0) {
      toast.error(t('selectAtLeastOneColumn') || 'Select at least one column');
      return;
    }
    // Persist selection per building.
    try { localStorage.setItem(APT_EXPORT_LS_KEY, JSON.stringify(aptExportCols)); } catch { /* noop */ }

    let dateFrom = '', dateTo = '';
    if (aptPeriodFilter === 'month') {
      const y = aptPeriodYear;
      const m = String(aptPeriodMonth).padStart(2, '0');
      const lastDay = new Date(y, aptPeriodMonth, 0).getDate();
      dateFrom = `${y}-${m}-01`;
      dateTo = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
    } else if (aptPeriodFilter === 'year') {
      dateFrom = `${aptPeriodYear}-01-01`;
      dateTo = `${aptPeriodYear}-12-31`;
    } else if (aptPeriodFilter === 'custom' && aptPeriodFrom && aptPeriodTo) {
      dateFrom = aptPeriodFrom;
      dateTo = aptPeriodTo;
    }
    try {
      // Phase 331 — Preserve selected order in the canonical APT_EXPORT_COLS order.
      const orderedCols = APT_EXPORT_COLS.filter(c => aptExportCols.includes(c));
      let url = `${API}/buildings/${buildingId}/apartments/pdf?lang=${language}&disposition=attachment&columns=${orderedCols.join(',')}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setAptExportDialogOpen(false);
    } catch { toast.error(t('errorOccurred')); }
  };

  // Phase 337 — One-click View PDF. Opens the preview modal directly using
  // the columns saved per-building in localStorage (defaults to all 8). No
  // intermediate column-selector dialog — keeps parity with the other PDF
  // viewers in the app.
  const viewRepairPdf = async (rep) => {
    if (!rep) return;
    setRepairExportTarget(rep);
    const cols = loadRepairExportCols();
    // Preserve canonical order.
    const orderedCols = REPAIR_EXPORT_COLS.filter(c => cols.includes(c));
    // Clean up any prior blob before fetching the new one.
    if (repairPreviewUrl) {
      try { URL.revokeObjectURL(repairPreviewUrl); } catch { /* noop */ }
      setRepairPreviewUrl('');
    }
    setRepairPreviewLoading(true);
    setRepairPreviewOpen(true);
    try {
      // Phase 735.11 — Use the shared blob-URL pipeline (cookie auth, no
      // `?token=` query string) so adblockers / Capacitor cannot block it.
      const objUrl = await getPdfBlobUrl(
        `/buildings/${buildingId}/repairs/${rep.repair_id}/statement-pdf`,
        { columns: orderedCols.join(','), lang: language },
      );
      setRepairPreviewUrl(objUrl);
    } catch (err) {
      toast.error(err?.message || t('errorOccurred') || 'Failed to generate PDF');
      setRepairPreviewOpen(false);
    } finally {
      setRepairPreviewLoading(false);
    }
  };

  // Phase 336 — Trigger download of the currently-previewed PDF.
  const downloadRepairPdfFromPreview = () => {
    if (!repairPreviewUrl || !repairExportTarget) return;
    const a = document.createElement('a');
    a.href = repairPreviewUrl;
    a.download = `repair-statement-${repairExportTarget.repair_id}.pdf`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch { /* noop */ } }, 800);
  };

  // Phase 336 — Cleanup blob URL when preview modal closes.
  const closeRepairPreview = () => {
    setRepairPreviewOpen(false);
    if (repairPreviewUrl) {
      try { URL.revokeObjectURL(repairPreviewUrl); } catch { /* noop */ }
      setRepairPreviewUrl('');
    }
  };

  // Notes CRUD
  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNote) {
        await axios.put(`${API}/notes/${editingNote.note_id}`, { text: noteText });
      } else {
        await axios.post(`${API}/buildings/${buildingId}/notes`, { building_id: buildingId, text: noteText });
      }
      toast.success(t('savedSuccess'));
      setNoteDialogOpen(false);
      setEditingNote(null);
      setNoteText('');
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDeleteNote = async () => {
    try {
      await axios.delete(`${API}/notes/${deletingNote.note_id}`);
      toast.success(t('deletedSuccess'));
      setDeleteNoteDialogOpen(false);
      setDeletingNote(null);
      fetchAll();
    } catch (error) { toast.error(t('errorOccurred')); }
  };

  const openNoteDialog = (note = null) => {
    setEditingNote(note);
    setNoteText(note ? note.text : '');
    setNoteDialogOpen(true);
  };

  // Insurance CRUD
  const openInsuranceDialog = (isRenew = false, policyToEdit = null) => {
    if (policyToEdit && !isRenew) {
      // Edit specific existing policy
      setEditingInsurance(true);
      setEditingInsuranceId(policyToEdit.insurance_id);
      setRenewingInsuranceId(null);
      const ia = policyToEdit.insured_amounts || {};
      setInsuranceForm({
        company_name: policyToEdit.company_name || '',
        insurance_company_id: policyToEdit.insurance_company_id || '',
        policy_number: policyToEdit.policy_number || '',
        policy_start_date: policyToEdit.policy_start_date || '',
        policy_end_date: policyToEdit.policy_end_date || '',
        notes: policyToEdit.notes || '',
        total_premium: String(policyToEdit.total_premium || ''),
        num_installments: String(policyToEdit.num_installments || '1'),
        insured_building: String(ia.building || ''),
        insured_common_areas: String(ia.common_areas || ''),
        insured_outdoor: String(ia.outdoor || ''),
        insured_civil_liability: String(ia.civil_liability || ''),
        insured_excess: String(ia.excess || ''),
      });
    } else if (isRenew && policyToEdit) {
      // Renew from specific policy: pre-fill company, set new start from old end
      setEditingInsurance(false);
      setEditingInsuranceId(null);
      setRenewingInsuranceId(policyToEdit.insurance_id);
      const ia = policyToEdit.insured_amounts || {};
      setInsuranceForm({
        company_name: policyToEdit.company_name || '',
        insurance_company_id: policyToEdit.insurance_company_id || '',
        policy_number: '',
        policy_start_date: policyToEdit.policy_end_date || new Date().toISOString().split('T')[0],
        policy_end_date: '',
        notes: '',
        total_premium: String(policyToEdit.total_premium || ''),
        num_installments: String(policyToEdit.num_installments || '1'),
        insured_building: String(ia.building || ''),
        insured_common_areas: String(ia.common_areas || ''),
        insured_outdoor: String(ia.outdoor || ''),
        insured_civil_liability: String(ia.civil_liability || ''),
        insured_excess: String(ia.excess || ''),
      });
    } else {
      // New (no previous policy)
      setEditingInsurance(false);
      setEditingInsuranceId(null);
      setRenewingInsuranceId(null);
      setInsuranceForm({
        company_name: '', insurance_company_id: '', policy_number: '',
        policy_start_date: new Date().toISOString().split('T')[0], policy_end_date: '', notes: '',
        total_premium: '', num_installments: '1',
        insured_building: '', insured_common_areas: '', insured_outdoor: '',
    insured_civil_liability: '', insured_excess: '',
      });
    }
    setInsuranceDialogOpen(true);
  };

  const handleInsuranceSubmit = async (e) => {
    e.preventDefault();
    try {
      const insuredAmounts = {};
      if (insuranceForm.insured_building) insuredAmounts.building = parseFloat(insuranceForm.insured_building);
      if (insuranceForm.insured_common_areas) insuredAmounts.common_areas = parseFloat(insuranceForm.insured_common_areas);
      if (insuranceForm.insured_outdoor) insuredAmounts.outdoor = parseFloat(insuranceForm.insured_outdoor);
      // Phase 311 — New insured fields: Civil Liability + Excess.
      if (insuranceForm.insured_civil_liability) insuredAmounts.civil_liability = parseFloat(insuranceForm.insured_civil_liability);
      if (insuranceForm.insured_excess) insuredAmounts.excess = parseFloat(insuranceForm.insured_excess);
      
      const payload = {
        building_id: buildingId,
        company_name: insuranceForm.company_name,
        insurance_company_id: insuranceForm.insurance_company_id,
        policy_number: insuranceForm.policy_number,
        policy_start_date: insuranceForm.policy_start_date,
        policy_end_date: insuranceForm.policy_end_date,
        total_premium: parseFloat(insuranceForm.total_premium) || 0,
        num_installments: parseInt(insuranceForm.num_installments) || 1,
        insured_amounts: insuredAmounts,
        notes: insuranceForm.notes,
      };
      if (editingInsurance && editingInsuranceId) {
        // Update existing policy
        await axios.put(`${API}/insurance/${editingInsuranceId}`, payload);
      } else if (renewingInsuranceId) {
        // Create renewal linked to previous
        await axios.post(`${API}/insurance/${renewingInsuranceId}/renew`, payload);
      } else {
        // Create brand new
        await axios.post(`${API}/insurance`, payload);
      }
      toast.success(t('savedSuccess'));
      setInsuranceDialogOpen(false);
      const invalidatedId = editingInsuranceId || null;
      setRenewingInsuranceId(null);
      setEditingInsuranceId(null);
      // Phase 1011 — Invalidate installment tracking cache so the newly
      // saved total_premium / num_installments / installment_amount
      // are reflected immediately in the expanded panel.
      invalidateInsuranceStatements(invalidatedId);
      fetchInsuranceHistory();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDeleteInsurance = async () => {
    if (!deletingInsurance) return;
    try {
      await axios.delete(`${API}/insurance/${deletingInsurance.insurance_id}`);
      toast.success(t('deletedSuccess'));
      setDeleteInsuranceDialogOpen(false);
      invalidateInsuranceStatements(deletingInsurance.insurance_id);
      setDeletingInsurance(null);
      fetchInsuranceHistory();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleInsuranceCompanySelect = (companyId) => {
    const company = insuranceCompanies.find(c => c.company_id === companyId);
    if (company) {
      setInsuranceForm({
        ...insuranceForm,
        insurance_company_id: companyId,
        company_name: company.name,
      });
    }
  };

  // ==================== COMMITTEE ROLES ====================
  // Edit Building details
  const openEmailDialog = (pdfUrl, filename, defaultSubject = '', defaultEmail = '') => {
    setEmailDialogProps({ pdfUrl, filename, defaultSubject, defaultEmail });
    setEmailDialogOpen(true);
  };

  const openEditBldgDialog = () => {
    setEditBldgForm({
      name: building?.name || '',
      // Phase 235 — Pre-fill structured address fields, falling back to
      // legacy `location` for older buildings on first edit.
      street_address: building?.street_address || building?.location || '',
      zip_code: building?.zip_code || '',
      city: building?.city || '',
      country: building?.country || '',
      building_type: building?.building_type || 'managed',
      management_fee: building?.management_fee || 0,
      management_vat_rate: building?.management_vat_rate ?? 19,
      cleaning_fee: building?.cleaning_fee || 0,
      cleaning_vat_rate: building?.cleaning_vat_rate ?? 19,
      cleaning_charge_mode: building?.cleaning_charge_mode || 'per_cleaning',
      managers: building?.managers || [],
      notes: building?.notes || '',
      // Phase 239 — Reminder cadence settings (defaults match common AR practice).
      reminder_enabled: building?.reminder_enabled || false,
      reminder_first_days: building?.reminder_first_days ?? 5,
      reminder_second_days: building?.reminder_second_days ?? 15,
      reminder_final_days: building?.reminder_final_days ?? 30,
      // Phase 337 — Manager/Committee labels for Email Templates auto-fill.
      manager_name: building?.manager_name || '',
      committee_name: building?.committee_name || '',
    });
    setEditBldgDialogOpen(true);
  };

  const handleEditBldgSubmit = async (e) => {
    e.preventDefault();
    try {
      // Phase 252 — Strip contact (managers) + billing/pricing fields from
      // the payload. These were removed from the Edit Building dialog UI;
      // the existing values on the building doc are preserved server-side
      // because we never PATCH them. Any prior values remain untouched.
      const {
        managers: _mgrs,
        cleaning_fee: _cf,
        cleaning_vat_rate: _cv,
        cleaning_charge_mode: _cm,
        ...payload
      } = editBldgForm;
      await axios.patch(`${API}/buildings/${buildingId}`, payload);
      toast.success(t('savedSuccess'));
      setEditBldgDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const openCommitteeDialog = () => {
    const c = building?.committee || {};
    const roleDefault = { name: '', apartment_id: '', is_authorized: false, authorized_by: '', notes: '' };
    setCommitteeForm({
      chairman: { ...roleDefault, ...c.chairman },
      treasurer: { ...roleDefault, ...c.treasurer },
      secretary: { ...roleDefault, ...c.secretary },
    });
    setCommitteeDialogOpen(true);
  };

  const handleCommitteeSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/buildings/${buildingId}/committee`, committeeForm);
      toast.success(t('savedSuccess'));
      setCommitteeDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleCommitteeRoleChange = (role, field, value) => {
    setCommitteeForm(prev => ({
      ...prev,
      [role]: { ...prev[role], [field]: value }
    }));
  };

  const handleCommitteeApartmentSelect = (role, apartmentId) => {
    if (apartmentId === '__manual__') {
      setCommitteeForm(prev => ({ ...prev, [role]: { ...prev[role], apartment_id: '', name: '' } }));
      return;
    }
    const apt = apartments.find(a => a.apartment_id === apartmentId);
    if (apt) {
      setCommitteeForm(prev => ({
        ...prev,
        [role]: { ...prev[role], apartment_id: apartmentId, name: apt.owner_name || '' }
      }));
    }
  };

  // ==================== ELEVATOR CERTIFICATION CRUD ====================
  // New logic (cycles):
  // - "Add Certification" creates a brand-new record (new cycle). Does not touch previous records.
  // - "Edit" mutates the current record in-place via PUT. Only allowed when result ≠ 'passed'.
  // - Passed records are LOCKED (server enforces 409 on PUT; UI hides the Edit button).
  // - Previous cycles remain intact as separate records in history.
  const openElevCertDialog = async (cert = null) => {
    if (cert) {
      // EDIT mode — prefill from the existing record
      setEditingElevCert(cert);
      const nc = cert.net_cost || 0;
      const vr = cert.vat_rate !== undefined ? cert.vat_rate : 19;
      // Phase 506 — Fetch attachments meta (without base64 blobs) so the
      // dialog can list / preview / delete uploaded files.
      let atts = [];
      try {
        const r = await axios.get(`${API}/buildings/${buildingId}/elevator-certifications/${cert.cert_id}/attachments`);
        atts = Array.isArray(r.data) ? r.data : [];
      } catch { /* attachments list is best-effort */ }
      setElevCertForm({
        company_name: cert.company_name || '',
        certificate_number: cert.certificate_number || '',
        inspection_date: cert.inspection_date || '',
        certification_date: cert.certification_date || '',
        next_inspection_date: cert.next_inspection_date || '',
        inspector_name: cert.inspector_name || '',
        result: cert.result || 'in_progress',
        failure_reasons: cert.failure_reasons || (cert.failure_reason ? cert.failure_reason.split(',').map(s => s.trim()).filter(Boolean) : []),
        notes: cert.notes || '',
        net_cost: nc,
        vat_rate: vr,
        vat_amount: parseFloat((nc * vr / 100).toFixed(2)),
        total_cost: parseFloat((nc * (1 + vr / 100)).toFixed(2)),
        followup_of: null,
        supersedes: null,
        repair_date: '',
        attachments: atts,
      });
    } else {
      // ADD mode — brand new cycle
      setEditingElevCert(null);
      setElevCertForm({
        company_name: '',
        certificate_number: '',
        inspection_date: new Date().toISOString().split('T')[0],
        certification_date: '',
        next_inspection_date: '',
        inspector_name: '',
        result: 'in_progress',
        failure_reasons: [],
        notes: '',
        net_cost: 0,
        vat_rate: 19,
        vat_amount: 0,
        total_cost: 0,
        followup_of: null,
        supersedes: null,
        repair_date: '',
        attachments: [],
      });
    }
    setNewFailureReason('');
    setElevCertDialogOpen(true);
  };

  // Strip fields that must NOT be submitted based on the chosen result
  const buildCertPayload = (form) => {
    const base = {
      company_name: form.company_name,
      inspection_date: form.inspection_date || new Date().toISOString().split('T')[0],
      result: form.result,
      notes: form.notes,
      // Amount fields captured for any status (cost is an invoice, independent of result)
      net_cost: form.net_cost,
      vat_rate: form.vat_rate,
      vat_amount: form.vat_amount,
      total_cost: form.total_cost,
    };
    if (form.result === 'passed') {
      base.certificate_number = form.certificate_number;
      base.certification_date = form.certification_date;
      base.inspector_name = form.inspector_name;
      base.next_inspection_date = form.next_inspection_date; // required for Passed since Phase 78
    } else if (form.result === 'failed') {
      base.failure_reasons = form.failure_reasons || [];
      base.next_inspection_date = form.next_inspection_date;
      base.inspector_name = form.inspector_name;
    }
    return base;
  };

  const [elevCertSaving, setElevCertSaving] = useState(false);
  const handleElevCertSubmit = async (e) => {
    e.preventDefault();
    if (elevCertSaving) return; // Guard against double-submit
    // Client-side validation per result
    if (!elevCertForm.company_name) { toast.error(t('companyName') + ': *'); return; }
    if (!elevCertForm.inspection_date) { toast.error(t('inspectionDate') + ': *'); return; }
    if (elevCertForm.result === 'passed') {
      if (!elevCertForm.certificate_number) { toast.error(t('certificateNumber') + ': *'); return; }
      if (!elevCertForm.certification_date) { toast.error(t('certificationDate') + ': *'); return; }
      if (!elevCertForm.next_inspection_date) { toast.error(t('nextInspectionDate') + ': *'); return; }
    } else if (elevCertForm.result === 'failed') {
      if (!elevCertForm.next_inspection_date) { toast.error(t('nextInspectionDate') + ': *'); return; }
    }
    setElevCertSaving(true);
    try {
      const payload = buildCertPayload(elevCertForm);
      if (editingElevCert) {
        // EDIT — in-place PUT (no new record). Server rejects with 409 if record is already Passed.
        await axios.put(`${API}/buildings/${buildingId}/elevator-certifications/${editingElevCert.cert_id}`, payload);
      } else {
        // ADD — new cycle (POST)
        await axios.post(`${API}/buildings/${buildingId}/elevator-certifications`, payload);
      }
      toast.success(t('savedSuccess'));
      setElevCertDialogOpen(false);
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setElevCertSaving(false);
    }
  };

  const handleDeleteElevCert = async () => {
    try {
      await axios.delete(`${API}/buildings/${buildingId}/elevator-certifications/${deletingElevCert.cert_id}`);
      toast.success(t('deletedSuccess'));
      setDeleteElevCertDialogOpen(false);
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  // Phase 704 — Inspection History entry edit / delete
  const openEditHistEntry = (certId, snap) => {
    setEditingHistEntry({ cert_id: certId, snapshot: snap });
    setEditHistForm({
      result: snap.result || '',
      certificate_number: snap.certificate_number || '',
      inspector_name: snap.inspector_name || '',
      company_name: snap.company_name || '',
      next_inspection_date: snap.next_inspection_date || '',
      failure_reasons: Array.isArray(snap.failure_reasons) ? [...snap.failure_reasons] : [],
      notes: snap.notes || '',
    });
  };

  const handleSaveHistEntry = async () => {
    if (!editingHistEntry) return;
    try {
      const { cert_id, snapshot } = editingHistEntry;
      await axios.put(`${API}/buildings/${buildingId}/elevator-certifications/${cert_id}/history/${snapshot.snapshot_id}`, editHistForm);
      toast.success(t('savedSuccess') || 'Saved');
      setEditingHistEntry(null);
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDeleteHistEntry = async () => {
    if (!deletingHistEntry) return;
    try {
      const { cert_id, snapshot } = deletingHistEntry;
      await axios.delete(`${API}/buildings/${buildingId}/elevator-certifications/${cert_id}/history/${snapshot.snapshot_id}`);
      toast.success(t('deletedSuccess'));
      setDeletingHistEntry(null);
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  // ==================== ELEVATOR INVOICE CRUD ====================
  const openElevExpenseDialog = (expense = null) => {
    setAddingNewCompany(false);
    setNewCompanyName('');
    if (expense) {
      setEditingElevExpense(expense);
      const mn = expense.maintenance_notes || '';
      const parts = mn.split(' ');
      const monthsEn = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const monthsEl = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];
      let parsedMonth = '';
      let parsedYear = '';
      if (parts.length >= 2) {
        const mName = parts.slice(0, -1).join(' ');
        parsedYear = parts[parts.length - 1];
        const mIdx = monthsEn.findIndex(m => m === mName);
        const mIdxEl = monthsEl.findIndex(m => m === mName);
        if (mIdx >= 0) parsedMonth = String(mIdx + 1);
        else if (mIdxEl >= 0) parsedMonth = String(mIdxEl + 1);
      }
      // Phase 407/408 — Migrate legacy invoices on edit:
      //   - Legacy primary invoice (invoice_amount > 0 + vat_rate) becomes the maintenance line item
      //   - Modern invoices: first extra_charges item with is_maintenance:true is extracted into maintenance_line
      //   - Remaining extra_charges items (without is_maintenance) gain qty=1, price=amount if missing
      let maintenanceLine = { quantity: 1, price: 0, vat_rate: 19, category: '202' };
      let remainingExtras = [...(expense.extra_charges || [])];

      const maintIdx = remainingExtras.findIndex(ec => ec && ec.is_maintenance);
      if (maintIdx >= 0) {
        const m = remainingExtras[maintIdx];
        const mq = (m.quantity !== undefined && m.quantity !== null && m.quantity !== '') ? parseFloat(m.quantity) || 0 : 1;
        const mp = (m.price !== undefined && m.price !== null && m.price !== '') ? parseFloat(m.price) || 0 : parseFloat(m.amount || 0);
        maintenanceLine = {
          quantity: mq,
          price: mp,
          vat_rate: parseFloat(m.vat_rate ?? 19),
          category: m.category || '202',
        };
        remainingExtras.splice(maintIdx, 1);
      } else {
        const legacyNet = parseFloat(expense.invoice_amount || 0);
        if (legacyNet > 0) {
          maintenanceLine = {
            quantity: 1,
            price: legacyNet,
            vat_rate: parseFloat(expense.vat_rate ?? 19),
            category: '202',
          };
        }
      }

      const migratedLines = [];
      for (const ec of remainingExtras) {
        const hasQty = ec.quantity !== undefined && ec.quantity !== null && ec.quantity !== '';
        const hasPrice = ec.price !== undefined && ec.price !== null && ec.price !== '';
        const qty = hasQty ? parseFloat(ec.quantity) || 0 : 1;
        const price = hasPrice ? parseFloat(ec.price) || 0 : parseFloat(ec.amount || 0);
        migratedLines.push({
          description: ec.description || '',
          quantity: qty,
          price: price,
          amount: qty * price,
          vat_rate: parseFloat(ec.vat_rate ?? 19),
          category: ec.category || '203',
        });
      }
      setElevExpenseForm({
        date: expense.date,
        description: expense.description || '',
        invoice_number: expense.invoice_number || '', invoice_amount: 0,
        maintenance_notes: expense.maintenance_notes || '',
        maintenance_month: parsedMonth || String(new Date().getMonth() + 1),
        maintenance_year: parsedYear || String(new Date().getFullYear()),
        extra_charges: migratedLines,
        contract_id: expense.contract_id || '',
        vat_rate: 0,
        discount_type: expense.discount_type || 'amount',
        discount_value: parseFloat(expense.discount_value || 0),
        maintenance_line: maintenanceLine,
      });
    } else {
      setEditingElevExpense(null);
      setElevExpenseForm({
        date: new Date().toISOString().split('T')[0],
        description: '', invoice_number: '', invoice_amount: 0, maintenance_notes: '',
        maintenance_month: String(new Date().getMonth() + 1),
        maintenance_year: String(new Date().getFullYear()),
        extra_charges: [],
        contract_id: '',
        vat_rate: 0,
        discount_type: 'amount',
        discount_value: 0,
        maintenance_line: { quantity: 1, price: 0, vat_rate: 19, category: '202' },
      });
    }
    setElevExpenseDialogOpen(true);
  };

  const handleElevExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const monthsEn = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const monthsEl = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];
      const mIdx = parseInt(elevExpenseForm.maintenance_month) - 1;
      const monthName = language === 'el' ? (monthsEl[mIdx] || '') : (monthsEn[mIdx] || '');
      const maintenanceStr = monthName && elevExpenseForm.maintenance_year ? `${monthName} ${elevExpenseForm.maintenance_year}` : '';

      // Phase 407 — Normalise line items: ensure quantity, price, amount(=qty*price), vat_rate, category.
      const normalisedExtras = (elevExpenseForm.extra_charges || [])
        .filter(ec => (ec.description || '').trim().length > 0 || (parseFloat(ec.price) || 0) > 0 || (parseFloat(ec.amount) || 0) > 0)
        .map(ec => {
          const qty = parseFloat(ec.quantity) || 0;
          const price = parseFloat(ec.price) || 0;
          const amount = qty && price ? qty * price : (parseFloat(ec.amount) || 0);
          return {
            description: ec.description || '',
            quantity: qty || 1,
            price: price || amount,
            amount: amount,
            vat_rate: parseFloat(ec.vat_rate) || 0,
            category: ec.category || '202',
            is_maintenance: false,
          };
        });

      // Phase 408 — Maintenance line stays separate from extra_charges in the UI
      // but is persisted as the FIRST item in `extra_charges` tagged with
      // `is_maintenance: true`, so the existing backend breakdown helper can
      // include it in the subtotal/discount/VAT/Final Total calculation
      // alongside the extra charges. Only emitted when month + year are set.
      const mLine = elevExpenseForm.maintenance_line || {};
      const mQty = parseFloat(mLine.quantity) || 0;
      const mPrice = parseFloat(mLine.price) || 0;
      const hasMaintenance = !!(maintenanceStr) && (mQty > 0 || mPrice > 0);
      const combinedLines = [];
      if (hasMaintenance) {
        combinedLines.push({
          description: `${t('monthlyMaintenance') || 'Monthly Maintenance'} - ${maintenanceStr}`,
          quantity: mQty || 1,
          price: mPrice,
          amount: (mQty || 1) * mPrice,
          vat_rate: parseFloat(mLine.vat_rate) || 0,
          category: mLine.category || '202',
          is_maintenance: true,
        });
      }
      combinedLines.push(...normalisedExtras);

      const payload = {
        date: elevExpenseForm.date,
        description: elevExpenseForm.description,
        invoice_number: elevExpenseForm.invoice_number,
        maintenance_notes: maintenanceStr,
        extra_charges: combinedLines,
        contract_id: elevExpenseForm.contract_id || null,
        // Phase 407 — All amount/VAT lives in `extra_charges` line items now.
        vat_rate: 0,
        discount_type: elevExpenseForm.discount_type || 'amount',
        discount_value: parseFloat(elevExpenseForm.discount_value) || 0,
      };
      if (editingElevExpense) {
        if ((editingElevExpense.payment_count || 0) === 0) {
          payload.invoice_amount = 0;
        }
        await axios.put(`${API}/elevator/expenses/${editingElevExpense.expense_id}`, payload);
      } else {
        payload.building_id = buildingId;
        payload.invoice_amount = 0;
        await axios.post(`${API}/elevator/expenses`, payload);
      }
      toast.success(t('savedSuccess'));
      setElevExpenseDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDeleteElevExpense = async () => {
    try {
      await axios.delete(`${API}/elevator/expenses/${deletingElevExpense.expense_id}`);
      toast.success(t('deletedSuccess'));
      setDeleteElevExpenseDialogOpen(false);
      setDeletingElevExpense(null);
      fetchAll();
    } catch { toast.error(t('errorOccurred')); }
  };

  // ==================== ELEVATOR INVOICE PAYMENTS ====================
  const handleElevSavePayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(elevPaymentForm.amount);
    if (!amount || amount <= 0) { toast.error(t('amount') + ' > 0'); return; }
    // Build allocations as array of {expense_id, amount, category}.
    // Keys are "${expense_id}|${category}" for per-category allocations
    // (new UI). Legacy keys without '|' fall back to no category (backend will
    // proportional-split by invoice composition).
    const allocArr = Object.entries(elevPaymentForm.allocations || {})
      .map(([key, v]) => {
        const amt = parseFloat(v) || 0;
        if (amt <= 0) return null;
        // Phase 735.34 — Per-invoice Reserve Fund deduction. When
        // `alloc_reserve[key]` is true, tag this allocation entry with
        // `paid_from_reserve: true` so the backend records a Reserve
        // Fund deduction for THIS invoice only. Other allocations in the
        // same payment stay unchanged (paid from cash/bank normally).
        const paidFromReserve = !!(elevPaymentForm.alloc_reserve && elevPaymentForm.alloc_reserve[key]);
        if (key.includes('|')) {
          const [eid, cat] = key.split('|');
          return { expense_id: eid, amount: amt, category: cat, paid_from_reserve: paidFromReserve };
        }
        return { expense_id: key, amount: amt, paid_from_reserve: paidFromReserve };
      })
      .filter(Boolean);
    try {
      const commonPayload = {
        amount,
        date: elevPaymentForm.date,
        notes: elevPaymentForm.notes,
        reference: elevPaymentForm.reference,
        payment_number: elevPaymentForm.payment_number || undefined,
        payment_source: elevPaymentForm.payment_source,
        paid_from_reserve_fund: elevPaymentForm.paid_from_reserve_fund || false,
        allocations: allocArr,
      };
      if (editingElevPayment) {
        await axios.put(`${API}/elevator/expense-payments/${editingElevPayment.payment_id}`, commonPayload);
      } else if (selectedElevExpense) {
        await axios.post(`${API}/elevator/expenses/${selectedElevExpense.expense_id}/payments`, commonPayload);
      } else {
        // Standalone payment — may have allocations across multiple invoices
        await axios.post(`${API}/elevator/payments`, { ...commonPayload, building_id: buildingId });
      }
      toast.success(t('savedSuccess'));
      setElevPaymentDialogOpen(false);
      setEditingElevPayment(null);
      if (selectedElevExpense) {
        const res = await axios.get(`${API}/elevator/expenses/${selectedElevExpense.expense_id}/payments`);
        setElevExpensePayments(res.data);
      }
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openElevViewPayments = async (expense) => {
    setSelectedElevExpense(expense);
    setElevViewPaymentsDialogOpen(true);
    setLoadingElevPayments(true);
    try {
      const res = await axios.get(`${API}/elevator/expenses/${expense.expense_id}/payments`);
      setElevExpensePayments(res.data);
    } catch { toast.error(t('errorOccurred')); }
    finally { setLoadingElevPayments(false); }
  };

  const confirmDeleteElevPayment = (payment) => {
    setDeletingElevPayment(payment);
    setDeleteElevPaymentDialogOpen(true);
  };

  const handleDeleteElevPayment = async () => {
    if (!deletingElevPayment) return;
    try {
      await axios.delete(`${API}/elevator/expense-payments/${deletingElevPayment.payment_id}`);
      toast.success(t('deletedSuccess'));
      setDeleteElevPaymentDialogOpen(false);
      setDeletingElevPayment(null);
      if (selectedElevExpense) {
        const res = await axios.get(`${API}/elevator/expenses/${selectedElevExpense.expense_id}/payments`);
        setElevExpensePayments(res.data);
      }
      fetchAll();
    } catch { toast.error(t('errorOccurred')); }
  };

  // Phase 382 — Attachment manager helpers
  const openAttManager = (kind, id, label, attachments) => {
    setAttManagerTarget({ kind, id, label, attachments: attachments || [] });
    setAttManagerOpen(true);
  };
  const attBaseUrl = (kind, id) => kind === 'inv'
    ? `${API}/elevator/expenses/${id}/attachments`
    : `${API}/elevator/expense-payments/${id}/attachments`;
  const attViewUrl = (kind, id, idx) => `${attBaseUrl(kind, id)}/${idx}`;
  const handleAttUpload = async (files) => {
    if (!attManagerTarget || !files || files.length === 0) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    setAttUploading(true);
    try {
      const r = await axios.post(attBaseUrl(attManagerTarget.kind, attManagerTarget.id), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttManagerTarget({ ...attManagerTarget, attachments: r.data.attachments || [] });
      toast.success(t('savedSuccess'));
      // Refresh the underlying list so the column updates without a page reload
      if (attManagerTarget.kind === 'inv') {
        const exps = await axios.get(`${API}/elevator/expenses?building_id=${buildingId}`);
        setElevExpenses(exps.data || []);
      } else {
        const ledger = await axios.get(`${API}/elevator/expenses/ledger?building_id=${buildingId}`);
        setElevLedger(ledger.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('failedToSave') || 'Failed'));
    } finally {
      setAttUploading(false);
    }
  };
  const handleAttDelete = async (idx) => {
    if (!attManagerTarget) return;
    try {
      const r = await axios.delete(`${attBaseUrl(attManagerTarget.kind, attManagerTarget.id)}/${idx}`);
      setAttManagerTarget({ ...attManagerTarget, attachments: r.data.attachments || [] });
      toast.success(t('savedSuccess'));
      if (attManagerTarget.kind === 'inv') {
        const exps = await axios.get(`${API}/elevator/expenses?building_id=${buildingId}`);
        setElevExpenses(exps.data || []);
      } else {
        const ledger = await axios.get(`${API}/elevator/expenses/ledger?building_id=${buildingId}`);
        setElevLedger(ledger.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('failedToDelete') || 'Failed'));
    }
  };

  // Phase 379 — Open Invoice / Opening Balance payment history modal
  const openElevInvHistory = async (target) => {
    // target = { kind: 'invoice', expense_id } or { kind: 'opening' }
    setElevInvHistoryOpen(true);
    setElevInvHistoryLoading(true);
    setElevInvHistory(null);
    try {
      const url = target.kind === 'opening'
        ? `${API}/elevator/opening-balance/payment-history?building_id=${buildingId}`
        : `${API}/elevator/invoices/${target.expense_id}/payment-history`;
      const r = await axios.get(url);
      setElevInvHistory(r.data);
    } catch (e) {
      toast.error(t('failedToLoad') || 'Failed to load');
    } finally {
      setElevInvHistoryLoading(false);
    }
  };

  const openEditElevPayment = async (payment) => {    setEditingElevPayment(payment);
    const amt = payment.amount ?? payment.payment ?? '';
    // Preload allocations: prefer stored array on payment; else synthesize single from expense_id.
    // For per-category allocations, use composite key `${expense_id}|${category}`.
    let allocations = {};
    // Phase 735.34 — Rebuild the per-invoice Reserve Fund checkbox state
    // from the stored allocation entries so re-opening a payment shows
    // the same ticks the user chose originally.
    let alloc_reserve = {};
    if (Array.isArray(payment.allocations) && payment.allocations.length > 0) {
      payment.allocations.forEach(a => {
        if (a.expense_id && a.amount > 0) {
          // Phase 378 — Allocation modal only manages OPENING + real invoices.
          // Skip CREDIT- targets so they don't sneak back into the save.
          if (typeof a.expense_id === 'string' && a.expense_id.startsWith('CREDIT-')) return;
          const key = a.category ? `${a.expense_id}|${a.category}` : a.expense_id;
          allocations[key] = String(a.amount);
          if (a.paid_from_reserve) alloc_reserve[key] = true;
        }
      });
    } else if (payment.expense_id && amt !== '') {
      allocations[payment.expense_id] = String(amt);
    }
    setElevPaymentForm({
      amount: amt !== '' ? String(amt) : '',
      date: payment.date || new Date().toISOString().split('T')[0],
      notes: payment.notes || '',
      payment_source: payment.payment_source || 'cash',
      paid_from_reserve_fund: payment.paid_from_reserve_fund || false,
      reference: payment.reference || '',
      payment_number: payment.payment_number || '',
      allocations,
      alloc_reserve,
    });
    try {
      // Phase 376 — Edit Payment must load payable-items so the dialog
      // shows OPENING / CREDIT rows. Backend filters out fully-paid OPENING
      // and zero-remaining credits, so we explicitly merge in any OPENING /
      // CREDIT targets that THIS payment was already allocated against —
      // otherwise the user opens Edit and the allocation row simply
      // vanishes, even though the amount is still in the form state.
      const [fbRes, sbRes, piRes] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}/fund-balances`),
        axios.get(`${API}/buildings/${buildingId}/source-balances`),
        axios.get(`${API}/elevator/payable-items?building_id=${buildingId}`),
      ]);
      setFundBalances(fbRes.data);
      setSourceBalances(sbRes.data);
      const items = Array.isArray(piRes.data?.items) ? [...piRes.data.items] : [];
      // Ensure OPENING is present if this payment had an OPENING allocation,
      // even when remaining is 0 (the user must be able to see and adjust it).
      const allocList = Array.isArray(payment.allocations) ? payment.allocations : [];
      const hadOpening = allocList.some(a => a?.expense_id === 'OPENING');
      const hasOpeningItem = items.some(it => it.kind === 'opening');
      if (hadOpening && !hasOpeningItem) {
        const obAmt = Number(piRes.data?.opening_balance || 0);
        const obDate = piRes.data?.opening_balance_date || '';
        const obPaid = Number(piRes.data?.opening_balance_paid || 0);
        const obRem = Number(piRes.data?.opening_balance_remaining || 0);
        if (obAmt > 0.005) {
          // Phase 380 — Inject OPENING with the LIVE remaining (not pre-
          // payment value). The renderer adds `allocatedNum` to derive the
          // pre-payment outstanding correctly, so passing live values keeps
          // the math from double-counting this payment's allocation.
          items.unshift({
            kind: 'opening',
            key: 'OPENING',
            date: obDate,
            reference: 'OPENING',
            description: 'Opening Balance',
            amount: obAmt,
            paid: obPaid,
            remaining: obRem,
            category: 'opening',
          });
        }
      }
      // Phase 378 — Credits are no longer allocation targets in the modal,
      // so we don't need to inject CREDIT-* items from the payment's
      // allocations. OPENING merging stays since it's a real invoice-like
      // row.
      setElevPayableItems(items);
    } catch {}
    setElevPaymentDialogOpen(true);
  };

  // ==================== ELEVATOR COMPANIES ====================
  const handleInlineAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      await axios.post(`${API}/elevator/companies`, { name: newCompanyName.trim() });
      setElevExpenseForm({ ...elevExpenseForm, company: newCompanyName.trim() });
      setAddingNewCompany(false);
      setNewCompanyName('');
      // Refresh companies
      const res = await axios.get(`${API}/elevator/companies`);
      setElevatorCompanies(res.data);
      toast.success(t('savedSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openElevCompanyDialog = (company = null) => {
    if (company) {
      setEditingElevCompany(company);
      setElevCompanyForm({ name: company.name, phone: company.phone || '', email: company.email || '', notes: company.notes || '' });
    } else {
      setEditingElevCompany(null);
      setElevCompanyForm({ name: '', phone: '', email: '', notes: '' });
    }
    setElevCompanyDialogOpen(true);
  };

  const handleElevCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingElevCompany) {
        await axios.put(`${API}/elevator/companies/${editingElevCompany.company_id}`, elevCompanyForm);
      } else {
        await axios.post(`${API}/elevator/companies`, elevCompanyForm);
      }
      toast.success(t('savedSuccess'));
      setElevCompanyDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDeleteElevCompany = async () => {
    try {
      await axios.delete(`${API}/elevator/companies/${deletingElevCompany.company_id}`);
      toast.success(t('deletedSuccess'));
      setDeleteElevCompanyDialogOpen(false);
      setDeletingElevCompany(null);
      fetchAll();
    } catch { toast.error(t('errorOccurred')); }
  };

  // Computed values
  const totalIncome = aptSummary?.total_collected || 0;
  const totalExpenses = (expSummary?.total || 0) + (elevSummary?.total_expenses || 0);
  const ob = building?.opening_balance || 0;
  const balance = ob + totalIncome - totalExpenses;

  const getStatusBadge = (s) => {
    switch (s) {
      case 'paid': return 'bg-emerald-50 text-emerald-700 border-emerald-300';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-300';
      case 'overdue': return 'bg-red-50 text-red-700 border-red-300';
      default: return 'bg-zinc-50 text-zinc-700 border-zinc-300';
    }
  };

  const getRowBg = (s) => s === 'paid' ? 'bg-emerald-50/40' : s === 'overdue' ? 'bg-red-50/40' : '';

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="text-zinc-500">{t('loading')}</div></div></Layout>;
  }

  if (!building) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="text-zinc-500">Building not found</div></div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="building-details-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-5">
          <Button variant="outline" size="sm" onClick={() => navigate('/buildings')} className="rounded-none" data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{building.name}</h1>
            <p className="text-sm text-zinc-500">
              {[building.street_address, building.zip_code, building.city, building.country].filter(Boolean).join(', ') || building.location || ''}
            </p>
          </div>
          <span className={`px-3 py-1 text-xs font-mono uppercase border ${
            building.building_type === 'managed' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'
          }`}>{t(building.building_type || 'managed')}</span>
          {isInactive && (
            <span className="px-3 py-1 text-xs font-mono uppercase border bg-red-50 text-red-600 border-red-200" data-testid="building-closed-badge">{t('closed')}</span>
          )}
          {/* Phase 207j — Manual managers edit removed. Committee is derived
              automatically from the latest meeting (active members only). */}
          {/* Phase 347 — "Generate Email" header button removed per user
              request. The Email Templates tab is the single entry point. */}
          {isAdmin && activeTab === 'overview' && (
            <Button variant="outline" size="sm" onClick={openEditBldgDialog} className="rounded-none" data-testid="edit-building-btn">
              <PencilSimple size={16} className="mr-1.5" />{t('editBuilding')}
            </Button>
          )}
        </div>

        {/* Closed Building Banner */}
        {isInactive && (
          <div className="mb-5 bg-red-50 border border-red-200 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="closed-building-banner">
            <div>
              <p className="text-sm font-semibold text-red-800">{t('closedBuildingBanner')}</p>
              <p className="text-sm text-red-600 mt-1">
                {t('closedOn')}: {formatDateDMY(building.close_date)} &mdash; {t('closeReason')}: {building.close_reason}
              </p>
            </div>
            {isAdmin && (
              <Button onClick={handleReopenBuilding} variant="outline" size="sm" className="rounded-none border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="reopen-building-btn">
                {t('reopenBuilding')}
              </Button>
            )}
          </div>
        )}

        {/* Main Content Area */}
        <div className="min-h-[70vh]">

        {/* ====== OVERVIEW TAB ====== */}
        {activeTab === 'overview' && (
          <div className="space-y-3" data-testid="overview-dashboard">
            {/* Phase 207j — Active Committee strip (derived from latest meeting) */}
            <div className="bg-white border border-zinc-200 px-4 py-2.5" data-testid="overview-committee-section">
              <div className="flex items-center gap-2 mb-2">
                <UsersThree size={14} weight="fill" className="text-zinc-500" />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Διαχειριστική Επιτροπή</h3>
                {activeCommittee?.meeting_date && (
                  <span className="text-[10px] text-zinc-400 font-mono">
                    (από συνέλευση {(() => { const p = activeCommittee.meeting_date.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : activeCommittee.meeting_date; })()})
                  </span>
                )}
              </div>
              {(activeCommittee?.members || []).length === 0 ? (
                <p className="text-xs text-zinc-400 italic">Δεν υπάρχει επιτροπή. Δημιουργήστε ένα meeting και ορίστε τα μέλη.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {activeCommittee.members.map((m, i) => {
                    const ROLE_LABELS = { president: 'Πρόεδρος', chairman: 'Πρόεδρος', treasurer: 'Ταμίας', secretary: 'Γραμματέας' };
                    const roleLabel = ROLE_LABELS[(m.role || '').toLowerCase()] || (m.role || 'Μέλος');
                    return (
                      <div key={i} className="flex items-center gap-1.5 text-xs bg-zinc-50 border border-zinc-200 px-2 py-1" data-testid={`committee-member-${i}`}>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{roleLabel}:</span>
                        <span className="font-medium text-zinc-800">{m.name || '—'}</span>
                        {m.apartment_number && <span className="text-zinc-400 font-mono">#{m.apartment_number}</span>}
                        {m.phone && <span className="text-zinc-400 font-mono">·{m.phone}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Phase 193 — Meetings info strip (Last / Next + 30-day alert) */}
            {(() => {
              const ms = overviewDash?.meetings_summary;
              if (!ms) return null;
              const last = ms.last_meeting_date || '';
              const next = ms.next_meeting_date || '';
              const daysLeft = ms.next_meeting_days_left;
              const isSoon = typeof daysLeft === 'number' && daysLeft >= 0 && daysLeft <= 30;
              const isOverdue = typeof daysLeft === 'number' && daysLeft < 0;
              const nextFmt = next ? (() => { const p = next.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : next; })() : '';
              const lastFmt = last ? (() => { const p = last.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : last; })() : '';
              return (
                <div
                  className={`bg-white border px-4 py-2.5 flex flex-wrap items-center gap-4 ${isOverdue ? 'border-red-300 bg-red-50/40' : isSoon ? 'border-amber-300 bg-amber-50/40' : 'border-zinc-200'}`}
                  data-testid="overview-meetings-strip"
                >
                  <div className="flex items-center gap-2">
                    <UsersThree size={14} weight="fill" className="text-zinc-500" />
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Συνελεύσεις</span>
                  </div>
                  {/* Last Meeting */}
                  <div className="flex items-center gap-1.5 px-3 border-l border-zinc-200">
                    <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Τελευταία</span>
                    {lastFmt ? (
                      <button
                        type="button"
                        onClick={() => setActiveTab('meetings')}
                        className="text-sm font-mono font-semibold text-zinc-900 hover:underline"
                        data-testid="overview-last-meeting"
                      >
                        {lastFmt}
                      </button>
                    ) : (
                      <span className="text-xs text-zinc-400 italic" data-testid="overview-last-meeting">Δεν έχει οριστεί</span>
                    )}
                  </div>
                  {/* Next Meeting */}
                  <div className="flex items-center gap-1.5 px-3 border-l border-zinc-200">
                    <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Επόμενη</span>
                    {nextFmt ? (
                      <button
                        type="button"
                        onClick={() => setActiveTab('meetings')}
                        className={`text-sm font-mono font-semibold hover:underline ${isOverdue ? 'text-red-700' : isSoon ? 'text-amber-700' : 'text-zinc-900'}`}
                        data-testid="overview-next-meeting"
                      >
                        {nextFmt}
                      </button>
                    ) : (
                      <span className="text-xs text-zinc-400 italic" data-testid="overview-next-meeting">Δεν έχει οριστεί</span>
                    )}
                  </div>
                  {/* 30-day alert chip */}
                  {isOverdue && (
                    <span className="ml-auto text-[11px] font-semibold px-2 py-1 bg-red-600 text-white" data-testid="next-meeting-alert">
                      Η προγραμματισμένη ημερομηνία έχει παρέλθει ({Math.abs(daysLeft)} ημέρες)
                    </span>
                  )}
                  {!isOverdue && isSoon && (
                    <span className="ml-auto text-[11px] font-semibold px-2 py-1 bg-amber-500 text-white" data-testid="next-meeting-alert">
                      Πλησιάζει η επόμενη γενική συνέλευση σε {daysLeft} {daysLeft === 1 ? 'ημέρα' : 'ημέρες'}
                    </span>
                  )}
                </div>
              );
            })()}

            {/* 1. Compact Summary Cards — single row */}
            <div className={`grid ${(overviewDash?.quick_stats?.outstanding_fund_repairs || 0) > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-3`} data-testid="quick-stats">
              <div className="bg-white border border-zinc-200 px-4 py-2.5">
                <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{t('apartments')}</p>
                <p className="text-xl font-bold font-mono text-zinc-900 mt-0.5" data-testid="stat-apartments">{apartments.length}</p>
              </div>
              <div className="bg-white border border-red-200 px-4 py-2.5 hover:bg-red-50/40 cursor-pointer transition-colors" onClick={() => { setOutstandingModalOpen(true); loadOutstandingBalances(); }} data-testid="stat-outstanding-card" title={t('viewOutstandingDetails') || 'Click to view all apartment outstanding balances'}>
                <p className="text-[10px] text-red-500 uppercase tracking-wider">{t('totalOutstanding')}</p>
                <p className="text-xl font-bold font-mono text-red-700 mt-0.5" data-testid="stat-outstanding">&euro;{(overviewDash?.quick_stats?.total_outstanding || 0).toFixed(2)}</p>
              </div>
              {(overviewDash?.quick_stats?.outstanding_fund_repairs || 0) > 0 && (
                <div className="bg-white border border-amber-200 px-4 py-2.5" data-testid="stat-fund-repairs-card">
                  <p className="text-[10px] text-amber-600 uppercase tracking-wider">{t('unpaidRepairs')}</p>
                  <p className="text-xl font-bold font-mono text-amber-700 mt-0.5" data-testid="stat-fund-repairs">&euro;{(overviewDash?.quick_stats?.outstanding_fund_repairs || 0).toFixed(2)}</p>
                </div>
              )}
              <div className="bg-white border border-zinc-200 px-4 py-2.5">
                <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{t('buildingStatus')}</p>
                <p className={`text-base font-bold mt-0.5 ${isInactive ? 'text-zinc-400' : 'text-emerald-700'}`}>{isInactive ? t('inactive') : t('active')}</p>
              </div>
            </div>

            {/* 2-column overview: LEFT = Tasks (under Overview summary), RIGHT = Alerts + Cert + Debtors + Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="ov-main-grid">
              {/* LEFT COLUMN — Tasks (placed directly under Overview panel as requested) */}
              <div className="space-y-3 lg:col-span-1" data-testid="ov-left-col">
                {/* 3. Tasks Preview */}
                <div className="bg-white border border-zinc-200" data-testid="ov-tasks-section">
                  <div className="px-4 py-2 border-b border-zinc-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ClipboardText size={14} weight="fill" className="text-zinc-500" />
                      <h3 className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('tasks')}</h3>
                      {buildingTasks.filter(t => t.status !== 'completed').length > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm">{buildingTasks.filter(t => t.status !== 'completed').length} {t('pending')}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => navigate(`/tasks?building_id=${buildingId}`)} className="text-[10px] font-medium text-zinc-500 hover:text-zinc-900 underline underline-offset-2 px-1.5 py-0.5 transition-colors" data-testid="ov-view-all-tasks-btn">{t('viewAllTasks')}</button>
                      {isAdmin && (
                        <button onClick={() => setAddTaskDialogOpen(true)} className="text-[10px] font-medium text-white bg-zinc-900 hover:bg-zinc-700 px-2 py-1 transition-colors" data-testid="ov-add-task-btn">+ {t('addTask')}</button>
                      )}
                    </div>
                  </div>
                  {buildingTasks.length === 0 ? (
                    <div className="px-4 py-4 text-xs text-zinc-400 text-center">{t('noTasks')}</div>
                  ) : (
                    <div>
                      {(() => {
                        const active = buildingTasks.filter(tk => tk.status !== 'completed');
                        const completed = buildingTasks.filter(tk => tk.status === 'completed');
                        const renderRow = (tk) => {
                          const dueDate = tk.due_date ? new Date(tk.due_date) : null;
                          const now = new Date();
                          const isCompleted = tk.status === 'completed';
                          const isOverdue = dueDate && dueDate < now && !isCompleted;
                          const isDueSoon = dueDate && !isOverdue && (dueDate - now) < 3 * 24 * 60 * 60 * 1000;
                          return (
                            <div key={tk.task_id} className={`px-4 py-2 flex items-center gap-2 hover:bg-zinc-50 cursor-pointer transition-colors ${isOverdue ? 'bg-red-50/50' : ''} ${isCompleted ? 'opacity-70' : ''}`} onClick={() => openTaskDetails(tk)} data-testid={`ov-task-${tk.task_id}`}>
                              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCompleted ? 'bg-emerald-500' : isOverdue ? 'bg-red-500' : isDueSoon ? 'bg-amber-500' : 'bg-blue-400'}`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-medium truncate ${isCompleted ? 'text-zinc-400 line-through' : 'text-zinc-700'}`}>{tk.title || tk.notes || t('task')}</p>
                              </div>
                              {tk.type === 'apartment' ? (
                                <span className="text-[9px] font-semibold px-1.5 py-0.5 flex-shrink-0 bg-blue-50 text-blue-700 border border-blue-200" data-testid={`ov-task-scope-${tk.task_id}`}>
                                  {t('apartment')}{tk.apartment_number ? ` #${tk.apartment_number}` : ''}
                                </span>
                              ) : (
                                <span className="text-[9px] font-semibold px-1.5 py-0.5 flex-shrink-0 bg-zinc-100 text-zinc-600 border border-zinc-200" data-testid={`ov-task-scope-${tk.task_id}`}>
                                  {t('building')}
                                </span>
                              )}
                              {tk.is_important && !isCompleted && (
                                <span className="text-[9px] font-semibold px-1.5 py-0.5 flex-shrink-0 bg-red-50 text-red-700 border border-red-200" data-testid={`ov-task-important-${tk.task_id}`}>
                                  {t('important')}
                                </span>
                              )}
                              {tk.due_date && (
                                <span className={`text-[10px] font-mono flex-shrink-0 ${isCompleted ? 'text-zinc-400' : isOverdue ? 'text-red-600 font-semibold' : isDueSoon ? 'text-amber-600' : 'text-zinc-400'}`}>{formatDateDMY(tk.due_date)}</span>
                              )}
                              {/* Phase 598 — 4-bucket status pill (Pending / In Progress / Waiting / Completed). */}
                              {(() => {
                                const s = (tk.status || 'pending');
                                const isInProgress = s === 'in_progress';
                                const isWaiting = s === 'waiting';
                                let tone, label;
                                if (isCompleted)        { tone = 'bg-emerald-100 text-emerald-700'; label = t('completed') || 'Completed'; }
                                else if (isInProgress)  { tone = 'bg-blue-100 text-blue-700';       label = t('statusInProgress') || t('inProgress') || 'In Progress'; }
                                else if (isWaiting)     { tone = 'bg-amber-100 text-amber-700';     label = t('statusWaiting') || 'Waiting'; }
                                else if (isOverdue)     { tone = 'bg-red-100 text-red-700';         label = t('overdue') || 'Overdue'; }
                                else if (isDueSoon)     { tone = 'bg-amber-100 text-amber-700';     label = t('dueSoon') || 'Due soon'; }
                                else                    { tone = 'bg-zinc-100 text-zinc-500';       label = t('statusPending') || t('pending') || 'Pending'; }
                                return <span className={`text-[9px] font-semibold px-1.5 py-0.5 flex-shrink-0 ${tone}`} data-testid={`ov-task-status-${tk.task_id}`}>{label}</span>;
                              })()}
                            </div>
                          );
                        };
                        return (
                          <>
                            {/* Active Tasks */}
                            <div className="px-4 py-1.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between" data-testid="ov-tasks-active-header">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t('activeTasks')}</span>
                              <span className="text-[10px] font-mono text-zinc-400">{active.length}</span>
                            </div>
                            {active.length === 0 ? (
                              <div className="px-4 py-3 text-xs text-emerald-600 text-center" data-testid="ov-tasks-active-empty">{t('allTasksComplete')}</div>
                            ) : (
                              <div className="divide-y divide-zinc-100" data-testid="ov-tasks-active-list">
                                {active.slice(0, 6).map(renderRow)}
                                {active.length > 6 && (
                                  <button onClick={() => navigate(`/tasks?building_id=${buildingId}`)} className="w-full py-1.5 text-[10px] text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors" data-testid="ov-view-more-active">{t('viewAll')} ({active.length})</button>
                                )}
                              </div>
                            )}
                            {/* Completed Tasks */}
                            <div className="px-4 py-1.5 bg-zinc-50 border-y border-zinc-200 flex items-center justify-between" data-testid="ov-tasks-completed-header">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t('completedTasks')}</span>
                              <span className="text-[10px] font-mono text-zinc-400">{completed.length}</span>
                            </div>
                            {completed.length === 0 ? (
                              <div className="px-4 py-3 text-xs text-zinc-400 text-center" data-testid="ov-tasks-completed-empty">{t('noCompletedTasks')}</div>
                            ) : (
                              <div className="divide-y divide-zinc-100" data-testid="ov-tasks-completed-list">
                                {completed.slice(0, 4).map(renderRow)}
                                {completed.length > 4 && (
                                  <button onClick={() => navigate(`/tasks?building_id=${buildingId}`)} className="w-full py-1.5 text-[10px] text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors" data-testid="ov-view-more-completed">{t('viewAll')} ({completed.length})</button>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN — Alerts, Certification, Debtors, Recent Activity */}
              <div className="space-y-3 lg:col-span-2" data-testid="ov-right-col">

            {/* 2. Alerts — Prominent, right after cards */}
            {overviewDash?.alerts && overviewDash.alerts.length > 0 && (
              <div className={`border ${overviewDash.alerts.some(a => a.severity === 'critical') ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`} data-testid="alerts-section">
                <div className={`px-4 py-2 border-b flex items-center gap-2 ${overviewDash.alerts.some(a => a.severity === 'critical') ? 'border-red-200' : 'border-amber-200'}`}>
                  <Warning size={16} weight="fill" className={overviewDash.alerts.some(a => a.severity === 'critical') ? 'text-red-600' : 'text-amber-600'} />
                  <h3 className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('alertsNotifications')}</h3>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${overviewDash.alerts.some(a => a.severity === 'critical') ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>{overviewDash.alerts.length}</span>
                </div>
                <div className="divide-y divide-zinc-200/50 max-h-[160px] overflow-y-auto">
                  {overviewDash.alerts.map((alert, idx) => (
                    <div key={idx} className="px-4 py-2 flex items-center gap-2.5" data-testid={`alert-${idx}`}>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${alert.severity === 'critical' ? 'bg-red-500' : alert.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-400'}`} />
                      <span className="text-xs flex-1">{alert.type === 'cleaning_overdue' ? t(alert.cleaning_type || 'cleaning') + ' — ' : ''}{alert.message}</span>
                      {alert.date && <span className="text-[10px] text-zinc-500 font-mono flex-shrink-0">{formatDateDMY(alert.date)}</span>}
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 flex-shrink-0 ${alert.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{alert.severity === 'critical' ? t('critical') : t('warning')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2b. Latest Elevator Certification — always reflects newest record */}
            {overviewDash?.latest_elevator_cert && (() => {
              const lec = overviewDash.latest_elevator_cert;
              const todayStr = new Date().toISOString().split('T')[0];
              const nid = lec.next_inspection_date || '';
              let daysLeft = null;
              if (nid) {
                const d = new Date(nid); const t0 = new Date(todayStr);
                daysLeft = Math.round((d - t0) / 86400000);
              }
              const isOverdue = daysLeft !== null && daysLeft < 0;
              const isDueSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
              const isFailed = lec.result === 'failed';
              const borderClr = isFailed || isOverdue ? 'border-red-300' : isDueSoon ? 'border-amber-300' : 'border-emerald-300';
              const resultLabel = lec.result === 'passed' ? t('passed') : lec.result === 'failed' ? t('failed') : lec.result === 'in_progress' ? t('inProgress') : lec.result === 'conditional' ? t('conditional') : lec.result || '';
              const resultClr = lec.result === 'passed' ? 'bg-emerald-100 text-emerald-700' : lec.result === 'failed' ? 'bg-red-100 text-red-700' : lec.result === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700';
              return (
                <div className={`bg-white border ${borderClr}`} data-testid="ov-latest-cert-section">
                  <div className="px-4 py-2 border-b border-zinc-200 flex items-center gap-2">
                    <ShieldCheck size={14} weight="fill" className={isFailed || isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-600' : 'text-emerald-600'} />
                    <h3 className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('latestCertification')}</h3>
                  </div>
                  <div className="px-4 py-2.5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{t('result')}</p>
                      <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 mt-0.5 ${resultClr}`} data-testid="ov-latest-cert-result">{resultLabel}</span>
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{t('lastInspection')}</p>
                      <p className="font-mono text-zinc-800 mt-0.5" data-testid="ov-latest-cert-inspection">{lec.inspection_date ? formatDateDMY(lec.inspection_date) : '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{t('nextInspectionDate')}</p>
                      <p className={`font-mono mt-0.5 ${isOverdue ? 'text-red-600 font-semibold' : isDueSoon ? 'text-amber-600 font-semibold' : 'text-zinc-800'}`} data-testid="ov-latest-cert-next">
                        {nid ? formatDateDMY(nid) : '—'}
                        {isOverdue && <span className="ml-1 text-[10px]">({t('overdue')})</span>}
                        {isDueSoon && !isOverdue && <span className="ml-1 text-[10px]">({t('dueSoon')})</span>}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{t('companyName')}</p>
                      <p className="text-zinc-800 mt-0.5 truncate" title={lec.company_name || ''} data-testid="ov-latest-cert-company">{lec.company_name || '—'}</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* 4. Top Outstanding Balances — clickable rows */}
            {overviewDash?.top_debtors && overviewDash.top_debtors.length > 0 && (
              <div className="bg-white border border-zinc-200" data-testid="top-debtors-section">
                <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center gap-2">
                  <CurrencyEur size={14} weight="fill" className="text-red-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('topOutstanding')}</h3>
                </div>
                <div className="divide-y divide-zinc-100">
                  {overviewDash.top_debtors.map((d, idx) => (
                    <div key={idx} onClick={() => navigate(`/apartments/${d.apartment_id || ''}`)} className="px-4 py-2 flex items-center justify-between hover:bg-zinc-50 cursor-pointer transition-colors" data-testid={`debtor-row-${idx}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold font-mono text-zinc-700">#{d.apartment_number}</span>
                        <span className="text-xs text-zinc-500">{d.owner_name || '—'}</span>
                      </div>
                      <span className="font-mono font-semibold text-sm text-red-700">&euro;{d.outstanding.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 4. Recent Activity — Side by side, compact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Recent Payments */}
              <div className="bg-white border border-zinc-200" data-testid="recent-payments-section">
                <button
                  onClick={() => setOvRecentPayOpen(!ovRecentPayOpen)}
                  className="w-full px-4 py-2 border-b border-zinc-200 flex items-center justify-between hover:bg-zinc-50 transition-colors"
                  data-testid="toggle-recent-payments"
                >
                  <h3 className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('recentPayments')}</h3>
                  {ovRecentPayOpen ? <CaretUp size={12} className="text-zinc-400" /> : <CaretDown size={12} className="text-zinc-400" />}
                </button>
                {ovRecentPayOpen && (
                  <>
                {(overviewDash?.recent_payments || []).length > 0 ? (
                  <div className="divide-y divide-zinc-100">
                    {overviewDash.recent_payments.map((p, idx) => (
                      <div key={idx} className="px-4 py-2 flex items-center justify-between" data-testid={`recent-payment-${idx}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold font-mono text-zinc-700">#{p.apartment_number}</span>
                          <span className="text-[10px] text-zinc-400">{p.owner_name}</span>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <span className="font-mono font-semibold text-xs text-emerald-700">&euro;{p.amount.toFixed(2)}</span>
                          <span className="text-[10px] text-zinc-400 font-mono">{formatDateDMY(p.date)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-xs text-zinc-400">{t('noRecentPayments')}</div>
                )}
                  </>
                )}
              </div>

              {/* Recent Expenses */}
              <div className="bg-white border border-zinc-200" data-testid="recent-expenses-section">
                <button
                  onClick={() => setOvRecentExpOpen(!ovRecentExpOpen)}
                  className="w-full px-4 py-2 border-b border-zinc-200 flex items-center justify-between hover:bg-zinc-50 transition-colors"
                  data-testid="toggle-recent-expenses"
                >
                  <h3 className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('recentExpenses')}</h3>
                  {ovRecentExpOpen ? <CaretUp size={12} className="text-zinc-400" /> : <CaretDown size={12} className="text-zinc-400" />}
                </button>
                {ovRecentExpOpen && (
                  <>
                {(overviewDash?.recent_expenses || []).length > 0 ? (
                  <div className="divide-y divide-zinc-100">
                    {overviewDash.recent_expenses.map((e, idx) => (
                      <div key={idx} className="px-4 py-2 flex items-center justify-between" data-testid={`recent-expense-${idx}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">{getCatLabel(e.category, t, customCategories)}</span>
                          {e.notes && <span className="text-[10px] text-zinc-400 truncate max-w-[120px]">{e.notes}</span>}
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <span className="font-mono font-semibold text-xs">&euro;{e.amount.toFixed(2)}</span>
                          <span className="text-[10px] text-zinc-400 font-mono">{formatDateDMY(e.date)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-xs text-zinc-400">{t('noRecentExpenses')}</div>
                )}
                  </>
                )}
              </div>
            </div>

              </div> {/* /ov-right-col */}
            </div> {/* /ov-main-grid */}

            {/* Phase 207k — Old "Committee" collapsible section REMOVED.
                Single source of truth: top-of-Overview "Διαχειριστική Επιτροπή"
                strip (auto-derived from latest meeting committee active members). */}
          </div>
        )}

        {/* ====== INCOME TAB ====== */}
        {/* ====== APARTMENTS TAB ====== */}
        {activeTab === 'apartments' && (
          <div className="space-y-6">
            {/* Period Filter */}
            <div className="bg-white border border-zinc-200 p-4" data-testid="apt-period-filter">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{t('filterByPeriod')}</label>
                  <select value={aptPeriodFilter} onChange={(e) => setAptPeriodFilter(e.target.value)} className="mt-1 block w-full border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-400" data-testid="apt-period-type">
                    <option value="all">{t('allTime')}</option>
                    <option value="month">{t('month')}</option>
                    <option value="year">{t('year')}</option>
                    <option value="custom">{t('customRange')}</option>
                  </select>
                </div>
                {aptPeriodFilter === 'month' && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-zinc-500">{t('month')}</label>
                      <select value={aptPeriodMonth} onChange={(e) => setAptPeriodMonth(Number(e.target.value))} className="mt-1 block border border-zinc-300 px-3 py-1.5 text-sm font-mono focus:outline-none" data-testid="apt-period-month">
                        {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{t(`month_short_${i+1}`)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-zinc-500">{t('year')}</label>
                      <select value={aptPeriodYear} onChange={(e) => setAptPeriodYear(Number(e.target.value))} className="mt-1 block border border-zinc-300 px-3 py-1.5 text-sm font-mono focus:outline-none" data-testid="apt-period-year-month">
                        {Array.from({length: 5}, (_, i) => {const y = new Date().getFullYear() - 2 + i; return <option key={y} value={y}>{y}</option>;})}
                      </select>
                    </div>
                  </>
                )}
                {aptPeriodFilter === 'year' && (
                  <div>
                    <label className="text-xs font-medium text-zinc-500">{t('year')}</label>
                    <select value={aptPeriodYear} onChange={(e) => setAptPeriodYear(Number(e.target.value))} className="mt-1 block border border-zinc-300 px-3 py-1.5 text-sm font-mono focus:outline-none" data-testid="apt-period-year">
                      {Array.from({length: 5}, (_, i) => {const y = new Date().getFullYear() - 2 + i; return <option key={y} value={y}>{y}</option>;})}
                    </select>
                  </div>
                )}
                {aptPeriodFilter === 'custom' && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-zinc-500">{t('periodFrom')}</label>
                      <DatePickerDMY value={aptPeriodFrom} onChange={setAptPeriodFrom} className="mt-1 block border border-zinc-300 px-3 py-1.5 text-sm font-mono focus:outline-none" data-testid="apt-period-from" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-zinc-500">{t('periodTo')}</label>
                      <DatePickerDMY value={aptPeriodTo} onChange={setAptPeriodTo} className="mt-1 block border border-zinc-300 px-3 py-1.5 text-sm font-mono focus:outline-none" data-testid="apt-period-to" />
                    </div>
                  </>
                )}
                <div className="ml-auto">
                  <Button variant="outline" size="sm" className="rounded-none" onClick={openApartmentsExportDialog} data-testid="apt-export-pdf-btn">
                    <FilePdf size={16} className="mr-1.5" /> {t('exportPdf')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            {(() => {
              let expected, collected, outstanding;
              if (filteredAptSummary) {
                expected = filteredAptSummary.income?.expected || 0;
                collected = filteredAptSummary.income?.collected || 0;
                outstanding = expected - collected;
              } else {
                expected = aptSummary?.total_expected || 0;
                collected = aptSummary?.total_collected || 0;
                outstanding = expected - collected;
              }
              return (
                <div className="grid grid-cols-3 gap-3" data-testid="apt-summary-cards">
                  <div className="bg-white border border-zinc-200 px-4 py-2.5">
                    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{t('totalExpectedIncome')}</p>
                    <p className="text-lg font-bold font-mono mt-0.5">&euro;{expected.toFixed(2)}</p>
                  </div>
                  <div className="bg-white border border-zinc-200 px-4 py-2.5">
                    <p className="text-[10px] text-emerald-500 uppercase tracking-wider">{t('totalCollected')}</p>
                    <p className="text-lg font-bold font-mono text-emerald-700 mt-0.5">&euro;{collected.toFixed(2)}</p>
                  </div>
                  <div className="bg-white border border-red-200 px-4 py-2.5">
                    <p className="text-[10px] text-red-500 uppercase tracking-wider">{t('totalOutstanding')}</p>
                    <p className="text-lg font-bold font-mono text-red-700 mt-0.5">&euro;{outstanding.toFixed(2)}</p>
                  </div>
                </div>
              );
            })()}

            {/* Phase 239 — Payment Reminders Panel.
                Lists apartments with outstanding balances + a one-click
                "Send" action. Stage badge (first/second/final) is suggested
                based on the building's reminder cadence settings. */}
            {(reminderCandidates.length > 0 || reminderPanelOpen) && (
              <div className="bg-white border border-amber-300/60 mb-4" data-testid="reminders-panel">
                <button
                  onClick={() => setReminderPanelOpen(o => !o)}
                  className="w-full px-4 py-2.5 border-b border-amber-200/60 bg-amber-50/40 hover:bg-amber-50/70 flex items-center justify-between text-left"
                  data-testid="reminders-panel-toggle"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-amber-700 font-bold text-sm">⚠ {t('paymentReminders') || 'Payment Reminders'}</span>
                    <span className="text-[11px] bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 font-mono">
                      {reminderCandidates.length} {t('debtors') || 'debtors'}
                    </span>
                  </div>
                  <span className="text-[11px] text-amber-600">{reminderPanelOpen ? '▼' : '▶'}</span>
                </button>
                {reminderPanelOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="reminders-table">
                      <thead>
                        <tr className="border-b border-zinc-200 bg-zinc-50/50">
                          <th className="px-4 py-2 text-left text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{t('apartment')}</th>
                          <th className="px-4 py-2 text-left text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{t('owner')}</th>
                          <th className="px-4 py-2 text-right text-[11px] font-semibold text-red-600 uppercase tracking-wider">{t('balance') || 'Balance'}</th>
                          <th className="px-4 py-2 text-center text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{t('daysOverdue') || 'Days Overdue'}</th>
                          <th className="px-4 py-2 text-center text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{t('stage') || 'Stage'}</th>
                          <th className="px-4 py-2 text-left text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{t('lastReminder') || 'Last Reminder'}</th>
                          <th className="px-4 py-2 text-center text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{t('actions') || 'Actions'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reminderCandidates.map(c => {
                          const stageLabel = c.suggested_stage === 'final' ? (t('finalReminder') || 'Final')
                            : c.suggested_stage === 'second' ? (t('secondReminder') || 'Second')
                            : c.suggested_stage === 'first' ? (t('firstReminder') || 'First')
                            : '—';
                          const stageColor = c.suggested_stage === 'final' ? 'bg-red-100 text-red-700 border-red-300'
                            : c.suggested_stage === 'second' ? 'bg-amber-100 text-amber-700 border-amber-300'
                            : c.suggested_stage === 'first' ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                            : 'bg-zinc-100 text-zinc-500 border-zinc-200';
                          const isSending = !!reminderSending[c.apartment_id];
                          const lastDate = c.last_reminder_at ? new Date(c.last_reminder_at).toLocaleDateString('en-GB') : '—';
                          return (
                            <tr key={c.apartment_id} className="border-b border-zinc-100 hover:bg-zinc-50/30" data-testid={`reminder-row-${c.apartment_id}`}>
                              <td className="px-4 py-2 font-mono font-semibold">{c.apartment_number}</td>
                              <td className="px-4 py-2 text-zinc-700">{c.owner_name || '—'}</td>
                              <td className="px-4 py-2 text-right font-mono font-bold text-red-700">€{(c.balance || 0).toFixed(2)}</td>
                              <td className="px-4 py-2 text-center font-mono text-zinc-600">{c.days_overdue}</td>
                              <td className="px-4 py-2 text-center">
                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 border ${stageColor}`} data-testid={`stage-${c.apartment_id}`}>{stageLabel}</span>
                              </td>
                              <td className="px-4 py-2 text-[11px] text-zinc-500">
                                {c.last_reminder_at ? (
                                  <span className="font-mono" title={`${c.last_reminder_method} · ${c.last_reminder_status}`}>{lastDate}</span>
                                ) : <span className="text-zinc-400">—</span>}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => sendReminder(c.apartment_id, 'email')}
                                    disabled={isSending || !c.email}
                                    className={`text-[11px] px-2 py-1 border ${c.email ? 'bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-700' : 'bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed'} disabled:opacity-50`}
                                    title={c.email ? `Send email to ${c.email}` : 'No email on file'}
                                    data-testid={`send-reminder-email-${c.apartment_id}`}
                                  >
                                    {isSending ? '…' : (t('sendEmail') || 'Send Email')}
                                  </button>
                                  <button
                                    onClick={() => sendReminder(c.apartment_id, 'whatsapp')}
                                    disabled={isSending}
                                    className="text-[11px] px-2 py-1 border bg-zinc-100 hover:bg-zinc-200 text-zinc-600 border-zinc-300"
                                    title="WhatsApp (queued — coming soon)"
                                    data-testid={`send-reminder-whatsapp-${c.apartment_id}`}
                                  >
                                    WA
                                  </button>
                                  <button
                                    onClick={() => sendReminder(c.apartment_id, 'manual')}
                                    disabled={isSending}
                                    className="text-[11px] px-2 py-1 border bg-white hover:bg-zinc-50 text-zinc-700 border-zinc-300"
                                    title={t('logManualReminder') || 'Log manual reminder'}
                                    data-testid={`send-reminder-manual-${c.apartment_id}`}
                                  >
                                    {t('manual') || 'Manual'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Apartments Table — Simplified */}
            <div className="bg-white border border-zinc-200" data-testid="apartments-section">
              <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('apartments')}</h3>
                  <span className="text-[10px] text-zinc-400 font-mono">{apartments.length}</span>
                </div>
                <div className="flex items-center gap-2">
                  <select value={aptArchiveFilter} onChange={(e) => handleAptArchiveFilter(e.target.value)}
                    className="text-[11px] border border-zinc-200 rounded-none px-2 py-1 bg-white text-zinc-700 focus:outline-none" data-testid="apt-archive-filter">
                    <option value="active">{t('activeItems')}</option>
                    <option value="archived">{t('archivedItems')}</option>
                    <option value="all">{t('allItems')}</option>
                  </select>
                  {isAdmin && (
                    <Button onClick={openAddApt} className="bg-zinc-950 rounded-none text-xs h-7 px-3" data-testid="add-apartment-btn">
                      <Plus size={12} className="mr-1" />{t('addApartment')}
                    </Button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                {apartments.length === 0 ? (
                  <div className="p-8 text-center text-zinc-400 text-sm">{t('noApartments')}</div>
                ) : (
                  <table className="w-full text-sm" data-testid="apartments-table">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50/50">
                        <th className="px-4 py-2 text-left text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{t('apartment')}</th>
                        <th className="px-4 py-2 text-left text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{t('owner')}</th>
                        <th className="px-4 py-2 text-right text-[11px] font-semibold text-blue-600 uppercase tracking-wider">{t('totalDue')}</th>
                        <th className="px-4 py-2 text-right text-[11px] font-semibold text-emerald-600 uppercase tracking-wider">{t('totalPaid')}</th>
                        <th className="px-4 py-2 text-right text-[11px] font-semibold text-red-600 uppercase tracking-wider">{t('remainingBalance')}</th>
                        {isAdmin && <th className="px-4 py-2 w-20"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {apartments.map((apt) => (
                        <React.Fragment key={apt.apartment_id}>
                          <tr
                            className={`${apt.is_deleted ? 'bg-zinc-50 opacity-75' : ''} hover:bg-zinc-50 transition-colors cursor-pointer`}
                            onClick={() => navigate(`/apartments/${apt.apartment_id}`)}
                            data-testid={`apt-row-${apt.apartment_id}`}
                          >
                            <td className="px-4 py-2 font-semibold text-zinc-900" data-testid={`apt-link-${apt.apartment_id}`}>
                              #{apt.apartment_number}
                              {apt.is_deleted && <span className="ml-1.5 text-[9px] font-semibold bg-zinc-200 text-zinc-500 px-1 py-0.5">{t('archived')}</span>}
                            </td>
                            <td className="px-4 py-2 text-zinc-600 text-xs">{apt.owner_name || '—'}</td>
                            <td className="px-4 py-2 font-mono text-right font-semibold text-blue-700" data-testid={`apt-total-due-${apt.apartment_id}`}>&euro;{(apt.total_due || 0).toFixed(2)}</td>
                            <td className="px-4 py-2 font-mono text-right text-emerald-700">&euro;{(apt.total_paid || 0).toFixed(2)}</td>
                            <td className={`px-4 py-2 font-mono font-semibold text-right ${(apt.balance || 0) < -0.01 ? 'text-red-700' : (apt.balance || 0) > 0.01 ? 'text-emerald-600' : 'text-zinc-400'}`} data-testid={`apt-balance-${apt.apartment_id}`}>{(apt.balance || 0) < 0 ? '-' : ''}&euro;{Math.abs(apt.balance || 0).toFixed(2)}</td>
                            {isAdmin && (
                              <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                                <div className="flex gap-0.5 justify-end">
                                  {apt.is_deleted ? (<>
                                    <button onClick={() => { axios.post(`${API}/apartments/${apt.apartment_id}/restore`).then(() => { toast.success(t('itemRestored')); fetchApartments(); }).catch(() => toast.error(t('errorOccurred'))); }} className="p-1 hover:bg-emerald-50 text-emerald-500 hover:text-emerald-700 transition-colors" title={t('restore')} data-testid={`restore-apt-${apt.apartment_id}`}><ArrowCounterClockwise size={14} /></button>
                                  </>) : (<>
                                    <button onClick={() => navigate(`/apartments/${apt.apartment_id}?tab=ownerHistory`)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('viewOwnerHistory') || 'View Owner History'} data-testid={`owner-history-apt-${apt.apartment_id}`}><ClockClockwise size={14} /></button>
                                    <button onClick={() => openEditApt(apt)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`edit-apt-${apt.apartment_id}`}><PencilSimple size={14} /></button>
                                    <button onClick={() => { setDeletingApt(apt); setDeleteAptDialogOpen(true); }} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`delete-apt-${apt.apartment_id}`}><Trash size={14} /></button>
                                  </>)}
                                </div>
                              </td>
                            )}
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Share % Warning */}
            {apartments.length > 0 && (() => {
              const totalShare = apartments.reduce((s, a) => s + (a.share_percentage || 0), 0);
              const isValid = Math.abs(totalShare - 100) < 0.01;
              if (isValid) return null;
              return (
                <div className={`border px-4 py-3 text-sm flex items-center gap-2 ${totalShare > 100 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`} data-testid="share-warning">
                  <Warning size={18} weight="bold" />
                  <span>{t('sharePercentage')} {t('total')}: <strong className="font-mono">{totalShare.toFixed(2)}%</strong> — {totalShare < 100 ? t('shareUnder100') : t('shareOver100')}</span>
                </div>
              );
            })()}
          </div>
        )}

        {/* ====== INVOICES TAB ====== */}
        {activeTab === 'invoices' && (
          <InvoicesTab buildingId={buildingId} apartments={apartments} openEmailDialog={openEmailDialog} />
        )}

        {/* ====== PAYMENTS TAB ====== */}
        {activeTab === 'payments' && (
          <PaymentsTab buildingId={buildingId} apartments={apartments} />
        )}


        {/* ====== CREDIT NOTES TAB — Phase 659 ======
            Reusable supplier credit-notes tab; the same component is
            mounted on every supplier section (Bills / Elevator / Repairs
            / Fire Extinguishers / Insurance / General Expenses). Credit
            Notes reduce supplier-invoice outstanding balance and any
            unapplied amount becomes an available credit on the supplier. */}
        {activeTab === 'creditNotes' && (
          <SupplierCreditNotesTab buildingId={buildingId} section="bills" t={t} />
        )}


        {/* ====== EXPENSES TAB ====== */}
        {activeTab === 'expenses' && (
          <div className="space-y-6">
            {/* Phase 659 — Supplier Credit Notes for General Expenses section. */}
            <SupplierCreditNotesTab
              buildingId={buildingId}
              section="expense"
              t={t}
            />
            {(() => {
              const STATUS_ORDER = { unpaid: 0, partially_paid: 1, paid: 2 };
              const sortByStatus = (a, b) => (STATUS_ORDER[a.status] ?? 0) - (STATUS_ORDER[b.status] ?? 0);

              // Apply date, category, and status filters only. Billing period
              // is purely metadata — expenses without a period still appear
              // in the list and count toward totals; the period cell simply
              // renders empty when missing.
              //
              // Phase 707 — Category 301 (Elevator Works) is a payments-only
              // view. The system auto-creates a mirror expense row whenever
              // an Elevator Expense (the work order / extra-charge line) is
              // entered, and a separate mirror row for each Elevator Payment.
              // The work-order mirror is tracked in the Elevator tab; here we
              // only want the actual cash outflows toward elevator works, so
              // we keep ONLY rows whose `elevator_payment_id` is set (i.e.
              // mirror rows for real elevator payments). The originating
              // elevator-expense mirror (carrying `elevator_expense_id` or
              // `elevator_extra_id`) is hidden from this list.
              const isElevatorPaymentRow = (e) => !!e.elevator_payment_id;
              const dateFilteredExpenses = expenses.filter(e => {
                const d = e.date || e.expense_date || '';
                if (expDateFrom && d < expDateFrom) return false;
                if (expDateTo && d > expDateTo) return false;
                if (expCategoryFilter !== 'all' && e.category !== expCategoryFilter) return false;
                if (expStatusFilter !== 'all' && e.status !== expStatusFilter) return false;
                // Category 301 = payments-only view; drop the elevator-work
                // mirror (expense_type='capital' / 'elevator_expense' etc.)
                if (e.category === '301' && !isElevatorPaymentRow(e)) return false;
                return true;
              });

              // Unique categories from all expenses (for dropdown)
              const uniqueCategories = [...new Set(expenses.map(e => e.category).filter(Boolean))].sort();

              // Group filtered expenses by category.
              // Drafts (`is_draft=true`) are displayed to the user but are
              // EXCLUDED from category totals/paid/remaining so the numbers
              // only reflect billable expenses with a valid billing period.
              const grouped = {};
              dateFilteredExpenses.forEach(e => {
                const cat = e.category || 'uncategorized';
                if (!grouped[cat]) grouped[cat] = { expenses: [], total: 0, paid: 0, remaining: 0, unpaidCount: 0, partialCount: 0, paidCount: 0 };
                grouped[cat].expenses.push(e);
                grouped[cat].total += e.amount || 0;
                grouped[cat].paid += e.total_paid || 0;
                grouped[cat].remaining += (e.remaining_balance ?? e.amount ?? 0);
                if (e.status === 'unpaid') grouped[cat].unpaidCount++;
                else if (e.status === 'partially_paid') grouped[cat].partialCount++;
                else grouped[cat].paidCount++;
              });
              Object.values(grouped).forEach(g => g.expenses.sort(sortByStatus));

              // Classify categories
              const isMonthly = (cat) => MONTHLY_CATEGORIES.includes(cat);
              const isCapital = (cat) => CAPITAL_CATEGORIES.includes(cat);
              const allCatKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
              const monthlyCats = allCatKeys.filter(c => isMonthly(c));
              const capitalCats = allCatKeys.filter(c => isCapital(c));
              const uncategorizedCats = allCatKeys.filter(c => !isMonthly(c) && !isCapital(c));

              // Calculate type totals
              const monthlyTotal = monthlyCats.reduce((s, c) => s + (grouped[c]?.total || 0), 0);
              const monthlyPaid = monthlyCats.reduce((s, c) => s + (grouped[c]?.paid || 0), 0);
              const monthlyRemaining = monthlyCats.reduce((s, c) => s + (grouped[c]?.remaining || 0), 0);
              const monthlyCount = monthlyCats.reduce((s, c) => s + (grouped[c]?.expenses.length || 0), 0);
              const capitalTotal = capitalCats.reduce((s, c) => s + (grouped[c]?.total || 0), 0);
              const capitalPaid = capitalCats.reduce((s, c) => s + (grouped[c]?.paid || 0), 0);
              const capitalRemaining = capitalCats.reduce((s, c) => s + (grouped[c]?.remaining || 0), 0);
              const capitalCount = capitalCats.reduce((s, c) => s + (grouped[c]?.expenses.length || 0), 0);

              // Filter categories based on expTypeFilter
              const visibleCats = expTypeFilter === 'monthly' ? monthlyCats
                : expTypeFilter === 'capital' ? [...capitalCats, ...uncategorizedCats]
                : allCatKeys;

              // Current selection
              const displayExpenses = selectedExpCategory === 'all' ? null : (grouped[selectedExpCategory]?.expenses || []);
              const selectedCatData = selectedExpCategory !== 'all' ? grouped[selectedExpCategory] : null;

              // Render a sidebar category button
              const renderCatBtn = (cat) => {
                const g = grouped[cat];
                if (!g) return null;
                const isSelected = selectedExpCategory === cat;
                return (
                  <button key={cat} onClick={() => setSelectedExpCategory(cat)}
                    className={`w-full text-left px-4 py-2.5 border transition-colors ${isSelected ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white border-zinc-200 hover:bg-zinc-50'}`}
                    data-testid={`exp-cat-${cat}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{getCatLabel(cat, t, customCategories)}</span>
                      <span className={`text-xs font-mono flex-shrink-0 ${isSelected ? 'text-zinc-300' : 'text-zinc-400'}`}>{g.expenses.length}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-xs font-mono ${isSelected ? 'text-zinc-300' : 'text-zinc-600'}`}>&euro;{g.total.toFixed(2)}</span>
                      <div className="flex gap-1">
                        {g.unpaidCount > 0 && <span className={`text-[10px] px-1 py-0.5 font-mono ${isSelected ? 'bg-zinc-500/30 text-zinc-200' : 'bg-zinc-100 text-zinc-500 border border-zinc-200'}`}>{g.unpaidCount}</span>}
                        {g.partialCount > 0 && <span className={`text-[10px] px-1 py-0.5 font-mono ${isSelected ? 'bg-zinc-500/30 text-zinc-200' : 'bg-zinc-100 text-zinc-500 border border-zinc-200'}`}>{g.partialCount}</span>}
                      </div>
                    </div>
                  </button>
                );
              };

              // Render grouped content for "all" view — flat, minimal-color design
              const renderGroupedView = (catsToShow) => (
                <div className="bg-white border border-zinc-200 divide-y divide-zinc-100" data-testid="detail-expenses-grouped">
                  {catsToShow.length === 0 ? (
                    <div className="p-12 text-center text-zinc-500">{t('noExpenses')}</div>
                  ) : catsToShow.map(cat => {
                    const g = grouped[cat];
                    const isOpen = expandedExpCats[cat] === true;
                    const toggleExpCat = (ck) => setExpandedExpCats(prev => prev[ck] === true ? {} : { [ck]: true });
                    return (
                      <div key={cat} data-testid={`detail-cat-group-${cat}`}>
                        <button onClick={() => toggleExpCat(cat)} className="w-full text-left hover:bg-zinc-50/50 transition-colors" data-testid={`detail-toggle-cat-${cat}`}>
                          <div className="px-4 py-3 flex items-center gap-4">
                            {/* Caret + name + count */}
                            <span className="text-zinc-400 flex-shrink-0">{isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}</span>
                            <div className="flex-1 min-w-0 flex items-center gap-2">
                              <span className="text-sm font-medium text-zinc-800 truncate">{getCatLabel(cat, t, customCategories)}</span>
                              <span className="text-[11px] text-zinc-400 font-mono">({g.expenses.length})</span>
                            </div>
                            {/* Total only */}
                            <span className="text-sm font-mono font-semibold text-zinc-800 flex-shrink-0">&euro;{g.total.toFixed(2)}</span>
                          </div>
                        </button>
                        {isOpen && (
                          <div className="border-t border-zinc-100 bg-zinc-50/30">
                            {(() => {
                              const hasInsurance = g.expenses.some(e => e.is_insurance_expense);
                              const isElevator = cat === '203';
                              const showBillingPeriod = !isElevator && !hasInsurance || hasInsurance;
                              const colCount = 4 + (showBillingPeriod ? 1 : 0) + (hasInsurance ? 1 : 0) + 1 + 1 + (isAdmin ? 1 : 0);
                              return (
                            <table className="excel-table" data-testid={`detail-cat-table-${cat}`}>
                              <thead><tr className="bg-zinc-50">
                                <th className="text-xs">{t('expenseDate')}</th>
                                <th className="text-xs">{t('reference')}</th>
                                <th className="text-xs">{t('vendor')}</th>
                                <th className="text-xs text-right">{t('amount')}</th>
                                {!isElevator && <th className="text-xs">{hasInsurance ? t('insurancePeriod') : t('billingPeriod')}</th>}
                                {hasInsurance && <th className="text-xs">{t('numInstallments')}</th>}
                                {/* Phase 371 — Payment Source column (Cash / Bank / Reserve Fund). */}
                                <th className="text-xs">{t('paymentSource') || 'Payment Source'}</th>
                                <th className="text-xs">{t('notes')}</th>
                                {isAdmin && <th className="text-xs w-20"></th>}
                              </tr></thead>
                            <tbody>
                              {g.expenses.map(exp => {
                                const expPaid = exp.total_paid || 0;
                                const expCredited = exp.credited_amount || 0;
                                // Phase 667 — Subtract credited_amount so a fully credited
                                // expense doesn't show a phantom outstanding balance.
                                const expRem = exp.remaining_balance ?? ((exp.amount || 0) - expPaid - expCredited);
                                // Phase 309 — Payment History & Add Payment removed; in their
                                // place, an Attachments toggle reveals the saved files inline.
                                const isExpanded = expandedExpPayments[exp.expense_id] === true;
                                const attCount = Array.isArray(exp.attachments) ? exp.attachments.length : 0;
                                return (
                                <React.Fragment key={exp.expense_id}>
                                <tr className={`${exp.is_deleted ? 'bg-zinc-50 opacity-75' : ''} group`} data-testid={`detail-exp-${exp.expense_id}`}>
                                  <td className="font-mono text-sm">
                                    <div className="flex items-center gap-1.5">
                                      {formatDateDMY(exp.date)}
                                      {exp.is_recurring && <span className="text-[9px] px-1 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 font-semibold whitespace-nowrap" data-testid={`recurring-badge-${exp.expense_id}`}>{t('recurringMonthly')}</span>}
                                      {exp.fire_certification_id && <span className="text-[9px] px-1 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 font-semibold whitespace-nowrap" title={t('fireExtinguisherCertification') || 'Fire Extinguisher Certification'} data-testid={`fire-badge-${exp.expense_id}`}>FIRE</span>}
                                    </div>
                                  </td>
                                  <td className="font-mono text-xs text-zinc-500">{exp.reference_number || '-'}</td>
                                  <td className="text-sm text-zinc-600">{exp.vendor_name || '-'}</td>
                                  <td className="text-right">
                                    <div className="font-mono font-semibold">&euro;{(exp.amount || 0).toFixed(2)}</div>
                                  </td>
                                  {!isElevator && <td className="font-mono text-xs text-zinc-500" data-testid={`exp-billing-period-${exp.expense_id}`}>
                                    {exp.billing_period_start && exp.billing_period_end
                                      ? `${formatDateDMY(exp.billing_period_start)} – ${formatDateDMY(exp.billing_period_end)}`
                                      : ''}
                                  </td>}
                                  {hasInsurance && (
                                    <td className="text-xs" data-testid={`ins-installment-${exp.expense_id}`}>
                                      {exp.is_insurance_expense ? (
                                        <div className="flex flex-col gap-0.5">
                                          {/* Phase 955 — Per-payment installment. Show the
                                              specific installment number attached to THIS
                                              expense (installment_number), not the global
                                              paid-count of the policy. Fallback to the
                                              policy-level counter only when the expense has
                                              no explicit installment_number. */}
                                          <span className="font-mono font-semibold">
                                            {(exp.installment_number ? exp.installment_number : exp.insurance_paid_installments)} / {exp.insurance_num_installments}
                                          </span>
                                          <span className="text-[10px] text-zinc-400">({exp.insurance_remaining_installments} {t('remaining')})</span>
                                        </div>
                                      ) : '-'}
                                    </td>
                                  )}
                                  {/* Phase 371/373.1 — Payment Source pill.
                                      Base source = Cash | Bank. Reserve Fund is an
                                      OPTIONAL qualifier appended in parentheses. */}
                                  <td className="text-xs" data-testid={`exp-payment-source-${exp.expense_id}`}>
                                    {(() => {
                                      const psRaw = (exp.payment_source || 'cash').toLowerCase();
                                      const isRF = !!exp.paid_from_reserve_fund || psRaw === 'reserve_fund';
                                      // Legacy 'reserve_fund' rows had no separate base; treat as Cash.
                                      const base = psRaw === 'bank' ? (t('bank') || 'Bank') : (t('cash') || 'Cash');
                                      const qualifier = isRF ? ` (${t('reserveFund') || 'Reserve Fund'})` : '';
                                      const isBank = psRaw === 'bank';
                                      const cls = isBank
                                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                                        : 'bg-amber-50 text-amber-700 border-amber-200';
                                      return <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold border ${cls}`}>{base}{qualifier}</span>;
                                    })()}
                                  </td>
                                  <td className="text-sm text-zinc-500 max-w-xs truncate">{exp.notes || ''}</td>
                                  {isAdmin && (
                                    <td>
                                      <div className="flex gap-1 items-center">
                                        {exp.is_deleted ? (<>
                                          <button onClick={async () => { try { await axios.post(`${API}/expenses/${exp.expense_id}/restore`); toast.success(t('itemRestored')); fetchExpenses(); } catch { toast.error(t('errorOccurred')); }}} className="p-1 hover:bg-emerald-50 text-emerald-500 hover:text-emerald-700 transition-colors" title={t('restore')} data-testid={`restore-exp-${exp.expense_id}`}><ArrowCounterClockwise size={14} /></button>
                                          <button onClick={async () => { if (window.confirm(t('confirmPermanentDelete'))) { try { await axios.delete(`${API}/expenses/${exp.expense_id}/permanent`); toast.success(t('itemPermanentlyDeleted')); fetchExpenses(); } catch { toast.error(t('errorOccurred')); }}}} className="p-1 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title={t('permanentDelete')} data-testid={`perm-delete-exp-${exp.expense_id}`}><Trash size={14} /></button>
                                        </>) : (<>
                                          {attCount > 0 && (
                                            <button onClick={() => setExpandedExpPayments(prev => ({ ...prev, [exp.expense_id]: !prev[exp.expense_id] }))} className={`p-1 transition-colors flex items-center gap-0.5 ${isExpanded ? 'bg-zinc-100 text-zinc-700' : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`} title={t('attachments')} data-testid={`toggle-attachments-${exp.expense_id}`}>
                                              <Paperclip size={14} />
                                              <span className="text-[9px] font-mono">{attCount}</span>
                                            </button>
                                          )}
                                          <button onClick={() => openPdfInBrowser(`/expenses/${exp.expense_id}/pdf`)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('viewPdf')} data-testid={`view-pdf-exp-${exp.expense_id}`}><FilePdf size={14} /></button>
                                          <button onClick={() => openEditExpense(exp)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`edit-exp-${exp.expense_id}`}><PencilSimple size={14} /></button>
                                          <button onClick={() => { setDeletingExpense(exp); setDeleteExpDialogOpen(true); }} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`delete-exp-${exp.expense_id}`}><Trash size={14} /></button>
                                        </>)}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                                {isExpanded && attCount > 0 && (
                                  <tr data-testid={`attachments-row-${exp.expense_id}`}>
                                    <td colSpan={colCount} className="!p-0">
                                      <div className="bg-zinc-50 border-t border-b border-zinc-100 px-6 py-3">
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">{t('attachments')} ({attCount})</div>
                                        <ExpenseAttachmentsInline
                                          expenseId={exp.expense_id}
                                          attachments={exp.attachments || []}
                                          onRefresh={fetchAll}
                                          t={t}
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                </React.Fragment>
                                );
                              })}
                            </tbody>
                            <tfoot><tr className="bg-zinc-100 font-semibold border-t border-zinc-300">
                              <td className="text-right text-sm" colSpan={3}>{t('total')}</td>
                              <td className="font-mono text-right">&euro;{g.total.toFixed(2)}</td>
                              <td className="text-center"><span className="text-[10px] font-mono text-zinc-500">{t('paid')}: &euro;{g.paid.toFixed(2)}</span></td>
                              <td colSpan={hasInsurance ? (isAdmin ? 4 : 3) : (isAdmin ? 3 : 2)}></td>
                            </tr></tfoot>
                          </table>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );

              return (
                <>
                  {/* Inline summary — totals derived from the visible (filtered) rows
                      so drafts / billing-period-less expenses never inflate the figure. */}
                  <div className="bg-white border border-zinc-200 px-4 py-3 flex items-center gap-6 text-sm font-mono flex-wrap" data-testid="expense-type-summary">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] uppercase tracking-wider text-zinc-400">{t('total')}</span>
                      <span className="font-semibold text-zinc-800">&euro;{(monthlyTotal + capitalTotal).toFixed(2)}</span>
                    </div>
                    <div className="ml-auto flex items-center gap-3 text-[11px] text-zinc-400 font-sans">
                      <span>{t('monthlyExpenses')}: <span className="font-mono text-zinc-500">&euro;{monthlyTotal.toFixed(2)}</span></span>
                      <span>{t('capitalExpenses')}: <span className="font-mono text-zinc-500">&euro;{capitalTotal.toFixed(2)}</span></span>
                    </div>
                  </div>


                  {/* Single Filter Row + Actions */}
                  <div className="bg-white border border-zinc-200 px-4 py-2.5 flex items-end gap-3 flex-wrap" data-testid="expense-filters">
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">{t('type')}</span>
                      <Select value={expTypeFilter} onValueChange={(val) => { setExpTypeFilter(val); setSelectedExpCategory('all'); setExpandedExpCats({}); }}>
                        <SelectTrigger className="rounded-none h-8 w-40 text-xs" data-testid="exp-type-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('showAll')} ({dateFilteredExpenses.length})</SelectItem>
                          <SelectItem value="monthly">{t('monthlyExpenses')} ({monthlyCount})</SelectItem>
                          <SelectItem value="capital">{t('capitalExpenses')} ({capitalCount})</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">{t('fromDate')}</span>
                      <DatePickerDMY value={expDateFrom} onChange={setExpDateFrom} className="rounded-none h-8 w-36 font-mono text-xs" data-testid="exp-date-from" />
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">{t('toDate')}</span>
                      <DatePickerDMY value={expDateTo} onChange={setExpDateTo} className="rounded-none h-8 w-36 font-mono text-xs" data-testid="exp-date-to" />
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">{t('category')}</span>
                      <Select value={expCategoryFilter} onValueChange={setExpCategoryFilter}>
                        <SelectTrigger className="rounded-none h-8 w-44 text-xs" data-testid="exp-category-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('allCategories') || t('all')}</SelectItem>
                          <SelectGroup>
                            <SelectLabel>{t('monthlyExpenses')}</SelectLabel>
                            {customCategories.filter(c => c.group === 'monthly').map(c => (
                              <SelectItem key={c.category_code} value={c.category_code}>{getCatLabel(c.category_code, t, customCategories)}</SelectItem>
                            ))}
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>{t('capitalExpenses')}</SelectLabel>
                            {customCategories.filter(c => c.group === 'capital').map(c => (
                              <SelectItem key={c.category_code} value={c.category_code}>{getCatLabel(c.category_code, t, customCategories)}</SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Phase 309 — Status filter removed. All expenses are
                        automatically paid; the Unpaid/Partial concept no
                        longer exists for building expenses. */}
                    {(expDateFrom || expDateTo || expCategoryFilter !== 'all' || expTypeFilter !== 'all') && (
                      <Button variant="outline" size="sm" onClick={() => { setExpDateFrom(''); setExpDateTo(''); setExpCategoryFilter('all'); setExpStatusFilter('all'); setExpTypeFilter('all'); }} className="rounded-none h-8 text-xs gap-1" data-testid="exp-clear-filters">
                        <Funnel size={12} />{t('clearFilter')}
                      </Button>
                    )}
                    <div className="flex gap-2 ml-auto">
                      <Button
                        onClick={() => {
                          // Phase 813 — Bug fix (P1): Pass EVERY active filter
                          // to the PDF endpoint so the exported PDF matches
                          // exactly what the user sees in the on-screen list.
                          // Previously only `building_id` was passed which
                          // made the PDF ignore the date range and category
                          // filters entirely.
                          const params = { building_id: buildingId };
                          if (expDateFrom) params.date_from = expDateFrom;
                          if (expDateTo)   params.date_to   = expDateTo;
                          if (expCategoryFilter && expCategoryFilter !== 'all') params.category = expCategoryFilter;
                          if (expTypeFilter && expTypeFilter !== 'all')         params.category_group = expTypeFilter;
                          openPdfInBrowser('/expenses/export/pdf', params);
                        }}
                        variant="outline" size="sm" className="rounded-none h-8 text-xs" data-testid="view-pdf-expenses-btn">
                        <FilePdf size={14} className="mr-1" />{t('viewPdf')}
                      </Button>
                      <Button
                        onClick={() => {
                          // Phase 304b — Always open Add Expense with a CLEAN slate.
                          // Without this reset the form retains the previous record's
                          // category/amount/dates/etc. across openings. Edit Expense
                          // has its own loader and is unaffected.
                          setEditingExpense(null);
                          setExpenseForm({
                            building_id: buildingId,
                            expense_type: 'other',
                            category: '204',
                            amount: 0,
                            date: new Date().toISOString().split('T')[0],
                            status: 'paid',
                            notes: '',
                            vendor_id: '',
                            reference_number: '',
                            billing_period_start: '',
                            billing_period_end: '',
                            paid_from_reserve_fund: false,
                            insurance_id: '',
                            installment_number: '',
                            pay_full: false,
                            is_recurring: false,
                            recurring_day: '1',
                            payment_source: 'cash',
                          });
                          setExpenseDialogOpen(true);
                          // Phase 308 — Reset attachment state when opening Add Expense.
                          setPendingExpenseFiles([]);
                          setExpenseAttachments([]);
                        }}
                        size="sm"
                        className="bg-zinc-950 rounded-none h-8 text-xs"
                        data-testid="add-expense-detail-btn"
                      >
                        <Plus size={14} className="mr-1" />{t('addExpense')}
                      </Button>
                    </div>
                    {(expDateFrom || expDateTo || expCategoryFilter !== 'all' || expStatusFilter !== 'all') && (
                      <span className="text-xs text-zinc-400 font-mono w-full text-right">{dateFilteredExpenses.length} / {expenses.length} {t('items')}</span>
                    )}
                  </div>

                  {/* Content - Expand/Collapse Controls + Grouped View */}
                  <div data-testid="expenses-layout">
                    {visibleCats.length > 1 && (
                      <div className="flex justify-end gap-2 mb-3">
                        <button onClick={() => { const all = {}; visibleCats.forEach(c => { all[c] = true; }); setExpandedExpCats(all); }} className="text-xs text-zinc-500 hover:text-zinc-700 font-medium" data-testid="expand-all-expenses">{t('expandAll')}</button>
                        <span className="text-zinc-300">|</span>
                        <button onClick={() => setExpandedExpCats({})} className="text-xs text-zinc-500 hover:text-zinc-700 font-medium" data-testid="collapse-all-expenses">{t('collapseAll')}</button>
                      </div>
                    )}
                    <div className="min-w-0">
                      {expenses.length === 0 ? (
                        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('noExpenses')}</div>
                      ) : (
                        renderGroupedView(visibleCats)
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ====== REPAIRS TAB ====== */}
        {activeTab === 'repairs' && (
          <div className="space-y-6" data-testid="repairs-tab">
            <div className="bg-white border border-zinc-200" data-testid="repairs-fund-section">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>{t('repairs')}</h3>
                  <p className="text-xs text-zinc-400 mt-0.5">{t('repairsHistoryDesc')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs text-zinc-400">{t('activeRepairsTotal')}</p>
                    <p className="text-lg font-bold font-mono" data-testid="repairs-active-total">
                      &euro;{repairsList.filter(r => r.status === 'active').reduce((s, r) => s + (r.amount || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button onClick={() => { setEditingRepair(null); setRepairForm({ description: '', amount: '', from_date: new Date().toISOString().split('T')[0], due_date: '', generate_invoices: false, payment_type: 'shared', payment_source: 'distribution', payment_method: 'cash', distribution_method: 'share', building_fund_amount: '', advancing_apartment_id: '' }); setRepairDialogOpen(true); }} size="sm" className="bg-zinc-900 rounded-none" data-testid="add-repair-btn">
                      <Plus size={14} className="mr-1" />{t('addRepair')}
                    </Button>
                  )}
                </div>
              </div>
              {/* Sub-Tabs (mirrors Elevator module) */}
              <div className="flex border-b border-zinc-200" data-testid="rep-sub-tabs">
                {[
                  { key: 'invoices', label: t('invoices') },
                  { key: 'payments', label: t('payments') },
                  { key: 'creditNotes', label: t('creditNotes') || 'Credit Notes' },
                  { key: 'statement', label: t('statement') },
                ].map(st => (
                  <button
                    key={st.key}
                    onClick={() => setRepSubTab(st.key)}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${repSubTab === st.key ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-700'}`}
                    data-testid={`rep-subtab-${st.key}`}
                  >
                    {st.label}
                  </button>
                ))}
              </div>
              {repSubTab === 'invoices' && (
              <div className="overflow-x-auto">
                {/* Phase 661 — Free-text search above Repairs invoices table */}
                <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2 bg-zinc-50/40">
                  <div className="relative flex-1 max-w-md">
                    <Input
                      value={repInvSearch}
                      onChange={(e) => setRepInvSearch(e.target.value)}
                      placeholder={t('searchPlaceholderInvoices') || 'Search number, description, date...'}
                      className="rounded-none h-7 text-xs pl-7 pr-7"
                      data-testid="rep-inv-search"
                    />
                    <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                    {repInvSearch && (
                      <button type="button" onClick={() => setRepInvSearch('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-400 hover:text-zinc-700" data-testid="rep-inv-search-clear"><X size={12} /></button>
                    )}
                  </div>
                </div>
                {repairsList.length === 0 ? (
                  <div className="p-10 text-center text-zinc-400 text-sm">{t('noRepairs')}</div>
                ) : (
                  <table className="w-full text-sm" data-testid="repairs-list-table">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50/50">
                        <th className="px-4 py-2.5 w-8"></th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('reference') || 'Reference'}</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('description')}</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('type')}</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('total')}</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('date')}</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('paymentStatus')}</th>
                        {isAdmin && <th className="px-4 py-2.5 w-36 text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('actions')}</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {repairsList.filter(rep => {
                        const sq = (repInvSearch || '').trim().toLowerCase();
                        if (!sq) return true;
                        const hay = [
                          rep.reference_number,
                          rep.description, rep.repair_id, rep.from_date,
                          formatDateDMY(rep.from_date), rep.payment_type,
                          rep.payment_method, rep.notes,
                          (rep.fund_payments || []).map(p => `${p.vendor_name || ''} ${p.payment_source || ''}`).join(' '),
                        ].map(v => String(v || '').toLowerCase()).join(' \u00b7 ');
                        return hay.includes(sq);
                      }).map(rep => {
                        const repairAmount = rep.amount || 0;
                        const isFund = rep.payment_type === 'building_fund';
                        const isExpanded = expandedRepair === rep.repair_id;
                        // All repair rows can expand: distribution repairs show
                        // the per-apartment breakdown + attachments; building-fund
                        // repairs show only attachments (payment history lives in
                        // the Payments tab).
                        const canExpand = true;
                        return (
                          <React.Fragment key={rep.repair_id}>
                            <tr className={`hover:bg-zinc-50/50 transition-colors ${rep.status === 'closed' ? 'opacity-50' : ''}`} data-testid={`repair-row-${rep.repair_id}`}>
                              <td className="px-4 py-2.5">
                                {canExpand ? (
                                  <button onClick={() => setExpandedRepair(isExpanded ? null : rep.repair_id)} className={`p-1 hover:bg-zinc-100 transition-colors ${isExpanded ? 'bg-zinc-100' : ''}`} data-testid={`repair-expand-${rep.repair_id}`}>
                                    {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                                  </button>
                                ) : null}
                              </td>
                              <td className="px-4 py-2.5 font-mono text-xs text-indigo-700 font-semibold" data-testid={`repair-ref-${rep.repair_id}`}>{rep.reference_number || '—'}</td>
                              <td className="px-4 py-2.5">{rep.description || '-'}</td>
                              <td className="px-4 py-2.5">
                                {isFund
                                  ? <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 font-semibold uppercase" data-testid={`repair-type-fund-${rep.repair_id}`}>{t('buildingFund')}</span>
                                  : <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 font-semibold uppercase" data-testid={`repair-type-shared-${rep.repair_id}`}>{t('shared')}</span>}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono font-semibold" data-testid={`repair-total-${rep.repair_id}`}>&euro;{repairAmount.toFixed(2)}</td>
                              <td className="px-4 py-2.5 font-mono text-zinc-500">{rep.from_date ? formatDateDMY(rep.from_date) : '-'}</td>
                              <td className="px-4 py-2.5">
                                <InvoiceStatusBadge
                                  status={rep.payment_status}
                                  amount={rep.amount}
                                  paidAmount={rep.paid}
                                  testId={`repair-payment-status-${rep.repair_id}`}
                                />
                              </td>
                              {isAdmin && (
                                <td className="px-4 py-2.5">
                                  <div className="flex items-center gap-1">
                                    {!isFund && !rep.invoices_generated && rep.status === 'active' && (
                                      <button onClick={() => handleGenerateRepairInvoices(rep.repair_id)} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 font-semibold uppercase" data-testid={`repair-gen-inv-${rep.repair_id}`}>{t('generateInvoices')}</button>
                                    )}
                                    {/* Phase 957 — Building Fund repairs: quick "Add Payment"
                                        button directly in the row actions so users can
                                        record incremental payments without expanding the
                                        row. Reuses the existing repair payment dialog. */}
                                    {isFund && rep.status === 'active' && (rep.remaining === undefined || rep.remaining > 0.01) && (
                                      <button
                                        onClick={() => openRepairPaymentDialog(rep, null)}
                                        className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold uppercase"
                                        data-testid={`repair-add-payment-${rep.repair_id}`}
                                        title={t('addPayment') || 'Add Payment'}
                                      >
                                        <Plus size={10} className="inline mr-0.5" />{t('addPayment') || 'Add Payment'}
                                      </button>
                                    )}
                                    {rep.status === 'active' ? (
                                      <button onClick={() => handleToggleRepairStatus(rep)} className="text-xs px-1.5 py-0.5 border border-zinc-200 hover:bg-zinc-50" data-testid={`repair-toggle-${rep.repair_id}`} title={t('close')}>&#10005;</button>
                                    ) : (
                                      <button onClick={() => handleToggleRepairStatus(rep)} className="text-xs px-1.5 py-0.5 border border-zinc-200 hover:bg-zinc-50" data-testid={`repair-toggle-${rep.repair_id}`} title={t('reopen')}>&#8634;</button>
                                    )}
                                    <button onClick={() => { setEditingRepair(rep); const src = (rep.building_fund_amount && rep.building_fund_amount > 0) ? 'building_fund' : 'distribution'; setRepairForm({ description: rep.description || '', amount: String(rep.amount || 0), from_date: rep.from_date || '', due_date: rep.due_date || '', generate_invoices: false, payment_type: rep.payment_type || 'shared', payment_source: src, payment_method: rep.payment_method || 'cash', distribution_method: rep.distribution_method || 'share', building_fund_amount: '', advancing_apartment_id: rep.advancing_apartment_id || '' }); setRepairDialogOpen(true); }} className="text-xs px-1.5 py-0.5 border border-zinc-200 hover:bg-zinc-50" data-testid={`repair-edit-${rep.repair_id}`}><PencilSimple size={12} /></button>
                                    {/* Phase 337 — Single "View PDF" action: opens preview modal directly. */}
                                    <button onClick={() => viewRepairPdf(rep)} className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-zinc-50" title={t('viewPdf')} data-testid={`repair-view-pdf-${rep.repair_id}`}><FilePdf size={12} /></button>
                                    <button onClick={() => { setDeletingRepair(rep); setDeleteRepairDialogOpen(true); }} className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-zinc-50" data-testid={`repair-delete-${rep.repair_id}`}><Trash size={12} /></button>
                                  </div>
                                </td>
                              )}
                            </tr>
                            {isExpanded && !isFund && (
                              <tr data-testid={`repair-distribution-${rep.repair_id}`}>
                                <td colSpan={isAdmin ? 8 : 7} className="p-0">
                                  <div className="bg-zinc-50 border-t border-zinc-100 px-6 py-4">
                                    <div className="flex items-center justify-between mb-3">
                                      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">{t('distributionPerApartment')}</p>
                                      {isAdmin && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="rounded-none text-xs h-7"
                                          onClick={() => { setEditDistRepair(rep); setEditDistOpen(true); }}
                                          data-testid={`edit-dist-btn-${rep.repair_id}`}
                                        >
                                          <PencilSimple size={12} className="mr-1" />
                                          {t('editDistribution') || 'Edit Distribution'}
                                        </Button>
                                      )}
                                    </div>
                                    <table className="w-full text-sm" data-testid={`repair-dist-table-${rep.repair_id}`}>
                                      <thead>
                                        <tr className="border-b border-zinc-200">
                                          <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">{t('apartmentNumber')}</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">{t('ownerName')}</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">{t('invoiceNumber') || 'Invoice No.'}</th>
                                          <th className="px-3 py-2 text-center text-xs font-medium text-zinc-500">{t('sharePercentage')}</th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500">{t('allocatedAmount')}</th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500">{t('paid')}</th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500">{t('remaining')}</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">{t('status')}</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-zinc-100">
                                        {(() => {
                                          // Use stored snapshot if available, otherwise compute dynamically (legacy)
                                          const distRows = rep.apartment_distribution && rep.apartment_distribution.length > 0
                                            ? rep.apartment_distribution
                                            : apartments.map(apt => ({
                                                apartment_id: apt.apartment_id,
                                                apartment_number: apt.apartment_number,
                                                owner_name: apt.owner_name || '-',
                                                share_percentage: apt.share_percentage || 0,
                                                allocated_amount: round2((apt.share_percentage || 0) / 100 * repairAmount),
                                              }));
                                          return distRows.map((row) => {
                                            const allocated = row.allocated_amount || 0;
                                            // Phase 920.5 — Advancing apartment: no invoice, no receivable.
                                            const isAdvancing = rep.advancing_apartment_id && row.apartment_id === rep.advancing_apartment_id;
                                            const paidAmt = isAdvancing ? allocated : (row.paid_amount || 0);
                                            const rem = isAdvancing ? 0 : (row.remaining !== undefined ? row.remaining : Math.max(0, allocated - paidAmt));
                                            return (
                                              <tr key={row.apartment_id} className={isAdvancing ? 'bg-blue-50/50' : 'bg-white'} data-testid={`repair-dist-row-${rep.repair_id}-${row.apartment_id}`}>
                                                <td className="px-3 py-2 font-semibold">
                                                  {row.apartment_number}
                                                  {isAdvancing && (
                                                    <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200"
                                                          data-testid={`repair-dist-owner-advance-badge-${rep.repair_id}-${row.apartment_id}`}>
                                                      {language === 'el' ? 'Owner Advance' : 'Owner Advance'}
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="px-3 py-2 text-zinc-500">{row.owner_name || '-'}</td>
                                                <td className="px-3 py-2 font-mono text-xs text-zinc-700" data-testid={`repair-dist-invno-${rep.repair_id}-${row.apartment_id}`}>
                                                  {isAdvancing
                                                    ? <span className="text-blue-700 text-[10px] italic">{language === 'el' ? 'χωρίς τιμολόγιο' : 'no invoice'}</span>
                                                    : (row.invoice_number
                                                        ? <span className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 text-zinc-700">{row.invoice_number}</span>
                                                        : <span className="text-zinc-300">—</span>)}
                                                </td>
                                                <td className="px-3 py-2 text-center font-mono text-zinc-500">{(row.share_percentage || 0).toFixed(2)}%</td>
                                                <td className="px-3 py-2 text-right font-mono font-semibold">&euro;{allocated.toFixed(2)}</td>
                                                <td className="px-3 py-2 text-right font-mono text-emerald-700" data-testid={`repair-dist-paid-${rep.repair_id}-${row.apartment_id}`}>&euro;{paidAmt.toFixed(2)}</td>
                                                <td className="px-3 py-2 text-right font-mono" data-testid={`repair-dist-remaining-${rep.repair_id}-${row.apartment_id}`}>
                                                  <span className={rem > 0.01 ? 'text-amber-700 font-semibold' : 'text-zinc-400'}>&euro;{rem.toFixed(2)}</span>
                                                </td>
                                                <td className="px-3 py-2">
                                                  {isAdvancing
                                                    ? <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200 font-semibold" data-testid={`repair-dist-status-${rep.repair_id}-${row.apartment_id}`}>
                                                        {language === 'el' ? 'Owner Advance' : 'Owner Advance'}
                                                      </span>
                                                    : <InvoiceStatusBadge amount={allocated} paidAmount={paidAmt} testId={`repair-dist-status-${rep.repair_id}-${row.apartment_id}`} />}
                                                </td>
                                              </tr>
                                            );
                                          });
                                        })()}
                                      </tbody>
                                      <tfoot>
                                        <tr className="border-t-2 border-zinc-300 bg-zinc-100 font-semibold">
                                          <td className="px-3 py-2 text-right" colSpan={3}>{t('total')}</td>
                                          <td className="px-3 py-2 text-center font-mono">{(() => {
                                            const distRows = rep.apartment_distribution && rep.apartment_distribution.length > 0 ? rep.apartment_distribution : apartments;
                                            return distRows.reduce((s, a) => s + (a.share_percentage || 0), 0).toFixed(2);
                                          })()}%</td>
                                          <td className="px-3 py-2 text-right font-mono">&euro;{(() => {
                                            const distRows = rep.apartment_distribution && rep.apartment_distribution.length > 0 ? rep.apartment_distribution : apartments.map(a => ({ allocated_amount: round2((a.share_percentage || 0) / 100 * repairAmount) }));
                                            return distRows.reduce((s, a) => s + (a.allocated_amount || 0), 0).toFixed(2);
                                          })()}</td>
                                          {/* Phase 920.7 — Footer totals must ignore the advancing
                                              apartment's non-invoice contribution when tallying paid /
                                              remaining. The advancing apt's allocated amount is added
                                              to Paid (already contributed), and its row is excluded
                                              from Remaining (it has no invoice, no receivable). */}
                                          <td className="px-3 py-2 text-right font-mono text-emerald-700">&euro;{(() => {
                                            const distRows = rep.apartment_distribution && rep.apartment_distribution.length > 0 ? rep.apartment_distribution : [];
                                            const advId = rep.advancing_apartment_id;
                                            return distRows.reduce((s, a) => {
                                              if (advId && a.apartment_id === advId) {
                                                return s + (a.allocated_amount || 0);
                                              }
                                              return s + (a.paid_amount || 0);
                                            }, 0).toFixed(2);
                                          })()}</td>
                                          <td className="px-3 py-2 text-right font-mono">
                                            {(() => {
                                              const distRows = rep.apartment_distribution && rep.apartment_distribution.length > 0 ? rep.apartment_distribution : apartments.map(a => ({ allocated_amount: round2((a.share_percentage || 0) / 100 * repairAmount), paid_amount: 0 }));
                                              const advId = rep.advancing_apartment_id;
                                              const totalRemaining = distRows.reduce((s, a) => {
                                                if (advId && a.apartment_id === advId) return s;
                                                const alloc = a.allocated_amount || 0;
                                                const paid = a.paid_amount || 0;
                                                const r = a.remaining !== undefined ? a.remaining : Math.max(0, alloc - paid);
                                                return s + r;
                                              }, 0);
                                              return <span className={totalRemaining > 0.01 ? 'text-amber-700' : 'text-zinc-400'}>&euro;{totalRemaining.toFixed(2)}</span>;
                                            })()}
                                          </td>
                                          <td className="px-3 py-2"></td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                    {/* Phase 168 — Attachments panel for distribution repairs. */}
                                    <div className="mt-4">
                                      <RepairAttachmentsSection
                                        repairId={rep.repair_id}
                                        attachments={repairAttachments[rep.repair_id] || []}
                                        pending={!!repairUploadPending[rep.repair_id]}
                                        onUpload={(files) => uploadRepairAttachments(rep.repair_id, files)}
                                        onDelete={(fileId) => deleteRepairAttachment(rep.repair_id, fileId)}
                                        onOpen={(fileId, download) => openRepairAttachment(rep.repair_id, fileId, download)}
                                        t={t}
                                      />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                            {isExpanded && isFund && (
                              <tr data-testid={`repair-fund-expanded-${rep.repair_id}`}>
                                <td colSpan={isAdmin ? 8 : 7} className="p-0">
                                  <div className="bg-zinc-50 border-t border-zinc-100 px-6 py-4">
                                    <RepairAttachmentsSection
                                      repairId={rep.repair_id}
                                      attachments={repairAttachments[rep.repair_id] || []}
                                      pending={!!repairUploadPending[rep.repair_id]}
                                      onUpload={(files) => uploadRepairAttachments(rep.repair_id, files)}
                                      onDelete={(fileId) => deleteRepairAttachment(rep.repair_id, fileId)}
                                      onOpen={(fileId, download) => openRepairAttachment(rep.repair_id, fileId, download)}
                                      t={t}
                                    />
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              )}

              {/* ===== SUB-TAB: PAYMENTS ===== */}
              {repSubTab === 'payments' && (() => {
                // Flatten all fund_payments across all repairs into an Elevator-style list.
                const allPayments = [];
                (repairsList || []).forEach(rep => {
                  (rep.fund_payments || []).forEach(p => {
                    allPayments.push({ ...p, _repair: rep });
                  });
                });
                allPayments.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                const sqp = (repPaySearch || '').trim().toLowerCase();
                const filteredPayments = sqp ? allPayments.filter(p => {
                  const hay = [
                    p.payment_number, p.reference, p.payment_source,
                    p.vendor_name, p.notes, p.date, formatDateDMY(p.date),
                    p._repair?.description, p._repair?.repair_id,
                    p.paid_from_reserve_fund ? 'reserve fund' : '',
                  ].map(v => String(v || '').toLowerCase()).join(' \u00b7 ');
                  return hay.includes(sqp);
                }) : allPayments;
                return (
                  <div>
                    <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="text-sm font-semibold text-zinc-800" style={{ fontFamily: 'var(--font-heading)' }}>{t('payments')}</h3>
                      {/* Phase 661 — Free-text search above Repairs payments table */}
                      <div className="flex items-center gap-2 ml-auto">
                        <div className="relative">
                          <Input
                            value={repPaySearch}
                            onChange={(e) => setRepPaySearch(e.target.value)}
                            placeholder={t('searchPlaceholderPayments') || 'Search payment #, reference, source...'}
                            className="rounded-none h-7 text-xs pl-7 pr-7 w-72"
                            data-testid="rep-pay-search"
                          />
                          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                          {repPaySearch && (
                            <button type="button" onClick={() => setRepPaySearch('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-400 hover:text-zinc-700" data-testid="rep-pay-search-clear"><X size={12} /></button>
                          )}
                        </div>
                        {isAdmin && (
                          <Button size="sm" onClick={() => { fetchRepairs(); setRepPaymentPickerRepairId(''); setRepPaymentPickerOpen(true); }} className="bg-zinc-950 rounded-none h-7 text-xs" data-testid="rep-pay-add-btn">
                            <Plus size={14} className="mr-1" />{t('addPayment')}
                          </Button>
                        )}
                      </div>
                    </div>
                    <table className="w-full text-xs" data-testid="rep-payments-table">
                      <thead>
                        <tr className="bg-zinc-50 border-b border-zinc-200">
                          <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('paymentNumber')}</th>
                          <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('date')}</th>
                          <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold">{t('description')}</th>
                          <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('reference')}</th>
                          <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold w-16">{t('category')}</th>
                          <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-20">{t('source')}</th>
                          <th className="px-3 py-2 text-right text-[10px] text-zinc-400 font-semibold w-24">{t('amount')}</th>
                          <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold w-20">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPayments.length === 0 ? (
                          <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-400">{t('noPayments')}</td></tr>
                        ) : filteredPayments.map((p, idx) => {
                          const src = (p.payment_source || '').toLowerCase();
                          return (
                            <tr key={p.payment_id || idx} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`rep-pay-row-${p.payment_id}`}>
                              <td className="px-3 py-2.5 font-mono text-zinc-700 font-semibold" data-testid={`rep-pay-num-${p.payment_id}`}>{p.payment_number || '-'}</td>
                              <td className="px-3 py-2.5 font-mono text-zinc-500">{p.date ? formatDateDMY(p.date) : '-'}</td>
                              <td className="px-3 py-2.5 text-zinc-700">{p._repair?.description || '-'}</td>
                              <td className="px-3 py-2.5 font-mono text-zinc-500">{p.vendor_name || '-'}</td>
                              <td className="px-3 py-2.5 text-center">
                                {p.category ? (() => {
                                  const isCapital = CAPITAL_CATEGORIES.includes(p.category) || (/^3/).test(p.category);
                                  return (
                                    <span className={`text-[9px] px-1.5 py-0.5 font-semibold uppercase border ${isCapital ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-zinc-100 text-zinc-700 border-zinc-200'}`} title={getCatLabel(p.category, t, customCategories)} data-testid={`rep-pay-cat-${p.payment_id}`}>
                                      {p.category}
                                    </span>
                                  );
                                })() : <span className="text-zinc-300">—</span>}
                              </td>
                              <td className="px-3 py-2.5 text-zinc-500 text-[11px]">
                                {src === 'cash' ? t('cashReceiptsBuilding')
                                 : src === 'bank' ? t('bankReceiptsBuilding')
                                 : '—'}
                                {p.paid_from_reserve_fund && <span className="ml-1 text-[9px] px-1 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 uppercase">RF</span>}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono font-semibold text-emerald-700">€{(p.amount || 0).toFixed(2)}</td>
                              <td className="px-3 py-2.5 text-center">
                                {isAdmin && (
                                  <div className="flex justify-center gap-0.5">
                                    <button onClick={() => openRepairPaymentDialog(p._repair, p)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`rep-pay-edit-${p.payment_id}`}><PencilSimple size={14} /></button>
                                    {/* Phase 167 — per-payment receipt PDF */}
                                    <button onClick={() => downloadPdfUtil(`/buildings/${buildingId}/repairs/${p._repair.repair_id}/payments/${p.payment_id}/pdf`)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('paymentReceiptPdf')} data-testid={`rep-pay-pdf-${p.payment_id}`}><FilePdf size={14} /></button>
                                    <button onClick={() => handleDeleteRepairFundPayment(p._repair, p)} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`rep-pay-delete-${p.payment_id}`}><Trash size={14} /></button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* ===== SUB-TAB: CREDIT NOTES (Phase 659) ===== */}
              {repSubTab === 'creditNotes' && (
                <SupplierCreditNotesTab
                  buildingId={buildingId}
                  section="repairs"
                  t={t}
                />
              )}

              {/* ===== SUB-TAB: STATEMENT ===== */}
              {repSubTab === 'statement' && (() => {
                // Pull the fully-enriched ledger from the backend
                // (`GET /api/buildings/{id}/repairs/statement`). Each entry is
                // already tagged with source (Shared / Building Fund / cash /
                // bank / reserve fund) and an apartment number where relevant.
                const entries = (repairsStatement && repairsStatement.entries) || [];
                const openingBalance = 0;
                let running = openingBalance;
                const rows = entries.map((e) => {
                  if (e.type === 'charge') running = round2(running + (e.amount || 0));
                  else running = round2(running - (e.amount || 0));
                  return { ...e, balance: running };
                });
                const totalCharges = (repairsStatement && repairsStatement.total_charges) || 0;
                const totalPayments = (repairsStatement && repairsStatement.total_payments) || 0;
                const totalCreditNotes = (repairsStatement && repairsStatement.total_credit_notes) || 0;
                return (
                  <div>
                    <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-zinc-800" style={{ fontFamily: 'var(--font-heading)' }}>{t('statement')}</h3>
                      <div className="text-[11px] text-zinc-500 font-mono" data-testid="rep-stmt-totals">
                        {t('total')}: <span className="text-zinc-700 font-semibold">€{totalCharges.toFixed(2)}</span>
                        <span className="mx-2 text-zinc-300">·</span>
                        {t('paid')}: <span className="text-emerald-700 font-semibold">€{totalPayments.toFixed(2)}</span>
                        <span className="mx-2 text-zinc-300">·</span>
                        {t('creditNotes') || 'Credit Notes'}: <span className="text-red-700 font-semibold">€{totalCreditNotes.toFixed(2)}</span>
                        <span className="mx-2 text-zinc-300">·</span>
                        {t('remaining')}: <span className={`${running >= 0 ? 'text-amber-700' : 'text-zinc-500'} font-semibold`}>€{running.toFixed(2)}</span>
                      </div>
                    </div>
                    <table className="w-full text-xs" data-testid="rep-statement-table">
                      <thead>
                        <tr className="bg-zinc-50 border-b border-zinc-200">
                          <th className="px-4 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('date')}</th>
                          <th className="px-4 py-2 text-left text-[10px] text-zinc-400 font-semibold">{t('description')}</th>
                          <th className="px-4 py-2 text-left text-[10px] text-zinc-400 font-semibold w-28">{t('source')}</th>
                          <th className="px-6 py-2 text-right text-[10px] text-zinc-400 font-semibold w-32">{t('charge')}</th>
                          <th className="px-6 py-2 text-right text-[10px] text-zinc-400 font-semibold w-32">{t('payment')}</th>
                          <th className="px-6 py-2 text-right text-[10px] text-zinc-400 font-semibold w-32">{t('balance')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-zinc-200 bg-zinc-50/40" data-testid="rep-stmt-ob-row">
                          <td className="px-4 py-2 font-mono text-zinc-500">—</td>
                          <td className="px-4 py-2 font-medium text-zinc-600">{t('balanceBroughtForward')}</td>
                          <td className="px-4 py-2 text-zinc-300">-</td>
                          <td className="px-6 py-2 text-right text-zinc-300">-</td>
                          <td className="px-6 py-2 text-right text-zinc-300">-</td>
                          <td className="px-6 py-2 font-mono font-semibold text-right">€{openingBalance.toFixed(2)}</td>
                        </tr>
                        {rows.length === 0 ? (
                          <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">{t('noRepairs')}</td></tr>
                        ) : rows.map((e, idx) => (
                          <tr key={`rep-stmt-${idx}`} className={`border-b border-zinc-100 ${e.type === 'credit_note' ? 'bg-emerald-50/30' : ''}`} data-testid={`rep-stmt-row-${idx}`}>
                            <td className="px-4 py-2 font-mono text-zinc-500">{e.date ? formatDateDMY(e.date) : '-'}</td>
                            <td className="px-4 py-2 text-zinc-700">{e.description}</td>
                            <td className="px-4 py-2">
                              {e.source ? (
                                <span className={`text-[10px] px-2 py-0.5 border font-semibold uppercase ${e.type === 'credit_note' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-zinc-100 text-zinc-700 border-zinc-200'}`} data-testid={`rep-stmt-source-${idx}`}>{e.source}</span>
                              ) : <span className="text-zinc-300">-</span>}
                            </td>
                            <td className="px-6 py-2 text-right font-mono">{e.type === 'charge' ? `€${(e.amount || 0).toFixed(2)}` : <span className="text-zinc-300">-</span>}</td>
                            <td className="px-6 py-2 text-right font-mono">
                              {e.type === 'payment' ? <span className="text-emerald-700">€{(e.amount || 0).toFixed(2)}</span>
                                : e.type === 'credit_note' ? <span className="text-red-600 font-semibold">&minus;€{(e.amount || 0).toFixed(2)}</span>
                                : <span className="text-zinc-300">-</span>}
                            </td>
                            <td className="px-6 py-2 text-right font-mono font-semibold">€{e.balance.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        {/* Phase 735.28 — Financial-year filter helper. Filters the ledger
            entries to a specific calendar year and prepends a synthetic
            "Balance Brought Forward" row whose value equals the closing
            balance of the previous year (i.e. the running balance of the
            last chronological entry with `date < ${year}-01-01`). When the
            selected year is the earliest year with entries, no BB row is
            inserted (the real Opening Balance row is shown instead). */}
        {/* ====== CASH RECEIPTS TAB ====== */}
        {activeTab === 'cashReceipts' && (
          <div className="space-y-6" data-testid="cash-receipts-tab">
            {/* Phase 820.3 — Management Company outstanding summary (Cash) */}
            {mcaSummary.cash.advances > 0 || mcaSummary.cash.repayments > 0 ? (
              <div className="bg-white border border-zinc-200 px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="mca-summary-cash">
                <div className="flex items-center gap-6 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {t('mcaOutstandingCash') || (language === 'el' ? 'Εκκρεμές Προς Εταιρεία Διαχείρισης (Μετρητά)' : 'Management Company Outstanding (Cash)')}
                    </div>
                    <div className="text-xl font-bold font-mono text-red-700 mt-0.5" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="mca-outstanding-cash">
                      &euro;{(mcaSummary.cash.outstanding || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {language === 'el' ? 'Προκαταβολές:' : 'Advances:'} <b className="text-zinc-800">&euro;{(mcaSummary.cash.advances || 0).toFixed(2)}</b>
                    <span className="mx-1.5">·</span>
                    {language === 'el' ? 'Επιστροφές:' : 'Repayments:'} <b className="text-zinc-800">&euro;{(mcaSummary.cash.repayments || 0).toFixed(2)}</b>
                  </div>
                </div>
              </div>
            ) : null}
            {/* Phase 820.4 — Apartment Owner Credits outstanding summary (Cash) */}
            {aocSummary.cash.credits > 0 || aocSummary.cash.reimbursements > 0 ? (
              <div className="bg-white border border-zinc-200 px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="aoc-summary-cash">
                <div className="flex items-center gap-6 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {t('aocOutstandingCash') || (language === 'el' ? 'Εκκρεμές Πίστωσης Ιδιοκτητών (Μετρητά)' : 'Apartment Owner Credit Outstanding (Cash)')}
                    </div>
                    <div className="text-xl font-bold font-mono text-red-700 mt-0.5" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="aoc-outstanding-cash">
                      &euro;{(aocSummary.cash.outstanding || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {language === 'el' ? 'Πιστώσεις:' : 'Credits:'} <b className="text-zinc-800">&euro;{(aocSummary.cash.credits || 0).toFixed(2)}</b>
                    <span className="mx-1.5">·</span>
                    {language === 'el' ? 'Αποζημιώσεις:' : 'Reimbursements:'} <b className="text-zinc-800">&euro;{(aocSummary.cash.reimbursements || 0).toFixed(2)}</b>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="bg-white border border-zinc-200" data-testid="cash-receipts-section">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>{t('cashReceipts')}</h3>
                  {/* Phase 735.28 — Financial-year filter */}
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t('financialYear') || 'Financial Year'}:</span>
                    <Select value={String(cashYear)} onValueChange={(v) => setCashYear(v === 'all' ? 'all' : Number(v))}>
                      <SelectTrigger className="h-7 w-32 rounded-none text-xs" data-testid="cash-year-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" data-testid="cash-year-all">{t('allYears') || 'All Years'}</SelectItem>
                        {cashYearOptions.map(y => (
                          <SelectItem key={y} value={String(y)} data-testid={`cash-year-${y}`}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => openCashBankOb('cash')} data-testid="set-cash-ob-btn">
                      <PencilSimple size={14} className="mr-1" />{t('setOpeningBalance')}
                    </Button>
                  )}
                  {/* Phase 718 — Internal Cash → Bank transfer (no income/expense). */}
                  {isAdmin && (
                    <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => openTransferDialog('cash')} data-testid="cash-transfer-funds-btn">
                      <ArrowsLeftRight size={14} className="mr-1" />{t('transferFunds') || 'Transfer Funds'}
                    </Button>
                  )}
                  {/* Phase 820.3 — Management Company Advance / Repayment */}
                  {isAdmin && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-none text-xs border-emerald-300 text-emerald-800" onClick={() => openMcaDialog('cash', 'advance')} data-testid="cash-mca-advance-btn">
                        <Plus size={14} className="mr-1" />{t('mcaAdvance') || (language === 'el' ? 'Νέα Προκαταβολή' : 'New Advance')}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none text-xs border-red-300 text-red-800" onClick={() => openMcaDialog('cash', 'repayment')} data-testid="cash-mca-repayment-btn">
                        <Plus size={14} className="mr-1" />{t('mcaRepayment') || (language === 'el' ? 'Επιστροφή' : 'Repayment')}
                      </Button>
                      {/* Phase 1004g — MCA history + PDF */}
                      <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={openMcaHistory} data-testid="cash-mca-history-btn">
                        {language === 'el' ? 'Ιστορικό' : 'View History'}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={openMcaHistoryPdf} data-testid="cash-mca-pdf-btn">
                        <FilePdf size={14} className="mr-1" />{language === 'el' ? 'PDF Προκαταβολών' : 'MCA PDF'}
                      </Button>
                    </>
                  )}
                  {/* Phase 820.4 — Apartment Owner Credit / Reimbursement */}
                  {isAdmin && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-none text-xs border-sky-300 text-sky-800" onClick={() => openAocDialog('cash', 'credit')} data-testid="cash-aoc-credit-btn">
                        <Plus size={14} className="mr-1" />{t('aocCredit') || (language === 'el' ? 'Πίστωση Ιδιοκτήτη' : 'Owner Credit')}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none text-xs border-rose-300 text-rose-800" onClick={() => openAocDialog('cash', 'reimbursement')} data-testid="cash-aoc-reimbursement-btn">
                        <Plus size={14} className="mr-1" />{t('aocReimbursement') || (language === 'el' ? 'Αποζημίωση' : 'Reimbursement')}
                      </Button>
                    </>
                  )}
                  {/* Phase 703 + 735.32 — Toolbar "View PDF" for the full
                      Cash Receipts report. Passes the active Financial-Year
                      filter so the PDF mirrors the on-screen data exactly. */}
                  {cashLedger && (cashLedger.entries || []).length > 0 && (
                    <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => openPdfInBrowser(`/buildings/${buildingId}/cash-ledger/pdf?source=cash&lang=${language}${cashYear !== 'all' ? `&year=${cashYear}` : ''}`)} data-testid="cash-view-pdf-btn">
                      <FilePdf size={14} className="mr-1" />{t('viewPdf')}
                    </Button>
                  )}
                  {cashLedger && (
                    <span className={`text-sm font-mono font-semibold ${(cashLedger.balance || 0) >= 0 ? 'text-zinc-900' : 'text-red-600'}`}>
                      {t('balance')}: &euro;{(cashLedger.balance || 0).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto" data-testid="cash-ledger-table-wrapper">
                {!cashLedgerView || cashLedgerView.entries.length === 0 ? (
                  <div className="p-10 text-center text-zinc-400 text-sm">{t('noCashTransactions')}</div>
                ) : (
                  <table className="excel-table" data-testid="cash-ledger-table">
                    <thead>
                      <tr>
                        <th className="w-24">{t('date')}</th>
                        <th className="w-28">{t('reference')}</th>
                        <th>{t('description')}</th>
                        <th className="w-28 text-right">{t('income')} (&euro;)</th>
                        <th className="w-28 text-right">{t('expense')} (&euro;)</th>
                        <th className="w-28 text-right">{t('balance')} (&euro;)</th>
                        <th className="w-10 text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashLedgerView.entries.map((entry, idx) => {
                        // Phase 735.28 — Synthetic Balance Brought Forward row
                        // rendered as a single blue-highlighted line, no
                        // reference / ins / exp columns.
                        if (entry.is_brought_forward) {
                          return (
                            <tr key={`bf-${idx}`} className="bg-blue-50/50" data-testid={`cash-brought-forward-${idx}`}>
                              <td className="font-mono text-sm text-blue-700">{formatDateDMY(entry.date)}</td>
                              <td className="font-mono text-xs text-zinc-600">—</td>
                              <td className="text-sm font-medium text-blue-700">{entry.description}: &euro;{entry.balance.toFixed(2)}</td>
                              <td></td>
                              <td></td>
                              <td className={`font-mono text-right font-semibold ${entry.balance >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>&euro;{entry.balance.toFixed(2)}</td>
                              <td></td>
                            </tr>
                          );
                        }
                        // Phase 322 — Pixel-perfect parity with Annual
                        // Statement: render `entry.description` (composed
                        // by the backend) as a single string, optionally
                        // prefixed by the same neutral category tag.
                        const isAptReceipt = entry.type === 'apartment_payment';
                        const refLabel = isAptReceipt
                          ? (entry.notes || entry.receipt_number || '—')
                          : (entry.reference_number || entry.receipt_number || '—');
                        const catCode = entry.expense_category;
                        return (
                        <tr key={idx} className={entry.amount_in > 0 ? 'bg-emerald-50/30' : entry.amount_out > 0 ? 'bg-red-50/20' : ''} data-testid={`cash-entry-${idx}`}>
                          <td className="font-mono text-sm">{entry.date ? formatDateDMY(entry.date) : '—'}</td>
                          <td className="font-mono text-xs text-zinc-600" data-testid={`cash-ref-${idx}`}>{refLabel}</td>
                          <td className="text-sm max-w-md">
                            {entry.is_opening_balance ? (
                              <span className="font-medium text-blue-700" data-testid={`cash-opening-${idx}`}>{entry.description}</span>
                            ) : catCode ? (
                              <span>
                                <span className="inline-block px-1.5 py-0.5 mr-1.5 font-mono text-[10px] font-semibold bg-zinc-100 text-zinc-600 align-middle">
                                  {getCatLabel(catCode, t, customCategories)}
                                </span>
                                <span>{entry.description}</span>
                                {entry.paid_from_reserve_fund && (
                                  <span className="inline-block ml-1.5 px-1.5 py-0.5 font-mono text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-sm align-middle" title={t('reserveFundFunded') || 'Reserve Fund funded'} data-testid={`cash-rf-badge-${idx}`}>RF</span>
                                )}
                              </span>
                            ) : (
                              <span>
                                {entry.description || entry.notes || '—'}
                                {entry.paid_from_reserve_fund && (
                                  <span className="inline-block ml-1.5 px-1.5 py-0.5 font-mono text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-sm align-middle" title={t('reserveFundFunded') || 'Reserve Fund funded'} data-testid={`cash-rf-badge-${idx}`}>RF</span>
                                )}
                              </span>
                            )}
                            {/* Phase 1004f — Notes rendered as smaller sub-text
                                under the transaction type/description. Notes
                                are shown only when they differ from the
                                description shown above so we don't duplicate
                                the same text on two lines. The cell is
                                width-capped (max-w-md) and notes wrap with
                                a 2-line clamp so the ledger table never
                                stretches for long Notes text. */}
                            {(() => {
                              const desc = entry.description || '';
                              const notes = entry.notes || '';
                              const showNotes = !entry.is_opening_balance
                                && notes && notes.trim() && notes.trim() !== desc.trim();
                              if (!showNotes) return null;
                              return (
                                <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug line-clamp-2" title={notes} data-testid={`cash-notes-${idx}`}>
                                  {notes}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="font-mono text-right text-emerald-700 font-medium">
                            {entry.amount_in > 0 ? `€${entry.amount_in.toFixed(2)}` : ''}
                          </td>
                          <td className="font-mono text-right text-red-700 font-medium">
                            {entry.amount_out > 0 ? `€${entry.amount_out.toFixed(2)}` : ''}
                          </td>
                          <td className={`font-mono text-right font-semibold ${entry.balance >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>
                            &euro;{entry.balance.toFixed(2)}
                          </td>
                          <td className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {/* Phase 703 — Per-row View PDF (single ledger entry).
                                  Opening Balance has no ref_id so we skip it. */}
                              {!entry.is_opening_balance && entry.ref_id && (
                                <button
                                  type="button"
                                  onClick={() => openPdfInBrowser(`/buildings/${buildingId}/cash-ledger/entry/${entry.ref_id}/pdf?source=cash`)}
                                  className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                                  title={t('viewPdf')}
                                  data-testid={`cash-entry-pdf-${idx}`}
                                >
                                  <FilePdf size={14} />
                                </button>
                              )}
                              {entry.is_test ? (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteLedgerEntry(entry)}
                                  className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                  title={t('deleteTestRow') || 'Delete test row'}
                                  data-testid={`cash-entry-delete-${idx}`}
                                >
                                  <Trash size={14} />
                                </button>
                              ) : null}
                              {/* Phase 820.3 — Inline Edit/Delete for Management Company Advance / Repayment rows. */}
                              {(entry.type === 'management_advance' || entry.type === 'management_repayment') && isAdmin && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openMcaDialog('cash', entry.type === 'management_advance' ? 'advance' : 'repayment', { entry_id: entry.entry_id, entry_type: entry.type === 'management_advance' ? 'advance' : 'repayment', source: 'cash', date: entry.date, amount: entry.amount_in || entry.amount_out, reference: entry.reference_number, notes: entry.notes })}
                                    className="p-1 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
                                    title={t('edit') || 'Edit'}
                                    data-testid={`cash-mca-edit-${idx}`}
                                  >
                                    <PencilSimple size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteMcaEntry(entry.entry_id)}
                                    className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title={t('delete') || 'Delete'}
                                    data-testid={`cash-mca-delete-${idx}`}
                                  >
                                    <Trash size={14} />
                                  </button>
                                </>
                              )}
                              {/* Phase 820.4 — Inline Edit/Delete for Apartment Owner Credit / Reimbursement rows. */}
                              {(entry.type === 'apartment_owner_credit' || entry.type === 'apartment_owner_reimbursement') && isAdmin && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openAocDialog('cash', entry.type === 'apartment_owner_credit' ? 'credit' : 'reimbursement', { entry_id: entry.entry_id, entry_type: entry.type === 'apartment_owner_credit' ? 'credit' : 'reimbursement', source: 'cash', apartment_id: entry.apartment_id, apartment_number: entry.apartment, owner_name: '', date: entry.date, amount: entry.amount_in || entry.amount_out, reference: entry.reference_number, notes: entry.notes })}
                                    className="p-1 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
                                    title={t('edit') || 'Edit'}
                                    data-testid={`cash-aoc-edit-${idx}`}
                                  >
                                    <PencilSimple size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteAocEntry(entry.entry_id)}
                                    className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title={t('delete') || 'Delete'}
                                    data-testid={`cash-aoc-delete-${idx}`}
                                  >
                                    <Trash size={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-zinc-100 font-semibold">
                        <td colSpan={3} className="text-right text-xs uppercase tracking-wider text-zinc-600">{t('total')}</td>
                        <td className="font-mono text-right text-emerald-700">&euro;{(cashLedgerView.total_in || 0).toFixed(2)}</td>
                        <td className="font-mono text-right text-red-700">&euro;{(cashLedgerView.total_out || 0).toFixed(2)}</td>
                        <td className={`font-mono text-right font-bold ${(cashLedgerView.balance || 0) < 0 ? 'text-red-700' : 'text-zinc-900'}`}>&euro;{(cashLedgerView.balance || 0).toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ====== BANK RECEIPTS TAB ====== */}
        {activeTab === 'bankReceipts' && (
          <div className="space-y-6" data-testid="bank-receipts-tab">
            {/* Phase 820.3 — Management Company outstanding summary (Bank) */}
            {mcaSummary.bank.advances > 0 || mcaSummary.bank.repayments > 0 ? (
              <div className="bg-white border border-zinc-200 px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="mca-summary-bank">
                <div className="flex items-center gap-6 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {t('mcaOutstandingBank') || (language === 'el' ? 'Εκκρεμές Προς Εταιρεία Διαχείρισης (Τράπεζα)' : 'Management Company Outstanding (Bank)')}
                    </div>
                    <div className="text-xl font-bold font-mono text-red-700 mt-0.5" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="mca-outstanding-bank">
                      &euro;{(mcaSummary.bank.outstanding || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {language === 'el' ? 'Προκαταβολές:' : 'Advances:'} <b className="text-zinc-800">&euro;{(mcaSummary.bank.advances || 0).toFixed(2)}</b>
                    <span className="mx-1.5">·</span>
                    {language === 'el' ? 'Επιστροφές:' : 'Repayments:'} <b className="text-zinc-800">&euro;{(mcaSummary.bank.repayments || 0).toFixed(2)}</b>
                  </div>
                </div>
              </div>
            ) : null}
            {/* Phase 820.4 — Apartment Owner Credits outstanding summary (Bank) */}
            {aocSummary.bank.credits > 0 || aocSummary.bank.reimbursements > 0 ? (
              <div className="bg-white border border-zinc-200 px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="aoc-summary-bank">
                <div className="flex items-center gap-6 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {t('aocOutstandingBank') || (language === 'el' ? 'Εκκρεμές Πίστωσης Ιδιοκτητών (Τράπεζα)' : 'Apartment Owner Credit Outstanding (Bank)')}
                    </div>
                    <div className="text-xl font-bold font-mono text-red-700 mt-0.5" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="aoc-outstanding-bank">
                      &euro;{(aocSummary.bank.outstanding || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {language === 'el' ? 'Πιστώσεις:' : 'Credits:'} <b className="text-zinc-800">&euro;{(aocSummary.bank.credits || 0).toFixed(2)}</b>
                    <span className="mx-1.5">·</span>
                    {language === 'el' ? 'Αποζημιώσεις:' : 'Reimbursements:'} <b className="text-zinc-800">&euro;{(aocSummary.bank.reimbursements || 0).toFixed(2)}</b>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="bg-white border border-zinc-200" data-testid="bank-receipts-section">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>{t('bankReceipts')}</h3>
                  {/* Phase 735.28 — Financial-year filter */}
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t('financialYear') || 'Financial Year'}:</span>
                    <Select value={String(bankYear)} onValueChange={(v) => setBankYear(v === 'all' ? 'all' : Number(v))}>
                      <SelectTrigger className="h-7 w-32 rounded-none text-xs" data-testid="bank-year-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" data-testid="bank-year-all">{t('allYears') || 'All Years'}</SelectItem>
                        {bankYearOptions.map(y => (
                          <SelectItem key={y} value={String(y)} data-testid={`bank-year-${y}`}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => openCashBankOb('bank')} data-testid="set-bank-ob-btn">
                      <PencilSimple size={14} className="mr-1" />{t('setOpeningBalance')}
                    </Button>
                  )}
                  {/* Phase 718 — Internal Bank → Cash transfer (no income/expense). */}
                  {isAdmin && (
                    <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => openTransferDialog('bank')} data-testid="bank-transfer-funds-btn">
                      <ArrowsLeftRight size={14} className="mr-1" />{t('transferFunds') || 'Transfer Funds'}
                    </Button>
                  )}
                  {/* Phase 820.3 — Management Company Advance / Repayment */}
                  {isAdmin && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-none text-xs border-emerald-300 text-emerald-800" onClick={() => openMcaDialog('bank', 'advance')} data-testid="bank-mca-advance-btn">
                        <Plus size={14} className="mr-1" />{t('mcaAdvance') || (language === 'el' ? 'Νέα Προκαταβολή' : 'New Advance')}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none text-xs border-red-300 text-red-800" onClick={() => openMcaDialog('bank', 'repayment')} data-testid="bank-mca-repayment-btn">
                        <Plus size={14} className="mr-1" />{t('mcaRepayment') || (language === 'el' ? 'Επιστροφή' : 'Repayment')}
                      </Button>
                      {/* Phase 1004g — MCA history + PDF (Bank tab) */}
                      <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={openMcaHistory} data-testid="bank-mca-history-btn">
                        {language === 'el' ? 'Ιστορικό' : 'View History'}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={openMcaHistoryPdf} data-testid="bank-mca-pdf-btn">
                        <FilePdf size={14} className="mr-1" />{language === 'el' ? 'PDF Προκαταβολών' : 'MCA PDF'}
                      </Button>
                    </>
                  )}
                  {/* Phase 820.4 — Apartment Owner Credit / Reimbursement */}
                  {isAdmin && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-none text-xs border-sky-300 text-sky-800" onClick={() => openAocDialog('bank', 'credit')} data-testid="bank-aoc-credit-btn">
                        <Plus size={14} className="mr-1" />{t('aocCredit') || (language === 'el' ? 'Πίστωση Ιδιοκτήτη' : 'Owner Credit')}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none text-xs border-rose-300 text-rose-800" onClick={() => openAocDialog('bank', 'reimbursement')} data-testid="bank-aoc-reimbursement-btn">
                        <Plus size={14} className="mr-1" />{t('aocReimbursement') || (language === 'el' ? 'Αποζημίωση' : 'Reimbursement')}
                      </Button>
                    </>
                  )}
                  {/* Phase 703 + 735.32 — Toolbar "View PDF" for the full
                      Bank Receipts report. Respects the active
                      Financial-Year filter. */}
                  {bankLedger && (bankLedger.entries || []).length > 0 && (
                    <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => openPdfInBrowser(`/buildings/${buildingId}/cash-ledger/pdf?source=bank&lang=${language}${bankYear !== 'all' ? `&year=${bankYear}` : ''}`)} data-testid="bank-view-pdf-btn">
                      <FilePdf size={14} className="mr-1" />{t('viewPdf')}
                    </Button>
                  )}
                  {bankLedger && (
                    <span className={`text-sm font-mono font-semibold ${(bankLedger.balance || 0) >= 0 ? 'text-zinc-900' : 'text-red-600'}`}>
                      {t('balance')}: &euro;{(bankLedger.balance || 0).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                {!bankLedgerView || bankLedgerView.entries.length === 0 ? (
                  <div className="p-10 text-center text-zinc-400 text-sm">{t('noBankTransactions')}</div>
                ) : (
                  <table className="excel-table" data-testid="bank-ledger-table">
                    <thead>
                      <tr>
                        <th className="w-24">{t('date')}</th>
                        <th className="w-28">{t('reference')}</th>
                        <th>{t('description')}</th>
                        <th className="w-28 text-right">{t('income')} (&euro;)</th>
                        <th className="w-28 text-right">{t('expense')} (&euro;)</th>
                        <th className="w-28 text-right">{t('balance')} (&euro;)</th>
                        <th className="w-10 text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Phase 370 + 372 + 735.28 + 945 — Bank Receipts sorted
                          by date DESC (newest first). Within the same date,
                          sort by the backend-supplied `seq` index DESC.
                          Opening Balance sinks to the bottom AND, per the
                          user's Phase 945 preference for BUILDINGS ONLY, the
                          synthetic Balance Brought Forward row ALSO sinks to
                          the bottom (below current-year transactions). This
                          is display-only; the underlying balance calculation
                          is unchanged. Applies to the Building → Bank
                          Receipts view only. */}
                      {[...bankLedgerView.entries]
                        .sort((a, b) => {
                          // Phase 945 — Both "brought forward" and
                          // "opening balance" rows sink to the bottom so
                          // the current year's chronological transactions
                          // occupy the visible top of the list.
                          const aBottom = a.is_brought_forward || a.is_opening_balance;
                          const bBottom = b.is_brought_forward || b.is_opening_balance;
                          if (aBottom && !bBottom) return 1;
                          if (bBottom && !aBottom) return -1;
                          // Within the sunk group, keep opening balance
                          // below the brought forward row (older comes
                          // last visually).
                          if (aBottom && bBottom) {
                            if (a.is_brought_forward && b.is_opening_balance) return -1;
                            if (b.is_brought_forward && a.is_opening_balance) return 1;
                          }
                          const da = a.date || '', db = b.date || '';
                          const cmp = db.localeCompare(da);
                          if (cmp !== 0) return cmp;
                          return (b.seq || 0) - (a.seq || 0);
                        })
                        .map((entry, idx) => {
                        if (entry.is_brought_forward) {
                          return (
                            <tr key={`bf-${idx}`} className="bg-blue-50/50" data-testid={`bank-brought-forward-${idx}`}>
                              <td className="font-mono text-sm text-blue-700">{formatDateDMY(entry.date)}</td>
                              <td className="font-mono text-xs text-zinc-600">—</td>
                              <td className="text-sm font-medium text-blue-700">{entry.description}: &euro;{entry.balance.toFixed(2)}</td>
                              <td></td>
                              <td></td>
                              <td className={`font-mono text-right font-semibold ${entry.balance >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>&euro;{entry.balance.toFixed(2)}</td>
                              <td></td>
                            </tr>
                          );
                        }
                        // Phase 322 — Pixel-perfect parity with Annual
                        // Statement (same JSX as Cash Receipts above).
                        const isAptReceipt = entry.type === 'apartment_payment';
                        const refLabel = isAptReceipt
                          ? (entry.notes || entry.receipt_number || '—')
                          : (entry.reference_number || entry.receipt_number || '—');
                        const catCode = entry.expense_category;
                        return (
                        <tr key={idx} className={entry.amount_in > 0 ? 'bg-emerald-50/30' : entry.amount_out > 0 ? 'bg-red-50/20' : ''} data-testid={`bank-entry-${idx}`}>
                          <td className="font-mono text-sm">{entry.date ? formatDateDMY(entry.date) : '—'}</td>
                          <td className="font-mono text-xs text-zinc-600" data-testid={`bank-ref-${idx}`}>{refLabel}</td>
                          <td className="text-sm max-w-md">
                            {entry.is_opening_balance ? (
                              <span className="font-medium text-blue-700" data-testid={`bank-opening-${idx}`}>{entry.description}</span>
                            ) : catCode ? (
                              <span>
                                <span className="inline-block px-1.5 py-0.5 mr-1.5 font-mono text-[10px] font-semibold bg-zinc-100 text-zinc-600 align-middle">
                                  {getCatLabel(catCode, t, customCategories)}
                                </span>
                                <span>{entry.description}</span>
                                {entry.paid_from_reserve_fund && (
                                  <span className="inline-block ml-1.5 px-1.5 py-0.5 font-mono text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-sm align-middle" title={t('reserveFundFunded') || 'Reserve Fund funded'} data-testid={`bank-rf-badge-${idx}`}>RF</span>
                                )}
                              </span>
                            ) : (
                              <span>
                                {entry.description || entry.notes || '—'}
                                {entry.paid_from_reserve_fund && (
                                  <span className="inline-block ml-1.5 px-1.5 py-0.5 font-mono text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-sm align-middle" title={t('reserveFundFunded') || 'Reserve Fund funded'} data-testid={`bank-rf-badge-${idx}`}>RF</span>
                                )}
                              </span>
                            )}
                            {/* Phase 1004f — Notes rendered as smaller
                                sub-text under the transaction type on the
                                same cell (Bank Receipts). Cell is width-
                                capped + 2-line clamp so long Notes never
                                stretch the ledger. */}
                            {(() => {
                              const desc = entry.description || '';
                              const notes = entry.notes || '';
                              const showNotes = !entry.is_opening_balance
                                && notes && notes.trim() && notes.trim() !== desc.trim();
                              if (!showNotes) return null;
                              return (
                                <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug line-clamp-2" title={notes} data-testid={`bank-notes-${idx}`}>
                                  {notes}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="font-mono text-right text-emerald-700 font-medium">
                            {entry.amount_in > 0 ? `€${entry.amount_in.toFixed(2)}` : ''}
                          </td>
                          <td className="font-mono text-right text-red-700 font-medium">
                            {entry.amount_out > 0 ? `€${entry.amount_out.toFixed(2)}` : ''}
                          </td>
                          <td className={`font-mono text-right font-semibold ${entry.balance >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>
                            &euro;{entry.balance.toFixed(2)}
                          </td>
                          <td className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {/* Phase 703 — Per-row View PDF (single ledger entry). */}
                              {!entry.is_opening_balance && entry.ref_id && (
                                <button
                                  type="button"
                                  onClick={() => openPdfInBrowser(`/buildings/${buildingId}/cash-ledger/entry/${entry.ref_id}/pdf?source=bank`)}
                                  className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                                  title={t('viewPdf')}
                                  data-testid={`bank-entry-pdf-${idx}`}
                                >
                                  <FilePdf size={14} />
                                </button>
                              )}
                              {entry.is_test ? (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteLedgerEntry(entry)}
                                  className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                  title={t('deleteTestRow') || 'Delete test row'}
                                  data-testid={`bank-entry-delete-${idx}`}
                                >
                                  <Trash size={14} />
                                </button>
                              ) : null}
                              {/* Phase 820.3 — Inline Edit/Delete for Management Company Advance / Repayment rows. */}
                              {(entry.type === 'management_advance' || entry.type === 'management_repayment') && isAdmin && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openMcaDialog('bank', entry.type === 'management_advance' ? 'advance' : 'repayment', { entry_id: entry.entry_id, entry_type: entry.type === 'management_advance' ? 'advance' : 'repayment', source: 'bank', date: entry.date, amount: entry.amount_in || entry.amount_out, reference: entry.reference_number, notes: entry.notes })}
                                    className="p-1 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
                                    title={t('edit') || 'Edit'}
                                    data-testid={`bank-mca-edit-${idx}`}
                                  >
                                    <PencilSimple size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteMcaEntry(entry.entry_id)}
                                    className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title={t('delete') || 'Delete'}
                                    data-testid={`bank-mca-delete-${idx}`}
                                  >
                                    <Trash size={14} />
                                  </button>
                                </>
                              )}
                              {/* Phase 820.4 — Inline Edit/Delete for Apartment Owner Credit / Reimbursement rows. */}
                              {(entry.type === 'apartment_owner_credit' || entry.type === 'apartment_owner_reimbursement') && isAdmin && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openAocDialog('bank', entry.type === 'apartment_owner_credit' ? 'credit' : 'reimbursement', { entry_id: entry.entry_id, entry_type: entry.type === 'apartment_owner_credit' ? 'credit' : 'reimbursement', source: 'bank', apartment_id: entry.apartment_id, apartment_number: entry.apartment, owner_name: '', date: entry.date, amount: entry.amount_in || entry.amount_out, reference: entry.reference_number, notes: entry.notes })}
                                    className="p-1 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
                                    title={t('edit') || 'Edit'}
                                    data-testid={`bank-aoc-edit-${idx}`}
                                  >
                                    <PencilSimple size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteAocEntry(entry.entry_id)}
                                    className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title={t('delete') || 'Delete'}
                                    data-testid={`bank-aoc-delete-${idx}`}
                                  >
                                    <Trash size={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-zinc-100 font-semibold">
                        <td colSpan={3} className="text-right text-xs uppercase tracking-wider text-zinc-600">{t('total')}</td>
                        <td className="font-mono text-right text-emerald-700">&euro;{(bankLedgerView.total_in || 0).toFixed(2)}</td>
                        <td className="font-mono text-right text-red-700">&euro;{(bankLedgerView.total_out || 0).toFixed(2)}</td>
                        <td className={`font-mono text-right font-bold ${(bankLedgerView.balance || 0) < 0 ? 'text-red-700' : 'text-zinc-900'}`}>&euro;{(bankLedgerView.balance || 0).toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ====== BANK DETAILS TAB (nested under Bank Receipts) ====== */}
        {activeTab === 'bankDetails' && (
          <div className="space-y-6" data-testid="bank-details-tab">
            <div className="bg-white border border-zinc-200" data-testid="bank-details-section">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('bankDetails')}</h3>
                {isAdmin && !editingBankDetails && (
                  <button onClick={() => { setBankForm({ bank_account_name: building.bank_account_name || '', bank_account_number: building.bank_account_number || '', bank_iban: building.bank_iban || '', bank_swift: building.bank_swift || '', bank_name: building.bank_name || '', bank_phone: building.bank_phone || '' }); setEditingBankDetails(true); }} className="text-xs text-zinc-500 hover:text-zinc-900" data-testid="edit-bank-details-btn"><PencilSimple size={14} /></button>
                )}
              </div>
              {editingBankDetails ? (
                <form onSubmit={async (e) => { e.preventDefault(); try { await axios.put(`${API}/buildings/${buildingId}/bank-details`, bankForm); toast.success(t('changesSaved')); setEditingBankDetails(false); fetchAll(); } catch { toast.error(t('errorOccurred')); }}} className="p-5 space-y-3" data-testid="bank-details-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">{t('bankName')}</Label><Input value={bankForm.bank_name} onChange={e => setBankForm({...bankForm, bank_name: e.target.value})} className="mt-1 rounded-none" data-testid="bank-name-input" /></div>
                    <div><Label className="text-xs">{t('bankAccountName')}</Label><Input value={bankForm.bank_account_name} onChange={e => setBankForm({...bankForm, bank_account_name: e.target.value})} className="mt-1 rounded-none" data-testid="bank-account-name-input" /></div>
                    <div><Label className="text-xs">{t('bankIban')}</Label><Input value={bankForm.bank_iban} onChange={e => setBankForm({...bankForm, bank_iban: e.target.value})} className="mt-1 rounded-none font-mono" data-testid="bank-iban-input" /></div>
                    <div><Label className="text-xs">{t('bankAccountNumber')}</Label><Input value={bankForm.bank_account_number} onChange={e => setBankForm({...bankForm, bank_account_number: e.target.value})} className="mt-1 rounded-none font-mono" data-testid="bank-account-number-input" /></div>
                    <div><Label className="text-xs">{t('bankSwift')}</Label><Input value={bankForm.bank_swift} onChange={e => setBankForm({...bankForm, bank_swift: e.target.value})} className="mt-1 rounded-none font-mono" data-testid="bank-swift-input" /></div>
                    <div><Label className="text-xs">{t('bankPhone')}</Label><Input value={bankForm.bank_phone} onChange={e => setBankForm({...bankForm, bank_phone: e.target.value})} className="mt-1 rounded-none font-mono" data-testid="bank-phone-input" /></div>
                  </div>
                  <div className="flex gap-2 justify-end"><Button type="button" variant="outline" onClick={() => setEditingBankDetails(false)} className="rounded-none">{t('cancel')}</Button><Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-bank-details-btn">{t('save')}</Button></div>
                </form>
              ) : (
                <div className="p-5">
                  {building.bank_iban || building.bank_account_name || building.bank_name ? (
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                      {building.bank_name && <div><span className="text-zinc-500">{t('bankName')}:</span> <span className="font-medium">{building.bank_name}</span></div>}
                      {building.bank_account_name && <div><span className="text-zinc-500">{t('bankAccountName')}:</span> <span className="font-medium">{building.bank_account_name}</span></div>}
                      {building.bank_iban && <div><span className="text-zinc-500">{t('bankIban')}:</span> <span className="font-mono font-medium">{building.bank_iban}</span></div>}
                      {building.bank_account_number && <div><span className="text-zinc-500">{t('bankAccountNumber')}:</span> <span className="font-mono font-medium">{building.bank_account_number}</span></div>}
                      {building.bank_swift && <div><span className="text-zinc-500">{t('bankSwift')}:</span> <span className="font-mono font-medium">{building.bank_swift}</span></div>}
                      {building.bank_phone && <div><span className="text-zinc-500">{t('bankPhone')}:</span> <span className="font-mono font-medium">{building.bank_phone}</span></div>}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-400">{t('noDataAvailable')}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ====== RESERVE FUND TAB ====== */}
        {activeTab === 'reserveFundTab' && (
          <div className="space-y-6" data-testid="reserve-fund-tab">
            {/* Phase 817 — Manual Reserve Fund Opening Balance seed.
                Captures the RF balance that the building already had on
                the day we took over its management, so historical
                reports and every subsequent period start from the
                correct number. */}
            <div className="bg-white border border-zinc-200" data-testid="reserve-fund-opening-section">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>
                  {t('openingReserveFundBalance') || 'Opening Reserve Fund Balance'}
                </h3>
                <span className="text-[10px] uppercase tracking-wider text-zinc-400">
                  {t('managementTakeoverSeed') || 'management takeover seed'}
                </span>
              </div>
              <div className="p-5">
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const amt = rfSeedAmount === '' ? null : parseFloat(rfSeedAmount);
                      await axios.patch(`${API}/buildings/${buildingId}`, {
                        opening_reserve_fund_balance: amt,
                        opening_reserve_fund_balance_date: rfSeedDate || null,
                        opening_reserve_fund_balance_notes: (rfSeedNotes || '').trim(),
                      });
                      toast.success(t('savedSuccess'));
                      fetchAll();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || t('errorOccurred'));
                    }
                  }}
                  className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
                >
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">{t('amount') || 'Amount'} (&euro;)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={rfSeedAmount}
                      onChange={(e) => setRfSeedAmount(e.target.value)}
                      placeholder="0.00"
                      className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      data-testid="rf-seed-amount"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">{t('date') || 'Date'}</label>
                    <input
                      type="date"
                      value={rfSeedDate}
                      onChange={(e) => setRfSeedDate(e.target.value)}
                      className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      data-testid="rf-seed-date"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">{t('notes') || 'Notes'}</label>
                    <input
                      type="text"
                      value={rfSeedNotes}
                      onChange={(e) => setRfSeedNotes(e.target.value)}
                      placeholder={t('rfSeedNotesPlaceholder') || 'e.g. Balance at management takeover'}
                      className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      data-testid="rf-seed-notes"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                      {t('rfSeedHelp') || 'Only used as the initial balance when this building is first added. After that, every new period automatically carries forward the previous period\'s closing balance.'}
                    </p>
                  </div>
                  <div className="md:col-span-4 flex gap-2">
                    <Button type="submit" className="bg-zinc-950 rounded-none text-xs h-8" data-testid="rf-seed-save">{t('save') || 'Save'}</Button>
                    {(building?.opening_reserve_fund_balance != null) && (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none text-xs h-8"
                        onClick={async () => {
                          try {
                            await axios.patch(`${API}/buildings/${buildingId}`, {
                              opening_reserve_fund_balance: null,
                              opening_reserve_fund_balance_date: null,
                              opening_reserve_fund_balance_notes: '',
                            });
                            setRfSeedAmount(''); setRfSeedDate(''); setRfSeedNotes('');
                            toast.success(t('savedSuccess'));
                            fetchAll();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || t('errorOccurred'));
                          }
                        }}
                        data-testid="rf-seed-clear"
                      >{t('clear') || 'Clear'}</Button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Collected Reserve Fund summary — increases ONLY when invoices are paid */}
            <div className="bg-white border border-zinc-200" data-testid="reserve-fund-collected-section">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>{t('collectedReserveFund')}</h3>
                <span className="text-[10px] uppercase tracking-wider text-zinc-400">{t('basedOnPaidInvoicesOnly')}</span>
              </div>
              <div className="p-5">
                {rfCollected ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div data-testid="rf-collected-amount">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{t('collected')}</p>
                      <p className="text-2xl font-mono font-bold text-emerald-700">&euro;{(rfCollected.collected || 0).toFixed(2)}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {t('of')} <span className="font-mono">&euro;{(rfCollected.target_annual || 0).toFixed(2)}</span> {t('annualTarget').toLowerCase()}
                      </p>
                    </div>
                    <div data-testid="rf-spent-amount">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{t('spent')}</p>
                      <button
                        type="button"
                        onClick={() => setRfSpentOpen(v => !v)}
                        disabled={!(rfCollected.spent_breakdown && rfCollected.spent_breakdown.length)}
                        className={`text-2xl font-mono font-bold text-red-700 ${rfCollected.spent_breakdown && rfCollected.spent_breakdown.length ? 'hover:underline cursor-pointer' : 'cursor-default'}`}
                        data-testid="rf-spent-toggle"
                      >
                        &euro;{(rfCollected.spent || 0).toFixed(2)}
                        {rfCollected.spent_breakdown && rfCollected.spent_breakdown.length > 0 && (
                          <span className="ml-1 text-[11px] text-zinc-400 align-middle">{rfSpentOpen ? '▴' : '▾'}</span>
                        )}
                      </button>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{t('reserveFundDeductions')}</p>
                    </div>
                    <div data-testid="rf-available-amount">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{t('available')}</p>
                      <p className={`text-2xl font-mono font-bold ${(rfCollected.available || 0) < 0 ? 'text-red-700' : 'text-zinc-900'}`}>&euro;{(rfCollected.available || 0).toFixed(2)}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{t('collected')} − {t('spent').toLowerCase()}</p>
                    </div>
                    {/* Progress bar — full row */}
                    <div className="col-span-2 md:col-span-3 mt-1">
                      <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-1">
                        <span>{t('collectionProgress')}</span>
                        <span className="font-mono font-semibold text-zinc-700">{(rfCollected.collection_rate || 0).toFixed(1)}%</span>
                      </div>
                      <div className="h-2 bg-zinc-100 w-full">
                        <div
                          className="h-2 bg-emerald-600 transition-all"
                          style={{ width: `${Math.min(100, rfCollected.collection_rate || 0)}%` }}
                          data-testid="rf-collection-progress-bar"
                        />
                      </div>
                    </div>
                    {/* Spent breakdown — revealed when Spent card is clicked */}
                    {rfSpentOpen && rfCollected.spent_breakdown && rfCollected.spent_breakdown.length > 0 && (
                      <div className="col-span-2 md:col-span-3 mt-3" data-testid="rf-spent-breakdown">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">{t('spent')} — {t('detailBreakdown')}</div>
                        <table className="w-full text-xs" data-testid="rf-spent-breakdown-table">
                          <thead>
                            <tr className="bg-zinc-50 border-b border-zinc-200">
                              <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('date')}</th>
                              <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold">{t('description')}</th>
                              <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('source')}</th>
                              <th className="px-3 py-2 text-right text-[10px] text-zinc-400 font-semibold w-24">{t('amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rfCollected.spent_breakdown.map((row, idx) => (
                              <tr key={`rf-sp-${idx}`} className="border-b border-zinc-100" data-testid={`rf-spent-row-${idx}`}>
                                <td className="px-3 py-2 font-mono text-zinc-500">{row.date ? formatDateDMY(row.date) : '-'}</td>
                                <td className="px-3 py-2 text-zinc-700">{row.description || '-'}</td>
                                <td className="px-3 py-2"><span className="text-[10px] px-2 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200 font-semibold uppercase">{row.source || '-'}</span></td>
                                <td className="px-3 py-2 text-right font-mono text-red-700">&euro;{(row.amount || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                            <tr className="bg-zinc-50 font-semibold">
                              <td colSpan={3} className="px-3 py-2 text-right text-zinc-700">{t('total')}</td>
                              <td className="px-3 py-2 text-right font-mono text-red-700">&euro;{(rfCollected.spent || 0).toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400">{t('loading')}...</p>
                )}
              </div>
            </div>

            {/* Phase 1002 — Recurring Monthly Reserve Fund with effective
                Start/Finish dates. Powers the "Common Expenses + Reserve
                Fund" combined invoice (single invoice, two visible line
                items). Effective-dated: changing the amount from a new
                Start Date preserves previous periods. */}
            <div className="bg-white border border-zinc-200" data-testid="extra-reserve-section">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>
                    {t('extraReserveFund') || 'Extra Monthly Reserve'}
                  </h3>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    {t('extraReserveIncluded') || 'Extra reserve is included in common expenses invoice'}
                  </p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-400">
                  {t('recurringChargeSchedule') || 'recurring charge schedule'}
                </span>
              </div>
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">{t('amount') || 'Amount'} (&euro;)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={extraReserveMonthly}
                      onChange={(e) => setExtraReserveMonthly(e.target.value)}
                      placeholder="0.00"
                      className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      data-testid="extra-reserve-amount"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">{t('mode') || 'Mode'}</label>
                    <select
                      value={extraReserveMode}
                      onChange={(e) => setExtraReserveMode(e.target.value)}
                      className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      data-testid="extra-reserve-mode"
                    >
                      <option value="share">{t('shareOfMonthlyTotal') || 'Share % of monthly total'}</option>
                      <option value="fixed">{t('fixedPerApartment') || 'Fixed per apartment'}</option>
                    </select>
                  </div>
                  {extraReserveMode === 'fixed' && (
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-zinc-500">{t('extraReserveFixed') || 'Fixed Amount Per Apartment'}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={extraReserveFixed}
                        onChange={(e) => setExtraReserveFixed(e.target.value)}
                        placeholder="0.00"
                        className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                        data-testid="extra-reserve-fixed"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">{t('startDate') || 'Start Date'}</label>
                    <input
                      type="date"
                      value={extraReserveStartDate}
                      onChange={(e) => setExtraReserveStartDate(e.target.value)}
                      className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      data-testid="extra-reserve-start-date"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">
                      {t('finishDate') || 'Finish Date'} <span className="text-zinc-400">({t('optional') || 'optional'})</span>
                    </label>
                    <input
                      type="date"
                      value={extraReserveEndDate}
                      onChange={(e) => setExtraReserveEndDate(e.target.value)}
                      className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      data-testid="extra-reserve-end-date"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Button
                    type="button"
                    onClick={handleSaveExtraReserve}
                    className="bg-zinc-950 rounded-none text-xs h-8"
                    data-testid="extra-reserve-save"
                  >
                    {t('save') || 'Save'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleStopExtraReserve}
                    className="rounded-none text-xs h-8"
                    data-testid="extra-reserve-stop"
                  >
                    {t('stop') || 'Stop'}
                  </Button>
                </div>
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  {t('extraReserveHelp') ||
                    'From the Start Date onward, every monthly Common Expenses invoice automatically includes the Reserve Fund amount as a second visible line item on the same invoice. Changing the amount from a new Start Date preserves all previously-issued invoices. Leave Finish Date empty for an open-ended schedule.'}
                </p>

                {extraReserveHistory.length > 0 && (
                  <div className="mt-4 border border-zinc-200" data-testid="extra-reserve-history-table">
                    <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50/50 text-[11px] uppercase tracking-wider text-zinc-600">
                      {t('history') || 'History'}
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-100 text-[11px] uppercase tracking-wider text-zinc-500">
                          <th className="px-3 py-2 text-left">{t('startDate') || 'Start Date'}</th>
                          <th className="px-3 py-2 text-left">{t('endDate') || 'End Date'}</th>
                          <th className="px-3 py-2 text-right">{t('amount') || 'Amount'}</th>
                          <th className="px-3 py-2 text-left">{t('mode') || 'Mode'}</th>
                          <th className="px-3 py-2 text-right">{t('usage') || 'Invoices'}</th>
                          <th className="px-3 py-2 text-right">{t('actions') || ''}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {extraReserveHistory.map(r => (
                          <tr key={r.history_id} className={r.is_current ? 'bg-emerald-50/40' : ''} data-testid={`extra-reserve-row-${r.history_id}`}>
                            <td className="px-3 py-2 font-mono text-xs">{r.start_date || '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs text-zinc-500">
                              {editingHistoryId === r.history_id ? (
                                <input
                                  type="date"
                                  value={editingEndDate}
                                  onChange={(e) => setEditingEndDate(e.target.value)}
                                  className="border border-zinc-300 h-7 px-2 text-xs font-mono"
                                  data-testid={`extra-reserve-edit-enddate-${r.history_id}`}
                                />
                              ) : (
                                r.end_date || <span className="text-zinc-400 italic">{t('open') || 'open'}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-xs">
                              &euro;{(r.extra_reserve_monthly || 0).toFixed(2)}
                              {r.extra_reserve_mode === 'fixed' && (r.extra_reserve_fixed_amount || 0) > 0 && (
                                <span className="text-zinc-400 ml-1">/ &euro;{(r.extra_reserve_fixed_amount || 0).toFixed(2)}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-600">
                              {r.extra_reserve_mode === 'fixed'
                                ? (t('fixedPerApartment') || 'Fixed / apt')
                                : (t('shareOfMonthlyTotal') || 'Share %')}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-zinc-500">{r.usage_count ?? 0}</td>
                            <td className="px-3 py-2 text-right">
                              {editingHistoryId === r.history_id ? (
                                <div className="flex items-center gap-2 justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleSaveHistoryEndDate(r.history_id)}
                                    className="text-[11px] text-zinc-950 hover:underline font-semibold"
                                    data-testid={`extra-reserve-save-${r.history_id}`}
                                  >
                                    {t('save') || 'Save'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingEndDate('')}
                                    className="text-[11px] text-zinc-500 hover:text-zinc-800 underline"
                                    title={t('clearEndDate') || 'Clear end date (Open)'}
                                    data-testid={`extra-reserve-clear-${r.history_id}`}
                                  >
                                    {t('clear') || 'Clear'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setEditingHistoryId(null); setEditingEndDate(''); }}
                                    className="text-[11px] text-zinc-400 hover:text-zinc-700 underline"
                                  >
                                    {t('cancel') || 'Cancel'}
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-3 justify-end">
                                  <button
                                    type="button"
                                    onClick={() => { setEditingHistoryId(r.history_id); setEditingEndDate(r.end_date || ''); }}
                                    className="text-[11px] text-zinc-500 hover:text-zinc-900 underline"
                                    data-testid={`extra-reserve-edit-${r.history_id}`}
                                  >
                                    {t('edit') || 'Edit'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteExtraReserveHistory(r.history_id)}
                                    className="text-[11px] text-zinc-400 hover:text-red-600 underline"
                                    data-testid={`extra-reserve-delete-${r.history_id}`}
                                  >
                                    {t('delete') || 'Delete'}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Reserve Fund Statement — moved above Distribution per user request */}
            <div className="bg-white border border-zinc-200 p-5 flex items-center justify-between" data-testid="reserve-fund-statement-section">
              <div>
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('reserveFundStatement')}</h3>
                <p className="text-xs text-zinc-400 mt-0.5">{t('reserveStatementDesc')}</p>
              </div>
              <Button onClick={() => navigate(`/buildings/${buildingId}/reserve-fund-statement`)} className="bg-zinc-950 rounded-none" data-testid="reserve-statement-btn">
                <FileText size={16} className="mr-1.5" />{t('reserveFundStatement')}
              </Button>
            </div>

            {/* Phase 166 — Reserve Fund Distribution (READ-ONLY, sourced from Annual Expenses) */}
            <div className="bg-white border border-zinc-200" data-testid="reserve-fund-distribution-ro">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>{t('reserveFundDistributionTitle')}</h3>
                  <p className="text-[11px] text-zinc-400 mt-0.5">{t('reserveFundManagedInAnnualExpenses')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('annualExpenses')}
                  className="text-[11px] text-blue-600 hover:underline font-semibold"
                  data-testid="rf-open-annual-expenses"
                >
                  {t('openAnnualExpenses')} →
                </button>
              </div>
              {(() => {
                // Phase 411 — Mirror the full Reserve Fund Distribution from
                // Annual Expenses, including the per-apartment **Adjustment**
                // and **Final Monthly** columns. The Final Monthly value is
                // the single source of truth used by the Reserve Fund
                // Statement to credit each apartment payment.
                //
                // The same source values feed both screens:
                //   - rfRounded → annualCatValues['__rf'] (falls back to legacy)
                //   - monthlyAuto → effectiveIncluded / 12 across all included
                //     categories + RF.
                //   - per-apartment monthly_final_override on annualPlan.apartments
                const rfTotal = (() => {
                  const fromCatValues = parseFloat(annualCatValues?.['__rf']);
                  if (!isNaN(fromCatValues) && fromCatValues > 0) return fromCatValues;
                  if (annualPlan?.reserve_fund_budget != null) return Number(annualPlan.reserve_fund_budget || 0);
                  return Number((annualPlan?.categories || {}).reserve_fund?.amount || 0);
                })();
                const apts = ((annualPlan?.apartments) || []).slice().sort((a, b) => {
                  const an = String(a.apartment_number || '');
                  const bn = String(b.apartment_number || '');
                  return an.localeCompare(bn, undefined, { numeric: true });
                });
                if (!annualPlan || apts.length === 0 || rfTotal <= 0) {
                  return (
                    <div className="p-5 text-xs text-zinc-500">
                      {t('noReserveFundPlan')}
                    </div>
                  );
                }
                // ---- effectiveIncluded / monthlyAuto (same formula as Annual Expenses) ----
                const catView = annualPlan.category_view || [];
                const rfRounded = rfTotal;
                const effectiveOf = (c) => {
                  const raw = annualCatValues?.[c.code];
                  if (raw === undefined || raw === null || raw === '') return c.actual || 0;
                  const num = parseFloat(raw);
                  return isNaN(num) ? (c.actual || 0) : num;
                };
                const isIncluded = (code) => includeInDist?.[code] !== false;
                let effectiveIncluded = rfRounded;
                catView.forEach(c => { if (isIncluded(c.code)) effectiveIncluded += effectiveOf(c); });
                effectiveIncluded = Math.round(effectiveIncluded * 100) / 100;
                const monthlyAuto = effectiveIncluded / 12;
                // ---- Rounding-adjustment derivation (identical to Annual Expenses) ----
                const monthlyTotalBefore = monthlyAuto;
                const totalRounded = apts.reduce((s, a) => {
                  const share = a.share_percentage || 0;
                  const r = a.monthly_final_override != null
                    ? a.monthly_final_override
                    : Math.round((monthlyAuto * share / 100) * 100) / 100;
                  return s + r;
                }, 0);
                const totalRoundAdjustmentMonthly = Math.round((totalRounded - monthlyTotalBefore) * 100) / 100;
                const totalRoundAdjustmentAnnual = Math.round(totalRoundAdjustmentMonthly * 12 * 100) / 100;
                // ---- Per-row figures ----
                const totalShare = apts.reduce((s, a) => s + (a.share_percentage || 0), 0);
                const annualTotal = apts.reduce((s, a) => s + ((rfRounded * (a.share_percentage || 0)) / 100), 0);
                const monthlyTotal = annualTotal / 12;
                const totalAdjAnnual = apts.reduce((s, a) => s + ((totalRoundAdjustmentAnnual * (a.share_percentage || 0)) / 100), 0);
                const finalMonthlyTotal = monthlyTotal + (totalAdjAnnual / 12);
                return (
                  <div className="overflow-x-auto">
                    <div className="px-5 py-2 flex items-center justify-end gap-3 text-[11px] text-zinc-500 border-b border-zinc-100">
                      <span>{t('annualTotal')}: <span className="font-mono font-semibold text-zinc-800" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{rfRounded.toFixed(2)}</span></span>
                      <span>·</span>
                      <span>{t('monthly')}: <span className="font-mono font-semibold text-zinc-800" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{(rfRounded / 12).toFixed(2)}</span></span>
                      {Math.abs(totalRoundAdjustmentAnnual) > 0.001 && (
                        <>
                          <span>·</span>
                          <span>{t('rfAdjustmentColLabel') || 'Adjustment'}: <span className="font-mono font-semibold text-zinc-800" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{totalRoundAdjustmentAnnual.toFixed(2)}</span></span>
                        </>
                      )}
                    </div>
                    <table className="w-full text-sm" data-testid="rf-distribution-ro-table">
                      <thead>
                        <tr className="border-b border-zinc-200 bg-zinc-50/50">
                          <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('apartment')}</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('ownerName')}</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('share')} (%)</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('colAnnual')}</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('colMonthly')}</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider" title={t('rfAdjustmentColTitle')}>{t('rfAdjustmentColLabel') || 'Adjustment'} (€)</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-zinc-900 uppercase tracking-wider" title={t('rfFinalMonthlyColTitle')}>{t('rfFinalMonthlyColLabel') || 'Final Monthly'} (€)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {apts.map(a => {
                          const share = a.share_percentage || 0;
                          const annual = (rfRounded * share) / 100;
                          const monthly = annual / 12;
                          const adjustmentAnnual = (totalRoundAdjustmentAnnual * share) / 100;
                          const adjustmentMonthly = adjustmentAnnual / 12;
                          const finalMonthly = monthly + adjustmentMonthly;
                          const adjCls = adjustmentAnnual > 0.001 ? 'text-emerald-700' : adjustmentAnnual < -0.001 ? 'text-red-700' : 'text-zinc-400';
                          return (
                            <tr key={a.apartment_id} className="hover:bg-zinc-50/50" data-testid={`rf-dist-ro-row-${a.apartment_id}`}>
                              <td className="px-4 py-1.5 font-mono text-sm">{a.apartment_number || '—'}</td>
                              <td className="px-4 py-1.5 text-sm text-zinc-700">{a.owner_name || <span className="text-zinc-300">—</span>}</td>
                              <td className="px-4 py-1.5 text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{share.toFixed(2)}</td>
                              <td className="px-4 py-1.5 text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`rf-dist-ro-annual-${a.apartment_id}`}>&euro;{annual.toFixed(2)}</td>
                              <td className="px-4 py-1.5 text-right font-mono text-sm text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`rf-dist-ro-monthly-${a.apartment_id}`}>&euro;{monthly.toFixed(2)}</td>
                              <td className={`px-4 py-1.5 text-right font-mono text-sm ${adjCls}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`rf-dist-ro-adjustment-${a.apartment_id}`}>
                                {adjustmentAnnual >= 0 ? '+' : ''}€{adjustmentAnnual.toFixed(2)}
                              </td>
                              <td className="px-4 py-1.5 text-right font-mono text-sm font-bold text-zinc-900" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`rf-dist-ro-final-monthly-${a.apartment_id}`}>&euro;{finalMonthly.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-zinc-300 bg-zinc-50 font-semibold">
                          <td className="px-4 py-2" colSpan={2}>{t('total')}</td>
                          <td className="px-4 py-2 text-right font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{totalShare.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-mono" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-dist-ro-total-annual">&euro;{annualTotal.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-mono text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-dist-ro-total-monthly">&euro;{monthlyTotal.toFixed(2)}</td>
                          <td className={`px-4 py-2 text-right font-mono ${totalAdjAnnual > 0.001 ? 'text-emerald-700' : totalAdjAnnual < -0.001 ? 'text-red-700' : 'text-zinc-400'}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-dist-ro-total-adjustment">
                            {totalAdjAnnual >= 0 ? '+' : ''}€{totalAdjAnnual.toFixed(2)}
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-bold text-zinc-900" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-dist-ro-total-final-monthly">&euro;{finalMonthlyTotal.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ====== CATEGORIES TAB ====== */}
        {activeTab === 'categories' && (
          <div className="space-y-4" data-testid="categories-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>{t('categories')}</h2>
                <p className="text-xs text-zinc-500 mt-0.5">{t('categoriesManageHelp')}</p>
              </div>
              {isAdmin && (
                <Button
                  size="sm"
                  className="bg-zinc-900 rounded-none"
                  data-testid="add-category-btn"
                  onClick={() => {
                    // Phase 286 — Open the existing dialog with isNew=true.
                    // Previously this used window.prompt() which was silently
                    // blocked in the preview iframe, so nothing happened.
                    setEditingCat({ isNew: true });
                    setCatForm({ name: '', group: 'monthly', category_code: '' });
                    setCatDialogOpen(true);
                  }}
                >
                  <Plus size={14} className="mr-1" />{t('addCategory')}
                </Button>
              )}
            </div>
            <div className="space-y-6">
              {/* Render two separate sections: Monthly + Capital */}
              {[
                { group: 'monthly', label: t('monthlyExpenses') },
                { group: 'capital', label: t('capitalExpenses') },
              ].map(({ group, label }) => {
                const rows = customCategories.filter(c => (c.group || 'monthly') === group);
                return (
                  <div key={group} className="bg-white border border-zinc-200 overflow-x-auto" data-testid={`categories-section-${group}`}>
                    <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
                      <h3 className="text-xs font-bold tracking-wider uppercase text-zinc-700">{label}</h3>
                      <span className="text-[11px] text-zinc-400 font-mono">{rows.length}</span>
                    </div>
                    <table className="w-full text-sm" data-testid={`categories-table-${group}`}>
                      <thead>
                        <tr className="bg-white border-b border-zinc-200">
                          <th className="px-4 py-2.5 text-left text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-24">{t('code')}</th>
                          <th className="px-4 py-2.5 text-left text-[10px] text-zinc-400 font-semibold uppercase tracking-wider">{t('name')}</th>
                          <th className="px-4 py-2.5 text-right text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-28">{t('usage')}</th>
                          {isAdmin && <th className="px-4 py-2.5 text-right text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-36">{t('actions')}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr><td colSpan={isAdmin ? 4 : 3} className="px-4 py-8 text-center text-zinc-400 text-xs">{t('noCategories') || t('noData')}</td></tr>
                        ) : rows.map((c) => {
                          const used = c.usage_count || 0;
                          const displayName = c.name || t(`cat_${c.category_code}`) || '-';
                          return (
                            <tr key={c.category_code} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`cat-row-${c.category_code}`}>
                              <td className="px-4 py-2.5 font-mono font-semibold text-zinc-800">{c.category_code}</td>
                              <td className="px-4 py-2.5 text-zinc-700" data-testid={`cat-name-${c.category_code}`}>{displayName}</td>
                              <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-500" data-testid={`cat-usage-${c.category_code}`}>{used}</td>
                              {isAdmin && (
                                <td className="px-4 py-2.5 text-right">
                                  <div className="flex items-center gap-1 justify-end">
                                    <button
                                      className="p-1 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800"
                                      title={t('edit')}
                                      data-testid={`cat-edit-${c.category_code}`}
                                      onClick={() => {
                                        setEditingCat({ ...c, name: displayName, isNew: false });
                                        setCatForm({ name: displayName, group: c.group || 'monthly' });
                                        setCatDialogOpen(true);
                                      }}
                                    ><PencilSimple size={14} /></button>
                                    <button
                                      className={`p-1 ${used > 0 ? 'text-zinc-300 cursor-not-allowed' : 'hover:bg-red-50 text-red-400 hover:text-red-600'}`}
                                      title={used > 0 ? t('cannotDeleteInUse') : t('delete')}
                                      data-testid={`cat-delete-${c.category_code}`}
                                      onClick={() => {
                                        if (used > 0) { toast.error(t('cannotDeleteInUse')); return; }
                                        setCatToDelete(c);
                                        setCatDeleteOpen(true);
                                      }}
                                    ><Trash size={14} /></button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>

            {/* ===== Add/Edit Category Dialog ===== */}
            <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
              <DialogContent className="rounded-none max-w-md" data-testid="cat-dialog">
                <DialogHeader>
                  <DialogTitle>{editingCat?.isNew ? t('addCategory') : `${t('edit')}: ${editingCat?.category_code || ''}`}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">{t('name')}</Label>
                    <Input
                      value={catForm.name}
                      onChange={(e) => setCatForm(f => ({ ...f, name: e.target.value }))}
                      className="mt-1 rounded-none"
                      data-testid="cat-name-input"
                      placeholder={t('newCategoryNamePrompt')}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('type')}</Label>
                    <Select
                      value={catForm.group}
                      onValueChange={(v) => setCatForm(f => ({ ...f, group: v }))}
                      disabled={!editingCat?.isNew && (editingCat?.usage_count || 0) > 0}
                    >
                      <SelectTrigger className="mt-1 rounded-none" data-testid="cat-group-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">{t('monthly')}</SelectItem>
                        <SelectItem value="capital">{t('capital')}</SelectItem>
                      </SelectContent>
                    </Select>
                    {!editingCat?.isNew && (editingCat?.usage_count || 0) > 0 && (
                      <p className="text-[11px] text-zinc-500 mt-1">{t('cannotChangeTypeInUse')}</p>
                    )}
                  </div>
                  {/* Phase 286 — Optional manual code. Leave blank to auto-generate
                      the next available code in the matching series (200 for
                      monthly, 300 for capital). */}
                  {editingCat?.isNew && (
                    <div>
                      <Label className="text-xs">
                        {t('code')} <span className="text-zinc-400 text-[10px]">({t('optional')})</span>
                      </Label>
                      <Input
                        value={catForm.category_code || ''}
                        onChange={(e) => setCatForm(f => ({ ...f, category_code: e.target.value.replace(/[^0-9]/g, '') }))}
                        className="mt-1 rounded-none font-mono"
                        data-testid="cat-code-input"
                        placeholder={catForm.group === 'capital' ? '311' : '207'}
                        maxLength={4}
                      />
                      <p className="text-[11px] text-zinc-500 mt-1">{t('codeAutoGenerateHint')}</p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setCatDialogOpen(false)} className="rounded-none" data-testid="cat-cancel-btn">{t('cancel')}</Button>
                  <Button
                    className="bg-zinc-900 rounded-none"
                    data-testid="cat-save-btn"
                    onClick={async () => {
                      if (!catForm.name.trim()) {
                        toast.error(t('fillRequiredFields'));
                        return;
                      }
                      try {
                        if (editingCat?.isNew) {
                          const payload = { name: catForm.name.trim(), group: catForm.group };
                          // Phase 286 — Only forward category_code if user typed one
                          if (catForm.category_code && catForm.category_code.trim()) {
                            payload.category_code = catForm.category_code.trim();
                          }
                          await axios.post(`${API}/expense-categories`, payload);
                        } else {
                          await axios.put(`${API}/expense-categories/${editingCat.category_code}`, { name: catForm.name.trim(), group: catForm.group });
                        }
                        toast.success(t('savedSuccess'));
                        setCatDialogOpen(false);
                        setEditingCat(null);
                        const r = await axios.get(`${API}/expense-categories`);
                        setCustomCategories(r.data || []);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || t('errorOccurred'));
                      }
                    }}
                  >{t('save')}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>


            {/* ===== Delete Category Confirmation ===== */}
            <AlertDialog open={catDeleteOpen} onOpenChange={setCatDeleteOpen}>
              <AlertDialogContent className="rounded-none">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('delete')}: {catToDelete?.category_code}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('confirmDelete')}
                    {catToDelete?.name && (
                      <span className="block mt-2 text-xs text-zinc-500">
                        <span className="font-mono font-semibold">{catToDelete.category_code}</span> — {catToDelete.name}
                      </span>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none" data-testid="cat-delete-cancel">{t('cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 hover:bg-red-700 rounded-none"
                    data-testid="cat-delete-confirm"
                    onClick={async () => {
                      if (!catToDelete) return;
                      try {
                        await axios.delete(`${API}/expense-categories/${catToDelete.category_code}`);
                        toast.success(t('deletedSuccess'));
                        setCatDeleteOpen(false);
                        setCatToDelete(null);
                        const r = await axios.get(`${API}/expense-categories`);
                        setCustomCategories(r.data || []);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || t('errorOccurred'));
                      }
                    }}
                  >{t('delete')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* ====== ANNUAL EXPENSES TAB ====== */}
        {activeTab === 'annualExpenses' && (
          <div className="space-y-6" data-testid="annual-expenses-tab">
            {/* Phase 443 — Year arrows/labels removed; the Plan dropdown
                is now the single switcher. Picking a plan whose year differs
                from the current `annualYear` swaps the year automatically.
                Cleaner UX requested by the committee — no more "what year am
                I on?" confusion. */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 mr-1" data-testid="annual-plan-label">{t('activePlan') || 'Active Plan'}:</span>
                  {allPlansList.length === 0 ? (
                    <div className="flex items-center gap-2" data-testid="plans-empty-state">
                      <span className="text-xs text-zinc-500 italic">{t('noPlansAvailable')}</span>
                      {isAdmin && (
                        <Button
                          size="sm"
                          onClick={() => openPlanDialog('new')}
                          className="rounded-none h-8 bg-zinc-950 text-white hover:bg-zinc-800"
                          data-testid="plans-empty-create-btn"
                        >
                          <Plus size={14} className="mr-1" />
                          {t('createNewPlan')}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Select
                      value={currentPlanId || ''}
                      onValueChange={(v) => {
                        const target = allPlansList.find(p => p.plan_id === v);
                        if (!target) { setCurrentPlanId(v); fetchAnnualPlan(annualYear, v); return; }
                        const targetYear = parseInt(target.year || annualYear, 10);
                        setCurrentPlanId(v);
                        if (targetYear !== annualYear) setAnnualYear(targetYear);
                        fetchAnnualPlan(targetYear, v);
                      }}
                    >
                      <SelectTrigger className="rounded-none h-9 w-80 font-semibold" data-testid="plan-selector">
                        <SelectValue placeholder={t('selectPlan')} />
                      </SelectTrigger>
                      <SelectContent>
                        {allPlansList.map(p => (
                          <SelectItem key={p.plan_id} value={p.plan_id} data-testid={`plan-option-${p.plan_id}`}>
                            <span className="flex items-center gap-2">
                              {p.is_active && <Check size={12} className="text-emerald-600" weight="bold" />}
                              <span className="font-semibold text-zinc-800">{p.name || '—'}</span>
                              {p.year && (
                                <span className="font-mono text-[10px] text-zinc-400 tracking-wider">{p.year}</span>
                              )}
                              {p.is_active && (
                                <span className="text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1 border border-emerald-200">{t('activePlan') || 'Active Plan'}</span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {isAdmin && allPlansList.length > 0 && (
                    <>
                      <button onClick={() => openPlanDialog('new')} className="p-1.5 hover:bg-zinc-100 text-zinc-500" title={t('newPlan')} data-testid="plan-new-btn"><Plus size={14} /></button>
                      <button onClick={() => currentPlanId && openPlanDialog('duplicate', currentPlanId, `${allPlansList.find(p => p.plan_id === currentPlanId)?.name || ''} (copy)`)} className="p-1.5 hover:bg-zinc-100 text-zinc-500 disabled:opacity-40" title={t('duplicatePlan')} disabled={!currentPlanId} data-testid="plan-duplicate-btn"><Copy size={14} /></button>
                      <button onClick={() => { const p = allPlansList.find(p => p.plan_id === currentPlanId); if (p) openPlanDialog('rename', p.plan_id, p.name); }} className="p-1.5 hover:bg-zinc-100 text-zinc-500 disabled:opacity-40" title={t('renamePlan')} disabled={!currentPlanId} data-testid="plan-rename-btn"><PencilSimple size={14} /></button>
                      {/* Phase 281 — "Edit Plan Dates" pencil removed: plans no longer carry a start/end date that drives apartment behaviour. */}
                      <button onClick={() => activatePlan(currentPlanId)} className="px-2 py-1 hover:bg-emerald-50 text-emerald-700 text-xs font-semibold disabled:opacity-40" title={t('markAsActivePlan') || 'Mark as Active Plan'} disabled={!currentPlanId || allPlansList.find(p => p.plan_id === currentPlanId)?.is_active} data-testid="plan-activate-btn">{t('markActive') || t('activate')}</button>
                      <button
                        onClick={() => deletePlan(currentPlanId)}
                        className="p-1.5 hover:bg-red-50 text-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={(() => {
                          const sel = allPlansList.find(p => p.plan_id === currentPlanId);
                          if (!sel) return t('deletePlan');
                          return sel.is_active ? t('cannotDeleteActivePlan') : t('deletePlan');
                        })()}
                        disabled={(() => {
                          const sel = allPlansList.find(p => p.plan_id === currentPlanId);
                          return !currentPlanId || !!sel?.is_active;
                        })()}
                        data-testid="plan-delete-btn"
                      ><Trash size={14} /></button>
                    </>
                  )}
                </div>
                {/* Phase 281 — "Active from DD/MM/YYYY" label removed:
                      Annual Expenses are now planning-only; no period bound to a plan. */}
              </div>
              {isAdmin && !annualEditing && (
                <div className="flex gap-2 items-center">
                  {/* Phase 153 — auto-save status indicator */}
                  <AnnualSavePill status={annualSaveStatus} t={t} onManualSave={async () => {
                    if (annualSaveTimerRef.current) clearTimeout(annualSaveTimerRef.current);
                    try {
                      setAnnualSaveStatus('saving');
                      const { budgets, includedCodes, rfBudget } = buildCodePayload();
                      await axios.put(`${API}/buildings/${buildingId}/annual-expenses`, {
                        year: annualYear,
                        plan_id: currentPlanId,
                        category_budgets: budgets,
                        included_codes: includedCodes,
                        reserve_fund_budget: rfBudget,
                        distribution_source: distributionSource,
                        monthly_override: annualPlan?.monthly_override,
                      });
                      setAnnualCatBaseline({ ...annualCatValues });
                      setAnnualSaveStatus('saved');
                      fetchAnnualPlan(annualYear);
                    } catch { setAnnualSaveStatus('error'); toast.error(t('errorOccurred')); }
                  }} />
                  {/* Phase 253 — Explicit Save button when there are unsaved
                      changes. The pill alone was easy to miss; this button
                      gives users an unmistakable primary action. */}
                  {(annualSaveStatus === 'unsaved' || annualSaveStatus === 'error') && (
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          setAnnualSaveStatus('saving');
                          const { budgets, includedCodes, rfBudget } = buildCodePayload();
                          await axios.put(`${API}/buildings/${buildingId}/annual-expenses`, {
                            year: annualYear,
                            plan_id: currentPlanId,
                            category_budgets: budgets,
                            included_codes: includedCodes,
                            reserve_fund_budget: rfBudget,
                            distribution_source: distributionSource,
                            monthly_override: annualPlan?.monthly_override,
                          });
                          setAnnualCatBaseline({ ...annualCatValues });
                          setAnnualSaveStatus('saved');
                          toast.success(t('savedSuccess'));
                          fetchAnnualPlan(annualYear);
                        } catch { setAnnualSaveStatus('error'); toast.error(t('errorOccurred')); }
                      }}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-none"
                      data-testid="annual-explicit-save-btn"
                    >
                      <FloppyDisk size={14} className="mr-1" />{t('save')}
                    </Button>
                  )}
                  <Button onClick={() => printPdf(`/buildings/${buildingId}/annual-expenses/export/pdf`, { year: annualYear, plan_id: currentPlanId })} size="sm" variant="outline" className="rounded-none" data-testid="annual-print-pdf-btn">
                    <Printer size={14} className="mr-1" />{t('printPdf')}
                  </Button>
                  <Button onClick={() => downloadPdfUtil(`/buildings/${buildingId}/annual-expenses/export/pdf`, { year: annualYear, plan_id: currentPlanId })} size="sm" variant="outline" className="rounded-none" data-testid="annual-download-pdf-btn">
                    <DownloadSimple size={14} className="mr-1" />{t('downloadPdf')}
                  </Button>
                  <Button onClick={() => setAnnualEditing(true)} size="sm" className="bg-zinc-950 rounded-none" data-testid="annual-edit-btn">
                    <PencilSimple size={14} className="mr-1" />{t('editExpenses')}
                  </Button>
                </div>
              )}
              {annualEditing && (
                <div className="flex gap-2">
                  <Button onClick={handleLoadFromExpenses} size="sm" variant="outline" className="rounded-none" disabled={loadingFromExpenses} data-testid="load-from-expenses-btn">
                    {loadingFromExpenses ? '...' : t('loadFromExpenses')}
                  </Button>
                  <Button onClick={handleSaveAnnualPlan} size="sm" className="bg-zinc-950 rounded-none" data-testid="annual-save-btn">{t('saveAndDistribute')}</Button>
                  <Button onClick={() => { setAnnualEditing(false); fetchAnnualPlan(annualYear); }} size="sm" variant="outline" className="rounded-none">{t('cancel')}</Button>
                </div>
              )}
            </div>

            {annualLoading ? <div className="text-center py-12 text-zinc-400">{t('loading')}...</div> : annualPlan && (() => {
              // ---- Phase 60 helpers: code-keyed, sourced from category_view ----
              const catView = annualPlan.category_view || [];
              const rfView = annualPlan.reserve_fund_view || { budget: 0, actual: 0 };
              const isIncluded = (code) => includeInDist[code] !== false;

              // Compute live totals from current state (budgets + includes)
              const monthlyCats = catView.filter(c => c.group === 'monthly');
              const capitalCats = catView.filter(c => c.group === 'capital');
              const allCats = [...monthlyCats, ...capitalCats];

              // Reserve Fund is always included
              // Phase 62: Rounded actual (per category). Displayed as "Actual (€)" input
              // while preserving the true c.actual (from expenses) as reference. Rounded
              // value drives all Monthly and Distribution calculations.
              // Reserve Fund has no expense-sourced actual; its rounded value is the
              // sole figure and is always included.
              const rfRounded = parseFloat(annualCatValues['__rf']) || 0;
              // Effective per-category value: rounded override (if user entered any)
              // falling back to the expense-sourced actual when not overridden.
              const effectiveOf = (c) => {
                const raw = annualCatValues[c.code];
                if (raw === undefined || raw === null || raw === '') return c.actual || 0;
                const num = parseFloat(raw);
                return isNaN(num) ? (c.actual || 0) : num;
              };
              // Totals — drop Budget concept. Only one effective total now.
              let effectiveIncluded = rfRounded;
              let actualOriginalIncluded = rfView.actual || 0;  // original from expenses
              allCats.forEach(c => {
                if (!isIncluded(c.code)) return;
                effectiveIncluded += effectiveOf(c);
                actualOriginalIncluded += c.actual || 0;
              });
              effectiveIncluded = Math.round(effectiveIncluded * 100) / 100;
              actualOriginalIncluded = Math.round(actualOriginalIncluded * 100) / 100;
              const distTotal = effectiveIncluded;
              const monthlyAuto = effectiveIncluded / 12;  // Monthly = Rounded / 12
              const monthlyFinalLive = monthlyOverride !== null && monthlyOverride !== '' ? (parseFloat(monthlyOverride) || 0) : monthlyAuto;
              // Aliases kept so existing JSX below still compiles
              const actualIncluded = effectiveIncluded;
              const plannedIncluded = effectiveIncluded;

              // Label resolver: custom name → translated cat_XXX → code
              const catLabel = (c) => {
                if (c.name) return c.name;
                const key = `cat_${c.code}`;
                const trans = t(key);
                return trans !== key ? trans : c.code;
              };

              const rfLabel = language === 'el' ? 'Αποθεματικό' : 'Reserve Fund';

              return (
              <>
                {/* Auto-filled notice */}
                {annualPlan.auto_filled_from && !annualPlan.created_at && (
                  <div className="bg-blue-50 border border-blue-200 px-4 py-2 text-sm text-blue-700 flex items-center gap-2">
                    <span>&#8505;</span> {t('autoFilledFrom')} {annualPlan.auto_filled_from}
                  </div>
                )}

                {/* Phase 62: distribution source toggle removed — there is only one
                    effective value per category (rounded override or original actual). */}

                {/* Category Selector (multi-select sourced from system categories) */}
                <div className="bg-white border border-zinc-200 px-5 py-3 flex items-center justify-between gap-4" data-testid="category-selector">
                  <div>
                    <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('includedCategories')}</h3>
                    <p className="text-xs text-zinc-400 mt-0.5">{t('includedCategoriesDesc')}</p>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-none min-w-[220px] justify-between" data-testid="category-selector-btn">
                        <span className="text-left text-xs">
                          {(() => {
                            const selected = allCats.filter(c => isIncluded(c.code)).length;
                            return `${selected + 1} / ${allCats.length + 1} ${t('categoriesSelected')}`;
                          })()}
                        </span>
                        <span className="ml-2 text-zinc-400">&#9662;</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-96 p-0 rounded-none" align="end">
                      <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
                        <span className="text-xs font-semibold text-zinc-600">{t('selectCategories')}</span>
                        <div className="flex gap-1 text-[11px]">
                          <button type="button" onClick={() => {
                            const updated = { ...includeInDist };
                            allCats.forEach(c => { updated[c.code] = true; });
                            setIncludeInDist(updated);
                          }} className="text-blue-600 hover:underline" data-testid="select-all-cats-btn">{t('selectAll')}</button>
                          <span className="text-zinc-300">|</span>
                          <button type="button" onClick={() => {
                            const updated = { ...includeInDist };
                            allCats.forEach(c => { updated[c.code] = false; });
                            setIncludeInDist(updated);
                          }} className="text-zinc-500 hover:underline" data-testid="select-none-cats-btn">{t('selectNone')}</button>
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {/* Pinned Reserve Fund — always included */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-zinc-100" data-testid="category-option-reserve_fund">
                          <input type="checkbox" checked disabled className="cursor-not-allowed opacity-60" />
                          <span className="text-sm flex-1">{rfLabel}</span>
                          <span className="text-[10px] text-blue-600 font-semibold">{t('alwaysIncluded')}</span>
                        </div>
                        {/* Monthly group */}
                        <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-100">
                          <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{t('monthly')}</span>
                        </div>
                        {monthlyCats.map(c => (
                          <label key={c.code} className={`flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer border-b border-zinc-100 ${!isIncluded(c.code) ? 'opacity-50' : ''}`} data-testid={`category-option-${c.code}`}>
                            <input
                              type="checkbox"
                              checked={isIncluded(c.code)}
                              onChange={(e) => setIncludeInDist(prev => ({ ...prev, [c.code]: e.target.checked }))}
                              className="cursor-pointer"
                              data-testid={`category-option-checkbox-${c.code}`}
                            />
                            <span className="text-xs font-mono text-zinc-400 w-8">{c.code}</span>
                            <span className="text-sm flex-1">{catLabel(c)}</span>
                            <span className="text-xs font-mono text-emerald-700">&euro;{(c.actual || 0).toFixed(2)}</span>
                          </label>
                        ))}
                        {/* Capital group */}
                        <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-100">
                          <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{t('capital')}</span>
                        </div>
                        {capitalCats.map(c => (
                          <label key={c.code} className={`flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer border-b border-zinc-100 ${!isIncluded(c.code) ? 'opacity-50' : ''}`} data-testid={`category-option-${c.code}`}>
                            <input
                              type="checkbox"
                              checked={isIncluded(c.code)}
                              onChange={(e) => setIncludeInDist(prev => ({ ...prev, [c.code]: e.target.checked }))}
                              className="cursor-pointer"
                              data-testid={`category-option-checkbox-${c.code}`}
                            />
                            <span className="text-xs font-mono text-zinc-400 w-8">{c.code}</span>
                            <span className="text-sm flex-1">{catLabel(c)}</span>
                            <span className="text-xs font-mono text-emerald-700">&euro;{(c.actual || 0).toFixed(2)}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Expense Categories Table */}
                <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="annual-categories-table">
                  <table className="excel-table excel-table-compact">
                    <thead>
                      <tr>
                        <th className="w-1/3">{t('expenseCategory')}</th>
                        <th className="text-right">{t('actual')} (&euro;)</th>
                        <th className="text-right">{t('monthlyAmount')} (&euro;)</th>
                        {annualEditing && <th className="w-16"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Monthly group */}
                      {monthlyCats.filter(c => isIncluded(c.code)).length > 0 && (
                        <tr className="bg-blue-50"><td colSpan={annualEditing ? 4 : 3} className="text-xs font-semibold text-blue-700 uppercase tracking-wider py-1">{t('operationalExpenses')} — {t('monthly')}</td></tr>
                      )}
                      {monthlyCats.filter(c => isIncluded(c.code)).map(c => {
                        const actualRaw = c.actual || 0;
                        const effective = effectiveOf(c);
                        const overridden = annualCatValues[c.code] !== undefined && annualCatValues[c.code] !== '' && Math.abs(effective - actualRaw) > 0.001;
                        return (
                        <tr key={c.code} data-testid={`annual-row-${c.code}`}>
                          <td className="text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-zinc-400">{c.code}</span>
                              <span>{catLabel(c)}</span>
                              <button type="button" onClick={() => openAnnualHistory(c.code, catLabel(c))} className="p-0.5 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('viewHistory')} data-testid={`annual-history-btn-${c.code}`}>
                                <ClockClockwise size={14} />
                              </button>
                            </div>
                          </td>
                          <td className="text-right font-mono">
                            <div className="flex items-center justify-end gap-2">
                              {overridden && actualRaw > 0 && (
                                <span className="text-[10px] text-zinc-400 whitespace-nowrap" data-testid={`annual-original-${c.code}`}>
                                  {t('original')}: &euro;{actualRaw.toFixed(2)}
                                </span>
                              )}
                              <Input
                                type="number" step="0.01"
                                value={annualCatValues[c.code] ?? ''}
                                placeholder={actualRaw.toFixed(2)}
                                onChange={(e) => setAnnualCatValues(v => ({ ...v, [c.code]: e.target.value }))}
                                onBlur={(e) => setAnnualCatValues(v => ({ ...v, [c.code]: formatMoneyBlur(e.target.value) }))}
                                className={`w-24 h-6 rounded-none font-mono text-right text-[12px] px-1.5 ${overridden ? 'text-blue-700 font-semibold' : ''}`}
                                style={{ fontVariantNumeric: 'tabular-nums' }}
                                data-testid={`annual-cat-${c.code}`}
                              />
                            </div>
                          </td>
                          <td className="text-right font-mono text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`annual-monthly-${c.code}`}>&euro;{(effective / 12).toFixed(2)}</td>
                          {annualEditing && <td></td>}
                        </tr>
                        );
                      })}

                      {/* Capital group */}
                      {capitalCats.filter(c => isIncluded(c.code)).length > 0 && (
                        <tr className="bg-amber-50"><td colSpan={annualEditing ? 4 : 3} className="text-xs font-semibold text-amber-700 uppercase tracking-wider py-1">{t('capital')}</td></tr>
                      )}
                      {capitalCats.filter(c => isIncluded(c.code)).map(c => {
                        const actualRaw = c.actual || 0;
                        const effective = effectiveOf(c);
                        const overridden = annualCatValues[c.code] !== undefined && annualCatValues[c.code] !== '' && Math.abs(effective - actualRaw) > 0.001;
                        return (
                        <tr key={c.code} data-testid={`annual-row-${c.code}`}>
                          <td className="text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-zinc-400">{c.code}</span>
                              <span>{catLabel(c)}</span>
                              <button type="button" onClick={() => openAnnualHistory(c.code, catLabel(c))} className="p-0.5 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('viewHistory')} data-testid={`annual-history-btn-${c.code}`}>
                                <ClockClockwise size={14} />
                              </button>
                            </div>
                          </td>
                          <td className="text-right font-mono">
                            <div className="flex items-center justify-end gap-2">
                              {overridden && actualRaw > 0 && (
                                <span className="text-[10px] text-zinc-400 whitespace-nowrap">
                                  {t('original')}: &euro;{actualRaw.toFixed(2)}
                                </span>
                              )}
                              <Input
                                type="number" step="0.01"
                                value={annualCatValues[c.code] ?? ''}
                                placeholder={actualRaw.toFixed(2)}
                                onChange={(e) => setAnnualCatValues(v => ({ ...v, [c.code]: e.target.value }))}
                                onBlur={(e) => setAnnualCatValues(v => ({ ...v, [c.code]: formatMoneyBlur(e.target.value) }))}
                                className={`w-24 h-6 rounded-none font-mono text-right text-[12px] px-1.5 ${overridden ? 'text-blue-700 font-semibold' : ''}`}
                                style={{ fontVariantNumeric: 'tabular-nums' }}
                                data-testid={`annual-cat-${c.code}`}
                              />
                            </div>
                          </td>
                          <td className="text-right font-mono text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{(effective / 12).toFixed(2)}</td>
                          {annualEditing && <td></td>}
                        </tr>
                        );
                      })}

                      {/* Phase 287 — Reserve Fund moved BELOW Capital and visually
                          aligned with the other category rows (same neutral row style,
                          same number colors and alignment). */}
                      <tr className="bg-emerald-50"><td colSpan={annualEditing ? 4 : 3} className="text-xs font-semibold text-emerald-700 uppercase tracking-wider py-1">{t('reserveFund')}</td></tr>
                      <tr data-testid="annual-row-reserve_fund">
                        <td className="text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-zinc-400">RF</span>
                            <span>{rfLabel}</span>
                            <button type="button" onClick={() => openAnnualHistory('reserve_fund', rfLabel)} className="p-0.5 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('viewHistory')} data-testid="annual-history-btn-reserve_fund">
                              <ClockClockwise size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="text-right font-mono">
                          <div className="flex items-center justify-end gap-2">
                            <Input
                              type="number" step="0.01"
                              value={annualCatValues['__rf'] || ''}
                              onChange={(e) => setAnnualCatValues(v => ({ ...v, __rf: e.target.value }))}
                              onBlur={(e) => setAnnualCatValues(v => ({ ...v, __rf: formatMoneyBlur(e.target.value) }))}
                              className="w-24 h-6 rounded-none font-mono text-right text-[12px] px-1.5"
                              style={{ fontVariantNumeric: 'tabular-nums' }}
                              data-testid="annual-cat-reserve_fund"
                            />
                          </div>
                        </td>
                        <td className="text-right font-mono text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{(rfRounded / 12).toFixed(2)}</td>
                        {annualEditing && <td></td>}
                      </tr>

                      {/* Grand Total Row */}
                      <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300">
                        <td>{t('total')}</td>
                        <td className="text-right font-mono text-lg font-bold text-zinc-800" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="annual-grand-actual">&euro;{effectiveIncluded.toFixed(2)}</td>
                        <td className="text-right font-mono text-lg text-emerald-700 font-bold" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="annual-monthly-total-col">&euro;{(effectiveIncluded / 12).toFixed(2)}</td>
                        {annualEditing && <td></td>}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Info: Only operational expenses are distributed */}
                <div className="bg-zinc-50 border border-zinc-200 px-4 py-2 text-xs text-zinc-500">
                  &#9432; {t('onlyOperationalDistributed')}
                </div>

                {/* Monthly Total Section */}
                <div className="bg-white border border-zinc-200 p-5" data-testid="monthly-total-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('monthlyTotal')}</h3>
                      <p className="text-xs text-zinc-400 mt-0.5">{t('monthlyTotalDescRounded')}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Total (Rounded) */}
                      <div className="text-right">
                        <p className="text-xs text-zinc-400">{t('totalRounded')}</p>
                        <p className="text-lg font-mono text-zinc-700 font-semibold" data-testid="monthly-auto-value">&euro;{effectiveIncluded.toFixed(2)}</p>
                        <p className="text-[10px] text-zinc-400">{t('roundedDivided')}</p>
                      </div>
                      {/* Arrow */}
                      <span className="text-zinc-300 text-xl">&rarr;</span>
                      {/* Final Monthly */}
                      <div className="text-right">
                        <p className="text-xs text-zinc-500 font-medium">{t('finalMonthly')}</p>
                        {annualEditing ? (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-zinc-500">&euro;</span>
                            <Input
                              type="number" step="0.01"
                              value={monthlyOverride !== null ? monthlyOverride : ''}
                              onChange={(e) => setMonthlyOverride(e.target.value || null)}
                              placeholder={String((effectiveIncluded / 12).toFixed(2))}
                              className="w-28 rounded-none font-mono text-right h-8"
                              data-testid="monthly-override-input"
                            />
                            {monthlyOverride !== null && monthlyOverride !== '' && (
                              <button onClick={() => setMonthlyOverride(null)} className="text-xs text-blue-600 hover:underline" data-testid="monthly-reset-btn">{t('resetToAuto')}</button>
                            )}
                          </div>
                        ) : (
                          <div>
                            <p className={`text-2xl font-bold font-mono ${monthlyOverride != null && monthlyOverride !== '' ? 'text-blue-700' : 'text-zinc-900'}`} data-testid="monthly-final-value">
                              &euro;{monthlyFinalLive.toFixed(2)}
                            </p>
                            {monthlyOverride != null && monthlyOverride !== '' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 font-semibold">{t('reserveFundManual')}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* (Phase 61) Per-apartment chip preview removed — full info lives in the Distribution table below */}
                </div>

                {/* Distribution per Apartment — simplified (Phase 61) */}
                <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="annual-distribution-table">
                  <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
                    <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('distributionPerApartment')}</h3>
                    <div className="text-right">
                      <p className="text-xs text-zinc-400">{t('monthlyBase')}</p>
                      <p className="font-mono font-semibold text-blue-700" data-testid="annual-dist-operational-total">&euro;{monthlyAuto.toFixed(2)}</p>
                    </div>
                  </div>
                  <table className="excel-table excel-table-compact">
                    <thead>
                      <tr>
                        <th>{t('apartmentNumber')}</th>
                        <th>{t('ownerName')}</th>
                        <th className="text-right">{t('sharePercentage')}</th>
                        <th className="text-right">{t('annualTotal')} (&euro;)</th>
                        <th className="text-right">{t('monthly')} (&euro;)</th>
                        <th className="text-right">{t('rounded')} (&euro;)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(annualPlan.apartments || []).map(apt => {
                        // Phase 140/144 — Read-only calculated columns + user-editable Rounded.
                        // Annual Total = annual effective × share%. Monthly = Annual Total / 12 (rounded).
                        // Rounded is a manual override (`monthly_final_override`) that persists across
                        // budget changes until the user clears it explicitly.
                        const share = apt.share_percentage || 0;
                        const annualTotalRaw = effectiveIncluded * share / 100;
                        const monthlyTotalRaw = monthlyAuto * share / 100;
                        const monthly = Math.round(monthlyTotalRaw * 100) / 100; // read-only calc
                        const hasOverride = apt.monthly_final_override != null;
                        const roundedValue = hasOverride ? apt.monthly_final_override : monthly;
                        return (
                        <tr key={apt.apartment_id} className={hasOverride ? 'bg-blue-50/40' : ''} data-testid={`annual-apt-${apt.apartment_id}`}>
                          <td className="font-semibold"><button onClick={() => navigate(`/apartments/${apt.apartment_id}`)} className="text-blue-700 hover:underline">{apt.apartment_number}</button></td>
                          <td className="text-sm">{apt.owner_name || '-'}</td>
                          <td className="text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{share.toFixed(2)}%</td>
                          <td className="text-right font-mono text-sm text-zinc-600" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`annual-apt-annual-total-${apt.apartment_id}`}>
                            &euro;{annualTotalRaw.toFixed(2)}
                          </td>
                          <td className="text-right font-mono text-sm text-zinc-700" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`annual-apt-monthly-final-${apt.apartment_id}`} title={t('readOnlyCalculated')}>
                            &euro;{monthly.toFixed(2)}
                          </td>
                          <td className="text-right font-mono font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`annual-apt-rounded-${apt.apartment_id}`}>
                            {editingMonthlyApt === apt.apartment_id ? (
                              <div className="flex items-center gap-1 justify-end">
                                <Input type="number" step="0.01" value={monthlyAptEditValue}
                                  onChange={(e) => setMonthlyAptEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); handleAptMonthlyOverride(apt.apartment_id); }
                                    if (e.key === 'Escape') { e.preventDefault(); setEditingMonthlyApt(null); }
                                  }}
                                  className="w-20 h-7 rounded-none font-mono text-sm text-right"
                                  style={{ fontVariantNumeric: 'tabular-nums' }}
                                  autoFocus
                                  placeholder={t('pressEnterToSave')}
                                  data-testid={`annual-apt-rounded-edit-input-${apt.apartment_id}`} />
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 justify-end">
                                <button onClick={() => { setEditingMonthlyApt(apt.apartment_id); setMonthlyAptEditValue(String(roundedValue)); }} className={`hover:bg-zinc-100 px-2 py-0.5 transition-colors ${hasOverride ? 'text-blue-700' : ''}`} title={hasOverride ? t('manualOverride') : t('edit')} data-testid={`annual-apt-rounded-btn-${apt.apartment_id}`}>
                                  &euro;{roundedValue.toFixed(2)}
                                </button>
                                {hasOverride && (
                                  <button onClick={() => handleAptMonthlyReset(apt.apartment_id)} className="text-[10px] text-zinc-400 hover:text-red-500 p-0.5" title={t('resetToAuto')} data-testid={`annual-apt-rounded-reset-${apt.apartment_id}`}>&#10005;</button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                      {/* Totals row */}
                      <tr className="bg-zinc-50 font-semibold">
                        <td colSpan="3">{t('total')}</td>
                        <td className="text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="annual-annual-total">&euro;{(annualPlan.apartments || []).reduce((s, a) => s + effectiveIncluded * (a.share_percentage || 0) / 100, 0).toFixed(2)}</td>
                        <td className="text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="annual-monthly-final-total">&euro;{(annualPlan.apartments || []).reduce((s, a) => s + Math.round((monthlyAuto * (a.share_percentage || 0) / 100) * 100) / 100, 0).toFixed(2)}</td>
                        <td className="text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="annual-monthly-rounded-total">&euro;{(annualPlan.apartments || []).reduce((s, a) => {
                          const share = a.share_percentage || 0;
                          return s + (a.monthly_final_override != null ? a.monthly_final_override : Math.round((monthlyAuto * share / 100) * 100) / 100);
                        }, 0).toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Share % warning */}
                {apartments.length > 0 && (() => {
                  const totalShare = apartments.reduce((s, a) => s + (a.share_percentage || 0), 0);
                  if (Math.abs(totalShare - 100) < 0.01) return null;
                  return (
                    <div className={`border px-4 py-3 text-sm flex items-center gap-2 ${totalShare > 100 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                      <Warning size={18} weight="bold" />
                      <span>{t('sharePercentage')} {t('total')}: <strong className="font-mono">{totalShare.toFixed(2)}%</strong> — {totalShare < 100 ? t('shareUnder100') : t('shareOver100')}</span>
                    </div>
                  );
                })()}

                {/* Phase 146/147 — Reserve Fund rounding adjustment.
                    Formula: adjustment = sum(Rounded column) − monthly total before rounding.
                    If apartments collectively pay LESS than calculated → RF decreases.
                    If apartments collectively pay MORE than calculated → RF increases.
                    This reflects user-edited Rounded overrides, not just per-expense rounding residuals. */}
                {(annualPlan.apartments || []).length > 0 && (() => {
                  const apts = annualPlan.apartments || [];
                  // Monthly total before rounding = the true monthly base, shares sum to 100%.
                  const monthlyTotalBefore = monthlyAuto;
                  // Sum of Rounded column: per-apt override if set, otherwise the rounded calc.
                  const totalRounded = apts.reduce((s, a) => {
                    const share = a.share_percentage || 0;
                    const r = a.monthly_final_override != null
                      ? a.monthly_final_override
                      : Math.round((monthlyAuto * share / 100) * 100) / 100;
                    return s + r;
                  }, 0);
                  const totalRoundAdjustment = Math.round((totalRounded - monthlyTotalBefore) * 100) / 100;

                  const rfMonthlyOriginal = Math.round((rfRounded / 12) * 100) / 100;
                  const rfMonthlyAdjusted = Math.round((rfMonthlyOriginal + totalRoundAdjustment) * 100) / 100;
                  const rfAnnualOriginal = rfRounded;
                  const annualAdjustment = Math.round(totalRoundAdjustment * 12 * 100) / 100;
                  const rfAnnualAdjusted = Math.round((rfAnnualOriginal + annualAdjustment) * 100) / 100;

                  if (rfRounded <= 0 && totalRoundAdjustment === 0) return null;

                  const adjTone = totalRoundAdjustment > 0
                    ? { cls: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: t('rfIncreases'), sign: '+' }
                    : totalRoundAdjustment < 0
                      ? { cls: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: t('rfDecreases'), sign: '' }
                      : { cls: 'text-zinc-500', bg: 'bg-zinc-50', border: 'border-zinc-200', label: t('rfUnchanged'), sign: '' };

                  const fmt = (n) => `€${n.toFixed(2)}`;
                  const fmtSigned = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;

                  return (
                    <div className="bg-white border-2 border-blue-200 overflow-x-auto" data-testid="adjusted-reserve-fund">
                      {/* Header */}
                      <div className="px-5 py-3 border-b border-blue-200 bg-blue-50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-blue-500">reserve_fund</span>
                          <h4 className="text-sm font-semibold text-blue-900">{t('reserveFundAdjustmentTitle')}</h4>
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${adjTone.border} ${adjTone.bg} ${adjTone.cls}`} data-testid="rf-adj-tone-badge">{adjTone.label}</span>
                        </div>
                        <div className="text-right text-[11px] text-blue-700 italic max-w-md">
                          {t('reserveFundAdjustmentHint')}
                        </div>
                      </div>

                      {/* Unified 3-column matrix: Label | Monthly | Annual */}
                      <table className="excel-table excel-table-compact w-full" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '56%' }} />
                          <col style={{ width: '22%' }} />
                          <col style={{ width: '22%' }} />
                        </colgroup>
                        <thead>
                          <tr className="bg-blue-100">
                            <th className="text-left text-xs font-semibold text-blue-900 uppercase tracking-wider">&nbsp;</th>
                            <th className="text-right text-xs font-semibold text-blue-900 uppercase tracking-wider">{t('colMonthly')}</th>
                            <th className="text-right text-xs font-semibold text-blue-900 uppercase tracking-wider">{t('colAnnual')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Matrix rows */}
                          <tr>
                            <td className="text-sm text-zinc-700">{t('rowOriginal')}</td>
                            <td className="text-right font-mono text-sm text-zinc-700" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-monthly-original">{fmt(rfMonthlyOriginal)}</td>
                            <td className="text-right font-mono text-sm text-zinc-700" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-annual-original">{fmt(rfAnnualOriginal)}</td>
                          </tr>
                          <tr className={adjTone.bg}>
                            <td className="text-sm">
                              {t('rowAdjustment')}
                              <div className="text-[10px] text-zinc-500 italic mt-0.5">{t('roundingAdjustmentFormula')}</div>
                            </td>
                            <td className={`text-right font-mono text-sm font-semibold ${adjTone.cls}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-adjustment">
                              €{fmtSigned(totalRoundAdjustment)}
                            </td>
                            <td className={`text-right font-mono text-sm font-semibold ${adjTone.cls}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-adjustment-annual">
                              €{fmtSigned(annualAdjustment)}
                            </td>
                          </tr>
                          <tr className="bg-blue-100 border-t-2 border-blue-300 font-bold">
                            <td className="text-sm text-blue-900">{t('rowAdjusted')}</td>
                            <td className="text-right font-mono text-base text-blue-900" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-monthly-adjusted">{fmt(rfMonthlyAdjusted)}</td>
                            <td className="text-right font-mono text-base text-blue-900" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-annual-adjusted">{fmt(rfAnnualAdjusted)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {/* Phase 164 — Reserve Fund Distribution per Apartment
                    Shows how the plan's Reserve Fund budget splits across apartments
                    by share%. Exact values (no rounding). Separate from the main
                    Rounding Reconciliation and Distribution tables.

                    Phase 288 — added per-apartment Adjustment column (annual share
                    of the rounding adjustment) + Final Reserve Monthly column
                    (= Monthly + Adjustment / 12). Display-only; no impact on
                    invoices, balances or historical records. */}
                {(annualPlan.apartments || []).length > 0 && rfRounded > 0 && (() => {
                  const apts = (annualPlan.apartments || []).slice().sort((a, b) => {
                    const an = String(a.apartment_number || ''); const bn = String(b.apartment_number || '');
                    return an.localeCompare(bn, undefined, { numeric: true });
                  });
                  // Reproduce the same rounding-adjustment derivation used by
                  // the Adjusted Reserve Fund card just above this table so the
                  // numbers stay consistent.
                  const monthlyTotalBefore = monthlyAuto;
                  const totalRounded = apts.reduce((s, a) => {
                    const share = a.share_percentage || 0;
                    const r = a.monthly_final_override != null
                      ? a.monthly_final_override
                      : Math.round((monthlyAuto * share / 100) * 100) / 100;
                    return s + r;
                  }, 0);
                  const totalRoundAdjustmentMonthly = Math.round((totalRounded - monthlyTotalBefore) * 100) / 100;
                  const totalRoundAdjustmentAnnual = Math.round(totalRoundAdjustmentMonthly * 12 * 100) / 100;

                  const totalShare = apts.reduce((s, a) => s + (a.share_percentage || 0), 0);
                  const annualTotal = apts.reduce((s, a) => s + ((rfRounded * (a.share_percentage || 0)) / 100), 0);
                  const monthlyTotal = annualTotal / 12;
                  const totalAdjAnnual = apts.reduce((s, a) => s + ((totalRoundAdjustmentAnnual * (a.share_percentage || 0)) / 100), 0);
                  const finalMonthlyTotal = monthlyTotal + (totalAdjAnnual / 12);
                  return (
                    <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="reserve-fund-distribution">
                      <div className="px-5 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-zinc-400">reserve_fund</span>
                          <h4 className="text-sm font-semibold text-zinc-900">{t('reserveFundDistributionTitle')}</h4>
                        </div>
                        <div className="text-right text-[11px] text-zinc-500">
                          {t('annualTotal')}: <span className="font-mono font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{rfRounded.toFixed(2)}</span> · {t('monthly')}: <span className="font-mono font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{(rfRounded / 12).toFixed(2)}</span>
                          {Math.abs(totalRoundAdjustmentAnnual) > 0.001 && (
                            <> · <span className="text-zinc-400">{t('rfAdjustmentColLabel') || 'Adjustment'}:</span> <span className="font-mono font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>€{totalRoundAdjustmentAnnual.toFixed(2)}</span></>
                          )}
                        </div>
                      </div>
                      <table className="excel-table excel-table-compact w-full" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '28%' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '13%' }} />
                          <col style={{ width: '13%' }} />
                          <col style={{ width: '13%' }} />
                        </colgroup>
                        <thead>
                          <tr className="bg-zinc-100">
                            <th className="text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">{t('apartment')}</th>
                            <th className="text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">{t('ownerName')}</th>
                            <th className="text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">{t('share')} (%)</th>
                            <th className="text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">{t('colAnnual')}</th>
                            <th className="text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">{t('colMonthly')}</th>
                            <th className="text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider" title={t('rfAdjustmentColTitle')}>{t('rfAdjustmentColLabel') || 'Adjustment'} (€)</th>
                            <th className="text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider" title={t('rfFinalMonthlyColTitle')}>{t('rfFinalMonthlyColLabel') || 'Final Monthly'} (€)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {apts.map((a) => {
                            const share = a.share_percentage || 0;
                            const annual = (rfRounded * share) / 100;
                            const monthly = annual / 12;
                            // Phase 288 — Per-apartment rounding adjustment share.
                            // Distributed annually using the same share% so the
                            // sum of every row's adjustment equals the full
                            // annual rounding adjustment.
                            const adjustmentAnnual = (totalRoundAdjustmentAnnual * share) / 100;
                            const adjustmentMonthly = adjustmentAnnual / 12;
                            const finalMonthly = monthly + adjustmentMonthly;
                            const adjCls = adjustmentAnnual > 0.001 ? 'text-emerald-700' : adjustmentAnnual < -0.001 ? 'text-red-700' : 'text-zinc-400';
                            return (
                              <tr key={a.apartment_id} data-testid={`rf-dist-row-${a.apartment_id}`}>
                                <td className="font-mono text-sm">{a.apartment_number || '—'}</td>
                                <td className="text-sm text-zinc-700">{a.owner_name || <span className="text-zinc-300">—</span>}</td>
                                <td className="text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{share.toFixed(2)}</td>
                                <td className="text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`rf-dist-annual-${a.apartment_id}`}>&euro;{annual.toFixed(2)}</td>
                                <td className="text-right font-mono text-sm text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`rf-dist-monthly-${a.apartment_id}`}>&euro;{monthly.toFixed(2)}</td>
                                <td className={`text-right font-mono text-sm ${adjCls}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`rf-dist-adjustment-${a.apartment_id}`}>
                                  {adjustmentAnnual >= 0 ? '+' : ''}€{adjustmentAnnual.toFixed(2)}
                                </td>
                                <td className="text-right font-mono text-sm font-semibold text-zinc-900" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid={`rf-dist-final-monthly-${a.apartment_id}`}>&euro;{finalMonthly.toFixed(2)}</td>
                              </tr>
                            );
                          })}
                          <tr className="bg-zinc-100 border-t-2 border-zinc-300 font-bold">
                            <td colSpan={2} className="text-sm text-zinc-900">{t('total')}</td>
                            <td className="text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{totalShare.toFixed(2)}</td>
                            <td className="text-right font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-dist-total-annual">&euro;{annualTotal.toFixed(2)}</td>
                            <td className="text-right font-mono text-sm text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-dist-total-monthly">&euro;{monthlyTotal.toFixed(2)}</td>
                            <td className={`text-right font-mono text-sm ${totalAdjAnnual > 0.001 ? 'text-emerald-700' : totalAdjAnnual < -0.001 ? 'text-red-700' : 'text-zinc-400'}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-dist-total-adjustment">
                              {totalAdjAnnual >= 0 ? '+' : ''}€{totalAdjAnnual.toFixed(2)}
                            </td>
                            <td className="text-right font-mono text-sm text-zinc-900" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="rf-dist-total-final-monthly">&euro;{finalMonthlyTotal.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </>
              );
            })()}

            {/* Add Category Dialog */}
            <Dialog open={addAnnualCatDialogOpen} onOpenChange={setAddAnnualCatDialogOpen}>
              <DialogContent className="sm:max-w-sm rounded-none">
                <DialogHeader><DialogTitle>{t('addCategory')}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t('categoryName')}</Label><Input value={newAnnualCatName} onChange={(e) => setNewAnnualCatName(e.target.value)} className="mt-1 rounded-none" placeholder="e.g. Pest Control" data-testid="annual-new-cat-input" /></div>
                  <div>
                    <Label>{t('type')}</Label>
                    <div className="mt-1 px-3 py-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200">
                      {t('operationalExpenses')} ({t('monthly')})
                    </div>
                    <input type="hidden" value="operational" data-testid="annual-new-cat-type" />
                  </div>
                  <Button onClick={handleAddAnnualCategory} className="w-full bg-zinc-950 rounded-none" data-testid="annual-add-cat-save">{t('add')}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* ====== ELEVATOR TAB ====== */}
        {activeTab === 'elevator' && (
          <div className="space-y-4">
            {/* Summary Cards — Phase 332 adds Unallocated Credit; Phase 667 adds Credit Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="bg-white border border-zinc-200 p-4" data-testid="elev-total-card"><p className="text-sm text-zinc-500">{t('totalElevatorCost')}</p><p className="text-2xl font-bold font-mono mt-1">&euro;{(elevSummary?.total_expenses || 0).toFixed(2)}</p></div>
              <div className="bg-white border border-emerald-200 p-4" data-testid="elev-paid-card"><p className="text-sm text-emerald-600">{t('paidExpenses')}</p><p className="text-2xl font-bold font-mono text-emerald-700 mt-1">&euro;{(elevSummary?.total_paid || 0).toFixed(2)}</p></div>
              {/* Phase 667 — Credit Notes applied total (reduces supplier balance). */}
              <div className={`bg-white border p-4 ${(elevSummary?.total_credit_notes || 0) > 0 ? 'border-emerald-200' : 'border-zinc-200'}`} data-testid="elev-credit-notes-card">
                <p className="text-sm text-emerald-600">{t('creditNotes') || 'Credit Notes'}</p>
                <p className="text-2xl font-bold font-mono text-emerald-700 mt-1">&euro;{(elevSummary?.total_credit_notes || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white border border-red-200 p-4" data-testid="elev-unpaid-card"><p className="text-sm text-red-600">{t('unpaidExpenses')}</p><p className="text-2xl font-bold font-mono text-red-700 mt-1">&euro;{(elevSummary?.total_unpaid || 0).toFixed(2)}</p></div>
              <div className={`bg-white border p-4 ${(elevSummary?.balance || 0) >= 0 ? 'border-emerald-200' : 'border-red-200'}`} data-testid="elev-balance-card">
                <p className="text-sm text-zinc-500">{t('elevatorBalance')}</p>
                <p className={`text-2xl font-bold font-mono mt-1 ${(elevSummary?.balance || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>&euro;{(elevSummary?.balance || 0).toFixed(2)}</p>
              </div>
              {/* Phase 332 — Unallocated Credit card. Green when > 0,
                  zinc when fully allocated. */}
              <div className={`bg-white border p-4 ${(elevSummary?.total_unallocated || 0) > 0 ? 'border-emerald-300' : 'border-zinc-200'}`} data-testid="elev-unallocated-card">
                <p className="text-sm text-zinc-500">{t('unallocatedCredit') || 'Unallocated Credit'}</p>
                <p className={`text-2xl font-bold font-mono mt-1 ${(elevSummary?.total_unallocated || 0) > 0 ? 'text-emerald-700' : 'text-zinc-500'}`}>&euro;{(elevSummary?.total_unallocated || 0).toFixed(2)}</p>
              </div>
            </div>

            {/* Company + Opening Balance row */}
            <div className="flex items-center gap-4 flex-wrap text-sm" data-testid="elev-company-row">
              <div className="flex items-center gap-2 text-zinc-500">
                <span className="font-medium text-zinc-700">{t('assignedCompany')}:</span>
                {building.elevator_company_name ? (
                  <span className="text-zinc-800">{building.elevator_company_name}</span>
                ) : (
                  <span className="text-zinc-400">{t('noCompanyAssigned')}</span>
                )}
                {isAdmin && (
                  <Select value={building.elevator_company_id || ''} onValueChange={async (val) => {
                    try {
                      await axios.put(`${API}/buildings/${buildingId}/elevator-company`, { elevator_company_id: val === '_none' ? '' : val });
                      fetchAll();
                      toast.success(t('savedSuccess'));
                    } catch (e) { toast.error(t('errorOccurred')); }
                  }}>
                    <SelectTrigger className="w-[180px] rounded-none h-7 text-xs" data-testid="assign-company-select"><SelectValue placeholder={t('selectCompany')} /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="_none">{t('noCompany')}</SelectItem>
                      {elevatorCompanies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <span className="text-zinc-300">|</span>
              <div className="flex items-center gap-2 text-zinc-500">
                <span className="font-medium text-zinc-700">{t('elevatorOpeningBalance')}:</span>
                {elevSummary?.opening_balance ? (
                  <span className={`font-mono font-semibold ${elevSummary.opening_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>&euro;{elevSummary.opening_balance.toFixed(2)}</span>
                ) : (
                  <span className="text-zinc-400">&mdash;</span>
                )}
                {isAdmin && (
                  <Button variant="ghost" size="sm" onClick={openElevObDialog} className="rounded-none h-6 px-1.5 text-[10px]" data-testid="set-elev-ob-btn">
                    <PencilSimple size={12} />
                  </Button>
                )}
              </div>
            </div>

            {/* Sub-Tabs */}
            <div className="flex border-b border-zinc-200" data-testid="elev-sub-tabs">
              {[
                { key: 'invoices', label: t('invoices') },
                { key: 'payments', label: t('payments') },
                { key: 'creditNotes', label: t('creditNotes') || 'Credit Notes' },
                { key: 'statement', label: t('statement') },
              ].map(st => (
                <button
                  key={st.key}
                  onClick={() => setElevSubTab(st.key)}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${elevSubTab === st.key ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-700'}`}
                  data-testid={`elev-subtab-${st.key}`}
                >
                  {st.label}
                </button>
              ))}
            </div>

            {/* ===== SUB-TAB: INVOICES ===== */}
            {elevSubTab === 'invoices' && (
              <div className="bg-white border border-zinc-200">
                <div className="px-4 py-3 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-zinc-800" style={{ fontFamily: 'var(--font-heading)' }}>{t('elevatorInvoices')}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Phase 661 — Free-text search across number / date / description */}
                    <div className="relative">
                      <Input
                        value={elevInvSearch}
                        onChange={(e) => setElevInvSearch(e.target.value)}
                        placeholder={t('searchPlaceholderInvoices') || 'Search number, description, date...'}
                        className="rounded-none h-7 text-xs pl-7 pr-7 w-64"
                        data-testid="elev-inv-search"
                      />
                      <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                      {elevInvSearch && (
                        <button type="button" onClick={() => setElevInvSearch('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-400 hover:text-zinc-700" data-testid="elev-inv-search-clear"><X size={12} /></button>
                      )}
                    </div>
                    {/* Phase 375 — Client-side period filter for invoices */}
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('from') || 'From'}</Label>
                    <Input type="date" value={elevInvFrom} onChange={(e) => setElevInvFrom(e.target.value)} className="rounded-none h-7 text-xs w-36" data-testid="elev-inv-from" />
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('to') || 'To'}</Label>
                    <Input type="date" value={elevInvTo} onChange={(e) => setElevInvTo(e.target.value)} className="rounded-none h-7 text-xs w-36" data-testid="elev-inv-to" />
                    {(elevInvFrom || elevInvTo) && (
                      <button type="button" onClick={() => { setElevInvFrom(''); setElevInvTo(''); }} className="text-[10px] text-zinc-500 hover:text-zinc-900 underline" data-testid="elev-inv-clear-period">
                        {t('clear') || 'Clear'}
                      </button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => {
                      const params = { building_id: buildingId };
                      if (elevInvFrom) params.date_from = elevInvFrom;
                      if (elevInvTo) params.date_to = elevInvTo;
                      openPdfInBrowser('/elevator/invoices/export-pdf', params);
                    }} className="rounded-none h-7 text-xs" data-testid="view-pdf-elev-invoices-btn">
                      <FilePdf size={14} className="mr-1" />{t('viewPdf')}
                    </Button>
                    {isAdmin && (
                      <Button size="sm" onClick={() => openElevExpenseDialog()} className="bg-zinc-950 rounded-none h-7 text-xs" data-testid="add-elev-expense-btn">
                        <Plus size={14} className="mr-1" />{t('addElevatorInvoice')}
                      </Button>
                    )}
                  </div>
                </div>
                <table className="w-full text-xs" data-testid="elev-invoices-table">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('date')}</th>
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold">{t('invoiceNumber')}</th>
                      <th className="px-3 py-2 text-right text-[10px] text-zinc-400 font-semibold w-20">{t('netAmount')}</th>
                      <th className="px-3 py-2 text-right text-[10px] text-zinc-400 font-semibold w-16">{t('vat')}</th>
                      <th className="px-3 py-2 text-right text-[10px] text-zinc-400 font-semibold w-20">{t('total')}</th>
                      <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold w-16">{t('status')}</th>
                      <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold w-24">{t('attachedFile') || 'Attached File'}</th>
                      <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold w-16">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Phase 374 — Synthetic Opening Balance row that behaves
                      // like an invoice: appears at the top of the list, shows
                      // status (Paid/Partial/Unpaid), and the remaining
                      // outstanding updates after every allocation/payment.
                      // Phase 375 — Honor From/To filters: hide rows whose
                      // `date` falls outside the selected range.
                      const inRange = (d) => {
                        if (!d) return !(elevInvFrom || elevInvTo);
                        const ds = String(d).slice(0, 10);
                        if (elevInvFrom && ds < elevInvFrom) return false;
                        if (elevInvTo && ds > elevInvTo) return false;
                        return true;
                      };
                      const obAmt = Number(elevSummary?.opening_balance || 0);
                      const obPaid = Number(elevSummary?.opening_balance_paid || 0);
                      const obRem = Number(elevSummary?.opening_balance_remaining ?? Math.max(obAmt - obPaid, 0));
                      const obDate = elevSummary?.opening_balance_date || '';
                      const showOb = obAmt > 0.005 && inRange(obDate);
                      let obStatus = 'unpaid', obStatusCls = 'bg-red-50 text-red-700 border border-red-200';
                      if (obRem <= 0.005 && obAmt > 0) { obStatus = 'paid'; obStatusCls = 'bg-emerald-50 text-emerald-700 border border-emerald-200'; }
                      else if (obPaid > 0.005) { obStatus = 'partial'; obStatusCls = 'bg-amber-50 text-amber-700 border border-amber-200'; }
                      const obLabel = `${t('openingBalance') || 'Opening Balance'}${obDate ? ` - ${obDate}` : ''}`;
                      const obRow = showOb ? (
                        <tr key="_ob_row_" className="border-b border-zinc-100 bg-zinc-50/60 hover:bg-zinc-100/60 cursor-pointer" onClick={() => openElevInvHistory({ kind: 'opening' })} data-testid="elev-inv-row-OPENING">
                          <td className="px-3 py-2.5 font-mono text-zinc-500">{obDate ? formatDateDMY(obDate) : '-'}</td>
                          <td className="px-3 py-2.5">
                            <span className="font-mono font-semibold text-zinc-800 hover:underline">{obLabel}</span>
                            {obPaid > 0.005 && obRem > 0.005 && (
                              <span className="ml-2 text-[10px] text-zinc-500">({t('paid') || 'Paid'} &euro;{obPaid.toFixed(2)} / {t('outstanding') || 'Outstanding'} &euro;{obRem.toFixed(2)})</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-zinc-400">—</td>
                          <td className="px-3 py-2.5 text-right font-mono text-zinc-400">—</td>
                          <td className="px-3 py-2.5 text-right font-mono font-semibold text-zinc-800">&euro;{obAmt.toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`text-[10px] px-2 py-0.5 ${obStatusCls}`} data-testid="elev-ob-status-badge">{t(obStatus)}</span>
                          </td>
                          <td className="px-3 py-2.5 text-center text-zinc-300">—</td>
                          <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                            {isAdmin && (
                              <div className="flex justify-center gap-0.5">
                                <button onClick={openElevObDialog} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid="elev-edit-ob-row"><PencilSimple size={14} /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ) : null;
                      const sq = (elevInvSearch || '').trim().toLowerCase();
                      const filteredExpenses = elevExpenses.filter(exp => {
                        if (!inRange(exp.date)) return false;
                        if (!sq) return true;
                        const hay = [
                          exp.invoice_number, exp.notes, exp.maintenance_notes,
                          exp.description, exp.date, formatDateDMY(exp.date),
                          building?.elevator_company_name,
                          (exp.extra_charges || []).map(ec => ec.description).join(' '),
                        ].map(v => String(v || '').toLowerCase()).join(' \u00b7 ');
                        return hay.includes(sq);
                      });
                      if (filteredExpenses.length === 0 && !showOb) {
                        return <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-400">{t('noInvoices')}</td></tr>;
                      }
                      const invRows = filteredExpenses.map(exp => {
                      // Phase 407 — Compute breakdown including discount (mirror backend).
                      const lines = exp.extra_charges || [];
                      const legacyNet = parseFloat(exp.invoice_amount || 0);
                      const legacyVat = parseFloat(exp.vat_rate || 0);
                      const lineNets = [];
                      if (legacyNet > 0) lineNets.push({ net: legacyNet, vat_rate: legacyVat });
                      for (const ec of lines) {
                        const q = parseFloat(ec.quantity);
                        const p = parseFloat(ec.price);
                        const lineNet = (!isNaN(q) && !isNaN(p)) ? q * p : parseFloat(ec.amount || 0);
                        lineNets.push({ net: lineNet, vat_rate: parseFloat(ec.vat_rate || 0) });
                      }
                      const subtotalNet = lineNets.reduce((s, l) => s + l.net, 0);
                      const dType = (exp.discount_type || 'amount').toLowerCase();
                      const dValue = parseFloat(exp.discount_value || 0);
                      let discountAmt = dType === 'percent' ? subtotalNet * (dValue / 100) : dValue;
                      if (discountAmt < 0) discountAmt = 0;
                      if (discountAmt > subtotalNet) discountAmt = subtotalNet;
                      const factor = subtotalNet > 0 ? discountAmt / subtotalNet : 0;
                      const net = subtotalNet - discountAmt; // displayed net (after discount)
                      const vatAmt = lineNets.reduce((s, l) => s + (l.net * (1 - factor) * l.vat_rate / 100), 0);
                      const total = (typeof exp.grand_total === 'number') ? exp.grand_total : (net + vatAmt);
                      const paid = exp.total_paid || 0;
                      const credited = exp.credited_amount || 0;
                      // Phase 667 — Trust the backend's authoritative `status` field
                      // (which already factors in payments + credit notes). Fall back
                      // to `remaining_balance` math only when the field is missing.
                      const backendStatus = exp.status;
                      const remainingBal = (typeof exp.remaining_balance === 'number')
                        ? exp.remaining_balance
                        : Math.max(0, total - paid - credited);
                      const isPaid = backendStatus
                        ? (backendStatus === 'paid')
                        : (remainingBal <= 0.005);
                      const isPartial = backendStatus
                        ? (backendStatus === 'partially_paid')
                        : (!isPaid && (paid + credited) > 0.005);
                      const statusKey = isPaid ? 'paid' : (isPartial ? 'partial' : 'unpaid');
                      const statusCls = isPaid
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : (isPartial ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-red-50 text-red-700 border border-red-200');
                      return (
                        <tr key={exp.expense_id} className="border-b border-zinc-100 hover:bg-zinc-50/50 cursor-pointer" onClick={() => openElevInvHistory({ kind: 'invoice', expense_id: exp.expense_id })} data-testid={`elev-inv-row-${exp.expense_id}`}>
                          <td className="px-3 py-2.5 font-mono text-zinc-500">{formatDateDMY(exp.date)}</td>
                          <td className="px-3 py-2.5">
                            <span className="font-mono font-medium text-zinc-800 hover:underline">{exp.invoice_number || '-'}</span>
                            {exp.maintenance_notes && <span className="text-zinc-400 ml-2">— {exp.maintenance_notes}</span>}
                            {(exp.extra_charges || []).length > 0 && (
                              <div className="mt-1 ml-3 pl-2 border-l border-zinc-200">
                                {exp.extra_charges.map((ec, i) => {
                                  // Phase 409 — Show each line at its discount-adjusted gross
                                  // so the breakdown sums up to the final invoice Total.
                                  const ecQty = parseFloat(ec.quantity);
                                  const ecPrice = parseFloat(ec.price);
                                  const ecNet = (!isNaN(ecQty) && !isNaN(ecPrice)) ? ecQty * ecPrice : parseFloat(ec.amount || 0);
                                  const ecVatRate = parseFloat(ec.vat_rate || 0);
                                  const ecDiscNet = ecNet * (1 - factor);
                                  const ecGross = ecDiscNet * (1 + ecVatRate / 100);
                                  return (
                                    <div key={i} className="text-[10px] text-zinc-500">{ec.description} <span className="font-mono">&euro;{ecGross.toFixed(2)}</span></div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-zinc-600">&euro;{net.toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right font-mono text-zinc-400">&euro;{vatAmt.toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right font-mono font-semibold text-zinc-800">&euro;{total.toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`text-[10px] px-2 py-0.5 ${statusCls}`} data-testid={`elev-inv-status-${exp.expense_id}`}>{t(statusKey)}</span>
                            {credited > 0.005 && (
                              <span className="ml-1 text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200" title={t('creditNotesApplied') || 'Credit Notes applied'} data-testid={`elev-inv-credited-${exp.expense_id}`}>CN &euro;{credited.toFixed(2)}</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const atts = exp.attachments || [];
                              return (
                                <button
                                  onClick={() => openAttManager('inv', exp.expense_id, exp.invoice_number || '-', atts)}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border rounded-none transition-colors ${atts.length > 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-zinc-200 bg-white text-zinc-400 hover:bg-zinc-50'}`}
                                  title={atts.length > 0 ? `${atts.length} ${t('attachedFiles') || 'file(s)'}` : (t('addAttachment') || 'Add attachment')}
                                  data-testid={`elev-inv-attachments-${exp.expense_id}`}
                                >
                                  <Paperclip size={11} /> {atts.length}
                                </button>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-center gap-0.5">
                              <button onClick={() => openElevExpenseDialog(exp)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`elev-edit-inv-${exp.expense_id}`}><PencilSimple size={14} /></button>
                              {isAdmin && (
                                <button onClick={() => { setDeletingElevExpense(exp); setDeleteElevExpenseDialogOpen(true); }} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`elev-delete-inv-${exp.expense_id}`}><Trash size={14} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                      });
                      // Phase 381 — Invoices table follows pure date order
                      // (newest first). OPENING participates in the same sort
                      // and naturally falls to its chronological position.
                      const allRows = [];
                      if (obRow) allRows.push({ date: obDate || '', jsx: obRow });
                      filteredExpenses.forEach((exp, i) => {
                        allRows.push({ date: exp.date || '', jsx: invRows[i] });
                      });
                      allRows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                      return allRows.map(r => r.jsx);
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {/* ===== SUB-TAB: PAYMENTS ===== */}
            {elevSubTab === 'payments' && (
              <div className="bg-white border border-zinc-200">
                <div className="px-4 py-3 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-zinc-800" style={{ fontFamily: 'var(--font-heading)' }}>{t('elevatorPayments')}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Phase 661 — Free-text search across payment # / invoice # / reference / source / date */}
                    <div className="relative">
                      <Input
                        value={elevPaySearch}
                        onChange={(e) => setElevPaySearch(e.target.value)}
                        placeholder={t('searchPlaceholderPayments') || 'Search payment #, invoice, reference, source...'}
                        className="rounded-none h-7 text-xs pl-7 pr-7 w-72"
                        data-testid="elev-pay-search"
                      />
                      <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                      {elevPaySearch && (
                        <button type="button" onClick={() => setElevPaySearch('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-400 hover:text-zinc-700" data-testid="elev-pay-search-clear"><X size={12} /></button>
                      )}
                    </div>
                    {/* Phase 375 — Client-side period filter for payments */}
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('from') || 'From'}</Label>
                    <Input type="date" value={elevPayFrom} onChange={(e) => setElevPayFrom(e.target.value)} className="rounded-none h-7 text-xs w-36" data-testid="elev-pay-from" />
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('to') || 'To'}</Label>
                    <Input type="date" value={elevPayTo} onChange={(e) => setElevPayTo(e.target.value)} className="rounded-none h-7 text-xs w-36" data-testid="elev-pay-to" />
                    {(elevPayFrom || elevPayTo) && (
                      <button type="button" onClick={() => { setElevPayFrom(''); setElevPayTo(''); }} className="text-[10px] text-zinc-500 hover:text-zinc-900 underline" data-testid="elev-pay-clear-period">
                        {t('clear') || 'Clear'}
                      </button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => {
                      const params = { building_id: buildingId, format: 'pdf' };
                      if (elevPayFrom) params.date_from = elevPayFrom;
                      if (elevPayTo) params.date_to = elevPayTo;
                      openPdfInBrowser('/elevator/payments/export', params);
                    }} className="rounded-none h-7 text-xs" data-testid="view-pdf-elev-payments-btn">
                      <FilePdf size={14} className="mr-1" />{t('viewPdf')}
                    </Button>
                    {isAdmin && (
                      <Button size="sm" onClick={async () => {
                        setEditingElevPayment(null);
                        setSelectedElevExpense(null);
                        // Phase 307 — Pre-fetch the next system payment_number
                        // so the dialog shows it pre-filled (editable). The
                        // Reference field starts empty per user requirement.
                        let nextPn = '';
                        try {
                          const r = await axios.get(`${API}/elevator/payments/next-number?building_id=${buildingId}`);
                          nextPn = r.data?.payment_number || '';
                        } catch {}
                        setElevPaymentForm({ amount: '', date: new Date().toISOString().split('T')[0], notes: '', payment_source: 'cash', reference: '', allocations: {}, alloc_reserve: {}, payment_number: nextPn });
                        try {
                          // Phase 306 — Load OPENING + unallocated credits +
                          // unpaid expenses in one canonical list.
                          const [fbRes, sbRes, piRes] = await Promise.all([
                            axios.get(`${API}/buildings/${buildingId}/fund-balances`),
                            axios.get(`${API}/buildings/${buildingId}/source-balances`),
                            axios.get(`${API}/elevator/payable-items?building_id=${buildingId}`),
                          ]);
                          setFundBalances(fbRes.data);
                          setSourceBalances(sbRes.data);
                          setElevPayableItems(piRes.data?.items || []);
                        } catch {}
                        setElevPaymentDialogOpen(true);
                      }} className="bg-zinc-950 rounded-none h-7 text-xs" data-testid="add-elev-payment-btn">
                        <Plus size={14} className="mr-1" />{t('addPayment')}
                      </Button>
                    )}
                  </div>
                </div>
                <table className="w-full text-xs" data-testid="elev-payments-table">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('paymentNumber')}</th>
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('date')}</th>
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-28">{t('invoiceNumber') || 'Invoice #'}</th>
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold">{t('description')}</th>
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-20">{t('reference')}</th>
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-20">{t('source')}</th>
                      <th className="px-3 py-2 text-right text-[10px] text-zinc-400 font-semibold w-24">{t('amount')}</th>
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold w-44">{t('allocatedStatus') || 'Allocated Status'}</th>
                      <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold w-24">{t('attachedFile') || 'Attached File'}</th>
                      <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold w-20">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Phase 375 — Filter payments by From/To dates (client-side).
                      const inPayRange = (d) => {
                        if (!d) return !(elevPayFrom || elevPayTo);
                        const ds = String(d).slice(0, 10);
                        if (elevPayFrom && ds < elevPayFrom) return false;
                        if (elevPayTo && ds > elevPayTo) return false;
                        return true;
                      };
                      const sqp = (elevPaySearch || '').trim().toLowerCase();
                      const payEntries = (elevLedger?.entries || []).filter(e => {
                        if (e.type !== 'payment') return false;
                        if (!inPayRange(e.date)) return false;
                        if (!sqp) return true;
                        const hay = [
                          e.payment_number, e.invoice_number, e.reference,
                          e.payment_source, e.description, e.notes,
                          e.date, formatDateDMY(e.date),
                          (e.invoice_numbers || []).join(' '),
                          e.paid_from_reserve_fund ? 'reserve fund' : '',
                        ].map(v => String(v || '').toLowerCase()).join(' \u00b7 ');
                        return hay.includes(sqp);
                      });
                      // Phase 381 — Sort payments DESC (newest first)
                      payEntries.sort((a, b) => {
                        const da = a.date || '';
                        const db = b.date || '';
                        if (da !== db) return db.localeCompare(da);
                        return (b.seq || 0) - (a.seq || 0);
                      });
                      if (payEntries.length === 0) return <tr><td colSpan={10} className="px-4 py-8 text-center text-zinc-400">{t('noPayments')}</td></tr>;
                      return payEntries.map((entry, idx) => {
                        // Phase 332 — Allocation status badge.
                        const status = entry.allocation_status || 'fully_allocated';
                        const unalloc = entry.unallocated_amount || 0;
                        let badgeBg = '', badgeText = '', badgeLabel = '';
                        if (status === 'fully_allocated') {
                          badgeBg = 'bg-zinc-100'; badgeText = 'text-zinc-600';
                          badgeLabel = t('fullyAllocated') || 'Fully Allocated';
                        } else if (status === 'partial') {
                          badgeBg = 'bg-amber-100'; badgeText = 'text-amber-800';
                          badgeLabel = `${t('partial') || 'Partial'} (€${unalloc.toFixed(2)} ${t('remaining') || 'remaining'})`;
                        } else {
                          badgeBg = 'bg-emerald-100'; badgeText = 'text-emerald-800';
                          badgeLabel = `${t('unallocated') || 'Unallocated'} (€${unalloc.toFixed(2)})`;
                        }
                        return (
                        <tr key={entry.payment_id || idx} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`elev-pay-row-${idx}`}>
                          <td className="px-3 py-2.5 font-mono text-zinc-700 font-semibold">{entry.payment_number || '-'}</td>
                          <td className="px-3 py-2.5 font-mono text-zinc-500">{formatDateDMY(entry.date)}</td>
                          <td className="px-3 py-2.5 font-mono text-zinc-700" data-testid={`elev-pay-invoices-${idx}`}>{(entry.invoice_numbers && entry.invoice_numbers.length > 0) ? entry.invoice_numbers.join(' + ') : (entry.invoice_number || '-')}</td>
                          <td className="px-3 py-2.5 text-zinc-600">{entry.description || t('payment')}</td>
                          <td className="px-3 py-2.5 font-mono text-zinc-500">{entry.reference || '-'}</td>
                          <td className="px-3 py-2.5 text-zinc-500">{entry.payment_source ? t(entry.payment_source) : '-'}</td>
                          <td className="px-3 py-2.5 text-right font-mono font-semibold text-emerald-700">&euro;{(entry.payment || 0).toFixed(2)}</td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold ${badgeBg} ${badgeText}`} data-testid={`elev-pay-alloc-${idx}`}>
                              {badgeLabel}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {(() => {
                              // Find the full payment record to read attachments
                              const fullPay = (() => {
                                // The ledger entry contains payment_id but not attachments directly.
                                // We look up in elevExpenses' linked payments isn't reliable, so use
                                // the entry itself if it has attachments, otherwise fall back to 0.
                                return entry;
                              })();
                              const atts = entry.attachments || [];
                              return (
                                <button
                                  onClick={() => openAttManager('pay', entry.payment_id, entry.payment_number || '-', atts)}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border rounded-none transition-colors ${atts.length > 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-zinc-200 bg-white text-zinc-400 hover:bg-zinc-50'}`}
                                  title={atts.length > 0 ? `${atts.length} ${t('attachedFiles') || 'file(s)'}` : (t('addAttachment') || 'Add attachment')}
                                  data-testid={`elev-pay-attachments-${entry.payment_id}`}
                                >
                                  <Paperclip size={11} /> {atts.length}
                                </button>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {isAdmin && (
                              <div className="flex justify-center gap-0.5">
                                <button onClick={() => { const exp = elevExpenses.find(e => e.expense_id === entry.expense_id); setSelectedElevExpense(exp || null); openEditElevPayment(entry); }} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`elev-edit-pay-${entry.payment_id}`}><PencilSimple size={14} /></button>
                                <button onClick={() => { setDeletingElevPayment(entry); setDeleteElevPaymentDialogOpen(true); }} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`elev-delete-pay-${entry.payment_id}`}><Trash size={14} /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {/* ===== SUB-TAB: CREDIT NOTES (Phase 659) =====
                Supplier credit notes / sales returns specific to the
                Elevator section. They appear as their own negative
                entries on the Statement tab (running balance is
                recomputed on the server).
                Phase 661 — Related-invoice dropdown receives RICH data
                (number / date / outstanding) so the component can show
                only unpaid + partially paid invoices in a clear format. */}
            {elevSubTab === 'creditNotes' && (
              <SupplierCreditNotesTab
                buildingId={buildingId}
                section="elevator"
                suppliers={building?.elevator_company_name ? [building.elevator_company_name] : []}
                invoices={(elevExpenses || []).map(e => {
                  const total = (typeof e.grand_total === 'number') ? e.grand_total : ((e.invoice_amount || 0) * (1 + (e.vat_rate || 0) / 100));
                  const paid = e.total_paid || 0;
                  const outstanding = Math.max(0, total - paid);
                  return {
                    id: e.expense_id,
                    number: e.invoice_number || e.expense_id,
                    date: e.date,
                    outstanding,
                    amount: total,
                  };
                })}
                t={t}
              />
            )}

            {/* ===== SUB-TAB: STATEMENT ===== */}
            {elevSubTab === 'statement' && (
              <div className="bg-white border border-zinc-200">
                <div className="px-4 py-3 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-zinc-800" style={{ fontFamily: 'var(--font-heading)' }}>{t('elevatorStatement')}</h3>
                  {/* Phase 351 — Period filter for the statement. Empty = all-time.
                      Balance Brought Forward is recomputed server-side based on
                      the selected From date. */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('from') || 'From'}</Label>
                    <Input
                      type="date"
                      value={elevStmtFrom}
                      onChange={(e) => setElevStmtFrom(e.target.value)}
                      className="rounded-none h-7 text-xs w-36"
                      data-testid="elev-stmt-from"
                    />
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('to') || 'To'}</Label>
                    <Input
                      type="date"
                      value={elevStmtTo}
                      onChange={(e) => setElevStmtTo(e.target.value)}
                      className="rounded-none h-7 text-xs w-36"
                      data-testid="elev-stmt-to"
                    />
                    {(elevStmtFrom || elevStmtTo) && (
                      <button
                        type="button"
                        onClick={() => { setElevStmtFrom(''); setElevStmtTo(''); }}
                        className="text-[10px] text-zinc-500 hover:text-zinc-900 underline"
                        data-testid="elev-stmt-clear-period"
                      >
                        {t('clear') || 'Clear'}
                      </button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const params = { building_id: buildingId };
                        if (elevStmtFrom) params.date_from = elevStmtFrom;
                        if (elevStmtTo) params.date_to = elevStmtTo;
                        openPdfInBrowser(`/elevator/expenses/statement-pdf`, params);
                      }}
                      className="rounded-none h-7 text-xs"
                      data-testid="elev-stmt-download-btn"
                    >
                      <FilePdf size={14} className="mr-1" />{t('viewPdf')}
                    </Button>
                  </div>
                </div>
                <table className="w-full text-xs" data-testid="elev-statement-table">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="px-4 py-2 text-left text-[10px] text-zinc-400 font-semibold w-24">{t('date')}</th>
                      <th className="px-4 py-2 text-left text-[10px] text-zinc-400 font-semibold w-28">{t('invoiceNumber') || 'Invoice/Ref'}</th>
                      <th className="px-4 py-2 text-left text-[10px] text-zinc-400 font-semibold">{t('description')}</th>
                      <th className="px-6 py-2 text-right text-[10px] text-zinc-400 font-semibold w-32">{t('amount')}</th>
                      <th className="px-6 py-2 text-right text-[10px] text-zinc-400 font-semibold w-32">{t('payment')}</th>
                      <th className="px-6 py-2 text-right text-[10px] text-zinc-400 font-semibold w-32">{t('total') || 'Total'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Phase 713 — Balance Brought Forward row.
                        - When a Date From filter is active: ALWAYS show the
                          row (even if the computed balance is exactly 0) so
                          the filtered statement always opens with the
                          carried-forward closing balance from the prior
                          period. The date column is the user-selected
                          Date From (the period start), not the original
                          opening-balance date.
                        - When NO date filter is active: keep the legacy
                          behaviour — only show the row when the manual
                          opening balance is non-zero, and label the date
                          column with the original opening-balance date. */}
                    {elevLedger && (elevStmtFrom || (elevLedger.opening_balance || 0) !== 0) && (
                      <tr className="border-b border-zinc-200 bg-zinc-50/40" data-testid="elev-stmt-ob-row">
                        <td className="px-4 py-2 font-mono text-zinc-500">{elevStmtFrom ? formatDateDMY(elevStmtFrom) : (elevLedger.opening_balance_date ? formatDateDMY(elevLedger.opening_balance_date) : '-')}</td>
                        <td className="px-4 py-2 font-mono text-zinc-400">-</td>
                        <td className="px-4 py-2 font-medium text-zinc-600">{t('balanceBroughtForward')}</td>
                        <td className="px-6 py-2 text-right text-zinc-300">-</td>
                        <td className="px-6 py-2 text-right text-zinc-300">-</td>
                        <td className="px-6 py-2 font-mono font-semibold text-right">&euro;{(elevLedger.opening_balance || 0).toFixed(2)}</td>
                      </tr>
                    )}
                    {(() => {
                      // Phase 500 — Statement entries displayed ASC (oldest
                      // → newest). The backend computes `entry.balance` as
                      // the running balance after each transaction in
                      // chronological order, so the table reads top-to-bottom
                      // exactly like a bank statement: Opening Balance row
                      // first, then every charge / payment in date order,
                      // each row's Total column showing the running balance
                      // immediately after that transaction.
                      // Phase 664 — Parent/child layout. The parent invoice
                      // row owns the Date, Invoice# and the gross Amount /
                      // running Balance. Sub-rows (description-only line
                      // items) appear indented under the parent with EMPTY
                      // Date and Invoice# columns so the eye reads them as
                      // child rows, not as separate transactions. The `↳`
                      // arrow next to the description has been removed —
                      // indentation alone communicates hierarchy.
                      const sortedEntries = [...(elevLedger?.entries || [])].sort((a, b) => {
                        const da = a.date || '';
                        const db = b.date || '';
                        if (da !== db) return da.localeCompare(db);
                        return (a.seq || 0) - (b.seq || 0);
                      });
                      const rows = [];
                      sortedEntries.forEach((entry, idx) => {
                        if (entry.type === 'invoice') {
                          const invNum = entry.invoice_number || '-';
                          const parentLabel = entry.description || `${t('invoice') || 'Invoice'} ${invNum}`;
                          // Phase 667 — Pull the back-end's authoritative settlement
                          // figures for the parent invoice so users see at a glance
                          // when a credit note has been applied or it's been paid.
                          const linkedExp = (elevExpenses || []).find(e => e.expense_id === entry.expense_id) || {};
                          const expCredited = linkedExp.credited_amount || 0;
                          const expStatus = linkedExp.status || '';
                          const expRemaining = (typeof linkedExp.remaining_balance === 'number') ? linkedExp.remaining_balance : null;
                          rows.push(
                            <tr key={`stmt-${idx}`} className="border-b border-zinc-100" data-testid={`elev-stmt-row-${idx}`}>
                              <td className="px-4 py-2 font-mono text-zinc-500 align-top">{formatDateDMY(entry.date)}</td>
                              <td className="px-4 py-2 font-mono text-zinc-700 align-top">{invNum}</td>
                              <td className="px-4 py-2 align-top">
                                <span className="font-medium text-zinc-800">{parentLabel}</span>
                                {expStatus === 'paid' && (
                                  <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase font-semibold" data-testid={`elev-stmt-paid-${idx}`}>{t('paid') || 'Paid'}</span>
                                )}
                                {expStatus === 'partially_paid' && expRemaining !== null && (
                                  <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 uppercase font-semibold" title={`${t('outstanding') || 'Outstanding'}: €${expRemaining.toFixed(2)}`} data-testid={`elev-stmt-partial-${idx}`}>{t('partial') || 'Partial'} &euro;{expRemaining.toFixed(2)}</span>
                                )}
                                {expCredited > 0.005 && (
                                  <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200" title={t('creditNotesApplied') || 'Credit Notes applied'} data-testid={`elev-stmt-credited-${idx}`}>CN &euro;{expCredited.toFixed(2)}</span>
                                )}
                              </td>
                              <td className="px-6 py-2 font-mono text-right align-top">{(entry.charge || 0) > 0 ? <span className="text-zinc-700">&euro;{entry.charge.toFixed(2)}</span> : <span className="text-zinc-300">-</span>}</td>
                              <td className="px-6 py-2 font-mono text-right align-top text-zinc-300">-</td>
                              <td className={`px-6 py-2 font-mono font-semibold text-right align-top ${entry.balance < 0 ? 'text-red-600' : ''}`}>&euro;{entry.balance.toFixed(2)}</td>
                            </tr>
                          );
                          (entry.extra_charges || []).forEach((ec, ei) => {
                            if ((ec.gross || 0) <= 0.005) return; // skip €0 lines
                            // Phase 664 — Sub-row keeps Date + Invoice#
                            // EMPTY so it visually nests under its parent.
                            // No "↳" arrow, no repeated invoice number,
                            // no repeated date — just an indented label.
                            rows.push(
                              <tr key={`stmt-${idx}-ec-${ei}`} className="border-b border-dotted border-zinc-100" data-testid={`elev-stmt-extra-${idx}-${ei}`}>
                                <td className="px-4 py-1.5 align-top"></td>
                                <td className="px-4 py-1.5 align-top"></td>
                                <td className="px-4 py-1.5 text-[11px] text-zinc-600 align-top pl-10">
                                  {ec.description}
                                  {ec.is_maintenance && <span className="ml-1.5 text-[8px] uppercase tracking-wider text-blue-600 font-semibold">MNT</span>}
                                </td>
                                <td className="px-6 py-1.5 align-top"></td>
                                <td className="px-6 py-1.5 align-top"></td>
                                <td className="px-6 py-1.5 align-top"></td>
                              </tr>
                            );
                          });
                        } else if (entry.type === 'credit_note') {
                          // Phase 659 — Credit Note row: shows the supplier's
                          // credit note number, the supplier name as the
                          // description, and the credit amount in the same
                          // column as Payment (because both REDUCE the
                          // outstanding balance). The running balance is
                          // already reduced server-side.
                          const cnLabel = entry.credit_note_number || entry.invoice_number || '-';
                          const cnDesc = (entry.supplier ? `${entry.supplier} \u2014 ` : '') + (t('creditNote') || 'Credit Note');
                          rows.push(
                            <tr key={`stmt-${idx}`} className="border-b border-zinc-100 bg-emerald-50/30" data-testid={`elev-stmt-row-${idx}`}>
                              <td className="px-4 py-2 font-mono text-zinc-500 align-top">{formatDateDMY(entry.date)}</td>
                              <td className="px-4 py-2 font-mono text-emerald-700 font-semibold align-top">{cnLabel}</td>
                              <td className="px-4 py-2 align-top">
                                <span className="text-zinc-700">{cnDesc}</span>
                                {entry.description && entry.description !== 'Credit Note' && <span className="text-zinc-400 ml-1 text-[11px]">{entry.description}</span>}
                              </td>
                              <td className="px-6 py-2 font-mono text-right align-top text-zinc-300">-</td>
                              <td className="px-6 py-2 font-mono text-right align-top">{(entry.credit_note || 0) > 0 ? <span className="text-red-600 font-semibold">&minus;&euro;{(entry.credit_note || 0).toFixed(2)}</span> : <span className="text-zinc-300">-</span>}</td>
                              <td className={`px-6 py-2 font-mono font-semibold text-right align-top ${entry.balance < 0 ? 'text-red-600' : ''}`}>&euro;{entry.balance.toFixed(2)}</td>
                            </tr>
                          );
                        } else {
                          const payInvList = (entry.invoice_numbers && entry.invoice_numbers.length > 0)
                            ? entry.invoice_numbers.join(' + ')
                            : (entry.invoice_number || '-');
                          rows.push(
                            <tr key={`stmt-${idx}`} className="border-b border-zinc-100" data-testid={`elev-stmt-row-${idx}`}>
                              <td className="px-4 py-2 font-mono text-zinc-500 align-top">{formatDateDMY(entry.date)}</td>
                              <td className="px-4 py-2 font-mono text-zinc-700 align-top">{payInvList}</td>
                              <td className="px-4 py-2 align-top">
                                <span className="text-zinc-600">{entry.description}</span>
                              </td>
                              <td className="px-6 py-2 font-mono text-right align-top text-zinc-300">-</td>
                              <td className="px-6 py-2 font-mono text-right align-top">{(entry.payment || 0) > 0 ? <span className="text-emerald-700">&euro;{entry.payment.toFixed(2)}</span> : <span className="text-zinc-300">-</span>}</td>
                              <td className={`px-6 py-2 font-mono font-semibold text-right align-top ${entry.balance < 0 ? 'text-red-600' : ''}`}>&euro;{entry.balance.toFixed(2)}</td>
                            </tr>
                          );
                        }
                      });
                      return rows;
                    })()}
                    {elevLedger && (elevLedger.entries || []).length > 0 && (
                      <tr className="border-t-2 border-zinc-800 bg-zinc-900">
                        <td colSpan={5} className="px-3 py-2.5 font-semibold text-white uppercase tracking-wider text-[11px]">{t('finalBalance')}</td>
                        <td className={`px-3 py-2.5 text-right font-mono font-bold text-sm ${(elevLedger.final_balance || 0) < 0 ? 'text-red-300' : 'text-white'}`} data-testid="elev-final-balance">&euro;{(elevLedger.final_balance || 0).toFixed(2)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Phase 665 — Extra Charges section now SPLITS Monthly
                    Maintenance from all other charges into two dedicated
                    sub-sections. Each invoice's `extra_charges[]` is
                    flattened; if an entry has `is_maintenance:true` it
                    goes into the Maintenance bucket. Legacy invoices
                    (empty extra_charges + positive total) still get a
                    synthesized "Monthly Maintenance - {month}" row that
                    lands in the Maintenance bucket. */}
                {(elevLedger?.entries || []).length > 0 && (() => {
                  const invoiceEntries = (elevLedger.entries || []).filter(e => e.type === 'invoice');
                  const allExtras = invoiceEntries.flatMap(inv => {
                    const rawExtras = (inv.extra_charges || []).map(ec => ({ ...ec, date: inv.date, invoice_number: inv.invoice_number }));
                    const hasStructuredMaintenance = rawExtras.some(ec => ec.is_maintenance);
                    const legacyNet = Number(inv.net_amount || 0);
                    const legacyVatRate = Number(inv.vat_rate || 0);
                    const legacyVat = Number(inv.vat_amount || 0);
                    const legacyGross = Number(inv.total_with_vat || 0);
                    const monthLabel = inv.maintenance_notes || '';
                    if (!hasStructuredMaintenance && legacyGross > 0.005) {
                      return [
                        {
                          description: monthLabel
                            ? `${t('monthlyMaintenance') || 'Monthly Maintenance'} - ${monthLabel}`
                            : (t('monthlyMaintenance') || 'Monthly Maintenance'),
                          net_amount: legacyNet,
                          vat_rate: legacyVatRate,
                          vat_amount: legacyVat,
                          gross: legacyGross,
                          is_maintenance: true,
                          date: inv.date,
                          invoice_number: inv.invoice_number,
                        },
                        ...rawExtras,
                      ];
                    }
                    return rawExtras;
                  });
                  const maintList = allExtras.filter(ec => ec.is_maintenance).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                  const otherList = allExtras.filter(ec => !ec.is_maintenance).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                  const totalMaint = maintList.reduce((s, ec) => s + Number(ec.gross || 0), 0);
                  const totalOther = otherList.reduce((s, ec) => s + Number(ec.gross || 0), 0);
                  const totalAll = totalMaint + totalOther;

                  const renderTable = (list) => (
                    <table className="w-full text-[11px]" data-testid="elev-extras-detail-table">
                      <thead>
                        <tr className="text-zinc-400">
                          <th className="text-left py-1 font-medium w-20">{t('date')}</th>
                          <th className="text-left py-1 font-medium w-28">{t('invoice')}</th>
                          <th className="text-left py-1 font-medium">{t('description')}</th>
                          <th className="text-right py-1 font-medium w-20">{t('netAmount')}</th>
                          <th className="text-right py-1 font-medium w-16">{t('vat')}</th>
                          <th className="text-right py-1 font-medium w-20">{t('total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((ec, i) => {
                          const net = Number(ec.net_amount || 0);
                          const gross = Number(ec.gross || 0);
                          const vatAmt = Math.max(0, gross - net);
                          return (
                            <tr key={`x-${i}`} className={`border-t border-zinc-100 ${ec.is_maintenance ? 'bg-blue-50/30' : ''}`} data-testid={`elev-extra-row-${ec.is_maintenance ? 'mnt' : 'oth'}-${i}`}>
                              <td className="py-1 font-mono text-zinc-500">{formatDateDMY(ec.date)}</td>
                              <td className="py-1 font-mono text-zinc-600 font-semibold">{ec.invoice_number || '-'}</td>
                              <td className="py-1 text-zinc-700">
                                {ec.description || '-'}
                                {ec.is_maintenance && <span className="ml-1.5 text-[8px] uppercase tracking-wider text-blue-600 font-semibold">MNT</span>}
                              </td>
                              <td className="py-1 text-right font-mono text-zinc-600">&euro;{net.toFixed(2)}</td>
                              <td className="py-1 text-right font-mono text-zinc-400">&euro;{vatAmt.toFixed(2)}</td>
                              <td className="py-1 text-right font-mono font-semibold text-zinc-800">&euro;{gross.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );

                  return (
                    <div className="mt-6 pt-4 border-t-2 border-zinc-200 bg-white" data-testid="elev-stmt-extra-charges">
                      <div className="w-full px-4 py-2.5 flex items-center justify-between">
                        <button onClick={() => setExpandedGrandExtra(v => !v)} className="flex items-center gap-2 flex-1 text-left" data-testid="elev-toggle-extras">
                          {expandedGrandExtra ? <CaretDown size={12} className="text-zinc-400" /> : <CaretRight size={12} className="text-zinc-400" />}
                          <span className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">{t('extraCharges') || 'Extra Charges'}</span>
                          <span className="text-[10px] text-zinc-400 font-mono">({maintList.length + otherList.length})</span>
                        </button>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm font-semibold text-zinc-800">&euro;{totalAll.toFixed(2)}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openPdfInBrowser('/elevator/expenses/statement-pdf', { building_id: buildingId, mode: 'extra_charges' }); }}
                            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-700 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-400 px-2 py-1 transition-colors rounded-none"
                            title={t('viewPdf') || 'View PDF'}
                            data-testid="elev-extra-charges-view-pdf"
                          >
                            <Eye size={12} />
                            <span>{t('viewPdf') || 'View PDF'}</span>
                          </button>
                        </div>
                      </div>
                      {expandedGrandExtra && (
                        <div className="border-t border-zinc-100">
                          {/* === MONTHLY MAINTENANCE === */}
                          <div className="px-4 py-2 bg-blue-50/40" data-testid="elev-extras-maintenance-section">
                            <div className="flex items-center justify-between mb-1.5">
                              <h4 className="text-[11px] font-bold uppercase tracking-wider text-blue-700">
                                {t('monthlyMaintenance') || 'Monthly Maintenance'} <span className="text-zinc-400 ml-1 font-mono">({maintList.length})</span>
                              </h4>
                              <span className="font-mono text-xs font-semibold text-blue-800">&euro;{totalMaint.toFixed(2)}</span>
                            </div>
                            {maintList.length === 0 ? (
                              <div className="py-2 text-center text-[11px] text-zinc-400 italic" data-testid="elev-extras-maint-empty">
                                {t('noMaintenanceCharges') || 'No monthly maintenance charges for selected period.'}
                              </div>
                            ) : renderTable(maintList)}
                          </div>
                          {/* === OTHER EXTRA CHARGES === */}
                          <div className="px-4 py-2 border-t border-zinc-200 bg-zinc-50/40" data-testid="elev-extras-other-section">
                            <div className="flex items-center justify-between mb-1.5">
                              <h4 className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">
                                {t('otherExtraCharges') || 'Other Extra Charges'} <span className="text-zinc-400 ml-1 font-mono">({otherList.length})</span>
                              </h4>
                              <span className="font-mono text-xs font-semibold text-zinc-800">&euro;{totalOther.toFixed(2)}</span>
                            </div>
                            {otherList.length === 0 ? (
                              <div className="py-2 text-center text-[11px] text-zinc-400 italic" data-testid="elev-extras-other-empty">
                                {t('noOtherExtraCharges') || 'No other extra charges for selected period.'}
                              </div>
                            ) : renderTable(otherList)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}
        {activeTab === 'meetings' && (
          <MeetingsTab
            buildingId={buildingId}
            buildingName={building?.name || ''}
            apartments={apartments}
            isAdmin={isAdmin}
          />
        )}

        {/* ====== TOPIC CATEGORIES TAB ====== */}
        {activeTab === 'topicCategories' && (
          <TopicCategoriesContent />
        )}

        {/* ====== INSURANCE TAB ====== */}
        {activeTab === 'insuranceSection' && (
          <div className="space-y-6" data-testid="insurance-section">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('insurance')}</h2>
              <Button onClick={() => openInsuranceDialog(false, null)} className="bg-zinc-950 rounded-none" data-testid="add-new-insurance-btn">
                <Plus size={18} className="mr-2" />{t('addInsurance')}
              </Button>
            </div>

            {/* Phase 659 — Supplier Credit Notes for Insurance section. */}
            <SupplierCreditNotesTab
              buildingId={buildingId}
              section="insurance"
              suppliers={(insuranceHistory || []).map(p => p.company_name).filter(Boolean)}
              t={t}
            />

            {insuranceHistory.length === 0 ? (
              <div className="bg-white border border-zinc-200 text-center py-12">
                <ShieldWarning size={48} className="mx-auto mb-3 text-zinc-300" />
                <p className="text-zinc-500 mb-4">{t('noInsurance')}</p>
                <Button onClick={() => openInsuranceDialog(false, null)} className="bg-zinc-950 rounded-none" data-testid="add-insurance-btn">
                  <Plus size={18} className="mr-2" />{t('addInsurance')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {insuranceHistory.map((policy, idx) => {
                  const isExpanded = expandedInsuranceId === policy.insurance_id;
                  const stmt = insuranceStatements[policy.insurance_id];
                  const statusColors = {
                    active: 'bg-emerald-50 text-emerald-700 border-emerald-300',
                    expiring_soon: 'bg-amber-50 text-amber-700 border-amber-300',
                    expired: 'bg-zinc-100 text-zinc-500 border-zinc-300',
                    cancelled: 'bg-red-50 text-red-600 border-red-300',
                  };
                  const statusIcons = {
                    active: <ShieldCheck size={24} weight="fill" className="text-emerald-600" />,
                    expiring_soon: <ShieldWarning size={24} weight="fill" className="text-amber-600" />,
                    expired: <Warning size={24} weight="fill" className="text-zinc-400" />,
                    cancelled: <Prohibit size={24} weight="fill" className="text-red-500" />,
                  };
                  return (
                    <div key={policy.insurance_id} className={`bg-white border ${policy.status === 'active' ? 'border-emerald-200' : policy.status === 'cancelled' ? 'border-red-200' : 'border-zinc-200'}`} data-testid={`insurance-policy-${policy.insurance_id}`}>
                      {/* Policy Header — always visible */}
                      <button
                        onClick={() => handleExpandInsurance(policy.insurance_id)}
                        className="w-full px-5 py-4 flex items-center justify-between hover:bg-zinc-50/50 transition-colors text-left"
                        data-testid={`expand-insurance-${policy.insurance_id}`}
                      >
                        <div className="flex items-center gap-4">
                          {statusIcons[policy.status] || statusIcons.active}
                          <div>
                            <h3 className="font-bold text-sm" style={{ fontFamily: 'var(--font-heading)' }}>{policy.company_name}</h3>
                            <p className="text-xs text-zinc-500 font-mono mt-0.5">
                              {formatDateDMY(policy.policy_start_date)} — {formatDateDMY(policy.policy_end_date)}
                              {policy.policy_number && <span className="ml-3 text-zinc-400">#{policy.policy_number}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {policy.total_premium > 0 && (
                            <span className="text-sm font-mono font-semibold">&euro;{policy.total_premium.toFixed(2)}</span>
                          )}
                          <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase border ${statusColors[policy.status] || statusColors.active}`}>
                            {t(policy.status)}
                          </span>
                          {policy.previous_insurance_id && (
                            <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 uppercase font-semibold">{t('renewal')}</span>
                          )}
                          {isExpanded ? <CaretUp size={16} className="text-zinc-400" /> : <CaretDown size={16} className="text-zinc-400" />}
                        </div>
                      </button>

                      {/* Expanded Detail */}
                      {isExpanded && (
                        <div className="border-t border-zinc-100 px-5 py-4 space-y-4">
                          {/* Policy Details Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase">{t('totalPremium')}</p>
                              <p className="text-lg font-bold font-mono">&euro;{(policy.total_premium || 0).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase">{t('numInstallments')}</p>
                              <p className="text-lg font-bold font-mono">{policy.num_installments || 1}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase">{t('installmentAmount')}</p>
                              <p className="text-lg font-bold font-mono text-blue-700">&euro;{(policy.installment_amount || 0).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase">{t('status')}</p>
                              <p className="text-sm font-semibold capitalize">{t(policy.status)}</p>
                              {policy.status !== 'cancelled' && policy.days_until_expiry !== undefined && (
                                <p className={`text-xs font-mono ${policy.days_until_expiry < 0 ? 'text-red-600' : policy.days_until_expiry < 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {policy.days_until_expiry < 0
                                    ? `${t('expiredDaysAgo')} ${Math.abs(policy.days_until_expiry)} ${t('days')}`
                                    : `${t('expiresIn')} ${policy.days_until_expiry} ${t('days')}`}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Insured Amounts */}
                          {policy.insured_amounts && Object.keys(policy.insured_amounts).length > 0 && (
                            <div className="py-3 border-t border-zinc-100">
                              <p className="text-[10px] text-zinc-400 mb-2 font-semibold uppercase">{t('insuredAmounts')}</p>
                              <div className="grid grid-cols-3 gap-4">
                                {policy.insured_amounts.building > 0 && (
                                  <div className="flex items-baseline justify-between py-1.5 border-b border-zinc-50">
                                    <span className="text-sm text-zinc-500">{t('insuredBuilding')}</span>
                                    <span className="text-sm font-semibold font-mono">&euro;{policy.insured_amounts.building.toLocaleString()}</span>
                                  </div>
                                )}
                                {policy.insured_amounts.common_areas > 0 && (
                                  <div className="flex items-baseline justify-between py-1.5 border-b border-zinc-50">
                                    <span className="text-sm text-zinc-500">{t('insuredCommonAreas')}</span>
                                    <span className="text-sm font-semibold font-mono">&euro;{policy.insured_amounts.common_areas.toLocaleString()}</span>
                                  </div>
                                )}
                                {policy.insured_amounts.outdoor > 0 && (
                                  <div className="flex items-baseline justify-between py-1.5 border-b border-zinc-50">
                                    <span className="text-sm text-zinc-500">{t('insuredOutdoor')}</span>
                                    <span className="text-sm font-semibold font-mono">&euro;{policy.insured_amounts.outdoor.toLocaleString()}</span>
                                  </div>
                                )}
                                {policy.insured_amounts.civil_liability > 0 && (
                                  <div className="flex items-baseline justify-between py-1.5 border-b border-zinc-50">
                                    <span className="text-sm text-zinc-500">{t('insuredCivilLiability')}</span>
                                    <span className="text-sm font-semibold font-mono">&euro;{policy.insured_amounts.civil_liability.toLocaleString()}</span>
                                  </div>
                                )}
                                {policy.insured_amounts.excess > 0 && (
                                  <div className="flex items-baseline justify-between py-1.5 border-b border-zinc-50">
                                    <span className="text-sm text-zinc-500">{t('insuredExcess')}</span>
                                    <span className="text-sm font-semibold font-mono">&euro;{policy.insured_amounts.excess.toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Installment Tracking */}
                          {stmt && policy.total_premium > 0 && (
                            <div className="border-t border-zinc-100 pt-3">
                              <h4 className="text-[10px] font-bold uppercase text-zinc-400 mb-3">{t('installmentTracking')}</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                                <div className="bg-zinc-50 border border-zinc-200 p-2">
                                  <p className="text-[10px] text-zinc-400 uppercase">{t('totalPremium')}</p>
                                  <p className="text-sm font-bold font-mono">&euro;{(stmt.total_premium || 0).toFixed(2)}</p>
                                </div>
                                <div className="bg-emerald-50 border border-emerald-200 p-2">
                                  <p className="text-[10px] text-emerald-600 uppercase">{t('paidInstallments')}</p>
                                  <p className="text-sm font-bold font-mono text-emerald-700">{stmt.paid_installments} / {stmt.num_installments}</p>
                                </div>
                                <div className="bg-amber-50 border border-amber-200 p-2">
                                  <p className="text-[10px] text-amber-600 uppercase">{t('remainingInstallments')}</p>
                                  <p className="text-sm font-bold font-mono text-amber-700">{stmt.remaining_installments} / {stmt.num_installments}</p>
                                </div>
                                <div className="bg-blue-50 border border-blue-200 p-2">
                                  <p className="text-[10px] text-blue-500 uppercase">{t('remainingBalance')}</p>
                                  <p className="text-sm font-bold font-mono text-blue-700">&euro;{(stmt.remaining_balance || 0).toFixed(2)}</p>
                                </div>
                              </div>
                              <div className="border border-zinc-200">
                                <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-zinc-100 text-[10px] font-semibold text-zinc-500 uppercase">
                                  <span>{t('installment')}</span>
                                  <span>{t('amount')}</span>
                                  <span>{t('status')}</span>
                                  <span>{t('date')}</span>
                                </div>
                                {(stmt.installments || []).map(inst => (
                                  <div key={inst.number} className={`grid grid-cols-4 gap-2 px-3 py-2 border-t border-zinc-100 text-sm ${inst.status === 'paid' ? 'bg-emerald-50/50' : ''}`}>
                                    <span className="font-mono font-semibold">{inst.number} / {stmt.num_installments}</span>
                                    <span className="font-mono">&euro;{(inst.amount || 0).toFixed(2)}</span>
                                    <span>
                                      {inst.status === 'paid' && <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-200 font-semibold uppercase">{t('paid')}</span>}
                                      {inst.status === 'partially_paid' && <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 font-semibold uppercase">{t('partiallyPaid')}</span>}
                                      {inst.status === 'unpaid' && <span className="text-[10px] px-2 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-200 font-semibold uppercase">{t('unpaid')}</span>}
                                    </span>
                                    <span className="font-mono text-xs text-zinc-500">{inst.paid_date ? formatDateDMY(inst.paid_date) : '—'}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-2 flex items-center gap-3">
                                <div className="flex-1 h-2 bg-zinc-200 overflow-hidden">
                                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${stmt.total_premium > 0 ? ((stmt.total_paid / stmt.total_premium) * 100) : 0}%` }} />
                                </div>
                                <span className="text-xs font-mono text-zinc-500">&euro;{(stmt.total_paid || 0).toFixed(2)} / &euro;{(stmt.total_premium || 0).toFixed(2)}</span>
                              </div>
                            </div>
                          )}

                          {/* Notes */}
                          {policy.notes && (
                            <div className="py-2 border-t border-zinc-100">
                              <p className="text-xs text-zinc-400 uppercase mb-1">{t('notes')}</p>
                              <p className="text-sm text-zinc-600">{policy.notes}</p>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
                            <Button variant="outline" size="sm" onClick={() => openInsuranceDialog(false, policy)} className="rounded-none" data-testid={`edit-insurance-${policy.insurance_id}`}>
                              <PencilSimple size={16} className="mr-1.5" />{t('editInsurance')}
                            </Button>
                            {policy.status !== 'cancelled' && (
                              <Button variant="outline" size="sm" onClick={() => openInsuranceDialog(true, policy)} className="rounded-none border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`renew-insurance-${policy.insurance_id}`}>
                                <ArrowsClockwise size={16} className="mr-1.5" />{t('renewInsurance')}
                              </Button>
                            )}
                            {policy.status !== 'cancelled' && policy.status !== 'expired' && (
                              <Button variant="outline" size="sm" onClick={() => { setCancellingInsuranceId(policy.insurance_id); setCancelInsuranceDialogOpen(true); }} className="rounded-none border-red-200 text-red-700 hover:bg-red-50" data-testid={`cancel-insurance-${policy.insurance_id}`}>
                                <Prohibit size={16} className="mr-1.5" />{t('cancelInsurance')}
                              </Button>
                            )}
                            {policy.status === 'cancelled' && (
                              <Button variant="outline" size="sm" onClick={() => handleReactivateInsurance(policy.insurance_id)} className="rounded-none border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`reactivate-insurance-${policy.insurance_id}`}>
                                <ArrowsClockwise size={16} className="mr-1.5" />{t('reactivate')}
                              </Button>
                            )}
                            {isAdmin && (
                              <Button variant="outline" size="sm" onClick={() => { setDeletingInsurance(policy); setDeleteInsuranceDialogOpen(true); }} className="rounded-none border-red-200 text-red-700 hover:bg-red-50" data-testid={`delete-insurance-${policy.insurance_id}`}>
                                <Trash size={16} className="mr-1.5" />{t('deleteInsurance')}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ====== NOTES TAB ====== */}
        {/* ==================== APARTMENTS TAB ==================== */}
        {/* ==================== STATEMENTS TAB ==================== */}
        {activeTab === 'statements' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('statements')}</h2>
            </div>
            <div className="bg-white border border-zinc-200 p-6">
              <h3 className="text-sm font-semibold mb-3">{t('annualFinancialStatement')}</h3>
              <p className="text-sm text-zinc-500 mb-4">{t('annualStatementDesc')}</p>
              <Button onClick={() => navigate(`/buildings/${buildingId}/annual-statement`)} className="bg-zinc-950 rounded-none" data-testid="annual-statement-btn">
                <FileText size={16} className="mr-2" />{t('annualFinancialStatement')}
              </Button>
            </div>
          </div>
        )}

        {/* ==================== NOTES TAB ==================== */}
        {activeTab === 'buildingNotes' && (
          <div className="space-y-6">
            <div className="flex justify-end">
              <Button onClick={() => openNoteDialog()} className="bg-zinc-950 rounded-none" data-testid="add-note-btn">
                <Plus size={16} className="mr-2" />{t('addNote')}
              </Button>
            </div>
            {notes.length === 0 ? (
              <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('noNotes')}</div>
            ) : (
              <div className="space-y-3">
                {notes.map(note => (
                  <div key={note.note_id} className="bg-white border border-zinc-200 p-5" data-testid={`note-${note.note_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm whitespace-pre-wrap">{note.text}</p>
                        <p className="text-xs text-zinc-400 font-mono mt-3">
                          {t('noteCreated')}: {formatDateTimeDMY(note.created_at)}
                          {note.updated_at !== note.created_at && (
                            <span className="ml-3">({t('editNote')}: {formatDateTimeDMY(note.updated_at)})</span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-1 ml-4">
                        <button onClick={() => openNoteDialog(note)} className="p-2 hover:bg-zinc-100 transition-colors" title={t('editNote')} data-testid={`edit-note-${note.note_id}`}>
                          <PencilSimple size={16} />
                        </button>
                        {isAdmin && (
                          <button onClick={() => { setDeletingNote(note); setDeleteNoteDialogOpen(true); }} className="p-2 hover:bg-red-50 text-red-600 transition-colors" title={t('deleteNote')} data-testid={`delete-note-${note.note_id}`}>
                            <Trash size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== ELEVATOR CERTIFICATION TAB ====== */}
        {activeTab === 'elevatorCertification' && (() => {
          // "Latest" = most recently CREATED certification (so new records always show on top).
          // Tie-break by inspection_date desc, then updated_at.
          const sortedCerts = [...elevCerts].sort((a, b) => {
            const kA = (a.created_at || '') + '|' + (a.inspection_date || '') + '|' + (a.updated_at || '');
            const kB = (b.created_at || '') + '|' + (b.inspection_date || '') + '|' + (b.updated_at || '');
            return kB.localeCompare(kA);
          });
          const latest = sortedCerts[0] || null;
          const todayStr = new Date().toISOString().split('T')[0];
          const isOverdue = latest && latest.next_inspection_date && latest.next_inspection_date < todayStr;
          const isDueSoon = latest && latest.next_inspection_date && !isOverdue && latest.next_inspection_date < new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];

          return (
          <div className="space-y-3" data-testid="elevator-certification-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h4 className="text-sm font-bold text-zinc-800">{t('elevatorCertification')}</h4>
              {isAdmin && (
                <Button onClick={() => openElevCertDialog()} className="bg-zinc-950 rounded-none" data-testid="add-elev-cert-btn">
                  <Plus size={16} className="mr-2" />{t('addCertification')}
                </Button>
              )}
            </div>

            {!latest ? (
              <div className="bg-white border border-zinc-200 p-8 text-center text-zinc-500 text-sm" data-testid="no-elev-certs">{t('noCertifications')}</div>
            ) : (
              <>
              {/* === COMPACT LATEST CARD === */}
              <div className="bg-white border border-zinc-200" data-testid="elev-cert-latest-card">
                <div className="px-3 py-1.5 border-b border-zinc-200 flex items-center gap-2 bg-zinc-50/60">
                  <ShieldCheck size={12} weight="fill" className={
                    latest.result === 'failed' ? 'text-red-600'
                    : latest.result === 'in_progress' ? 'text-blue-600'
                    : isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-600' : 'text-emerald-600'
                  } />
                  <h3 className="text-[11px] font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('latestCertification')}</h3>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 ml-auto ${
                    latest.result === 'passed' ? 'bg-emerald-100 text-emerald-700'
                    : latest.result === 'failed' ? 'bg-red-100 text-red-700'
                    : latest.result === 'in_progress' ? 'bg-blue-100 text-blue-700'
                    : 'bg-amber-100 text-amber-700'
                  }`} data-testid="latest-cert-result-badge">
                    {latest.result === 'passed' ? t('passed')
                      : latest.result === 'failed' ? t('failed')
                      : latest.result === 'in_progress' ? t('inProgress')
                      : latest.result === 'conditional' ? t('conditional')
                      : latest.result}
                  </span>
                  {isAdmin && (
                    <div className="flex items-center gap-0.5 ml-1">
                      <button
                        type="button"
                        onClick={() => openElevCertDialog(latest)}
                        className="p-1 hover:bg-zinc-200 transition-colors"
                        title={t('edit')}
                        data-testid="edit-cert-btn"
                      >
                        <PencilSimple size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDeletingElevCert(latest); setDeleteElevCertDialogOpen(true); }}
                        className="p-1 hover:bg-red-50 text-red-500 transition-colors"
                        title={t('delete')}
                        data-testid="delete-cert-btn"
                      >
                        <Trash size={11} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="px-3 py-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-[11px]">
                  <div>
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('companyName')}</p>
                    <p className="text-zinc-800 truncate" title={latest.company_name || ''} data-testid="latest-cert-company">{latest.company_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('inspectionDate')}</p>
                    <p className="font-mono text-zinc-800" data-testid="latest-cert-inspection">{latest.inspection_date ? formatDateDMY(latest.inspection_date) : '—'}</p>
                  </div>

                  {latest.result === 'passed' && (
                    <>
                      <div>
                        <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('certificateNumber')}</p>
                        <p className="font-mono text-zinc-800" data-testid="latest-cert-number">{latest.certificate_number || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('certificationDate')}</p>
                        <p className="font-mono text-zinc-800" data-testid="latest-cert-certification-date">{latest.certification_date ? formatDateDMY(latest.certification_date) : '—'}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('nextInspectionDate')}</p>
                        <p className={`font-mono ${isOverdue ? 'text-red-600 font-semibold' : isDueSoon ? 'text-amber-600 font-semibold' : 'text-zinc-800'}`} data-testid="latest-cert-next">
                          {latest.next_inspection_date ? formatDateDMY(latest.next_inspection_date) : '—'}
                          {isOverdue && <span className="ml-1 text-[9px]">({t('overdue')})</span>}
                          {isDueSoon && !isOverdue && <span className="ml-1 text-[9px]">({t('dueSoon')})</span>}
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('inspector')}</p>
                        <p className="text-zinc-800 truncate" data-testid="latest-cert-inspector">{latest.inspector_name || '—'}</p>
                      </div>
                    </>
                  )}

                  {latest.result === 'failed' && (
                    <>
                      <div>
                        <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('nextInspectionDate')}</p>
                        <p className={`font-mono ${isOverdue ? 'text-red-600 font-semibold' : isDueSoon ? 'text-amber-600 font-semibold' : 'text-zinc-800'}`} data-testid="latest-cert-next">
                          {latest.next_inspection_date ? formatDateDMY(latest.next_inspection_date) : '—'}
                          {isOverdue && <span className="ml-1 text-[9px]">({t('overdue')})</span>}
                          {isDueSoon && !isOverdue && <span className="ml-1 text-[9px]">({t('dueSoon')})</span>}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('failureReasons')}</p>
                        <div className="flex flex-wrap gap-0.5 mt-0.5" data-testid="latest-cert-failures">
                          {(latest.failure_reasons && latest.failure_reasons.length > 0)
                            ? latest.failure_reasons.map((r, i) => (
                              <span key={i} className="text-[9px] px-1 py-0.5 bg-red-50 text-red-700 border border-red-200">{r}</span>
                            ))
                            : <span className="text-zinc-400">—</span>}
                        </div>
                      </div>
                    </>
                  )}

                  {latest.result === 'in_progress' && (
                    <div className="md:col-span-2">
                      <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('status')}</p>
                      <span className="text-blue-700 text-[11px]" data-testid="latest-cert-in-progress-msg">{t('inspectionInProgressMsg')}</span>
                    </div>
                  )}

                  {(latest.total_cost || latest.net_cost || 0) > 0 && (
                    <div>
                      <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('amount')}</p>
                      <p className="font-mono font-semibold text-zinc-900" data-testid="latest-cert-amount">
                        €{(latest.total_cost || 0).toFixed(2)}
                        {(latest.vat_amount || 0) > 0 && (
                          <span className="text-[9px] font-normal text-zinc-400 ml-1">({t('incl')} VAT)</span>
                        )}
                      </p>
                    </div>
                  )}

                  {latest.notes && (
                    <div className="md:col-span-4">
                      <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('notes')}</p>
                      <pre
                        className="bg-zinc-50 border border-zinc-200 mt-1 px-2.5 py-2 text-[11px] text-zinc-700 whitespace-pre-wrap break-words font-sans max-h-[280px] overflow-y-auto"
                        data-testid="latest-cert-notes"
                      >{latest.notes}</pre>
                    </div>
                  )}
                </div>
              </div>

              {/* === COLLAPSIBLE HISTORY — Phase 704: full audit trail with all snapshot
                   fields (inspector, company, failure reasons, etc.) plus per-row Edit / Delete. === */}
              {(latest.history && latest.history.length > 0) && (() => {
                // Newest snapshot first
                const snapshots = [...latest.history].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
                const fmtDateTime = (iso) => {
                  if (!iso) return '—';
                  try {
                    const dt = new Date(iso);
                    const dd = String(dt.getDate()).padStart(2, '0');
                    const mm = String(dt.getMonth() + 1).padStart(2, '0');
                    const yy = dt.getFullYear();
                    const hh = String(dt.getHours()).padStart(2, '0');
                    const mi = String(dt.getMinutes()).padStart(2, '0');
                    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
                  } catch { return iso; }
                };
                return (
                <div className="border border-zinc-200 bg-white" data-testid="cert-history-collapsible">
                  <button
                    type="button"
                    onClick={() => setElevCertHistoryOpen(!elevCertHistoryOpen)}
                    className="w-full px-3 py-1.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors text-left"
                    data-testid="cert-history-toggle"
                    aria-expanded={elevCertHistoryOpen}
                  >
                    {elevCertHistoryOpen
                      ? <CaretDown size={10} className="text-zinc-500" />
                      : <CaretRight size={10} className="text-zinc-500" />}
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600" style={{ fontFamily: 'var(--font-heading)' }}>
                      {t('history')}
                    </span>
                    <span className="text-[9px] font-mono text-zinc-500 bg-zinc-100 px-1 py-0.5">{snapshots.length}</span>
                    <span className="ml-auto text-[9px] text-zinc-400 normal-case">{t('historyOfThisCert')}</span>
                  </button>

                  {elevCertHistoryOpen && (
                    <div className="border-t border-zinc-100 bg-zinc-50/20 divide-y divide-zinc-100" data-testid="cert-history-panel">
                      {snapshots.map((s, idx) => {
                        const reasons = Array.isArray(s.failure_reasons) ? s.failure_reasons : [];
                        const isFailed = (s.result || '').toLowerCase() === 'failed';
                        return (
                          <div key={s.snapshot_id || idx} className="px-3 py-2.5" data-testid={`cert-history-row-${idx}`}>
                            <div className="flex items-start gap-3">
                              {/* Result + timestamp + actions */}
                              <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5 text-[11px]">
                                {/* Result + timestamp band (always shown) */}
                                <div className="flex items-center gap-2 col-span-full">
                                  {s.result === 'passed' && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold uppercase tracking-wider">{t('passed')}</span>}
                                  {s.result === 'failed' && <span className="text-[9px] px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 font-semibold uppercase tracking-wider">{t('failed')}</span>}
                                  {s.result === 'in_progress' && <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 font-semibold uppercase tracking-wider">{t('inProgress')}</span>}
                                  {(!s.result || s.result === 'conditional') && <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 font-semibold uppercase tracking-wider">{s.result || '—'}</span>}
                                  <span className="font-mono text-[10px] text-zinc-500">{fmtDateTime(s.timestamp)}</span>
                                  {s.user && <span className="text-[10px] text-zinc-400 truncate">— {s.user}</span>}
                                  {isAdmin && (
                                    <div className="ml-auto flex items-center gap-1">
                                      <button type="button" onClick={() => openEditHistEntry(latest.cert_id, s)} className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors" title={t('edit')} data-testid={`cert-history-edit-${idx}`}><PencilSimple size={12} /></button>
                                      <button type="button" onClick={() => setDeletingHistEntry({ cert_id: latest.cert_id, snapshot: s })} className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors" title={t('delete')} data-testid={`cert-history-delete-${idx}`}><Trash size={12} /></button>
                                    </div>
                                  )}
                                </div>
                                {/* Field grid */}
                                <div><span className="text-[9px] uppercase tracking-wider text-zinc-400">{t('inspectionDate')}</span><div className="font-mono text-zinc-700">{s.inspection_date ? formatDateDMY(s.inspection_date) : '—'}</div></div>
                                <div><span className="text-[9px] uppercase tracking-wider text-zinc-400">{t('certificateNumber')}</span><div className="font-mono text-zinc-700 break-all">{s.certificate_number || '—'}</div></div>
                                <div><span className="text-[9px] uppercase tracking-wider text-zinc-400">{t('nextInspectionDate')}</span><div className="font-mono text-zinc-700">{s.next_inspection_date ? formatDateDMY(s.next_inspection_date) : '—'}</div></div>
                                <div><span className="text-[9px] uppercase tracking-wider text-zinc-400">{t('inspector') || 'Inspector'}</span><div className="text-zinc-700 truncate" title={s.inspector_name || ''}>{s.inspector_name || '—'}</div></div>
                                <div className="sm:col-span-2"><span className="text-[9px] uppercase tracking-wider text-zinc-400">{t('company') || 'Company'}</span><div className="text-zinc-700 truncate" title={s.company_name || ''}>{s.company_name || '—'}</div></div>
                                <div className="col-span-full"><span className="text-[9px] uppercase tracking-wider text-zinc-400">{t('notes')}</span><div className="text-zinc-700 italic break-words">{s.notes || '—'}</div></div>
                                {isFailed && reasons.length > 0 && (
                                  <div className="col-span-full">
                                    <span className="text-[9px] uppercase tracking-wider text-red-700 font-semibold">{t('failureReasons') || t('failureReason') || 'Failure Reasons'}</span>
                                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`cert-history-failures-${idx}`}>
                                      {reasons.map((r, ri) => (
                                        <span key={ri} className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200">{r}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {s.edited_at && (
                                  <div className="col-span-full text-[9px] text-zinc-400 italic">
                                    {t('edited') || 'Edited'} {fmtDateTime(s.edited_at)}{s.edited_by ? ` — ${s.edited_by}` : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                );
              })()}
              </>
            )}
          </div>
          );
        })()}

        {/* ====== OWNER HISTORY TAB (Phase 282) ====== */}
        {activeTab === 'ownerHistory' && (
          <BuildingOwnerHistoryTab buildingId={buildingId} />
        )}

        {/* ====== EMAIL TEMPLATES TAB (Phase 337) ====== */}
        {activeTab === 'emails' && (
          <div className="space-y-3" data-testid="building-emails-tab-wrapper">
            <BuildingEmailsTab buildingId={buildingId} buildingName={building?.name || ''} />
          </div>
        )}

        {/* ====== COLLECTIONS TAB (Phase 900 — isolated module) ====== */}
        {activeTab === 'collections' && (
          <div className="space-y-3" data-testid="building-collections-tab">
            <CollectionsPanel
              buildingId={buildingId}
              apartments={apartments || []}
              language={language}
              isAdmin={isAdmin}
            />
          </div>
        )}

        {/* ====== COMMUNICATIONS TAB (Phase 910 — isolated module) ====== */}
        {activeTab === 'communications' && (
          <div className="space-y-3" data-testid="building-communications-tab">
            <CommunicationsPanel
              buildingId={buildingId}
              apartments={apartments || []}
              language={language}
              isAdmin={isAdmin}
            />
          </div>
        )}

        {/* ====== OWNER ADVANCES TAB (Phase 920.3 — isolated module) ====== */}
        {activeTab === 'ownerAdvances' && (
          <div className="space-y-3" data-testid="building-owner-advances-tab">
            <OwnerAdvancesPanel
              buildingId={buildingId}
              apartments={apartments || []}
              language={language}
              isAdmin={isAdmin}
            />
          </div>
        )}
        {/* Phase 349 — Legacy `emailTemplates` tab kept for URL backward
            compatibility; routes to the new BuildingEmailsTab as well. */}
        {activeTab === 'emailTemplates' && (
          <div className="space-y-3" data-testid="email-templates-tab">
            <BuildingEmailsTab buildingId={buildingId} buildingName={building?.name || ''} />
          </div>
        )}

        {/* ====== FIRE EXTINGUISHER CERTIFICATION TAB (Phase 341) ====== */}
        {activeTab === 'fireCertification' && (
          <div className="space-y-6">
            {/* Phase 659 — Supplier Credit Notes for Fire Extinguisher section. */}
            <SupplierCreditNotesTab
              buildingId={buildingId}
              section="fire_extinguisher"
              t={t}
            />
            <FireCertificationTab buildingId={buildingId} isAdmin={isAdmin} />
          </div>
        )}

        {activeTab === 'ownerFeedback' && <OwnerFeedbackTab buildingId={buildingId} />}

        {/* ====== COMPANY DETAILS TAB ====== */}
        {activeTab === 'companySettings' && (
          <div className="space-y-6" data-testid="company-settings-tab">
            <h4 className="text-sm font-bold text-zinc-800">{t('companyDetails')}</h4>
            {bldgSettingsLoading ? (
              <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('loading')}</div>
            ) : (
              <form onSubmit={handleSaveBldgSettings}>
                {isAdmin && (
                  <div className="flex justify-end mt-4">
                    <Button type="submit" disabled={bldgSettingsSaving} className="bg-zinc-950 rounded-none px-8" data-testid="save-bldg-settings">
                      {bldgSettingsSaving ? t('saving') : t('save')}
                    </Button>
                  </div>
                )}
              </form>
            )}
          </div>
        )}

        </div>{/* end Main Content Area */}

        {/* ====== ADD/EDIT APARTMENT DIALOG — Phase 652 ======
            Both Add and Edit now share the ApartmentFormBody component so
            the two flows render identical UI / spacing / field order /
            validation. Only the title text and the form's initial values
            differ between the two cases.
            ====== */}
        <Dialog open={aptDialogOpen} onOpenChange={setAptDialogOpen}>
          <DialogContent className="rounded-none p-0 flex flex-col w-[95vw] max-w-[1400px] max-h-[90vh] sm:!max-w-[1400px]" data-testid="apt-form-dialog">
            <DialogHeader className="px-5 py-3 border-b border-zinc-200 shrink-0 bg-zinc-50/60">
              <div className="flex items-baseline justify-between gap-4">
                <DialogTitle style={{ fontFamily: 'var(--font-heading)' }} className="text-base font-bold tracking-tight">
                  {editingApt ? t('editApartment') : t('addApartment')}
                </DialogTitle>
                {editingApt && (
                  <p className="text-[11px] text-zinc-500 font-mono">
                    {editingApt.apartment_number ? `#${editingApt.apartment_number}` : ''}{building?.name ? ` · ${building.name}` : ''}
                  </p>
                )}
              </div>
            </DialogHeader>
            <form onSubmit={handleAptSubmit} className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto px-5 py-4 bg-zinc-50/30">
                <ApartmentFormBody
                  form={aptForm}
                  setForm={setAptForm}
                  plansList={plansList}
                  t={t}
                  apartmentIdHint={editingApt?.apartment_id}
                />
              </div>
              <div className="border-t border-zinc-200 bg-white px-5 py-2.5 flex items-center justify-end gap-2 shrink-0">
                <Button type="button" variant="outline" onClick={() => setAptDialogOpen(false)} className="rounded-none h-8 text-xs px-3">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none h-8 text-xs px-4" data-testid="apt-form-submit">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>



        {/* Phase 290 — Change Owner dialog. Opens from inside the Edit
            Apartment dialog. Preserves the previous owner in Owner History
            with an automatic End Date and creates the new owner as Active. */}
        <ChangeOwnerDialog
          apartment={editingApt}
          open={changeOwnerOpen}
          onClose={() => setChangeOwnerOpen(false)}
          onChanged={async () => {
            // Refresh the apartments list AND refresh the editingApt snapshot
            // so the banner / owner_name field show the new active owner.
            await fetchApartments();
            try {
              const res = await axios.get(`${API}/apartments/${editingApt.apartment_id}`);
              if (res.data) {
                setEditingApt(res.data);
                setAptForm((f) => ({ ...f, owner_name: res.data.owner_name || '', phone: res.data.phone || '', email: res.data.email || '' }));
              }
            } catch { /* non-fatal */ }
          }}
        />

        {/* ====== DELETE APARTMENT DIALOG ====== */}
        <AlertDialog open={deleteAptDialogOpen} onOpenChange={setDeleteAptDialogOpen}>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>{t('deleteApartmentWarning')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteApt} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete-apt">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== DELETE PLAN DIALOG (Phase 161) — top-level so it works across tabs ====== */}
        <AlertDialog open={planDeleteDialogOpen} onOpenChange={setPlanDeleteDialogOpen}>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('confirmDeletePlan')}</AlertDialogTitle>
              <AlertDialogDescription>
                {planDeleteTarget && (
                  <>
                    <span className="block">
                      {t('confirmDelete')}: <span className="font-semibold">{planDeleteTarget.name}</span>
                    </span>
                    <span className="block mt-2 text-xs text-zinc-500">
                      {t('planYear')}: {annualYear}
                    </span>
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="plan-delete-cancel">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 rounded-none"
                onClick={confirmDeletePlan}
                data-testid="plan-delete-confirm"
              >{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        {/* ====== CASH/BANK OPENING BALANCE DIALOG ====== */}
        <Dialog open={cashBankObDialogOpen} onOpenChange={setCashBankObDialogOpen}>
          <DialogContent className="sm:max-w-sm rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('setOpeningBalance')} — {cashBankObSource === 'cash' ? t('cashReceipts') : t('bankReceipts')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveCashBankOb} className="space-y-4">
              <div>
                <Label>{t('amount')} (&euro;)</Label>
                <Input type="number" step="0.01" value={cashBankObAmount} onChange={e => setCashBankObAmount(e.target.value)} required className="mt-1 rounded-none font-mono" data-testid="cash-bank-ob-amount" />
              </div>
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={cashBankObDate} onChange={setCashBankObDate} required className="mt-1 rounded-none font-mono" data-testid="cash-bank-ob-date" />
              </div>
              {/* Phase 249 — Optional free-text opening notes. Persisted per
                  building+source. Shown in: Cash Receipts ledger Notes column,
                  Apartment Statement (where applicable), and Annual
                  Financial Statement opening row. */}
              <div>
                <Label>{t('openingNotes') || 'Opening Notes'}{' '}<span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span></Label>
                <Input
                  value={cashBankObNotes}
                  onChange={e => setCashBankObNotes(e.target.value)}
                  placeholder={t('openingNotesPlaceholder') || 'e.g. Outstanding Jan + Feb 2025'}
                  className="mt-1 rounded-none"
                  maxLength={200}
                  data-testid="cash-bank-ob-notes"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCashBankObDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-cash-bank-ob">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== PHASE 718 — TRANSFER FUNDS (Cash ↔ Bank) DIALOG ====== */}
        <Dialog open={xferOpen} onOpenChange={setXferOpen}>
          <DialogContent className="sm:max-w-md rounded-none" data-testid="transfer-funds-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('transferFunds') || 'Transfer Funds'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveTransfer} className="space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 p-3 text-[11px] text-zinc-600">
                {t('transferFundsHint') || 'Internal transfer between Internal Cash and the Bank Account. Updates both balances but creates NO income or expense in accounting.'}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('fromAccount') || 'From Account'}</Label>
                  <Select value={xferFrom} onValueChange={(v) => { setXferFrom(v); if (v === xferTo) setXferTo(v === 'cash' ? 'bank' : 'cash'); }}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="transfer-from-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t('internalCash') || 'Internal Cash'}</SelectItem>
                      <SelectItem value="bank">{t('bankAccount') || 'Bank Account'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t('toAccount') || 'To Account'}</Label>
                  <Select value={xferTo} onValueChange={(v) => { setXferTo(v); if (v === xferFrom) setXferFrom(v === 'cash' ? 'bank' : 'cash'); }}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="transfer-to-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t('internalCash') || 'Internal Cash'}</SelectItem>
                      <SelectItem value="bank">{t('bankAccount') || 'Bank Account'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('amount')} (&euro;)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={xferAmount}
                    onChange={(e) => setXferAmount(e.target.value)}
                    required
                    autoFocus
                    className="mt-1 rounded-none font-mono"
                    data-testid="transfer-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('date')}</Label>
                  <DatePickerDMY
                    value={xferDate}
                    onChange={setXferDate}
                    required
                    className="mt-1 rounded-none font-mono"
                    data-testid="transfer-date-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('reference') || 'Reference'}{' '}<span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span></Label>
                <Input
                  value={xferReference}
                  onChange={(e) => setXferReference(e.target.value)}
                  placeholder={t('transferReferencePlaceholder') || 'e.g. Cash deposit slip #1234'}
                  className="mt-1 rounded-none"
                  maxLength={120}
                  data-testid="transfer-reference-input"
                />
              </div>
              <div>
                <Label className="text-xs">{t('notes')}{' '}<span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span></Label>
                <Input
                  value={xferNotes}
                  onChange={(e) => setXferNotes(e.target.value)}
                  className="mt-1 rounded-none"
                  maxLength={300}
                  data-testid="transfer-notes-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setXferOpen(false)} className="rounded-none" data-testid="transfer-cancel-btn">
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={xferSaving} className="bg-zinc-950 rounded-none" data-testid="transfer-save-btn">
                  {xferSaving ? (t('saving') || 'Saving…') : (t('transferFunds') || 'Transfer Funds')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== PHASE 820.3 — MANAGEMENT COMPANY ADVANCE / REPAYMENT DIALOG ====== */}
        <Dialog open={!!mcaDialog} onOpenChange={(v) => !v && setMcaDialog(null)}>
          <DialogContent className="sm:max-w-md rounded-none" data-testid="mca-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {mcaDialog?.entry_id
                  ? (language === 'el' ? 'Επεξεργασία Κίνησης' : 'Edit Entry')
                  : (mcaDialog?.entry_type === 'advance'
                      ? (language === 'el' ? 'Νέα Προκαταβολή Εταιρείας Διαχείρισης' : 'New Management Company Advance')
                      : (language === 'el' ? 'Επιστροφή προς Εταιρεία Διαχείρισης' : 'Management Company Repayment'))}
              </DialogTitle>
            </DialogHeader>
            {mcaDialog && (
              <form onSubmit={(e) => { e.preventDefault(); saveMcaEntry(); }} className="space-y-3">
                <div className="bg-zinc-50 border border-zinc-200 p-2.5 text-[11px] text-zinc-600">
                  {mcaDialog.entry_type === 'advance'
                    ? (language === 'el'
                        ? 'Η κίνηση θα εμφανιστεί ως έσοδο στο επιλεγμένο ταμείο.'
                        : 'Will appear as income on the selected account.')
                    : (language === 'el'
                        ? 'Η κίνηση θα εμφανιστεί ως έξοδο στο επιλεγμένο ταμείο.'
                        : 'Will appear as an expense on the selected account.')}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('type') || (language === 'el' ? 'Τύπος' : 'Type')}</Label>
                    <Select value={mcaDialog.entry_type} onValueChange={(v) => setMcaDialog({ ...mcaDialog, entry_type: v })}>
                      <SelectTrigger className="mt-1 rounded-none" data-testid="mca-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="advance">{language === 'el' ? 'Προκαταβολή' : 'Advance'}</SelectItem>
                        <SelectItem value="repayment">{language === 'el' ? 'Επιστροφή' : 'Repayment'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{language === 'el' ? 'Ταμείο' : 'Account'}</Label>
                    <Select value={mcaDialog.source} onValueChange={(v) => setMcaDialog({ ...mcaDialog, source: v })}>
                      <SelectTrigger className="mt-1 rounded-none" data-testid="mca-source-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank">{t('bankAccount') || 'Bank Account'}</SelectItem>
                        <SelectItem value="cash">{t('internalCash') || 'Internal Cash'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('date') || 'Date'}</Label>
                    <DatePickerDMY value={mcaDialog.date} onChange={(v) => setMcaDialog({ ...mcaDialog, date: v })} data-testid="mca-date" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('amount') || 'Amount'} (&euro;)</Label>
                    <Input type="number" step="0.01" min="0" value={mcaDialog.amount} onChange={(e) => setMcaDialog({ ...mcaDialog, amount: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="mca-amount" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t('reference') || 'Reference'}</Label>
                  <Input value={mcaDialog.reference} onChange={(e) => setMcaDialog({ ...mcaDialog, reference: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="mca-reference" />
                </div>
                <div>
                  <Label className="text-xs">{t('notes') || 'Notes'}</Label>
                  <Textarea rows={2} value={mcaDialog.notes} onChange={(e) => setMcaDialog({ ...mcaDialog, notes: e.target.value })} className="mt-1 rounded-none text-sm" data-testid="mca-notes" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setMcaDialog(null)} className="rounded-none" data-testid="mca-cancel">
                    {t('cancel') || 'Cancel'}
                  </Button>
                  <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="mca-save">
                    {t('save') || 'Save'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* ====== PHASE 820.4 — APARTMENT OWNER CREDIT / REIMBURSEMENT DIALOG ====== */}
        <Dialog open={!!aocDialog} onOpenChange={(v) => !v && setAocDialog(null)}>
          <DialogContent className="sm:max-w-md rounded-none" data-testid="aoc-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {aocDialog?.entry_id
                  ? (language === 'el' ? 'Επεξεργασία Κίνησης Ιδιοκτήτη' : 'Edit Owner Entry')
                  : (aocDialog?.entry_type === 'credit'
                      ? (language === 'el' ? 'Νέα Πίστωση Ιδιοκτήτη' : 'New Apartment Owner Credit')
                      : (language === 'el' ? 'Αποζημίωση Ιδιοκτήτη' : 'Apartment Owner Reimbursement'))}
              </DialogTitle>
            </DialogHeader>
            {aocDialog && (
              <form onSubmit={(e) => { e.preventDefault(); saveAocEntry(); }} className="space-y-3">
                <div className="bg-zinc-50 border border-zinc-200 p-2.5 text-[11px] text-zinc-600">
                  {aocDialog.entry_type === 'credit'
                    ? (language === 'el'
                        ? 'Η κίνηση θα εμφανιστεί ως έσοδο στο επιλεγμένο ταμείο. Δημιουργεί εκκρεμή υποχρέωση προς τον ιδιοκτήτη.'
                        : 'Will appear as income on the selected account. Creates an outstanding liability to the owner.')
                    : (language === 'el'
                        ? 'Η κίνηση θα εμφανιστεί ως έξοδο στο επιλεγμένο ταμείο. Μειώνει την υποχρέωση προς τον ιδιοκτήτη.'
                        : 'Will appear as an expense on the selected account. Reduces the outstanding liability to the owner.')}
                </div>
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Διαμέρισμα' : 'Apartment'}</Label>
                  <Select
                    value={aocDialog.apartment_id || ''}
                    onValueChange={(v) => {
                      const apt = (apartments || []).find(a => a.apartment_id === v);
                      setAocDialog({
                        ...aocDialog,
                        apartment_id: v,
                        apartment_number: apt?.apartment_number || '',
                        owner_name: aocDialog.owner_name || apt?.owner_name || '',
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1 rounded-none" data-testid="aoc-apartment-select"><SelectValue placeholder={language === 'el' ? 'Επίλεξε…' : 'Select…'} /></SelectTrigger>
                    <SelectContent>
                      {(apartments || []).map(a => (
                        <SelectItem key={a.apartment_id} value={a.apartment_id}>
                          {a.apartment_number || '?'}{a.owner_name ? ` — ${a.owner_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Ιδιοκτήτης' : 'Owner Name'}</Label>
                  <Input value={aocDialog.owner_name || ''} onChange={(e) => setAocDialog({ ...aocDialog, owner_name: e.target.value })} className="mt-1 rounded-none" data-testid="aoc-owner" placeholder={language === 'el' ? '(αυτόματα από το διαμέρισμα)' : '(auto from apartment)'} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('type') || (language === 'el' ? 'Τύπος' : 'Type')}</Label>
                    <Select value={aocDialog.entry_type} onValueChange={(v) => setAocDialog({ ...aocDialog, entry_type: v })}>
                      <SelectTrigger className="mt-1 rounded-none" data-testid="aoc-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit">{language === 'el' ? 'Πίστωση' : 'Credit'}</SelectItem>
                        <SelectItem value="reimbursement">{language === 'el' ? 'Αποζημίωση' : 'Reimbursement'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{language === 'el' ? 'Ταμείο' : 'Account'}</Label>
                    <Select value={aocDialog.source} onValueChange={(v) => setAocDialog({ ...aocDialog, source: v })}>
                      <SelectTrigger className="mt-1 rounded-none" data-testid="aoc-source-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank">{t('bankAccount') || 'Bank Account'}</SelectItem>
                        <SelectItem value="cash">{t('internalCash') || 'Internal Cash'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('date') || 'Date'}</Label>
                    <DatePickerDMY value={aocDialog.date} onChange={(v) => setAocDialog({ ...aocDialog, date: v })} data-testid="aoc-date" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('amount') || 'Amount'} (&euro;)</Label>
                    <Input type="number" step="0.01" min="0" value={aocDialog.amount} onChange={(e) => setAocDialog({ ...aocDialog, amount: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="aoc-amount" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t('reference') || 'Reference'}</Label>
                  <Input value={aocDialog.reference} onChange={(e) => setAocDialog({ ...aocDialog, reference: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="aoc-reference" />
                </div>
                <div>
                  <Label className="text-xs">{t('notes') || 'Notes'}</Label>
                  <Textarea rows={2} value={aocDialog.notes} onChange={(e) => setAocDialog({ ...aocDialog, notes: e.target.value })} className="mt-1 rounded-none text-sm" data-testid="aoc-notes" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAocDialog(null)} className="rounded-none" data-testid="aoc-cancel">
                    {t('cancel') || 'Cancel'}
                  </Button>
                  <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="aoc-save">
                    {t('save') || 'Save'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* ====== RECURRING EXPENSE DIALOG ====== */}
        <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{editingRecurring ? t('editRecurringExpense') : t('addRecurringExpense')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRecurringSave} className="space-y-4">
              <div>
                <Label className="text-xs">{t('expenseCategory')}</Label>
                <Select value={recurringForm.category} onValueChange={v => setRecurringForm({ ...recurringForm, category: v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="recurring-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup><SelectLabel>{t('monthlyExpenses')}</SelectLabel>{customCategories.filter(c => c.group === 'monthly').map(c => <SelectItem key={c.category_code} value={c.category_code}>{getCatLabel(c.category_code, t, customCategories)}</SelectItem>)}</SelectGroup>
                    <SelectGroup><SelectLabel>{t('capitalExpenses')}</SelectLabel>{customCategories.filter(c => c.group === 'capital').map(c => <SelectItem key={c.category_code} value={c.category_code}>{getCatLabel(c.category_code, t, customCategories)}</SelectItem>)}</SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">{t('amount')} (&euro;)</Label>
                  <Input type="number" step="0.01" min="0" value={recurringForm.amount} onChange={e => setRecurringForm({ ...recurringForm, amount: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-none font-mono" required data-testid="recurring-amount" />
                </div>
                <div>
                  <Label className="text-xs">{t('dayOfMonth')}</Label>
                  <Select value={recurringForm.recurring_day} onValueChange={v => setRecurringForm({ ...recurringForm, recurring_day: v })}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="recurring-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,10,15,20,25,28].map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('selectVendor')}</Label>
                <Select value={recurringForm.vendor_id || '_none'} onValueChange={v => setRecurringForm({ ...recurringForm, vendor_id: v === '_none' ? '' : v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="recurring-vendor"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— {t('noSelection') || 'None'} —</SelectItem>
                    {vendorList.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('notes')}</Label>
                <Textarea value={recurringForm.notes} onChange={e => setRecurringForm({ ...recurringForm, notes: e.target.value })} className="mt-1 rounded-none resize-y" rows={3} style={{ minHeight: '80px', maxHeight: '50vh' }} data-testid="recurring-notes" />
              </div>
              <p className="text-[10px] text-zinc-400">{t('recurringExpenseHint')}</p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRecurringDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-recurring-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== ADD EXPENSE DIALOG ====== */}
        <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('addExpense')}</DialogTitle></DialogHeader>
            <form onSubmit={handleExpenseSubmit}>
              <ExpenseFormFields
                form={expenseForm}
                setForm={setExpenseForm}
                mode="add"
                t={t}
                getCatLabel={getCatLabel}
                customCategories={customCategories}
                vendorList={vendorList}
                building={building}
                paidInstallments={paidInstallments}
                sourceBalances={sourceBalances}
                onCreateNewVendor={() => {
                  setQuickVendorTarget('add');
                  setQuickVendorForm({ name: '', phone: '', notes: '' });
                  setQuickVendorOpen(true);
                }}
                onFetchPaidInstallments={fetchPaidInstallments}
                attachments={[]}
                pendingFiles={pendingExpenseFiles}
                onPickFiles={handlePickExpenseFiles}
                onRemovePending={handleRemovePendingExpenseFile}
              />
              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setExpenseDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-quick-expense"><CurrencyEur size={18} className="mr-2" />{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== DELETE EXPENSE DIALOG ====== */}
        <AlertDialog open={deleteExpDialogOpen} onOpenChange={setDeleteExpDialogOpen}>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('deleteExpenseWarning')}
                {deletingExpense && (deletingExpense.total_paid || 0) > 0 && (
                  <span className="block mt-2 text-amber-600 font-semibold">{t('paidAmount')}: &euro;{(deletingExpense.total_paid || 0).toFixed(2)}</span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteExpense} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete-expense">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== Phase 820.5 — DELETE MCA / AOC ENTRY DIALOGS ====== */}
        <AlertDialog open={!!mcaDeleteId} onOpenChange={(v) => { if (!v) setMcaDeleteId(null); }}>
          <AlertDialogContent className="rounded-none" data-testid="mca-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{language === 'el' ? 'Διαγραφή Κίνησης' : 'Delete Entry'}</AlertDialogTitle>
              <AlertDialogDescription>
                {language === 'el'
                  ? 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή τη Χρηματοδότηση Διαχειριστή / Επιστροφή; Η ενέργεια θα ανανεώσει άμεσα το υπόλοιπο του λογαριασμού.'
                  : 'Are you sure you want to delete this Management Company Advance / Repayment? The account balance will refresh immediately.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="mca-delete-cancel">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteMcaEntry} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="mca-delete-confirm">{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!aocDeleteId} onOpenChange={(v) => { if (!v) setAocDeleteId(null); }}>
          <AlertDialogContent className="rounded-none" data-testid="aoc-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{language === 'el' ? 'Διαγραφή Κίνησης' : 'Delete Entry'}</AlertDialogTitle>
              <AlertDialogDescription>
                {language === 'el'
                  ? 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή τη Πίστωση / Αποζημίωση Ιδιοκτήτη; Η ενέργεια θα ανανεώσει άμεσα το υπόλοιπο του λογαριασμού.'
                  : 'Are you sure you want to delete this Apartment Owner Credit / Reimbursement? The account balance will refresh immediately.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="aoc-delete-cancel">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteAocEntry} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="aoc-delete-confirm">{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== ADD PAYMENT TO EXPENSE DIALOG ====== */}
        <Dialog open={expPaymentDialog.open} onOpenChange={(v) => setExpPaymentDialog(prev => ({ ...prev, open: v }))}>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('addPayment')}</DialogTitle></DialogHeader>
            <div className="bg-zinc-50 border border-zinc-200 p-3 mb-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">{t('totalAmount')}</span><span className="font-mono font-semibold">&euro;{expPaymentDialog.expense_amount.toFixed(2)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-zinc-500">{t('remaining')}</span><span className="font-mono font-bold text-red-600">&euro;{expPaymentDialog.remaining.toFixed(2)}</span></div>
              {expPaymentDialog.expense_notes && <div className="text-xs text-zinc-400 mt-1">{expPaymentDialog.expense_notes}</div>}
            </div>
            <div className="space-y-3">
              <div><Label className="text-xs font-semibold uppercase text-zinc-500">{t('amount')} (&euro;)</Label><Input type="number" step="0.01" min="0" value={expPaymentForm.amount} onChange={e => setExpPaymentForm(p => ({ ...p, amount: e.target.value }))} className="rounded-none mt-1 font-mono" data-testid="exp-pay-amount" /></div>
              <div><Label className="text-xs font-semibold uppercase text-zinc-500">{t('date')}</Label><DatePickerDMY value={expPaymentForm.date} onChange={v => setExpPaymentForm(p => ({...p, date: v}))} className="rounded-none mt-1 font-mono" data-testid="exp-pay-date" /></div>
              <div><Label className="text-xs font-semibold uppercase text-zinc-500">{t('notes')}</Label><Textarea value={expPaymentForm.notes} onChange={e => setExpPaymentForm(p => ({ ...p, notes: e.target.value }))} className="rounded-none mt-1 resize-y" rows={3} style={{ minHeight: '80px', maxHeight: '50vh' }} data-testid="exp-pay-notes" /></div>
              <div>
                <Label className="text-xs font-semibold uppercase text-zinc-500">{t('paymentMethod')}</Label>
                <Select value={expPaymentForm.payment_source || 'cash'} onValueChange={v => setExpPaymentForm(p => ({ ...p, payment_source: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="exp-pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('cash')} <span className={`ml-1 font-mono ${(sourceBalances.cash?.available || 0) < 0 ? 'text-red-500' : 'text-zinc-500'}`}>(&euro;{(sourceBalances.cash?.available || 0).toFixed(2)} {t('available')})</span></SelectItem>
                    <SelectItem value="bank">{t('bank')} <span className={`ml-1 font-mono ${(sourceBalances.bank?.available || 0) < 0 ? 'text-red-500' : 'text-zinc-500'}`}>(&euro;{(sourceBalances.bank?.available || 0).toFixed(2)} {t('available')})</span></SelectItem>
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="exp-pay-reserve-fund-toggle">
                  <input type="checkbox" checked={expPaymentForm.paid_from_reserve_fund || false} onChange={e => setExpPaymentForm(p => ({ ...p, paid_from_reserve_fund: e.target.checked }))} className="rounded-none w-4 h-4 accent-zinc-700" />
                  <span className="text-xs text-zinc-600">{t('deductFromReserveFund')}</span>
                  <span className="text-[10px] font-mono text-zinc-400">(&euro;{(sourceBalances.reserve_fund?.available || 0).toFixed(2)} {t('available')})</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setExpPaymentDialog(prev => ({ ...prev, open: false }))} className="rounded-none">{t('cancel')}</Button>
                <Button onClick={handleExpPaymentSubmit} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="exp-pay-save">{t('save')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ====== EDIT EXPENSE DIALOG ====== */}
        <Dialog open={editExpDialogOpen} onOpenChange={setEditExpDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editExpense')}</DialogTitle></DialogHeader>
            <form onSubmit={handleEditExpenseSubmit}>
              <ExpenseFormFields
                form={editExpForm}
                setForm={setEditExpForm}
                mode="edit"
                t={t}
                getCatLabel={getCatLabel}
                customCategories={customCategories}
                vendorList={vendorList}
                building={building}
                paidInstallments={paidInstallments}
                sourceBalances={sourceBalances}
                onCreateNewVendor={() => {
                  setQuickVendorTarget('edit');
                  setQuickVendorForm({ name: '', phone: '', notes: '' });
                  setQuickVendorOpen(true);
                }}
                onFetchPaidInstallments={fetchPaidInstallments}
                attachments={expenseAttachments}
                pendingFiles={pendingExpenseFiles}
                onPickFiles={handlePickExpenseFiles}
                onRemovePending={handleRemovePendingExpenseFile}
                onRemoveAttachment={handleRemoveSavedExpenseAttachment}
                onDownloadAttachment={handleDownloadExpenseAttachment}
              />
              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setEditExpDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-edit-expense">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== QUICK VENDOR CREATION DIALOG ====== */}
        <Dialog open={quickVendorOpen} onOpenChange={setQuickVendorOpen}>
          <DialogContent className="max-w-md" data-testid="quick-vendor-dialog">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('createNewVendor')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('vendorName')} *</Label>
                <Input value={quickVendorForm.name} onChange={(e) => setQuickVendorForm({ ...quickVendorForm, name: e.target.value })} className="mt-1 rounded-none" placeholder={t('vendorName')} required data-testid="quick-vendor-name" />
              </div>
              <div>
                <Label>{t('phone')}</Label>
                <Input value={quickVendorForm.phone} onChange={(e) => setQuickVendorForm({ ...quickVendorForm, phone: e.target.value })} className="mt-1 rounded-none" placeholder={t('phone')} data-testid="quick-vendor-phone" />
              </div>
              <div>
                <Label>{t('notes')}</Label>
                <Textarea value={quickVendorForm.notes} onChange={(e) => setQuickVendorForm({ ...quickVendorForm, notes: e.target.value })} className="mt-1 rounded-none" rows={2} placeholder={t('notes')} data-testid="quick-vendor-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuickVendorOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button type="button" onClick={handleQuickVendorCreate} disabled={!quickVendorForm.name.trim()} className="bg-zinc-950 rounded-none" data-testid="quick-vendor-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== EXPORT EXPENSES DIALOG ====== */}
        <Dialog open={expExportOpen} onOpenChange={setExpExportOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('exportExpenses')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('exportFormat')}</Label>
                <Select value={expExportForm.format} onValueChange={(val) => setExpExportForm({ ...expExportForm, format: val })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="export-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('expExportDateFrom')}</Label>
                  <DatePickerDMY value={expExportForm.dateFrom} onChange={v => setExpExportForm({ ...expExportForm, dateFrom: v })} className="mt-1" data-testid="export-date-from" />
                </div>
                <div>
                  <Label>{t('expExportDateTo')}</Label>
                  <DatePickerDMY value={expExportForm.dateTo} onChange={v => setExpExportForm({ ...expExportForm, dateTo: v })} className="mt-1" data-testid="export-date-to" />
                </div>
              </div>
              <div>
                <Label>{t('expExportType')}</Label>
                <Select value={expExportForm.categoryGroup} onValueChange={(val) => setExpExportForm({ ...expExportForm, categoryGroup: val })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="export-category-group"><SelectValue placeholder={t('allTypes')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allTypes')}</SelectItem>
                    <SelectItem value="monthly">{t('monthlyExpenses')}</SelectItem>
                    <SelectItem value="capital">{t('capitalExpenses')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setExpExportOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button onClick={handleExpenseExport} className="bg-zinc-950 rounded-none" data-testid="export-expenses-submit">
                  <DownloadSimple size={16} className="mr-2" />{expExportForm.format === 'pdf' ? t('exportPdf') : t('exportExcel')}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* ====== EXPORT ELEVATOR (INVOICES / PAYMENTS) DIALOG ====== */}
        <Dialog open={elevExportOpen} onOpenChange={setElevExportOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {elevExportKind === 'payments' ? t('exportPayments') : t('exportInvoices')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('exportFormat')}</Label>
                <Select value={elevExportForm.format} onValueChange={(val) => setElevExportForm({ ...elevExportForm, format: val })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="elev-export-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('expExportDateFrom')}</Label>
                  <DatePickerDMY value={elevExportForm.dateFrom} onChange={v => setElevExportForm({ ...elevExportForm, dateFrom: v })} className="mt-1" data-testid="elev-export-date-from" />
                </div>
                <div>
                  <Label>{t('expExportDateTo')}</Label>
                  <DatePickerDMY value={elevExportForm.dateTo} onChange={v => setElevExportForm({ ...elevExportForm, dateTo: v })} className="mt-1" data-testid="elev-export-date-to" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setElevExportOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button onClick={handleElevExport} className="bg-zinc-950 rounded-none" data-testid="elev-export-submit">
                  <DownloadSimple size={16} className="mr-2" />{elevExportForm.format === 'pdf' ? t('exportPdf') : t('exportExcel')}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* ====== NOTE DIALOG ====== */}
        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{editingNote ? t('editNote') : t('addNote')}</DialogTitle></DialogHeader>
            <form onSubmit={handleNoteSubmit} className="space-y-4">
              <div>
                <Label>{t('noteText')}</Label>
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} className="mt-1 rounded-none" rows={5} required data-testid="note-text-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setNoteDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-note-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== DELETE NOTE DIALOG ====== */}
        {deleteNoteDialogOpen && (
          <Dialog open={deleteNoteDialogOpen} onOpenChange={setDeleteNoteDialogOpen}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader><DialogTitle>{t('deleteNote')}</DialogTitle></DialogHeader>
              <p className="text-sm text-zinc-500">{t('confirmDelete')}</p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDeleteNoteDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button onClick={handleDeleteNote} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete-note">{t('delete')}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Phase 331 — APARTMENTS PDF EXPORT OPTIONS DIALOG */}
        <Dialog open={aptExportDialogOpen} onOpenChange={setAptExportDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="apt-export-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('exportOptions') || 'Export Options'}</DialogTitle>
              <DialogDescription>
                {t('selectColumnsHint') || 'Select the columns you want in the PDF.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {APT_EXPORT_COLS.map(col => {
                const labels = {
                  apartment: t('apartment') || 'Apartment',
                  owner: t('owner') || 'Owner',
                  total_due: t('totalDue') || 'Total Due',
                  total_paid: t('totalPaid') || 'Total Paid',
                  remaining_balance: t('remainingBalance') || 'Remaining Balance',
                };
                const checked = aptExportCols.includes(col);
                return (
                  <label
                    key={col}
                    className="flex items-center gap-3 px-3 py-2 border border-zinc-200 hover:bg-zinc-50 cursor-pointer transition-colors"
                    data-testid={`apt-export-col-${col}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAptExportCols(prev => Array.from(new Set([...prev, col])));
                        } else {
                          setAptExportCols(prev => prev.filter(c => c !== col));
                        }
                      }}
                      className="h-4 w-4 accent-zinc-900"
                      data-testid={`apt-export-col-${col}-input`}
                    />
                    <span className="text-sm font-medium text-zinc-800">{labels[col]}</span>
                  </label>
                );
              })}
              <div className="flex items-center justify-between text-xs text-zinc-500 pt-1">
                <button
                  type="button"
                  className="underline hover:text-zinc-900"
                  onClick={() => setAptExportCols([...APT_EXPORT_COLS])}
                  data-testid="apt-export-select-all"
                >
                  {t('selectAll') || 'Select all'}
                </button>
                <button
                  type="button"
                  className="underline hover:text-zinc-900"
                  onClick={() => setAptExportCols([])}
                  data-testid="apt-export-deselect-all"
                >
                  {t('deselectAll') || 'Deselect all'}
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAptExportDialogOpen(false)} className="rounded-none" data-testid="apt-export-cancel">
                {t('cancel')}
              </Button>
              <Button
                onClick={downloadApartmentsPdf}
                className="bg-zinc-900 hover:bg-zinc-800 rounded-none"
                disabled={aptExportCols.length === 0}
                data-testid="apt-export-download"
              >
                <FilePdf size={16} className="mr-1.5" /> {t('exportPdf') || 'Export PDF'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 337 — REPAIRS PDF INLINE PREVIEW MODAL. Opens directly from
            "View PDF" with the columns saved per-building in localStorage. */}
        <Dialog open={repairPreviewOpen} onOpenChange={(open) => { if (!open) closeRepairPreview(); }}>
          <DialogContent className="sm:max-w-5xl max-h-[92vh] flex flex-col p-0" data-testid="repair-preview-dialog">
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-200">
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('pdfPreview') || 'PDF Preview'}</DialogTitle>
              <DialogDescription>
                {t('previewAndDownload') || 'Preview and download the repair statement PDF.'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 bg-zinc-100 min-h-[60vh]">
              {repairPreviewUrl ? (
                <iframe
                  src={repairPreviewUrl}
                  title="Repair PDF Preview"
                  className="w-full h-[70vh] border-0"
                  data-testid="repair-preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-[60vh] text-zinc-500 text-sm">
                  {repairPreviewLoading ? (t('loading') || 'Loading…') : '…'}
                </div>
              )}
            </div>
            <DialogFooter className="px-6 py-4 border-t border-zinc-200">
              <Button type="button" variant="outline" onClick={closeRepairPreview} className="rounded-none" data-testid="repair-preview-close">
                {t('closePreview') || t('cancel')}
              </Button>
              <Button
                onClick={downloadRepairPdfFromPreview}
                className="bg-zinc-900 hover:bg-zinc-800 rounded-none"
                disabled={!repairPreviewUrl}
                data-testid="repair-preview-download"
              >
                <FilePdf size={16} className="mr-1.5" /> {t('downloadPdf') || 'Download PDF'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 347 — Generate Email header button & dialog removed per
            user request. The Email Templates tab is the single entry point. */}

        {/* ====== INSURANCE ADD/EDIT DIALOG ====== */}
        <Dialog open={insuranceDialogOpen} onOpenChange={setInsuranceDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {editingInsurance ? t('editInsurance') : renewingInsuranceId ? t('renewInsurance') : t('addInsurance')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInsuranceSubmit} className="space-y-4">
              <div>
                <Label>{t('insuranceCompany')}</Label>
                <Select
                  value={insuranceForm.insurance_company_id || ''}
                  onValueChange={handleInsuranceCompanySelect}
                >
                  <SelectTrigger className="mt-1 rounded-none" data-testid="insurance-company-select">
                    <SelectValue placeholder={t('selectCompany')} />
                  </SelectTrigger>
                  <SelectContent>
                    {insuranceCompanies.map(c => (
                      <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('policyNumber')}</Label>
                <Input
                  type="text"
                  value={insuranceForm.policy_number}
                  onChange={(e) => setInsuranceForm({ ...insuranceForm, policy_number: e.target.value })}
                  className="mt-1 rounded-none font-mono"
                  data-testid="insurance-policy-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('startDate')}</Label>
                  <DatePickerDMY value={insuranceForm.policy_start_date} onChange={v => setInsuranceForm({ ...insuranceForm, policy_start_date: v })} className="mt-1 font-mono" required data-testid="insurance-start-input" />
                </div>
                <div>
                  <Label>{t('endDate')}</Label>
                  <DatePickerDMY value={insuranceForm.policy_end_date} onChange={v => setInsuranceForm({ ...insuranceForm, policy_end_date: v })} className="mt-1 font-mono" required data-testid="insurance-end-input" />
                </div>
              </div>
              {/* Premium & Installments — Phase 311: inline € suffix in input */}
              <div className="border-t border-zinc-100 pt-3">
                <p className="text-xs font-semibold text-zinc-500 uppercase mb-2">{t('totalPremium')} & {t('numInstallments')}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('totalPremium')}</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number" step="0.01"
                        value={insuranceForm.total_premium}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, total_premium: e.target.value })}
                        className="rounded-none font-mono pr-7"
                        data-testid="insurance-premium-input"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none select-none">&euro;</span>
                    </div>
                  </div>
                  <div>
                    <Label>{t('numInstallments')}</Label>
                    <Input
                      type="number" min="1" max="24"
                      value={insuranceForm.num_installments}
                      onChange={(e) => setInsuranceForm({ ...insuranceForm, num_installments: e.target.value })}
                      className="mt-1 rounded-none font-mono"
                      data-testid="insurance-installments-input"
                    />
                  </div>
                </div>
                {parseFloat(insuranceForm.total_premium) > 0 && parseInt(insuranceForm.num_installments) > 0 && (
                  <p className="text-xs text-zinc-500 mt-2">
                    {t('installmentAmount')}: <span className="font-bold font-mono text-zinc-800">&euro;{(parseFloat(insuranceForm.total_premium) / parseInt(insuranceForm.num_installments)).toFixed(2)}{t('perMonth')}</span>
                  </p>
                )}
              </div>
              {/* Insured Amounts — Phase 311: Building, Common Areas, Outdoor,
                  Civil Liability, Excess — with inline € suffix in each input */}
              <div className="border-t border-zinc-100 pt-3">
                <p className="text-xs font-semibold text-zinc-500 uppercase mb-2">{t('insuredAmounts')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">{t('insuredBuilding')}</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number" step="1"
                        value={insuranceForm.insured_building}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, insured_building: e.target.value })}
                        className="rounded-none font-mono pr-7" placeholder="0"
                        data-testid="insurance-insured-building"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none select-none">&euro;</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t('insuredCommonAreas')}</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number" step="1"
                        value={insuranceForm.insured_common_areas}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, insured_common_areas: e.target.value })}
                        className="rounded-none font-mono pr-7" placeholder="0"
                        data-testid="insurance-insured-common"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none select-none">&euro;</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t('insuredOutdoor')}</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number" step="1"
                        value={insuranceForm.insured_outdoor}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, insured_outdoor: e.target.value })}
                        className="rounded-none font-mono pr-7" placeholder="0"
                        data-testid="insurance-insured-outdoor"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none select-none">&euro;</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t('insuredCivilLiability')}</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number" step="1"
                        value={insuranceForm.insured_civil_liability}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, insured_civil_liability: e.target.value })}
                        className="rounded-none font-mono pr-7" placeholder="0"
                        data-testid="insurance-insured-civil-liability"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none select-none">&euro;</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t('insuredExcess')}</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number" step="1"
                        value={insuranceForm.insured_excess}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, insured_excess: e.target.value })}
                        className="rounded-none font-mono pr-7" placeholder="0"
                        data-testid="insurance-insured-excess"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none select-none">&euro;</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Phase 311 — Notes moved to bottom, right above Cancel/Save */}
              <div className="border-t border-zinc-100 pt-3">
                <Label>{t('notes')}</Label>
                <Textarea
                  value={insuranceForm.notes}
                  onChange={(e) => setInsuranceForm({ ...insuranceForm, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={2}
                  data-testid="insurance-notes-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInsuranceDialogOpen(false)} className="rounded-none">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-insurance-btn">
                  {t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== DELETE INSURANCE DIALOG ====== */}
        <AlertDialog open={deleteInsuranceDialogOpen} onOpenChange={setDeleteInsuranceDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteInsurance')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDeleteInsurance')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteInsurance} className="bg-red-600 rounded-none" data-testid="confirm-delete-insurance-btn">
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== CANCEL INSURANCE DIALOG ====== */}
        <AlertDialog open={cancelInsuranceDialogOpen} onOpenChange={setCancelInsuranceDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('cancelInsurance')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmCancelInsurance')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" onClick={() => setCancellingInsuranceId(null)}>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelInsurance} className="bg-red-600 rounded-none" data-testid="confirm-cancel-insurance-btn">
                {t('cancelInsurance')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== EDIT BUILDING DIALOG ====== */}
        <Dialog open={editBldgDialogOpen} onOpenChange={setEditBldgDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editBuilding')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditBldgSubmit} className="space-y-4">
              {/* Building Name & Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">{t('buildingName')} *</Label>
                  <Input value={editBldgForm.name} onChange={e => setEditBldgForm({...editBldgForm, name: e.target.value})} required className="mt-1 rounded-none" data-testid="edit-bldg-name" />
                </div>
                <div>
                  <Label className="text-xs">{t('buildingType')}</Label>
                  <Select value={editBldgForm.building_type} onValueChange={v => setEditBldgForm({...editBldgForm, building_type: v})}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="edit-bldg-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="managed">{t('managed')}</SelectItem>
                      <SelectItem value="cleaning_only">{t('cleaning_only')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Structured Address */}
              <div className="space-y-3 border-t border-zinc-200 pt-3">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t('address')}</p>
                <div>
                  <Label className="text-xs">{t('streetAddress')}</Label>
                  <Input value={editBldgForm.street_address} onChange={e => setEditBldgForm({...editBldgForm, street_address: e.target.value})} className="mt-1 rounded-none" data-testid="edit-bldg-street" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">{t('zipCode')}</Label>
                    <Input value={editBldgForm.zip_code} onChange={e => setEditBldgForm({...editBldgForm, zip_code: e.target.value})} className="mt-1 rounded-none font-mono" data-testid="edit-bldg-zip" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('city')}</Label>
                    <Input value={editBldgForm.city} onChange={e => setEditBldgForm({...editBldgForm, city: e.target.value})} className="mt-1 rounded-none" data-testid="edit-bldg-city" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('countryRegion')}</Label>
                    <Input value={editBldgForm.country} onChange={e => setEditBldgForm({...editBldgForm, country: e.target.value})} className="mt-1 rounded-none" data-testid="edit-bldg-country" />
                  </div>
                </div>
              </div>

              {/* Phase 196 — Management Fee + Cleaning Fee blocks removed
                  per user request. These values are not edited here anymore;
                  cleaning fees live in the Distribution matrix and management
                  fees are tracked via the company-level pricing flow. */}

              {/* Phase 252 — Contact Person + Billing/Pricing sections were
                  removed from Edit Building per user request. Contact info
                  and pricing are managed elsewhere; the Edit dialog now
                  focuses strictly on building identity, address, reminder
                  settings, and notes. */}

              {/* Phase 239 — Payment Reminder Settings.
                  Drives the in-app Reminders panel: which apartments are
                  flagged at first / second / final stage based on how many
                  days the oldest unpaid invoice has been overdue. */}
              <div className="border-t border-zinc-200 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">{t('reminderSettings') || 'Payment Reminder Settings'}</Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editBldgForm.reminder_enabled}
                      onChange={e => setEditBldgForm({...editBldgForm, reminder_enabled: e.target.checked})}
                      className="h-4 w-4 accent-emerald-700"
                      data-testid="edit-bldg-reminder-enabled"
                    />
                    <span className="text-xs font-semibold">{t('enableReminders') || 'Enable reminders'}</span>
                  </label>
                </div>
                <div className={`grid grid-cols-3 gap-3 ${editBldgForm.reminder_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                  <div>
                    <Label className="text-[10px]">{t('firstReminderDays') || 'First (days)'}</Label>
                    <Input type="number" min="0" value={editBldgForm.reminder_first_days}
                      onChange={e => setEditBldgForm({...editBldgForm, reminder_first_days: parseInt(e.target.value || '0')})}
                      className="mt-1 rounded-none font-mono" data-testid="edit-bldg-reminder-first" />
                  </div>
                  <div>
                    <Label className="text-[10px]">{t('secondReminderDays') || 'Second (days)'}</Label>
                    <Input type="number" min="0" value={editBldgForm.reminder_second_days}
                      onChange={e => setEditBldgForm({...editBldgForm, reminder_second_days: parseInt(e.target.value || '0')})}
                      className="mt-1 rounded-none font-mono" data-testid="edit-bldg-reminder-second" />
                  </div>
                  <div>
                    <Label className="text-[10px]">{t('finalReminderDays') || 'Final (days)'}</Label>
                    <Input type="number" min="0" value={editBldgForm.reminder_final_days}
                      onChange={e => setEditBldgForm({...editBldgForm, reminder_final_days: parseInt(e.target.value || '0')})}
                      className="mt-1 rounded-none font-mono" data-testid="edit-bldg-reminder-final" />
                  </div>
                </div>
                <p className="text-[10px] text-zinc-500">{t('reminderHint') || 'Apartments with unpaid balances are flagged at each stage based on how many days the oldest unpaid invoice has been overdue.'}</p>
              </div>

              {/* Phase 337 — Manager / Committee labels for Email Templates auto-fill */}
              <div className="border-t border-zinc-200 pt-3 space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">{t('emailContactLabels') || 'Email Contact Labels'}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px]">{t('managerName') || 'Manager Name'}</Label>
                    <Input
                      value={editBldgForm.manager_name || ''}
                      onChange={e => setEditBldgForm({...editBldgForm, manager_name: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder={t('managerNamePh') || 'e.g. John Smith'}
                      data-testid="edit-bldg-manager-name"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">{t('committeeName') || 'Committee Name'}</Label>
                    <Input
                      value={editBldgForm.committee_name || ''}
                      onChange={e => setEditBldgForm({...editBldgForm, committee_name: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder={t('committeeNamePh') || 'e.g. Building Committee'}
                      data-testid="edit-bldg-committee-name"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-zinc-500">{t('emailContactLabelsHint') || 'Used by Email Templates to auto-fill {{manager_name}} and {{committee_name}}.'}</p>
              </div>

              {/* Notes */}
              <div className="border-t border-zinc-200 pt-3">
                <Label className="text-xs">{t('notes')}</Label>
                <Textarea value={editBldgForm.notes} onChange={e => setEditBldgForm({...editBldgForm, notes: e.target.value})} className="mt-1 rounded-none" rows={2} data-testid="edit-bldg-notes" />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditBldgDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-bldg-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Phase 207i — Managers (Διαχειριστική Επιτροπή) standalone dialog */}
        <BuildingManagersDialog
          open={managersDialogOpen}
          onOpenChange={setManagersDialogOpen}
          buildingId={buildingId}
          onSaved={(mgrs) => setBuilding(b => b ? { ...b, managers: mgrs } : b)}
        />

        {/* ====== COMMITTEE EDIT DIALOG ====== */}
        <Dialog open={committeeDialogOpen} onOpenChange={setCommitteeDialogOpen}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editCommittee')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCommitteeSubmit} className="space-y-5">
              {['chairman', 'treasurer', 'secretary'].map(role => (
                <div key={role} className="border border-zinc-200 p-4 space-y-3">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-600" style={{ fontFamily: 'var(--font-heading)' }}>{t(role)}</h4>
                  {/* Select from apartments or manual */}
                  <div>
                    <Label className="text-xs">{t('selectApartment')}</Label>
                    <Select
                      value={committeeForm[role]?.apartment_id || '__manual__'}
                      onValueChange={(val) => handleCommitteeApartmentSelect(role, val)}
                    >
                      <SelectTrigger className="mt-1 rounded-none" data-testid={`committee-${role}-apt-select`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__manual__">{t('manualEntryCommittee')}</SelectItem>
                        {apartments.map(a => (
                          <SelectItem key={a.apartment_id} value={a.apartment_id}>
                            #{a.apartment_number} — {a.owner_name || t('noOwner')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{t('name')}</Label>
                    <Input
                      value={committeeForm[role]?.name || ''}
                      onChange={e => handleCommitteeRoleChange(role, 'name', e.target.value)}
                      className="mt-1 rounded-none"
                      placeholder={t(role)}
                      data-testid={`committee-${role}-name`}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={`committee-${role}-auth-label`}>
                      <input
                        type="checkbox"
                        checked={committeeForm[role]?.is_authorized || false}
                        onChange={e => handleCommitteeRoleChange(role, 'is_authorized', e.target.checked)}
                        className="w-4 h-4 accent-amber-500"
                        data-testid={`committee-${role}-auth-checkbox`}
                      />
                      <span className="text-sm font-medium">{t('electedWithAuthorization')}</span>
                    </label>
                  </div>
                  {committeeForm[role]?.is_authorized && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">{t('authorizedBy')}</Label>
                        <Input
                          value={committeeForm[role]?.authorized_by || ''}
                          onChange={e => handleCommitteeRoleChange(role, 'authorized_by', e.target.value)}
                          className="mt-1 rounded-none"
                          placeholder={t('authorizedBy')}
                          data-testid={`committee-${role}-authorized-by`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('notes')}</Label>
                        <Input
                          value={committeeForm[role]?.notes || ''}
                          onChange={e => handleCommitteeRoleChange(role, 'notes', e.target.value)}
                          className="mt-1 rounded-none"
                          data-testid={`committee-${role}-notes`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCommitteeDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-committee-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== ELEVATOR CERTIFICATION ADD/EDIT DIALOG ====== */}
        <Dialog open={elevCertDialogOpen} onOpenChange={setElevCertDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {editingElevCert ? t('editCertification') : t('addCertification')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleElevCertSubmit} className="space-y-4">
              {/* === BASE FIELDS (always visible) === */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('companyName')} *</Label>
                  {editingElevCert ? (
                    // Edit flow — company name is locked to the original
                    <Input
                      value={elevCertForm.company_name}
                      readOnly
                      disabled
                      className="rounded-none mt-1 bg-zinc-50 text-zinc-700 cursor-not-allowed"
                      data-testid="cert-form-company-readonly"
                    />
                  ) : (
                    <select
                      value={elevCertForm.company_name}
                      onChange={e => setElevCertForm({ ...elevCertForm, company_name: e.target.value })}
                      className="w-full mt-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      data-testid="cert-form-company"
                      required
                    >
                      <option value="">{t('selectCompany')}</option>
                      {elevCertCompanies.map(co => (
                        <option key={co.company_id} value={co.name}>{co.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                {/* Inspection Date — hidden in Edit+Failed (redundant with Next Inspection Date) */}
                {!(editingElevCert && elevCertForm.result === 'failed') && (
                  <div>
                    <Label>{t('inspectionDate')} *</Label>
                    <DatePickerDMY value={elevCertForm.inspection_date} onChange={v => setElevCertForm({ ...elevCertForm, inspection_date: v })} className="mt-1 font-mono" required data-testid="cert-form-inspection" />
                  </div>
                )}
              </div>

              {/* === RESULT DROPDOWN === */}
              <div>
                <Label>{t('result')} *</Label>
                <select
                  value={elevCertForm.result}
                  onChange={e => {
                    const newResult = e.target.value;
                    setElevCertForm({
                      ...elevCertForm,
                      result: newResult,
                      // Clear fields that don't apply to the new result
                      failure_reasons: newResult !== 'failed' ? [] : elevCertForm.failure_reasons,
                      certificate_number: newResult !== 'passed' ? '' : elevCertForm.certificate_number,
                      certification_date: newResult !== 'passed' ? '' : elevCertForm.certification_date,
                      next_inspection_date: newResult !== 'failed' ? '' : elevCertForm.next_inspection_date,
                    });
                  }}
                  className="w-full mt-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-400"
                  data-testid="cert-form-result"
                >
                  <option value="passed">{t('passed')}</option>
                  <option value="failed">{t('failed')}</option>
                  <option value="in_progress">{t('inProgress')}</option>
                </select>
              </div>

              {/* === CONDITIONAL FIELDS: PASSED === */}
              {elevCertForm.result === 'passed' && (
                <div className="space-y-3 p-3 bg-emerald-50/40 border border-emerald-200" data-testid="cert-passed-fields">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t('certificateNumber')} *</Label>
                      <Input
                        value={elevCertForm.certificate_number}
                        onChange={e => setElevCertForm({ ...elevCertForm, certificate_number: e.target.value })}
                        className="rounded-none mt-1 font-mono"
                        required
                        data-testid="cert-form-number"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('certificationDate')} *</Label>
                      <DatePickerDMY
                        value={elevCertForm.certification_date}
                        onChange={v => setElevCertForm({ ...elevCertForm, certification_date: v })}
                        className="mt-1 font-mono"
                        required
                        data-testid="cert-form-certification-date"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t('inspector')}</Label>
                      <Input
                        value={elevCertForm.inspector_name}
                        onChange={e => setElevCertForm({ ...elevCertForm, inspector_name: e.target.value })}
                        className="rounded-none mt-1"
                        data-testid="cert-form-inspector"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('nextInspectionDate')} *</Label>
                      <DatePickerDMY
                        value={elevCertForm.next_inspection_date}
                        onChange={v => setElevCertForm({ ...elevCertForm, next_inspection_date: v })}
                        className="mt-1 font-mono"
                        required
                        data-testid="cert-form-next-inspection-passed"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* === CONDITIONAL FIELDS: FAILED === */}
              {elevCertForm.result === 'failed' && (
                <div className="space-y-3 p-3 bg-red-50/40 border border-red-200" data-testid="cert-failed-fields">
                  <div>
                    <Label className="text-xs">{t('nextInspectionDate')} *</Label>
                    <DatePickerDMY
                      value={elevCertForm.next_inspection_date}
                      onChange={v => setElevCertForm({ ...elevCertForm, next_inspection_date: v })}
                      className="mt-1 font-mono"
                      required
                      data-testid="cert-form-next-inspection"
                    />
                  </div>
                  <div data-testid="cert-failure-reasons-section">
                    <Label className="text-xs">{t('failureReasons')}</Label>
                    {/* Searchable multi-select dropdown */}
                    <Popover open={failureReasonsPopoverOpen} onOpenChange={setFailureReasonsPopoverOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="w-full mt-1 flex items-center justify-between border border-zinc-300 bg-white px-3 py-2 text-sm text-left hover:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                          data-testid="failure-reasons-trigger"
                        >
                          <span className={elevCertForm.failure_reasons.length === 0 ? 'text-zinc-400' : 'text-zinc-800'}>
                            {elevCertForm.failure_reasons.length === 0
                              ? t('selectFailureReasons')
                              : `${elevCertForm.failure_reasons.length} ${t('reasonsSelected')}`}
                          </span>
                          <CaretDown size={14} className="text-zinc-400 flex-shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[300px] rounded-none" align="start">
                        <Command shouldFilter={true}>
                          <CommandInput placeholder={t('searchReasons')} data-testid="failure-reasons-search" />
                          <CommandList>
                            <CommandEmpty>{t('noReasonFound')}</CommandEmpty>
                            <CommandGroup>
                              {failureReasonsList.map(r => {
                                const checked = elevCertForm.failure_reasons.includes(r.name);
                                return (
                                  <CommandItem
                                    key={r.reason_id}
                                    value={r.name}
                                    onSelect={() => {
                                      const updated = checked
                                        ? elevCertForm.failure_reasons.filter(n => n !== r.name)
                                        : [...elevCertForm.failure_reasons, r.name];
                                      setElevCertForm({ ...elevCertForm, failure_reasons: updated });
                                    }}
                                    className="cursor-pointer"
                                    data-testid={`failure-reason-option-${r.reason_id}`}
                                  >
                                    <div className={`mr-2 flex h-4 w-4 items-center justify-center border ${checked ? 'bg-red-600 border-red-600 text-white' : 'border-zinc-300'}`}>
                                      {checked && <Check size={12} weight="bold" />}
                                    </div>
                                    <span className={checked ? 'font-medium text-red-800' : ''}>{r.name}</span>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                        {/* Inline add new reason */}
                        <div className="border-t border-zinc-200 p-2 bg-zinc-50" data-testid="add-failure-reason-row">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={newFailureReason}
                              onChange={e => setNewFailureReason(e.target.value)}
                              placeholder={t('addNewFailureReason')}
                              className="flex-1 border border-zinc-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-400"
                              data-testid="new-failure-reason-input"
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const name = newFailureReason.trim();
                                  if (!name) return;
                                  try {
                                    await axios.post(`${API}/failure-reasons`, { name });
                                    const frRes = await axios.get(`${API}/failure-reasons`);
                                    setFailureReasonsList(frRes.data || []);
                                    setElevCertForm({ ...elevCertForm, failure_reasons: [...elevCertForm.failure_reasons, name] });
                                    setNewFailureReason('');
                                    toast.success(t('savedSuccess'));
                                  } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                const name = newFailureReason.trim();
                                if (!name) return;
                                try {
                                  await axios.post(`${API}/failure-reasons`, { name });
                                  const frRes = await axios.get(`${API}/failure-reasons`);
                                  setFailureReasonsList(frRes.data || []);
                                  setElevCertForm({ ...elevCertForm, failure_reasons: [...elevCertForm.failure_reasons, name] });
                                  setNewFailureReason('');
                                  toast.success(t('savedSuccess'));
                                } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
                              }}
                              className="px-2 py-1 bg-zinc-900 text-white text-[10px] font-semibold hover:bg-zinc-700 transition-colors"
                              data-testid="add-failure-reason-inline-btn"
                            >
                              + {t('add')}
                            </button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {/* Selected tags (shown below the dropdown) */}
                    {elevCertForm.failure_reasons.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2" data-testid="selected-failure-reasons">
                        {elevCertForm.failure_reasons.map(name => (
                          <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 text-xs border border-red-200">
                            {name}
                            <button type="button" onClick={() => setElevCertForm({ ...elevCertForm, failure_reasons: elevCertForm.failure_reasons.filter(n => n !== name) })} className="hover:text-red-600 font-bold ml-1">&times;</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* === CONDITIONAL: IN PROGRESS — informational only === */}
              {elevCertForm.result === 'in_progress' && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200" data-testid="cert-in-progress-info">
                  <Warning size={16} weight="fill" className="text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-blue-800">{t('inspectionInProgressMsg')}</span>
                </div>
              )}

              {/* === COST BLOCK REMOVED (Phase 506) ===
                  Elevator Certification is no longer used for financial
                  calculations — Net / VAT Rate / Total fields were
                  removed from the form per user spec. Legacy values
                  saved on existing records remain on the document but
                  are no longer surfaced in the UI.                  */}

              {/* === ATTACHMENTS (Phase 506) — PDFs / photos / certificates / invoices === */}
              <div className="bg-white border border-zinc-200 p-3" data-testid="cert-attachments-block">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{t('attachments') || 'Attachments'}</Label>
                  {editingElevCert ? (
                    <label
                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-zinc-900 text-white text-xs cursor-pointer hover:bg-zinc-700"
                      data-testid="cert-attachment-upload-btn"
                    >
                      <Paperclip size={12} weight="bold" />
                      {t('upload') || 'Upload'}
                      <input
                        type="file"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) { toast.error(`${t('fileTooLarge') || 'File too large'} (≤ 5 MB)`); e.target.value = ''; return; }
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            const r = await axios.post(
                              `${API}/buildings/${buildingId}/elevator-certifications/${editingElevCert.cert_id}/attachments`,
                              fd,
                              { headers: { 'Content-Type': 'multipart/form-data' } },
                            );
                            const newList = [...(elevCertForm.attachments || []), r.data];
                            setElevCertForm({ ...elevCertForm, attachments: newList });
                            // Also push it back into the editing object so subsequent saves see it.
                            setEditingElevCert({ ...editingElevCert, attachments: newList });
                            toast.success(t('uploadedSuccess') || 'Uploaded');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || (t('uploadFailed') || 'Upload failed'));
                          } finally {
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                  ) : (
                    <span className="text-[10px] text-zinc-400 italic">{t('saveFirstToAttach') || 'Save the certification first to attach files.'}</span>
                  )}
                </div>
                {(elevCertForm.attachments || []).length === 0 ? (
                  <p className="text-xs text-zinc-400 italic" data-testid="cert-attachments-empty">{t('noAttachmentsYet') || 'No attachments yet.'}</p>
                ) : (
                  <ul className="space-y-1" data-testid="cert-attachments-list">
                    {(elevCertForm.attachments || []).map(att => (
                      <li
                        key={att.file_id}
                        className="flex items-center justify-between gap-2 text-xs border border-zinc-100 px-2 py-1.5 bg-zinc-50"
                        data-testid={`cert-attachment-row-${att.file_id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Paperclip size={12} className="text-zinc-500 flex-shrink-0" />
                          <span className="truncate" title={att.name}>{att.name}</span>
                          <span className="text-[10px] text-zinc-400 font-mono flex-shrink-0">
                            {att.size_bytes < 1024 ? `${att.size_bytes} B` : att.size_bytes < 1024 * 1024 ? `${(att.size_bytes / 1024).toFixed(1)} KB` : `${(att.size_bytes / (1024 * 1024)).toFixed(2)} MB`}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <a
                            href={`${API}/buildings/${buildingId}/elevator-certifications/${editingElevCert?.cert_id}/attachments/${att.file_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 hover:text-blue-900"
                            data-testid={`cert-attachment-view-${att.file_id}`}
                            title={t('view') || 'View'}
                          >
                            <Eye size={14} />
                          </a>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!confirm(t('deleteAttachmentConfirm') || 'Delete this attachment?')) return;
                              try {
                                await axios.delete(`${API}/buildings/${buildingId}/elevator-certifications/${editingElevCert.cert_id}/attachments/${att.file_id}`);
                                const newList = (elevCertForm.attachments || []).filter(a => a.file_id !== att.file_id);
                                setElevCertForm({ ...elevCertForm, attachments: newList });
                                setEditingElevCert({ ...editingElevCert, attachments: newList });
                              } catch (err) {
                                toast.error(err.response?.data?.detail || (t('deleteFailed') || 'Delete failed'));
                              }
                            }}
                            className="text-red-600 hover:text-red-800"
                            data-testid={`cert-attachment-delete-${att.file_id}`}
                            title={t('delete') || 'Delete'}
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10px] text-zinc-400 mt-2 italic">{t('certAttachmentsHint') || 'PDF inspection reports, photos, certificates, invoices. Max 5 MB per file, 25 MB total per certification.'}</p>
              </div>

              {/* === NOTES (always visible base field) === */}
              <div>
                <Label>{t('notes')}</Label>
                <Textarea
                  value={elevCertForm.notes}
                  onChange={e => setElevCertForm({ ...elevCertForm, notes: e.target.value })}
                  className="rounded-none mt-1 resize-y"
                  rows={6}
                  style={{ minHeight: '140px', maxHeight: '60vh' }}
                  placeholder={t('notesPh') || 'Write multi-line notes here…'}
                  data-testid="cert-form-notes"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setElevCertDialogOpen(false)} className="rounded-none" disabled={elevCertSaving}>{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" disabled={elevCertSaving} data-testid="cert-form-submit">{elevCertSaving ? t('saving') : t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== DELETE ELEVATOR CERTIFICATION DIALOG ====== */}
        <AlertDialog open={deleteElevCertDialogOpen} onOpenChange={setDeleteElevCertDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteCertification')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDeleteCertification')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteElevCert} className="bg-red-600 rounded-none" data-testid="confirm-delete-cert-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== Phase 704 — EDIT INSPECTION HISTORY ENTRY DIALOG ====== */}
        <Dialog open={!!editingHistEntry} onOpenChange={(o) => !o && setEditingHistEntry(null)}>
          <DialogContent className="w-[95vw] sm:max-w-[700px] max-h-[90vh] overflow-y-auto p-6 rounded-none" data-testid="hist-edit-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editHistoryEntry') || 'Edit History Entry'}</DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                {t('editHistoryHint') || 'Update this historical inspection snapshot. The audit trail will record this change.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('result')}</Label>
                  <Select value={editHistForm.result || 'passed'} onValueChange={(v) => setEditHistForm({ ...editHistForm, result: v })}>
                    <SelectTrigger className="rounded-none h-9 mt-1" data-testid="hist-edit-result"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="passed">{t('passed')}</SelectItem>
                      <SelectItem value="failed">{t('failed')}</SelectItem>
                      <SelectItem value="in_progress">{t('inProgress')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t('nextInspectionDate')}</Label>
                  <DatePickerDMY value={editHistForm.next_inspection_date} onChange={(v) => setEditHistForm({ ...editHistForm, next_inspection_date: v })} className="mt-1 font-mono" data-testid="hist-edit-next-date" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('certificateNumber')}</Label>
                  <Input value={editHistForm.certificate_number} onChange={(e) => setEditHistForm({ ...editHistForm, certificate_number: e.target.value })} className="rounded-none mt-1 font-mono h-9" data-testid="hist-edit-cert-no" />
                </div>
                <div>
                  <Label className="text-xs">{t('inspector') || 'Inspector'}</Label>
                  <Input value={editHistForm.inspector_name} onChange={(e) => setEditHistForm({ ...editHistForm, inspector_name: e.target.value })} className="rounded-none mt-1 h-9" data-testid="hist-edit-inspector" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('company') || 'Company'}</Label>
                <Input value={editHistForm.company_name} onChange={(e) => setEditHistForm({ ...editHistForm, company_name: e.target.value })} className="rounded-none mt-1 h-9" data-testid="hist-edit-company" />
              </div>
              {editHistForm.result === 'failed' && (
                <div>
                  <Label className="text-xs text-red-700 font-semibold">{t('failureReasons') || t('failureReason') || 'Failure Reasons'}</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {(editHistForm.failure_reasons || []).map((r, ri) => (
                      <span key={ri} className="text-[11px] px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1">
                        {r}
                        <button type="button" onClick={() => setEditHistForm({ ...editHistForm, failure_reasons: editHistForm.failure_reasons.filter((_, i) => i !== ri) })} className="hover:text-red-900" data-testid={`hist-edit-remove-reason-${ri}`}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Input value={newFailureReason} onChange={(e) => setNewFailureReason(e.target.value)} placeholder={t('addFailureReason') || 'Add reason and press Enter'} className="rounded-none h-9 text-sm" onKeyDown={(e) => { if (e.key === 'Enter' && newFailureReason.trim()) { e.preventDefault(); setEditHistForm({ ...editHistForm, failure_reasons: [...(editHistForm.failure_reasons || []), newFailureReason.trim()] }); setNewFailureReason(''); } }} data-testid="hist-edit-new-reason" />
                    <Button type="button" variant="outline" className="rounded-none h-9 text-xs" onClick={() => { if (newFailureReason.trim()) { setEditHistForm({ ...editHistForm, failure_reasons: [...(editHistForm.failure_reasons || []), newFailureReason.trim()] }); setNewFailureReason(''); } }} data-testid="hist-edit-add-reason-btn">{t('add')}</Button>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">{t('notes')}</Label>
                <Textarea value={editHistForm.notes} onChange={(e) => setEditHistForm({ ...editHistForm, notes: e.target.value })} className="rounded-none mt-1 resize-y" rows={4} data-testid="hist-edit-notes" />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setEditingHistEntry(null)} data-testid="hist-edit-cancel">{t('cancel')}</Button>
              <Button type="button" className="bg-zinc-950 rounded-none" onClick={handleSaveHistEntry} data-testid="hist-edit-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== Phase 704 — DELETE INSPECTION HISTORY ENTRY ====== */}
        <AlertDialog open={!!deletingHistEntry} onOpenChange={(o) => !o && setDeletingHistEntry(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteHistoryEntry') || 'Delete History Entry'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('confirmDeleteHistoryEntry') || 'This will permanently remove this inspection history snapshot. This action cannot be undone. Other inspection records and the current certification are not affected.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteHistEntry} className="bg-red-600 rounded-none" data-testid="confirm-delete-hist-entry">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== ELEVATOR EXPENSE ADD/EDIT DIALOG ====== */}
        <Dialog open={elevExpenseDialogOpen} onOpenChange={setElevExpenseDialogOpen}>
          <DialogContent className="w-[95vw] sm:max-w-[1100px] max-h-[90vh] overflow-y-auto p-6" data-testid="elev-expense-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {editingElevExpense ? t('editElevatorExpense') : t('addElevatorExpense')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleElevExpenseSubmit} className="space-y-4">
              {/* Top row — Invoice Number + Date + Maintenance Month/Year */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-zinc-500">{t('invoiceNumber')}</Label>
                  <Input value={elevExpenseForm.invoice_number} onChange={(e) => setElevExpenseForm({ ...elevExpenseForm, invoice_number: e.target.value })} className="mt-1 rounded-none font-mono h-9" placeholder={t('autoGenerated')} data-testid="elev-invoice-number-input" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('date')}</Label>
                  <DatePickerDMY value={elevExpenseForm.date} onChange={v => {
                    // Keep maintenance_year in sync with invoice date.
                    // Month selection is intentionally preserved.
                    const yStr = (typeof v === 'string' && v.length >= 4) ? v.slice(0, 4) : '';
                    setElevExpenseForm(prev => ({
                      ...prev,
                      date: v,
                      maintenance_year: yStr && /^\d{4}$/.test(yStr) ? yStr : prev.maintenance_year,
                    }));
                  }} className="mt-1 font-mono" required data-testid="elev-date-input" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('maintenanceMonth')}</Label>
                  <Select value={elevExpenseForm.maintenance_month || '_none'} onValueChange={(val) => setElevExpenseForm({ ...elevExpenseForm, maintenance_month: val === '_none' ? '' : val })}>
                    <SelectTrigger className="mt-1 rounded-none h-9" data-testid="elev-maintenance-month-select"><SelectValue placeholder={t('month')} /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="_none">-</SelectItem>
                      {(() => {
                        const months = language === 'el'
                          ? ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος']
                          : ['January','February','March','April','May','June','July','August','September','October','November','December'];
                        return months.map((m, i) => (
                          <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('year')}</Label>
                  <Select value={elevExpenseForm.maintenance_year || '_none'} onValueChange={(val) => setElevExpenseForm({ ...elevExpenseForm, maintenance_year: val === '_none' ? '' : val })}>
                    <SelectTrigger className="mt-1 rounded-none h-9" data-testid="elev-maintenance-year-select"><SelectValue placeholder={t('year')} /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="_none">-</SelectItem>
                      {(() => {
                        // Dynamic year range: 2020 → currentYear + 10.
                        // Also include any out-of-range year already present on
                        // this invoice (existing maintenance_year or the year
                        // parsed from the invoice date) so historical invoices
                        // remain fully editable.
                        const currentYear = new Date().getFullYear();
                        const years = new Set();
                        for (let y = 2020; y <= currentYear + 10; y++) years.add(y);
                        const existing = parseInt(elevExpenseForm.maintenance_year, 10);
                        if (!isNaN(existing) && existing > 1900) years.add(existing);
                        if (typeof elevExpenseForm.date === 'string' && elevExpenseForm.date.length >= 4) {
                          const yFromDate = parseInt(elevExpenseForm.date.slice(0, 4), 10);
                          if (!isNaN(yFromDate) && yFromDate > 1900) years.add(yFromDate);
                        }
                        return Array.from(years).sort((a, b) => a - b).map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Line Items Table — Phase 407/408 */}
              {(() => {
                // Phase 408 — Maintenance line is rendered as a separate visual row
                // above the extra charges. Visible whenever a maintenance month
                // is selected. Description is auto-generated from month + year.
                const monthsEn = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                const monthsEl = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];
                const mIdxRender = parseInt(elevExpenseForm.maintenance_month) - 1;
                const monthNameRender = (mIdxRender >= 0 && mIdxRender < 12)
                  ? (language === 'el' ? monthsEl[mIdxRender] : monthsEn[mIdxRender])
                  : '';
                const maintStrRender = monthNameRender && elevExpenseForm.maintenance_year
                  ? `${monthNameRender} - ${elevExpenseForm.maintenance_year}`
                  : '';
                const maintVisible = !!maintStrRender;
                const maintDescription = maintVisible
                  ? `${t('monthlyMaintenance') || 'Monthly Maintenance'} - ${maintStrRender}`
                  : '';
                const maintLine = elevExpenseForm.maintenance_line || { quantity: 1, price: 0, vat_rate: 19, category: '202' };

                // Combined breakdown (maintenance + extras) — single source of
                // truth for the live preview math. Subtotal → Discount → VAT → Final.
                const extrasArr = elevExpenseForm.extra_charges || [];
                const subtotalNet = (maintVisible ? (parseFloat(maintLine.quantity) || 0) * (parseFloat(maintLine.price) || 0) : 0)
                  + extrasArr.reduce((s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.price) || 0)), 0);
                const dType = elevExpenseForm.discount_type || 'amount';
                const dValue = parseFloat(elevExpenseForm.discount_value) || 0;
                let discountAmount = dType === 'percent' ? subtotalNet * (dValue / 100) : dValue;
                if (discountAmount < 0) discountAmount = 0;
                if (discountAmount > subtotalNet) discountAmount = subtotalNet;
                const factor = subtotalNet > 0 ? discountAmount / subtotalNet : 0;

                const updateMaintenance = (patch) => setElevExpenseForm({
                  ...elevExpenseForm,
                  maintenance_line: { ...(elevExpenseForm.maintenance_line || {}), ...patch },
                });

                const renderCategoryOptions = () => (
                  <>
                    {customCategories.length === 0 && <option value="202">202</option>}
                    {customCategories.filter(c => c.group === 'monthly').length > 0 && (
                      <optgroup label={t('monthlyExpenses')}>
                        {customCategories.filter(c => c.group === 'monthly').map(c => (
                          <option key={c.category_code} value={c.category_code}>{c.category_code} {'-'} {getCatLabel(c.category_code, t, customCategories)}</option>
                        ))}
                      </optgroup>
                    )}
                    {customCategories.filter(c => c.group === 'capital').length > 0 && (
                      <optgroup label={t('capitalExpenses')}>
                        {customCategories.filter(c => c.group === 'capital').map(c => (
                          <option key={c.category_code} value={c.category_code}>{c.category_code} {'-'} {getCatLabel(c.category_code, t, customCategories)}</option>
                        ))}
                      </optgroup>
                    )}
                  </>
                );

                const renderExtraRow = (charge, idx) => {
                  const qty = parseFloat(charge.quantity) || 0;
                  const price = parseFloat(charge.price) || 0;
                  const net = qty * price;
                  const discountedNet = net * (1 - factor);
                  const vatRate = parseFloat(charge.vat_rate) || 0;
                  const vatAmount = discountedNet * vatRate / 100;
                  const total = discountedNet + vatAmount;
                  return (
                    <tr key={`extra-${idx}`} className="border-b border-zinc-100" data-testid={`extra-charge-${idx}`}>
                      <td className="px-2 py-1.5">
                        <Input value={charge.description} onChange={(e) => { const u = [...extrasArr]; u[idx] = { ...u[idx], description: e.target.value }; setElevExpenseForm({ ...elevExpenseForm, extra_charges: u }); }} className="rounded-none text-xs h-8 border-zinc-200" placeholder={t('chargeDescription')} data-testid={`extra-charge-desc-${idx}`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.01" min="0" value={charge.quantity ?? 1} onChange={(e) => { const u = [...extrasArr]; const newQty = parseFloat(e.target.value) || 0; u[idx] = { ...u[idx], quantity: newQty, amount: newQty * (parseFloat(u[idx].price) || 0) }; setElevExpenseForm({ ...elevExpenseForm, extra_charges: u }); }} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`extra-charge-qty-${idx}`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.01" min="0" value={charge.price ?? 0} onChange={(e) => { const u = [...extrasArr]; const newPrice = parseFloat(e.target.value) || 0; u[idx] = { ...u[idx], price: newPrice, amount: (parseFloat(u[idx].quantity) || 0) * newPrice }; setElevExpenseForm({ ...elevExpenseForm, extra_charges: u }); }} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`extra-charge-price-${idx}`} />
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold text-zinc-700" data-testid={`extra-charge-net-${idx}`}>&euro;{net.toFixed(2)}</td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.5" min="0" max="100" value={charge.vat_rate ?? 19} onChange={(e) => { const u = [...extrasArr]; u[idx] = { ...u[idx], vat_rate: parseFloat(e.target.value) || 0 }; setElevExpenseForm({ ...elevExpenseForm, extra_charges: u }); }} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`extra-charge-vat-${idx}`} />
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs text-zinc-600" data-testid={`extra-charge-vat-amount-${idx}`}>&euro;{vatAmount.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs font-bold text-zinc-900" data-testid={`extra-charge-total-${idx}`}>&euro;{total.toFixed(2)}</td>
                      <td className="px-2 py-1.5">
                        <select
                          value={charge.category || '202'}
                          onChange={(e) => { const u = [...extrasArr]; u[idx] = { ...u[idx], category: e.target.value }; setElevExpenseForm({ ...elevExpenseForm, extra_charges: u }); }}
                          className="w-full h-8 text-xs border border-zinc-200 rounded-none font-mono px-1 bg-white"
                          data-testid={`extra-charge-category-${idx}`}
                        >
                          {renderCategoryOptions()}
                        </select>
                      </td>
                      <td className="px-1 py-1.5 text-center">
                        <button type="button" onClick={() => { const u = extrasArr.filter((_, i) => i !== idx); setElevExpenseForm({ ...elevExpenseForm, extra_charges: u }); }} className="p-1 text-zinc-400 hover:text-red-500" data-testid={`remove-extra-charge-${idx}`}><Trash size={14} /></button>
                      </td>
                    </tr>
                  );
                };

                const renderMaintenanceRow = () => {
                  const mQty = parseFloat(maintLine.quantity) || 0;
                  const mPrice = parseFloat(maintLine.price) || 0;
                  const mNet = mQty * mPrice;
                  const mDiscNet = mNet * (1 - factor);
                  const mVatRate = parseFloat(maintLine.vat_rate) || 0;
                  const mVatAmt = mDiscNet * mVatRate / 100;
                  const mTotal = mDiscNet + mVatAmt;
                  return (
                    <tr className="border-b-2 border-blue-100 bg-blue-50/40" data-testid="maintenance-row">
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-block w-1 h-6 bg-blue-500" aria-hidden="true"></span>
                          <Input value={maintDescription} disabled className="rounded-none text-xs h-8 border-blue-200 bg-white text-zinc-700 font-medium" data-testid="maintenance-desc" />
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.01" min="0" value={maintLine.quantity ?? 1} onChange={(e) => updateMaintenance({ quantity: parseFloat(e.target.value) || 0 })} className="rounded-none text-xs h-8 font-mono text-right border-blue-200" data-testid="maintenance-qty" />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.01" min="0" value={maintLine.price ?? 0} onChange={(e) => updateMaintenance({ price: parseFloat(e.target.value) || 0 })} className="rounded-none text-xs h-8 font-mono text-right border-blue-200" data-testid="maintenance-price" />
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold text-zinc-700" data-testid="maintenance-net">&euro;{mNet.toFixed(2)}</td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.5" min="0" max="100" value={maintLine.vat_rate ?? 19} onChange={(e) => updateMaintenance({ vat_rate: parseFloat(e.target.value) || 0 })} className="rounded-none text-xs h-8 font-mono text-right border-blue-200" data-testid="maintenance-vat" />
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs text-zinc-600" data-testid="maintenance-vat-amount">&euro;{mVatAmt.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs font-bold text-zinc-900" data-testid="maintenance-total">&euro;{mTotal.toFixed(2)}</td>
                      <td className="px-2 py-1.5">
                        <select
                          value={maintLine.category || '202'}
                          onChange={(e) => updateMaintenance({ category: e.target.value })}
                          className="w-full h-8 text-xs border border-blue-200 rounded-none font-mono px-1 bg-white"
                          data-testid="maintenance-category"
                        >
                          {renderCategoryOptions()}
                        </select>
                      </td>
                      <td className="px-1 py-1.5 text-center">
                        <span className="text-[8px] uppercase font-bold text-blue-600 tracking-wider" title={t('monthlyMaintenance') || 'Monthly Maintenance'} data-testid="maintenance-badge">MNT</span>
                      </td>
                    </tr>
                  );
                };

                const hasAnyRow = maintVisible || extrasArr.length > 0;

                return (
                  <div className="border border-zinc-200" data-testid="elev-line-items-section">
                    <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                      <span className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">{t('invoiceItems')}</span>
                      <button type="button" onClick={() => {
                        setElevExpenseForm({
                          ...elevExpenseForm,
                          extra_charges: [...extrasArr, { description: '', quantity: 1, price: 0, amount: 0, vat_rate: 19, category: '203' }]
                        });
                      }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium" data-testid="add-extra-charge-btn">
                        <Plus size={12} /> {t('addLine')}
                      </button>
                    </div>
                    {!hasAnyRow ? (
                      <div className="px-3 py-6 text-xs text-zinc-400 text-center">{t('noExtraCharges')}</div>
                    ) : (
                      <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-zinc-200 bg-zinc-50/50">
                            <th className="px-2 py-2 text-left text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[24%]">{t('description')}</th>
                            <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[8%]">{t('quantity')}</th>
                            <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[10%]">{t('netPerUnit')}</th>
                            <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[10%]">{t('lineNet')}</th>
                            <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[7%]">{t('vatRate')} %</th>
                            <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[9%]">{t('vatAmount')}</th>
                            <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[10%]">{t('lineTotal')}</th>
                            <th className="px-2 py-2 text-left text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[15%]">{t('expenseCategory')}</th>
                            <th className="w-[40px]"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {maintVisible && renderMaintenanceRow()}
                          {extrasArr.map((charge, idx) => renderExtraRow(charge, idx))}
                        </tbody>
                      </table>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Totals Footer + Discount — Phase 407/408 */}
              {(() => {
                // Phase 408 — Include maintenance line in totals when month+year set.
                const monthsEn2 = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                const monthsEl2 = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];
                const mIdx2 = parseInt(elevExpenseForm.maintenance_month) - 1;
                const monthName2 = (mIdx2 >= 0 && mIdx2 < 12) ? (language === 'el' ? monthsEl2[mIdx2] : monthsEn2[mIdx2]) : '';
                const maintVisible2 = !!(monthName2 && elevExpenseForm.maintenance_year);
                const mLine2 = elevExpenseForm.maintenance_line || {};
                const lines = [
                  ...(maintVisible2 ? [{ quantity: mLine2.quantity, price: mLine2.price, vat_rate: mLine2.vat_rate }] : []),
                  ...(elevExpenseForm.extra_charges || []),
                ];
                const subtotalNet = lines.reduce((s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.price) || 0)), 0);
                const dType = elevExpenseForm.discount_type || 'amount';
                const dValue = parseFloat(elevExpenseForm.discount_value) || 0;
                let discountAmount = dType === 'percent' ? subtotalNet * (dValue / 100) : dValue;
                if (discountAmount < 0) discountAmount = 0;
                if (discountAmount > subtotalNet) discountAmount = subtotalNet;
                const factor = subtotalNet > 0 ? discountAmount / subtotalNet : 0;
                const discountedSubtotal = subtotalNet - discountAmount;
                const totalVat = lines.reduce((s, it) => {
                  const net = (parseFloat(it.quantity) || 0) * (parseFloat(it.price) || 0);
                  const dn = net * (1 - factor);
                  return s + (dn * (parseFloat(it.vat_rate) || 0) / 100);
                }, 0);
                const finalTotal = discountedSubtotal + totalVat;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left — Discount controls */}
                    <div className="border border-zinc-200 p-3" data-testid="elev-discount-section">
                      <Label className="text-xs text-zinc-700 font-semibold uppercase tracking-wider">{t('discount')}</Label>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex border border-zinc-300 rounded-none overflow-hidden" data-testid="elev-discount-type-toggle">
                          <button type="button" onClick={() => setElevExpenseForm({ ...elevExpenseForm, discount_type: 'amount' })} className={`px-3 py-1.5 text-xs font-mono transition-colors ${dType === 'amount' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`} data-testid="elev-discount-type-amount">&euro;</button>
                          <button type="button" onClick={() => setElevExpenseForm({ ...elevExpenseForm, discount_type: 'percent' })} className={`px-3 py-1.5 text-xs font-mono transition-colors border-l border-zinc-300 ${dType === 'percent' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`} data-testid="elev-discount-type-percent">%</button>
                        </div>
                        <Input type="number" step="0.01" min="0" value={elevExpenseForm.discount_value || 0} onChange={(e) => setElevExpenseForm({ ...elevExpenseForm, discount_value: parseFloat(e.target.value) || 0 })} className="rounded-none font-mono h-9 flex-1 text-right" data-testid="elev-discount-value-input" />
                        <span className="text-xs text-zinc-500 font-mono min-w-[80px] text-right" data-testid="elev-discount-amount-display">−&euro;{discountAmount.toFixed(2)}</span>
                      </div>
                      <p className="mt-2 text-[10px] text-zinc-400">{t('discount')} → {t('vat')} → {t('finalTotal')}</p>
                    </div>

                    {/* Right — Totals breakdown */}
                    <div className="border border-zinc-200 bg-zinc-50/50 p-3 space-y-1.5 text-xs" data-testid="elev-totals-breakdown">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">{t('subtotal')} ({t('lineNet')})</span>
                        <span className="font-mono font-semibold text-zinc-800" data-testid="elev-subtotal-net">&euro;{subtotalNet.toFixed(2)}</span>
                      </div>
                      {discountAmount > 0 && (
                        <>
                          <div className="flex justify-between text-red-600">
                            <span>{t('discount')}</span>
                            <span className="font-mono" data-testid="elev-discount-applied">−&euro;{discountAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t border-zinc-200 pt-1">
                            <span className="text-zinc-500">{t('discountedSubtotal')}</span>
                            <span className="font-mono font-semibold text-zinc-800" data-testid="elev-discounted-subtotal">&euro;{discountedSubtotal.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span className="text-zinc-500">{t('totalVat')}</span>
                        <span className="font-mono text-zinc-700" data-testid="elev-total-vat">&euro;{totalVat.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between border-t-2 border-zinc-900 pt-2 mt-2">
                        <span className="font-bold text-zinc-900 uppercase tracking-wider">{t('finalTotal')}</span>
                        <span className="font-mono font-bold text-base text-zinc-900" data-testid="elev-grand-total">&euro;{finalTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setElevExpenseDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-elev-expense-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== DELETE ELEVATOR EXPENSE DIALOG ====== */}
        <AlertDialog open={deleteElevExpenseDialogOpen} onOpenChange={setDeleteElevExpenseDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteElevatorExpense')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDeleteElevatorExpense')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteElevExpense} className="bg-red-600 rounded-none" data-testid="confirm-delete-elev-expense-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Phase 382 — Attachments Manager Dialog */}
        <Dialog open={attManagerOpen} onOpenChange={setAttManagerOpen}>
          <DialogContent className="sm:max-w-2xl rounded-none" data-testid="att-manager-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('attachments') || 'Attachments'} · {attManagerTarget?.label || ''}
              </DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                {t('attachmentsHelp') || 'Upload invoice scans, receipts, payment proofs (PDF / image, up to 15MB each).'}
              </DialogDescription>
            </DialogHeader>
            {attManagerTarget && (
              <div className="space-y-3">
                {/* Upload area */}
                {isAdmin && (
                  <div className="border-2 border-dashed border-zinc-300 px-4 py-6 text-center">
                    <input
                      id="att-upload-input" type="file" multiple
                      accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                      onChange={(e) => { handleAttUpload(e.target.files); e.target.value = ''; }}
                      className="hidden"
                      data-testid="att-upload-input"
                    />
                    <label
                      htmlFor="att-upload-input"
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer ${attUploading ? 'bg-zinc-200 text-zinc-500' : 'bg-zinc-950 text-white hover:bg-zinc-800'}`}
                      data-testid="att-upload-trigger"
                    >
                      <Paperclip size={14} /> {attUploading ? (t('uploading') || 'Uploading…') : (t('uploadFiles') || 'Upload files')}
                    </label>
                    <div className="mt-2 text-[10px] text-zinc-400">{t('multipleFilesAllowed') || 'Multiple files allowed · PDF, PNG, JPG, WEBP'}</div>
                  </div>
                )}
                {/* Existing attachments list */}
                <div className="border border-zinc-200 max-h-80 overflow-y-auto">
                  {(attManagerTarget.attachments || []).length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-zinc-400" data-testid="att-empty">{t('noAttachments') || 'No attachments yet'}</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-[10px] text-zinc-500 font-semibold">{t('fileName') || 'File'}</th>
                          <th className="px-2 py-1.5 text-right text-[10px] text-zinc-500 font-semibold w-20">{t('size') || 'Size'}</th>
                          <th className="px-2 py-1.5 text-left text-[10px] text-zinc-500 font-semibold w-32">{t('uploadedAt') || 'Uploaded'}</th>
                          <th className="px-2 py-1.5 text-center text-[10px] text-zinc-500 font-semibold w-24">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(attManagerTarget.attachments || []).map((att, i) => (
                          <tr key={i} className="border-b border-zinc-100" data-testid={`att-row-${i}`}>
                            <td className="px-2 py-1.5 truncate" title={att.original_name}>
                              <span className="font-mono text-zinc-700">{att.original_name || att.filename}</span>
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-zinc-500">{((att.size || 0) / 1024).toFixed(1)} KB</td>
                            <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-500">{(att.uploaded_at || '').slice(0, 10)}</td>
                            <td className="px-2 py-1.5 text-center">
                              <div className="flex justify-center gap-1">
                                <button
                                  onClick={async () => {
                                    try {
                                      const absUrl = attViewUrl(attManagerTarget.kind, attManagerTarget.id, i);
                                      // Strip the `${API}` prefix so the shared helper can rebuild the URL.
                                      const path = absUrl.replace(/^https?:\/\/[^/]+\/api/, '');
                                      await openPdfInBrowser(path, {});
                                    } catch (e) {
                                      toast.error(e?.message || t('failedToLoad') || 'Failed');
                                    }
                                  }}
                                  className="p-1 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors"
                                  title={t('view') || 'View'}
                                  data-testid={`att-view-${i}`}
                                >
                                  <Eye size={14} />
                                </button>
                                {isAdmin && (
                                  <button
                                    onClick={() => handleAttDelete(i)}
                                    className="p-1 hover:bg-red-50 text-zinc-500 hover:text-red-600 transition-colors"
                                    title={t('delete')}
                                    data-testid={`att-delete-${i}`}
                                  >
                                    <Trash size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAttManagerOpen(false)} className="rounded-none" data-testid="att-manager-close">{t('close') || 'Close'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 379 — Invoice / Opening Balance Payment History Dialog */}
        <Dialog open={elevInvHistoryOpen} onOpenChange={setElevInvHistoryOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="elev-inv-history-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {elevInvHistory?.kind === 'opening'
                  ? (t('openingBalance') || 'Opening Balance')
                  : `${t('invoice') || 'Invoice'} ${elevInvHistory?.invoice_number || ''}`}
              </DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                {elevInvHistory?.date ? formatDateDMY(elevInvHistory.date) : ''}
                {elevInvHistory?.description ? ` · ${elevInvHistory.description}` : ''}
              </DialogDescription>
            </DialogHeader>
            {elevInvHistoryLoading && (
              <div className="py-12 text-center text-sm text-zinc-500">{t('loading') || 'Loading…'}</div>
            )}
            {!elevInvHistoryLoading && elevInvHistory && (
              <div className="space-y-4">
                {/* Summary strip */}
                <div className="grid grid-cols-3 gap-2 border border-zinc-200 bg-zinc-50">
                  <div className="px-3 py-2 border-r border-zinc-200">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('invoiceTotal') || 'Invoice Total'}</div>
                    <div className="text-base font-mono font-semibold text-zinc-900" data-testid="inv-hist-total">&euro;{Number(elevInvHistory.amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="px-3 py-2 border-r border-zinc-200">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('paid') || 'Paid'}</div>
                    <div className="text-base font-mono font-semibold text-emerald-700" data-testid="inv-hist-paid">&euro;{Number(elevInvHistory.total_paid || 0).toFixed(2)}</div>
                  </div>
                  <div className="px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('remaining') || 'Remaining'}</div>
                    <div className={`text-base font-mono font-semibold ${Number(elevInvHistory.remaining || 0) > 0.005 ? 'text-red-600' : 'text-zinc-700'}`} data-testid="inv-hist-remaining">&euro;{Number(elevInvHistory.remaining || 0).toFixed(2)}</div>
                  </div>
                </div>
                {/* Status badge */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">{t('status') || 'Status'}:</span>
                  {(() => {
                    const s = elevInvHistory.status;
                    const cls = s === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : s === 'partial' ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-red-50 text-red-700 border-red-200';
                    return (
                      <span className={`text-[10px] px-2 py-0.5 border ${cls} uppercase font-semibold`} data-testid="inv-hist-status">{t(s) || s}</span>
                    );
                  })()}
                </div>
                {/* Payments table */}
                <div className="border border-zinc-200">
                  <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-xs font-semibold text-zinc-700" style={{ fontFamily: 'var(--font-heading)' }}>
                    {t('paymentHistory') || 'Payment History'} ({(elevInvHistory.payments || []).length})
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-white border-b border-zinc-200">
                      <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                        <th className="px-2 py-2 text-left">{t('paymentNo') || 'Payment No.'}</th>
                        <th className="px-2 py-2 text-left">{t('date')}</th>
                        <th className="px-2 py-2 text-left">{t('reference')}</th>
                        <th className="px-2 py-2 text-right">{t('amount')}</th>
                        <th className="px-2 py-2 text-left">{t('source') || 'Source'}</th>
                        <th className="px-2 py-2 text-left">{t('notes')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(elevInvHistory.payments || []).length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-8 text-center text-zinc-400">{t('noPayments') || 'No payments yet'}</td></tr>
                      ) : (
                        (elevInvHistory.payments || []).map((p) => (
                          <tr key={p.payment_id} className="border-b border-zinc-100" data-testid={`inv-hist-row-${p.payment_id}`}>
                            <td className="px-2 py-2 font-mono text-zinc-700">{p.payment_number || '-'}</td>
                            <td className="px-2 py-2 font-mono text-zinc-500">{formatDateDMY(p.date)}</td>
                            <td className="px-2 py-2 font-mono text-zinc-500">{p.reference || '-'}</td>
                            <td className="px-2 py-2 text-right font-mono font-semibold text-zinc-800">&euro;{Number(p.amount_allocated || 0).toFixed(2)}</td>
                            <td className="px-2 py-2 capitalize text-zinc-600">{p.paid_from_reserve_fund ? (t('reserveFund') || 'Reserve Fund') : (p.payment_source || '-')}</td>
                            <td className="px-2 py-2 text-zinc-500 truncate max-w-[180px]" title={p.notes || ''}>{p.notes || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setElevInvHistoryOpen(false)} className="rounded-none" data-testid="inv-hist-close-btn">{t('close') || 'Close'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== ADD ELEVATOR PAYMENT DIALOG ====== */}
        <Dialog open={elevPaymentDialogOpen} onOpenChange={(open) => { setElevPaymentDialogOpen(open); if (!open) setEditingElevPayment(null); }}>
          <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{editingElevPayment ? t('edit') + ' ' + t('payment') : t('addPayment')}</DialogTitle>
              <DialogDescription className="sr-only">{t('addPayment')}</DialogDescription>
            </DialogHeader>
            {/* Phase 307 — Payment Number is auto-generated but editable.
                Always rendered as an Input so the user sees the next system
                number pre-filled and can override it freely. */}
            <div>
              <Label className="text-xs text-zinc-500">{t('paymentNumber')}</Label>
              <Input
                value={elevPaymentForm.payment_number || ''}
                onChange={(e) => setElevPaymentForm({ ...elevPaymentForm, payment_number: e.target.value })}
                className="mt-1 rounded-none font-mono text-sm"
                placeholder={t('autoGenerated')}
                data-testid="elev-payment-number-input"
              />
            </div>
            <form onSubmit={handleElevSavePayment} className="space-y-4">
              {/* Row 1: Date + Reference */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-500">{t('date')}</Label>
                  <DatePickerDMY value={elevPaymentForm.date} onChange={v => setElevPaymentForm({ ...elevPaymentForm, date: v })} className="mt-1 font-mono" required data-testid="elev-payment-date-input" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('reference')}</Label>
                  <Input value={elevPaymentForm.reference || ''} onChange={(e) => setElevPaymentForm({ ...elevPaymentForm, reference: e.target.value })} className="mt-1 rounded-none font-mono text-sm" placeholder={t('optional') || 'Optional'} data-testid="elev-payment-reference-input" />
                </div>
              </div>

              {/* Row 2: Source + Reserve Fund (always visible, editable) */}
              <div>
                <Label className="text-xs text-zinc-500">{t('paymentSource')}</Label>
                <Select value={elevPaymentForm.payment_source || 'cash'} onValueChange={(val) => setElevPaymentForm({ ...elevPaymentForm, payment_source: val })}>
                  <SelectTrigger className="mt-1 rounded-none text-sm" data-testid="elev-payment-source"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="cash" data-testid="elev-payment-source-cash">
                      <span>{t('cash')} <span className={`ml-1 font-mono ${(sourceBalances.cash?.available || 0) < 0 ? 'text-red-500' : 'text-zinc-500'}`}>(&euro;{(sourceBalances.cash?.available || 0).toFixed(2)} {t('available')})</span></span>
                    </SelectItem>
                    <SelectItem value="bank" data-testid="elev-payment-source-bank">
                      <span>{t('bank')} <span className={`ml-1 font-mono ${(sourceBalances.bank?.available || 0) < 0 ? 'text-red-500' : 'text-zinc-500'}`}>(&euro;{(sourceBalances.bank?.available || 0).toFixed(2)} {t('available')})</span></span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {/* Show remaining balance after payment (uses receipts-based available) */}
                {(() => {
                  const payAmt = parseFloat(elevPaymentForm.amount) || 0;
                  const isReserve = !!elevPaymentForm.paid_from_reserve_fund;
                  const sourceKey = isReserve ? 'reserve_fund' : (elevPaymentForm.payment_source || 'cash');
                  const available = sourceBalances[sourceKey]?.available ?? 0;
                  const remaining = available - payAmt;
                  if (payAmt > 0) {
                    return (
                      <div className="text-[10px] text-zinc-400 mt-1 font-mono" data-testid="source-balance-preview">
                        {isReserve ? t('reserveFund') : t(elevPaymentForm.payment_source || 'cash')}: &euro;{available.toFixed(2)} &rarr; <span className={remaining < 0 ? 'text-red-500 font-semibold' : ''}>&euro;{remaining.toFixed(2)}</span> {t('afterPayment')}
                      </div>
                    );
                  }
                  return null;
                })()}
                <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="elev-reserve-fund-toggle">
                  <input type="checkbox" checked={elevPaymentForm.paid_from_reserve_fund || false} onChange={(e) => setElevPaymentForm({ ...elevPaymentForm, paid_from_reserve_fund: e.target.checked })} className="rounded-none w-4 h-4 accent-zinc-700" />
                  <span className="text-xs text-zinc-600">{t('paidFromReserveFund')}</span>
                  <span className="text-[10px] font-mono text-zinc-400">(&euro;{(sourceBalances.reserve_fund?.available || 0).toFixed(2)} {t('available')})</span>
                </label>
              </div>

              {/* Amount */}
              <div>
                <Label className="text-xs text-zinc-500">{t('amount')} (&euro;)</Label>
                <Input type="number" step="0.01" min="0.01" value={elevPaymentForm.amount} onChange={(e) => setElevPaymentForm({ ...elevPaymentForm, amount: e.target.value })} className="mt-1 rounded-none font-mono" required placeholder="0.00" data-testid="elev-payment-amount-input" />
              </div>

              {/* Invoice Allocation Table — per category (203 Monthly / 301 Capital) — shown for both Add and Edit */}
              {/* Phase 411 — Also render when there are no elevator expenses
                  but the building has an unpaid Opening Balance (or leftover
                  unallocated credits). Previously the whole allocation UI was
                  hidden if `elevExpenses.length === 0`, which trapped payments
                  as "Unallocated" against an Opening Balance with no way out. */}
              {(elevExpenses.length > 0 || (elevPayableItems || []).some(it => it.kind === 'opening' || it.kind === 'credit')) && (
                <div>
                  <Label className="text-xs text-zinc-500 mb-1 block">{t('allocateToInvoices')}</Label>
                  <p className="text-[10px] text-zinc-400 mb-1">{t('allocatePerCategoryHelp')}</p>
                  <div className="border border-zinc-200 max-h-72 overflow-y-auto">
                    <table className="w-full text-xs table-fixed">
                      <colgroup>
                        <col className="w-28" />
                        <col className="w-20" />
                        <col className="w-20" />
                        <col className="w-20" />
                        <col className="w-20" />
                        <col className="w-20" />
                        <col className="w-24" />
                      </colgroup>
                      <thead className="bg-zinc-50 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1.5 text-[10px] text-zinc-400 font-semibold">{t('invoice')}</th>
                          <th className="text-left px-2 py-1.5 text-[10px] text-zinc-400 font-semibold">{t('date')}</th>
                          <th className="text-right px-2 py-1.5 text-[10px] text-zinc-400 font-semibold">{t('total')}</th>
                          <th className="text-right px-2 py-1.5 text-[10px] text-zinc-400 font-semibold">{t('paid')}</th>
                          <th className="text-right px-2 py-1.5 text-[10px] text-zinc-400 font-semibold">{t('remaining')}</th>
                          <th className="text-center px-2 py-1.5 text-[10px] text-zinc-400 font-semibold">{t('status')}</th>
                          <th className="text-right px-2 py-1.5 text-[10px] text-zinc-400 font-semibold">{t('allocate')}</th>
                          {/* Phase 735.34 — Per-invoice Reserve Fund checkbox.
                              Ticking a row's box will deduct that invoice's
                              allocated amount from the Reserve Fund; unticked
                              rows are paid normally from cash / bank. */}
                          <th className="text-center px-2 py-1.5 text-[10px] text-violet-500 font-semibold whitespace-nowrap" title={t('deductFromReserveFund') || 'Deduct from Reserve Fund'}>{t('reserveFund') || 'Reserve Fund'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Phase 380 — Unified, date-DESC ordered allocation
                          // rows. OPENING participates in the same sort as
                          // invoices and naturally falls into its chronological
                          // position (typically at the bottom since it's the
                          // earliest date). Each row shows: Invoice | Date |
                          // Total | Paid | Remaining | Status | Allocate.
                          const getSplit = (exp) => {
                            // Phase 409 — Use the discount-aware breakdown so the per-category
                            // split honours the invoice-level discount distribution.
                            const lineNets = [];
                            const baseNet = parseFloat(exp.invoice_amount || 0);
                            if (baseNet > 0) {
                              const cat = exp.category === '301' ? '301' : '203';
                              lineNets.push({ net: baseNet, vat_rate: parseFloat(exp.vat_rate || 0), cat });
                            }
                            for (const ec of (exp.extra_charges || [])) {
                              const q = parseFloat(ec.quantity);
                              const p = parseFloat(ec.price);
                              const ecNet = (!isNaN(q) && !isNaN(p)) ? q * p : parseFloat(ec.amount || 0);
                              const cat = ec.category === '301' ? '301' : '203';
                              lineNets.push({ net: ecNet, vat_rate: parseFloat(ec.vat_rate || 0), cat });
                            }
                            const subtotalNet = lineNets.reduce((s, l) => s + l.net, 0);
                            const dType = (exp.discount_type || 'amount').toLowerCase();
                            const dValue = parseFloat(exp.discount_value || 0);
                            let dAmt = dType === 'percent' ? subtotalNet * (dValue / 100) : dValue;
                            if (dAmt < 0) dAmt = 0;
                            if (dAmt > subtotalNet) dAmt = subtotalNet;
                            const factor = subtotalNet > 0 ? dAmt / subtotalNet : 0;
                            const split = { '203': 0, '301': 0 };
                            for (const l of lineNets) {
                              const dn = l.net * (1 - factor);
                              split[l.cat] += dn * (1 + l.vat_rate / 100);
                            }
                            return split;
                          };
                          // Build a unified list of rows BEFORE rendering, so
                          // we can sort them all together by date DESC.
                          const unified = [];
                          (elevPayableItems || []).forEach((it) => {
                            if (it.kind !== 'opening') return;
                            const key = it.key;
                            const allocated = elevPaymentForm.allocations?.[key] || '';
                            const allocatedNum = parseFloat(allocated) || 0;
                            const liveRem = Number(it.remaining || 0);
                            const total = Number(it.amount || 0);
                            const displayRem = editingElevPayment ? liveRem + allocatedNum : liveRem;
                            const paid = Math.max(total - displayRem, 0);
                            unified.push({
                              key, sortDate: it.date || '', kind: 'opening',
                              invoiceNum: 'OPENING',
                              date: it.date || '',
                              total, paid, remaining: displayRem,
                              allocated,
                              // Phase 920.35 — Never let a negative HTML `max` reach
                              // the <input>. If the opening balance is overpaid,
                              // clamp at 0 so the row renders as a disabled input.
                              max: Math.max(0, displayRem),
                              cat: null,
                            });
                          });
                          const linkedExpenseIds = new Set();
                          if (editingElevPayment) {
                            if (Array.isArray(editingElevPayment.allocations)) {
                              editingElevPayment.allocations.forEach(a => {
                                if (a?.expense_id) linkedExpenseIds.add(a.expense_id);
                              });
                            }
                            if (editingElevPayment.expense_id) linkedExpenseIds.add(editingElevPayment.expense_id);
                          }
                          elevExpenses.forEach(exp => {
                            const split = getSplit(exp);
                            const totalGross = split['203'] + split['301'];
                            const totalPaid = exp.total_paid || 0;
                            // Phase 667 — credited_amount is the supplier-credit-note
                            // settlement applied to this invoice. It must reduce the
                            // displayed Remaining exactly like a payment does, so a
                            // fully credited invoice drops out of the allocation list
                            // and a partially credited one shows the true outstanding.
                            const totalCredited = exp.credited_amount || 0;
                            const cats = ['203', '301'].filter(c => split[c] > 0.005);
                            const isLinked = linkedExpenseIds.has(exp.expense_id);
                            cats.forEach((cat) => {
                              const key = `${exp.expense_id}|${cat}`;
                              const allocated =
                                (elevPaymentForm.allocations?.[key] ?? '') !== ''
                                  ? elevPaymentForm.allocations[key]
                                  : (elevPaymentForm.allocations?.[exp.expense_id] ?? '');
                              const allocatedNum = parseFloat(allocated) || 0;
                              // Phase 920.34 — Use the REAL per-category paid amount
                              // reported by the backend (`paid_by_category`) instead of
                              // distributing total_paid proportionally. The proportional
                              // formula was wrong whenever a payment targeted only one
                              // category — it leaked paid amount into the OTHER category
                              // and produced ghost remaining balances (e.g. €37.50 on a
                              // fully-paid 3935/301 line).
                              const paidByCat = exp.paid_by_category || {};
                              const paidCat = (paidByCat[cat] !== undefined && paidByCat[cat] !== null)
                                ? Number(paidByCat[cat] || 0)
                                : (totalGross > 0 ? totalPaid * split[cat] / totalGross : 0);
                              const creditedCat = totalGross > 0 ? totalCredited * split[cat] / totalGross : 0;
                              const paidCatExcludingThis = editingElevPayment
                                ? Math.max(0, paidCat - allocatedNum)
                                : paidCat;
                              const remainingCat = split[cat] - paidCatExcludingThis - creditedCat;
                              if (remainingCat <= 0.01 && allocatedNum <= 0 && !isLinked) return;
                              unified.push({
                                key, sortDate: exp.date || '', kind: 'expense',
                                invoiceNum: exp.invoice_number || '-',
                                date: exp.date || '',
                                total: split[cat], paid: paidCatExcludingThis, remaining: remainingCat,
                                credited: creditedCat,
                                allocated,
                                // Phase 920.35 — Never emit a negative `max` (an
                                // overpaid invoice has remainingCat < 0). A negative
                                // HTML `max` on the <input type="number"> triggers a
                                // browser validation "min (0) must be less than max"
                                // and blocks the entire form. Clamp at 0.
                                max: Math.max(0, remainingCat + allocatedNum),
                                cat: cats.length > 1 ? cat : null,
                              });
                            });
                          });
                          // Phase 384 — Pure date DESC sort. OPENING is no
                          // longer pinned at top; it follows its real date so
                          // (e.g.) a 2025-12-31 OB naturally lands at the
                          // bottom under all 2026 invoices.
                          unified.sort((a, b) => {
                            const da = a.sortDate || '';
                            const db = b.sortDate || '';
                            if (da !== db) return db.localeCompare(da);
                            return 0;
                          });
                          const fmtDate = (d) => {
                            if (!d) return '-';
                            const s = String(d).slice(0, 10);
                            const [y, m, dd] = s.split('-');
                            return (dd && m && y) ? `${dd}/${m}/${y}` : s;
                          };
                          const statusFor = (paid, remaining, total) => {
                            if (total > 0 && remaining <= 0.005) return { k: 'paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
                            if (paid > 0.005) return { k: 'partial', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
                            return { k: 'unpaid', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' };
                          };
                          return unified.map((row) => {
                            const s = statusFor(row.paid, row.remaining, row.total);
                            const rowBg = row.kind === 'opening' ? 'bg-zinc-50/40' : (s.k === 'partial' ? 'bg-amber-50/30' : '');
                            return (
                              <tr key={row.key} className={`border-t border-zinc-100 hover:bg-zinc-100/50 ${rowBg} align-middle`} data-testid={`alloc-row-${row.key}`}>
                                <td className="px-2 py-1.5 truncate">
                                  <span className={`font-mono font-semibold ${row.kind === 'opening' ? 'text-zinc-700' : 'text-zinc-800'}`}>{row.invoiceNum}</span>
                                  {row.cat && (
                                    <span className={`ml-1 inline-block px-1 py-0.5 font-mono text-[8px] font-semibold ${row.cat === '203' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>{row.cat}</span>
                                  )}
                                </td>
                                <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-500">{fmtDate(row.date)}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-zinc-700">&euro;{Number(row.total || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-emerald-700">&euro;{Number(row.paid || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-red-600">&euro;{Number(row.remaining || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-center">
                                  <span className={`text-[9px] px-1.5 py-0.5 border ${s.cls} uppercase font-semibold`} data-testid={`alloc-status-${row.key}`}>{t(s.k)}</span>
                                </td>
                                <td className="px-1 py-1">
                                  <Input
                                    type="number" step="0.01" min="0"
                                    max={Math.max(0, Number(row.max || 0))}
                                    disabled={Number(row.max || 0) <= 0}
                                    value={row.allocated}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setElevPaymentForm(prev => ({
                                        ...prev,
                                        allocations: { ...prev.allocations, [row.key]: val }
                                      }));
                                    }}
                                    className="rounded-none h-7 text-xs font-mono text-right w-full disabled:bg-zinc-100 disabled:cursor-not-allowed"
                                    placeholder="0.00"
                                    data-testid={`alloc-input-${row.key}`}
                                  />
                                </td>
                                {/* Phase 735.34 — Per-invoice Reserve Fund
                                    checkbox. When ticked, `alloc_reserve[row.key]
                                    = true` — the backend must deduct THIS
                                    invoice's allocated amount from the
                                    Reserve Fund (not the whole payment). */}
                                <td className="px-1 py-1 text-center">
                                  <input
                                    type="checkbox"
                                    checked={!!elevPaymentForm.alloc_reserve?.[row.key]}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setElevPaymentForm(prev => ({
                                        ...prev,
                                        alloc_reserve: { ...(prev.alloc_reserve || {}), [row.key]: checked }
                                      }));
                                    }}
                                    className="w-4 h-4 accent-violet-600 cursor-pointer"
                                    data-testid={`alloc-reserve-${row.key}`}
                                    title={t('deductFromReserveFund') || 'Deduct from Reserve Fund'}
                                  />
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                    {elevExpenses.filter(exp => {
                      // Phase 667 — Trust backend `status` / `remaining_balance` which
                      // already factor in supplier credit notes. A fully credited invoice
                      // must NOT appear as "unpaid".
                      if (exp.status === 'paid') return false;
                      if (typeof exp.remaining_balance === 'number') return exp.remaining_balance > 0.01;
                      const total = (typeof exp.grand_total === 'number') ? exp.grand_total : ((exp.invoice_amount || 0) * (1 + (exp.vat_rate || 0) / 100));
                      return (total - (exp.total_paid || 0) - (exp.credited_amount || 0)) > 0.01;
                    }).length === 0 && (!elevPayableItems || elevPayableItems.filter(it => it.kind === 'opening').length === 0) && (
                      <div className="px-3 py-3 text-xs text-zinc-400 text-center">{t('allInvoicesPaid')}</div>
                    )}
                  </div>
                  {/* Allocation total */}
                  {(() => {
                    const totalAllocated = Object.values(elevPaymentForm.allocations || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                    const payAmount = parseFloat(elevPaymentForm.amount) || 0;
                    const unallocated = payAmount - totalAllocated;
                    return totalAllocated > 0 ? (
                      <div className="flex justify-between text-xs mt-1 px-1">
                        <span className="text-zinc-400">{t('allocated')}: <span className="font-mono">&euro;{totalAllocated.toFixed(2)}</span></span>
                        {unallocated > 0.01 && <span className="text-zinc-400">{t('unallocated')}: <span className="font-mono">&euro;{unallocated.toFixed(2)}</span></span>}
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Notes */}
              <div>
                <Label className="text-xs text-zinc-500">{t('notes')}</Label>
                <Textarea value={elevPaymentForm.notes} onChange={(e) => setElevPaymentForm({ ...elevPaymentForm, notes: e.target.value })} className="mt-1 rounded-none resize-y" rows={3} style={{ minHeight: '80px', maxHeight: '50vh' }} placeholder={t('notes') + '...'} data-testid="elev-payment-notes-input" />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setElevPaymentDialogOpen(false); setEditingElevPayment(null); }} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-elev-payment-btn">{editingElevPayment ? t('save') : t('addPayment')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== VIEW ELEVATOR PAYMENTS DIALOG ====== */}
        <Dialog open={elevViewPaymentsDialogOpen} onOpenChange={setElevViewPaymentsDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('paymentHistory')}</DialogTitle>
            </DialogHeader>
            {selectedElevExpense && (
              <div className="bg-zinc-50 border border-zinc-200 p-3 mb-2 space-y-1">
                <p className="text-sm font-medium">{selectedElevExpense.company} &mdash; {selectedElevExpense.invoice_number || '-'}</p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">{t('invoiceAmount')}:</span>
                  <span className="font-mono font-semibold">&euro;{(selectedElevExpense.invoice_amount || 0).toFixed(2)}</span>
                </div>
              </div>
            )}
            {loadingElevPayments ? (
              <div className="py-8 text-center text-zinc-500">{t('loading')}</div>
            ) : elevExpensePayments.length === 0 ? (
              <div className="py-8 text-center text-zinc-400">{t('noPayments')}</div>
            ) : (
              <div className="border border-zinc-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="text-left px-3 py-2 font-medium text-zinc-600">{t('date')}</th>
                      <th className="text-right px-3 py-2 font-medium text-zinc-600">{t('amount')}</th>
                      <th className="text-left px-3 py-2 font-medium text-zinc-600">{t('notes')}</th>
                      <th className="w-12 px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {elevExpensePayments.map((payment) => (
                      <tr key={payment.payment_id} className="border-b border-zinc-100 last:border-0" data-testid={`elev-payment-row-${payment.payment_id}`}>
                        <td className="px-3 py-2 font-mono">{payment.date}</td>
                        <td className="px-3 py-2 font-mono font-semibold text-right text-emerald-700">&euro;{(payment.amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-zinc-600">{payment.notes || <span className="text-zinc-300">&mdash;</span>}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <button onClick={() => { setElevViewPaymentsDialogOpen(false); openEditElevPayment(payment); }} className="p-1.5 hover:bg-zinc-100 transition-colors" title={t('edit')} data-testid={`elev-edit-payment-hist-${payment.payment_id}`}>
                              <PencilSimple size={16} />
                            </button>
                            <button onClick={() => confirmDeleteElevPayment(payment)} className="p-1.5 hover:bg-red-50 text-red-500 transition-colors" title={t('deletePayment')} data-testid={`elev-delete-payment-${payment.payment_id}`}>
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-zinc-50 border-t border-zinc-200">
                      <td className="px-3 py-2 font-semibold text-sm">{t('total')}</td>
                      <td className="px-3 py-2 font-mono font-bold text-right text-emerald-700">&euro;{elevExpensePayments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setElevViewPaymentsDialogOpen(false)} className="rounded-none">{t('close')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== DELETE ELEVATOR PAYMENT DIALOG ====== */}
        <AlertDialog open={deleteElevPaymentDialogOpen} onOpenChange={setDeleteElevPaymentDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePayment')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDeletePayment')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteElevPayment} className="bg-red-600 rounded-none" data-testid="confirm-delete-elev-payment-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Phase 1004g — Management Company Advance History dialog */}
        <Dialog open={mcaHistoryOpen} onOpenChange={setMcaHistoryOpen}>
          <DialogContent className="sm:max-w-3xl rounded-none max-h-[85vh] overflow-hidden flex flex-col" data-testid="mca-history-dialog">
            <DialogHeader>
              <DialogTitle>{t('mcaHistoryTitle') || (language === 'el' ? 'Ιστορικό Προκαταβολών Εταιρείας' : 'Management Company Advance — History')}</DialogTitle>
            </DialogHeader>
            {mcaHistoryData && (
              <>
                <div className="text-sm text-zinc-600 mt-1">
                  {t('building') || 'Building'}: <span className="font-semibold text-zinc-900">{mcaHistoryData.building?.name}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div className="border border-zinc-200 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('mcaTotalAdvances') || 'Total Advances'}</div>
                    <div className="text-lg font-mono font-semibold text-emerald-700">&euro;{(mcaHistoryData.summary?.total_advances || 0).toFixed(2)}</div>
                  </div>
                  <div className="border border-zinc-200 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('mcaTotalRepayments') || 'Total Repayments'}</div>
                    <div className="text-lg font-mono font-semibold text-red-700">&euro;{(mcaHistoryData.summary?.total_repayments || 0).toFixed(2)}</div>
                  </div>
                  <div className="border border-zinc-200 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('mcaOutstanding') || 'Outstanding'}</div>
                    <div className="text-lg font-mono font-semibold text-zinc-900">&euro;{(mcaHistoryData.summary?.outstanding || 0).toFixed(2)}</div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto border border-zinc-200 mt-2">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr className="text-[11px] uppercase tracking-wider text-zinc-500">
                        <th className="px-3 py-2 text-left">{t('date') || 'Date'}</th>
                        <th className="px-3 py-2 text-left">{t('type') || 'Type'}</th>
                        <th className="px-3 py-2 text-left">{t('source') || 'Source'}</th>
                        <th className="px-3 py-2 text-left">{t('reference') || 'Reference'}</th>
                        <th className="px-3 py-2 text-left">{t('notes') || 'Notes'}</th>
                        <th className="px-3 py-2 text-right">{t('amount') || 'Amount'}</th>
                        <th className="px-3 py-2 text-right">{t('outstanding') || 'Outstanding'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {(mcaHistoryData.entries || []).map(e => (
                        <tr key={e.entry_id} className={e.entry_type === 'advance' ? 'bg-emerald-50/30' : 'bg-red-50/20'} data-testid={`mca-hist-row-${e.entry_id}`}>
                          <td className="px-3 py-2 font-mono text-xs">{(e.date || '').split('-').reverse().join('/')}</td>
                          <td className={`px-3 py-2 text-xs font-semibold ${e.entry_type === 'advance' ? 'text-emerald-700' : 'text-red-700'}`}>
                            {e.entry_type === 'advance' ? (t('advance') || 'Advance') : (t('repayment') || 'Repayment')}
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-600">{(e.source || '—').replace(/^\w/, c => c.toUpperCase())}</td>
                          <td className="px-3 py-2 text-xs font-mono text-zinc-600">{e.reference || '—'}</td>
                          <td className="px-3 py-2 text-xs text-zinc-700 max-w-[220px] truncate" title={e.notes || ''}>{e.notes || '—'}</td>
                          <td className={`px-3 py-2 text-right font-mono text-xs ${e.entry_type === 'advance' ? 'text-emerald-700' : 'text-red-700'}`}>
                            {e.entry_type === 'advance' ? '+' : '-'}&euro;{(e.amount || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-xs font-semibold">&euro;{(e.outstanding_after || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={openMcaHistoryPdf} className="rounded-none" data-testid="mca-hist-pdf-btn">
                <FilePdf size={14} className="mr-1" />{t('viewPdf') || 'View PDF'}
              </Button>
              <Button type="button" onClick={() => setMcaHistoryOpen(false)} className="bg-zinc-950 rounded-none">
                {t('close') || 'Close'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* ====== OPENING BALANCE DIALOG ====== */}
        <Dialog open={obDialogOpen} onOpenChange={setObDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('setOpeningBalance')}</DialogTitle></DialogHeader>
            <form onSubmit={handleSaveOb} className="space-y-4">
              <div>
                <Label>{t('amount')} (&euro;)</Label>
                <Input type="number" step="0.01" value={obForm.amount} onChange={(e) => setObForm({ ...obForm, amount: e.target.value })} className="mt-1 rounded-none font-mono" required data-testid="ob-amount-input" placeholder="0.00" />
                <p className="text-xs text-zinc-400 mt-1">{t('openingBalanceHint')}</p>
              </div>
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={obForm.date} onChange={v => setObForm({ ...obForm, date: v })} className="mt-1 font-mono" required data-testid="ob-date-input" />
              </div>
              <DialogFooter className="flex justify-between">
                <div>
                  {building?.opening_balance != null && (
                    <Button type="button" variant="outline" onClick={handleClearOb} className="rounded-none text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-ob-btn">{t('clear')}</Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setObDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                  <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-ob-btn">{t('save')}</Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== ELEVATOR OPENING BALANCE DIALOG ====== */}
        <Dialog open={elevObDialogOpen} onOpenChange={setElevObDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('elevatorOpeningBalance')}</DialogTitle></DialogHeader>
            <form onSubmit={handleSaveElevOb} className="space-y-4">
              <div>
                <Label>{t('amount')} (&euro;)</Label>
                <Input type="number" step="0.01" value={elevObForm.amount} onChange={(e) => setElevObForm({ ...elevObForm, amount: e.target.value })} className="mt-1 rounded-none font-mono" required data-testid="elev-ob-amount" placeholder="0.00" />
                <p className="text-xs text-zinc-400 mt-1">{t('openingBalanceHint')}</p>
              </div>
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={elevObForm.date} onChange={v => setElevObForm({ ...elevObForm, date: v })} className="mt-1 font-mono" required data-testid="elev-ob-date" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setElevObDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-elev-ob-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== ADD PAYMENT + RECEIPT DIALOG ====== */}
        <Dialog open={addPayDialogOpen} onOpenChange={(open) => { if (!open) setAddPayDialogOpen(false); }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {payStep === 'select' && t('addPayment')}
                {payStep === 'common_expense' && t('recordPayment')}
                {payStep === 'building_expense' && t('buildingExpenses')}
                {payStep === 'done' && t('receiptGenerated')}
              </DialogTitle>
            </DialogHeader>

            {/* Step 1: Select type */}
            {payStep === 'select' && (
              <div className="space-y-3 py-2">
                <button onClick={() => setPayStep('common_expense')} className="w-full flex items-center gap-4 p-4 border border-zinc-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-left" data-testid="pay-type-common">
                  <div className="w-10 h-10 bg-blue-100 flex items-center justify-center flex-shrink-0"><Buildings size={20} className="text-blue-700" /></div>
                  <div><p className="font-semibold text-sm">{t('commonExpenses')}</p><p className="text-xs text-zinc-500">{t('commonExpensesDesc')}</p></div>
                </button>
                <button onClick={() => setPayStep('building_expense')} className="w-full flex items-center gap-4 p-4 border border-zinc-200 hover:bg-amber-50 hover:border-amber-300 transition-colors text-left" data-testid="pay-type-expense">
                  <div className="w-10 h-10 bg-amber-100 flex items-center justify-center flex-shrink-0"><CurrencyEur size={20} className="text-amber-700" /></div>
                  <div><p className="font-semibold text-sm">{t('buildingExpenses')}</p><p className="text-xs text-zinc-500">{t('buildingExpensesDesc')}</p></div>
                </button>
              </div>
            )}

            {/* Step 2a: Common Expense Form — Invoice-Based */}
            {payStep === 'common_expense' && (
              <form onSubmit={handleReceiptSubmit} className="space-y-3">
                {/* Apartment selector */}
                <div>
                  <Label>{t('apartment')}</Label>
                  <select value={receiptForm.apartment_id} onChange={(e) => { setReceiptForm({ ...receiptForm, apartment_id: e.target.value }); fetchAptInvoices(e.target.value); }} className="mt-1 w-full border border-zinc-200 px-3 py-2 text-sm rounded-none font-mono bg-white" required data-testid="pay-apt-select">
                    <option value="">{t('selectApartment')}</option>
                    {apartments.map(a => <option key={a.apartment_id} value={a.apartment_id}>#{a.apartment_number} - {a.owner_name || '-'}</option>)}
                  </select>
                </div>

                {/* Total amount + Date + Method */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>{t('totalPayment')} (&euro;)</Label>
                    <Input type="number" step="0.01" min="0.01" value={receiptForm.amount} onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })} className="mt-1 rounded-none font-mono text-lg font-bold" placeholder="0.00" required data-testid="pay-total-input" />
                  </div>
                  <div>
                    <Label>{t('date')}</Label>
                    <DatePickerDMY value={receiptForm.payment_date} onChange={v => setReceiptForm({ ...receiptForm, payment_date: v })} className="mt-1 font-mono" required data-testid="pay-date" />
                  </div>
                  <div>
                    <Label>{t('paymentMethod')}</Label>
                    <select value={receiptForm.payment_method} onChange={(e) => setReceiptForm({ ...receiptForm, payment_method: e.target.value })} className="mt-1 w-full border border-zinc-200 px-3 py-2 text-sm rounded-none bg-white" data-testid="pay-method">
                      <option value="cash">{t('cash')}</option>
                      <option value="transfer">{t('bankTransfer')}</option>
                      <option value="other">{t('other')}</option>
                    </select>
                  </div>
                </div>

                {/* Auto-distribute button */}
                {bldPayTotalAmount > 0 && aptInvoices.length > 0 && (
                  <div className="flex justify-end">
                    <Button type="button" variant="outline" onClick={bldAutoDistribute} className="rounded-none text-xs h-7" data-testid="bld-auto-distribute-btn">
                      ⚡ {t('autoAllocateOldestFirst') || 'Auto Allocate Oldest First'}
                    </Button>
                  </div>
                )}

                {/* Invoice allocation list */}
                {receiptForm.apartment_id && (
                  <div className="border border-zinc-200 max-h-72 overflow-y-auto" data-testid="invoice-list">
                    {aptInvoices.length === 0 ? (
                      <div className="p-4 text-center text-zinc-400 text-sm">{t('noInvoices')}</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-zinc-50 sticky top-0">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs">{t('date')}</th>
                            <th className="px-2 py-2 text-left text-xs">{t('reference') || 'Reference'}</th>
                            <th className="px-2 py-2 text-left text-xs">{t('description') || 'Description'}</th>
                            <th className="px-2 py-2 text-right text-xs">{t('remaining')}</th>
                            <th className="px-2 py-2 text-right text-xs w-28">{t('allocate')}</th>
                            <th className="px-2 py-2 text-center text-xs w-44">{t('quickAllocate') || 'Quick'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aptInvoices.map(inv => {
                            const alloc = selectedInvoices[inv.invoice_id]?.amount || 0;
                            const isOver = alloc > (inv.remaining || 0) + 0.01;
                            const isOpening = !!inv.is_opening_balance;
                            const obDesc = isOpening
                              ? (inv.opening_balance_notes ? `${t('openingBalance') || 'Opening Balance'} – ${inv.opening_balance_notes}` : (t('openingBalance') || 'Opening Balance'))
                              : (inv.description || t('invoice') || 'Invoice');
                            // Phase 492 — always-populated invoice date.
                            // Falls back to opening_balance_date for OPENING.
                            const rowDate = isOpening
                              ? (inv.opening_balance_date || '')
                              : (inv.invoice_date || (inv.period ? `${inv.period}-01` : ''));
                            return (
                              <tr key={inv.invoice_id} className={`border-t border-zinc-100 ${alloc > 0 ? 'bg-emerald-50/30' : ''} ${isOpening ? 'bg-amber-50/40' : ''}`} data-testid={`inv-row-${inv.invoice_id}`}>
                                <td className="px-2 py-1.5 font-mono text-xs whitespace-nowrap" data-testid={`inv-date-${inv.invoice_id}`}>
                                  {rowDate ? formatDateDMY(rowDate) : '—'}
                                </td>
                                <td className="px-2 py-1.5 font-mono text-xs text-zinc-500">
                                  {isOpening ? <span className="text-amber-700 font-bold text-[10px] uppercase tracking-wider">OPENING</span> : (inv.invoice_number || '—')}
                                </td>
                                <td className="px-2 py-1.5 text-xs">
                                  <span className={isOpening ? 'text-amber-800 font-medium' : ''}>{obDesc}</span>
                                </td>
                                <td className="px-2 py-1.5 font-mono text-right font-semibold text-red-600">&euro;{(inv.remaining || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5">
                                  <Input
                                    type="number" step="0.01" min="0"
                                    max={Math.max(0, Number(inv.remaining || 0))}
                                    disabled={Number(inv.remaining || 0) <= 0}
                                    value={alloc || ''}
                                    onChange={(e) => updateInvoiceAllocation(inv.invoice_id, e.target.value)}
                                    className={`w-full rounded-none font-mono text-sm h-7 text-right disabled:bg-zinc-100 disabled:cursor-not-allowed ${isOver ? 'border-red-400 bg-red-50' : ''}`}
                                    data-testid={`inv-alloc-${inv.invoice_id}`}
                                  />
                                </td>
                                {/* Phase 492 — Per-row quick-allocate buttons */}
                                <td className="px-2 py-1.5">
                                  <div className="flex gap-1 justify-center">
                                    <button
                                      type="button"
                                      onClick={() => allocateFull(inv.invoice_id)}
                                      className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 transition"
                                      title={t('allocateFullTooltip') || 'Fill with full remaining balance'}
                                      data-testid={`inv-alloc-full-${inv.invoice_id}`}
                                    >
                                      {t('allocateFull') || 'Full'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => allocateRemainingTo(inv.invoice_id)}
                                      disabled={bldPayTotalAmount <= 0}
                                      className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                      title={t('allocateRemainingTooltip') || 'Apply whatever payment is still free'}
                                      data-testid={`inv-alloc-remaining-${inv.invoice_id}`}
                                    >
                                      {t('allocateRemaining') || 'Remaining'}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Allocation summary */}
                {bldPayTotalAmount > 0 && receiptForm.apartment_id && (
                  <div className={`flex justify-between items-center px-4 py-2 border ${bldAllocationMatch ? 'bg-emerald-50 border-emerald-200' : bldOverAllocated ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid="pay-total-display">
                    <span className="text-sm font-semibold">{t('allocated')}</span>
                    <div className="text-right">
                      <span className={`font-mono font-bold text-lg ${bldAllocationMatch ? 'text-emerald-700' : bldOverAllocated ? 'text-red-700' : 'text-amber-700'}`}>
                        &euro;{totalAllocated.toFixed(2)}
                      </span>
                      <span className="text-zinc-400 mx-1">/</span>
                      <span className="font-mono text-zinc-600">&euro;{bldPayTotalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Phase 241 — Optional manual receipt number; empty
                    delegates to backend auto-generator. */}
                <div>
                  <Label>{t('receiptNumber') || 'Receipt Number'}{' '}<span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span></Label>
                  <Input
                    value={receiptForm.receipt_number}
                    onChange={(e) => setReceiptForm({ ...receiptForm, receipt_number: e.target.value })}
                    className="mt-1 rounded-none font-mono"
                    placeholder={t('receiptNumberPlaceholder') || 'Leave empty to auto-generate (e.g. RCP-0017)'}
                    maxLength={64}
                    data-testid="pay-receipt-number"
                  />
                </div>
                <div>
                  <Label>{t('notes')}</Label>
                  <Textarea value={receiptForm.notes} onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })} className="mt-1 rounded-none resize-y" rows={3} style={{ minHeight: '80px', maxHeight: '50vh' }} data-testid="pay-notes" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAddPayDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                  <Button type="submit" className="bg-emerald-700 rounded-none" disabled={!bldAllocationMatch || bldPayTotalAmount <= 0} data-testid="pay-submit">{t('recordPayment')}</Button>
                </DialogFooter>
              </form>
            )}

            {/* Step 2b: Building Expense Form */}
            {payStep === 'building_expense' && (
              <form onSubmit={handleReceiptSubmit} className="space-y-3">
                <div>
                  <Label>{t('selectExpense')}</Label>
                  <select value={receiptForm.expense_id} onChange={(e) => setReceiptForm({ ...receiptForm, expense_id: e.target.value })} className="mt-1 w-full border border-zinc-200 px-3 py-2 text-sm rounded-none font-mono bg-white" data-testid="pay-expense-select">
                    <option value="">{t('manualEntry')}</option>
                    {expenses.filter(ex => ex.status !== 'paid').map(ex => (
                      <option key={ex.expense_id} value={ex.expense_id}>
                        {getCatLabel(ex.category, t, customCategories)} - &euro;{(ex.amount || 0).toFixed(2)} ({ex.date}) {ex.notes ? `- ${ex.notes.slice(0, 30)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>{t('supplier')}</Label>
                  <Input value={receiptForm.supplier} onChange={(e) => setReceiptForm({ ...receiptForm, supplier: e.target.value })} className="mt-1 rounded-none" placeholder={t('supplierPlaceholder')} data-testid="pay-supplier" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('amount')} (&euro;)</Label>
                    <Input type="number" step="0.01" min="0.01" value={receiptForm.amount} onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })} className="mt-1 rounded-none font-mono" required data-testid="pay-amount-exp" />
                  </div>
                  <div>
                    <Label>{t('date')}</Label>
                    <DatePickerDMY value={receiptForm.payment_date} onChange={v => setReceiptForm({ ...receiptForm, payment_date: v })} className="mt-1 font-mono" required data-testid="pay-date-exp" />
                  </div>
                </div>
                <div>
                  <Label>{t('notes')}</Label>
                  <Textarea value={receiptForm.notes} onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })} className="mt-1 rounded-none resize-y" rows={3} style={{ minHeight: '80px', maxHeight: '50vh' }} data-testid="pay-notes-exp" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setPayStep('select')} className="rounded-none">{t('back')}</Button>
                  <Button type="submit" className="bg-emerald-700 rounded-none" data-testid="pay-submit-exp">{t('recordPayment')}</Button>
                </DialogFooter>
              </form>
            )}

            {/* Step 3: Done — show receipt */}
            {payStep === 'done' && lastReceipt && (
              <div className="space-y-4 py-2">
                <div className="bg-emerald-50 border border-emerald-200 p-4 text-center">
                  <CheckCircle size={32} className="text-emerald-600 mx-auto mb-2" />
                  <p className="font-semibold text-emerald-800">{t('paymentRecorded')}</p>
                  <p className="text-sm text-emerald-600 font-mono mt-1">{lastReceipt.receipt_number}</p>
                </div>
                <div className="bg-white border border-zinc-200 p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-500">{t('amount')}</span><span className="font-mono font-bold">&euro;{lastReceipt.amount?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">{t('date')}</span><span className="font-mono">{lastReceipt.payment_date}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">{t('issuedTo')}</span><span>{lastReceipt.issued_to}</span></div>
                  {lastReceipt.payment_method && <div className="flex justify-between"><span className="text-zinc-500">{t('paymentMethod')}</span><span>{t(lastReceipt.payment_method)}</span></div>}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => printReceipt(lastReceipt.receipt_id)} variant="outline" className="rounded-none flex-1" data-testid="print-receipt-btn">
                    <Printer size={16} className="mr-2" />{t('printPdf')}
                  </Button>
                  <Button onClick={() => downloadReceipt(lastReceipt.receipt_id)} className="bg-zinc-950 rounded-none flex-1" data-testid="download-receipt-btn">
                    <DownloadSimple size={16} className="mr-2" />{t('downloadPdf')}
                  </Button>
                </div>
                <Button variant="outline" onClick={() => setAddPayDialogOpen(false)} className="rounded-none w-full">{t('close')}</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ====== CLOSE BUILDING DIALOG ====== */}
        <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('closeBuilding')}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-500">{t('confirmCloseBuilding')}</p>
            <form onSubmit={handleCloseBuilding} className="space-y-4 mt-2">
              <div>
                <Label>{t('closeDate')} *</Label>
                <DatePickerDMY value={closeForm.close_date} onChange={v => setCloseForm({ ...closeForm, close_date: v })} className="mt-1 font-mono" required data-testid="close-date-input" />
              </div>
              <div>
                <Label>{t('closeReason')} *</Label>
                <Select value={closeForm.close_reason} onValueChange={(val) => setCloseForm({ ...closeForm, close_reason: val })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="close-reason-select"><SelectValue placeholder={t('closeReason')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Client stopped cooperation">{t('reasonClientStopped')}</SelectItem>
                    <SelectItem value="Payment issues">{t('reasonPaymentIssues')}</SelectItem>
                    <SelectItem value="We stopped service">{t('reasonWeStoppedService')}</SelectItem>
                    <SelectItem value="Building sold">{t('reasonBuildingSold')}</SelectItem>
                    <SelectItem value="Other">{t('reasonOther')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCloseDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" disabled={!closeForm.close_reason} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-close-building-btn">{t('closeBuilding')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Phase 165 — Add/Edit Repair Dialog (Payment Source FIRST) */}
        <Dialog open={repairDialogOpen} onOpenChange={(open) => { setRepairDialogOpen(open); if (!open) setEditingRepair(null); }}>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader><DialogTitle>{editingRepair ? t('editRepair') : t('addRepair')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t('description')}</Label><Input value={repairForm.description} onChange={(e) => setRepairForm(f => ({ ...f, description: e.target.value }))} className="mt-1 rounded-none" placeholder={t('repairDescPlaceholder')} data-testid="repair-desc-input" /></div>

              {/* STEP 1 — WHO PAYS? (required first choice) */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('paymentSource')}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1" data-testid="repair-payment-source-group">
                  <label className={`flex items-start gap-2 px-3 py-2 border cursor-pointer transition-colors ${repairForm.payment_source === 'building_fund' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="repair-source-building-fund">
                    <input type="radio" name="repair_payment_source" checked={repairForm.payment_source === 'building_fund'} onChange={() => setRepairForm(f => ({ ...f, payment_source: 'building_fund' }))} className="accent-zinc-900 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold">{t('buildingFund')}</p>
                      <p className="text-[10px] text-zinc-500 leading-snug">{t('buildingFundHint')}</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 px-3 py-2 border cursor-pointer transition-colors ${repairForm.payment_source === 'distribution' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="repair-source-distribution">
                    <input type="radio" name="repair_payment_source" checked={repairForm.payment_source === 'distribution'} onChange={() => setRepairForm(f => ({ ...f, payment_source: 'distribution' }))} className="accent-zinc-900 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold">{t('distributeToApartments')}</p>
                      <p className="text-[10px] text-zinc-500 leading-snug">{t('distributeHint')}</p>
                    </div>
                  </label>
                </div>
              </div>

              <div><Label>{t('amount')} (&euro;)</Label><Input type="number" step="0.01" value={repairForm.amount} onChange={(e) => setRepairForm(f => ({ ...f, amount: e.target.value }))} className="mt-1 rounded-none font-mono" data-testid="repair-amount-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('fromDate')}</Label><DatePickerDMY value={repairForm.from_date} onChange={v => setRepairForm(f => ({ ...f, from_date: v }))} className="mt-1 font-mono" data-testid="repair-from-input" /></div>
                <div><Label>{t('dueDate')}</Label><DatePickerDMY value={repairForm.due_date} onChange={v => setRepairForm(f => ({ ...f, due_date: v }))} className="mt-1 font-mono" data-testid="repair-due-input" /></div>
              </div>

              {/* STEP 2 — conditional UI driven by payment_source */}
              {repairForm.payment_source === 'building_fund' && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('paymentMethod')}</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1" data-testid="repair-payment-method-group">
                    <label className={`flex items-center justify-between gap-2 px-3 py-2 border cursor-pointer transition-colors ${(repairForm.payment_method || 'cash') === 'cash' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="repair-payment-method-cash">
                      <div className="flex items-center gap-2">
                        <input type="radio" name="repair_payment_method" checked={(repairForm.payment_method || 'cash') === 'cash'} onChange={() => setRepairForm(f => ({ ...f, payment_method: 'cash' }))} className="accent-zinc-900" />
                        <p className="text-xs font-semibold">{t('cash')}</p>
                      </div>
                      <p className={`text-[11px] font-mono ${(repairFundBalances.cash || 0) < (parseFloat(repairForm.amount) || 0) ? 'text-red-600 font-semibold' : 'text-zinc-500'}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="repair-cash-available">
                        {t('available')}: &euro;{(repairFundBalances.cash || 0).toFixed(2)}
                      </p>
                    </label>
                    <label className={`flex items-center justify-between gap-2 px-3 py-2 border cursor-pointer transition-colors ${repairForm.payment_method === 'bank' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="repair-payment-method-bank">
                      <div className="flex items-center gap-2">
                        <input type="radio" name="repair_payment_method" checked={repairForm.payment_method === 'bank'} onChange={() => setRepairForm(f => ({ ...f, payment_method: 'bank' }))} className="accent-zinc-900" />
                        <p className="text-xs font-semibold">{t('bank')}</p>
                      </div>
                      <p className={`text-[11px] font-mono ${(repairFundBalances.bank || 0) < (parseFloat(repairForm.amount) || 0) ? 'text-red-600 font-semibold' : 'text-zinc-500'}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="repair-bank-available">
                        {t('available')}: &euro;{(repairFundBalances.bank || 0).toFixed(2)}
                      </p>
                    </label>
                  </div>
                  {/* Inline insufficient-funds warning */}
                  {(() => {
                    const m = repairForm.payment_method || 'cash';
                    const av = m === 'cash' ? (repairFundBalances.cash || 0) : (repairFundBalances.bank || 0);
                    const amt = parseFloat(repairForm.amount) || 0;
                    if (amt > 0 && amt > av + 0.001) {
                      return (
                        <p className="mt-2 text-[11px] text-red-600 font-semibold" data-testid="repair-insufficient-funds">
                          {(t('insufficientFundsWarning') || 'Insufficient funds: only €{available} available, €{needed} needed')
                            .replace('{available}', av.toFixed(2))
                            .replace('{needed}', amt.toFixed(2))}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              {repairForm.payment_source === 'distribution' && !editingRepair && (
                <>
                  {/* Phase 259 — Distribution method (Share % vs Equally). */}
                  <div className="ml-4 pl-3 border-l-2 border-zinc-200">
                    <Label className="text-[11px] uppercase tracking-wider text-zinc-500">{t('distributionMethod') || 'Distribution method'}</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1" data-testid="repair-distribution-method-group">
                      <label className={`flex items-start gap-2 px-3 py-2 border cursor-pointer transition-colors ${(repairForm.distribution_method || 'share') === 'share' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="repair-method-share">
                        <input type="radio" name="repair_distribution_method" checked={(repairForm.distribution_method || 'share') === 'share'} onChange={() => setRepairForm(f => ({ ...f, distribution_method: 'share' }))} className="accent-zinc-900 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold">{t('byShare') || 'By share (%)'}</p>
                          <p className="text-[10px] text-zinc-500 leading-snug">{t('byShareHint') || 'Allocate proportionally to ownership %.'}</p>
                        </div>
                      </label>
                      <label className={`flex items-start gap-2 px-3 py-2 border cursor-pointer transition-colors ${repairForm.distribution_method === 'equal' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="repair-method-equal">
                        <input type="radio" name="repair_distribution_method" checked={repairForm.distribution_method === 'equal'} onChange={() => setRepairForm(f => ({ ...f, distribution_method: 'equal' }))} className="accent-zinc-900 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold">{t('equally') || 'Equally'}</p>
                          <p className="text-[10px] text-zinc-500 leading-snug">{t('equallyHint') || 'Split the total equally per apartment.'}</p>
                        </div>
                      </label>
                    </div>
                    {/* Phase 259 — Live preview of "€X each" so the user
                        sees the per-apartment share before saving. */}
                    {repairForm.distribution_method === 'equal' && parseFloat(repairForm.amount) > 0 && apartments.length > 0 && (
                      <p className="mt-2 text-[11px] text-zinc-600 font-mono bg-zinc-50 border border-zinc-200 px-2 py-1" data-testid="repair-equal-preview">
                        {`${apartments.length} × €${(parseFloat(repairForm.amount) / apartments.length).toFixed(2)} = €${parseFloat(repairForm.amount).toFixed(2)}`}
                      </p>
                    )}
                  </div>

                  <div className="bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800 leading-snug" data-testid="repair-distribution-note">
                    {t('distributionNotPaidYet')}
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer py-1">
                    <input type="checkbox" checked={repairForm.generate_invoices} onChange={(e) => setRepairForm(f => ({ ...f, generate_invoices: e.target.checked }))} className="accent-zinc-900 w-4 h-4" data-testid="repair-gen-inv-checkbox" />
                    <span className="text-sm text-zinc-700">{t('generateInvoices')}</span>
                  </label>
                </>
              )}

              {/* Phase 920.5 / 920.6 — Advancing Apartment (Owner Advance).
                  Available for BOTH create AND edit of shared/distribution
                  repairs. Placed outside the `!editingRepair` gate above so
                  users can migrate existing repairs to Owner Advance. */}
              {repairForm.payment_source === 'distribution' && (
                <div className="border-t border-zinc-100 pt-3 mt-1" data-testid="repair-advancing-apt-section">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">
                    {language === 'el' ? 'Προκαταβολή από Ιδιοκτήτη (Owner Advance)' : 'Advancing Apartment (Owner Advance)'}
                  </label>
                  <p className="text-[11px] text-zinc-500 mb-2 leading-snug">
                    {language === 'el'
                      ? 'Προαιρετικό. Αν επιλεχθεί διαμέρισμα, δεν εκδίδεται τιμολόγιο για αυτό και δημιουργείται/ενημερώνεται αυτόματα το Owner Advance. Λειτουργεί και σε υπάρχουσες επισκευές.'
                      : 'Optional. If an apartment is selected, no invoice is generated for it and the linked Owner Advance is created/updated automatically. Works on existing repairs too.'}
                  </p>
                  <select
                    value={repairForm.advancing_apartment_id || ''}
                    onChange={(e) => setRepairForm(f => ({ ...f, advancing_apartment_id: e.target.value }))}
                    className="w-full text-sm px-2 py-1.5 border border-zinc-300 focus:border-zinc-900 focus:outline-none rounded-none bg-white"
                    data-testid="repair-advancing-apt"
                  >
                    <option value="">{language === 'el' ? '— Καμία (κανονική ροή) —' : '— None (default) —'}</option>
                    {(apartments || []).map((a) => (
                      <option key={a.apartment_id} value={a.apartment_id}>
                        {a.apartment_number} — {a.owner_name || '—'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Phase 168 — Attachments available only when editing an existing repair. */}
              {editingRepair?.repair_id && (
                <RepairAttachmentsSection
                  repairId={editingRepair.repair_id}
                  attachments={repairAttachments[editingRepair.repair_id] || []}
                  pending={!!repairUploadPending[editingRepair.repair_id]}
                  onUpload={(files) => uploadRepairAttachments(editingRepair.repair_id, files)}
                  onDelete={(fileId) => deleteRepairAttachment(editingRepair.repair_id, fileId)}
                  onOpen={(fileId, download) => openRepairAttachment(editingRepair.repair_id, fileId, download)}
                  t={t}
                />
              )}

              <Button onClick={handleAddRepair} className="w-full bg-zinc-950 rounded-none" data-testid="repair-save-btn">
                {editingRepair
                  ? t('save')
                  : (repairForm.payment_source === 'distribution' && repairForm.generate_invoices
                      ? t('generateInvoicesAndCreate')
                      : t('addRepair'))}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Repair Confirmation */}
        <Dialog open={deleteRepairDialogOpen} onOpenChange={setDeleteRepairDialogOpen}>
          <DialogContent className="sm:max-w-sm rounded-none">
            <DialogHeader><DialogTitle>{t('deleteRepair')}</DialogTitle></DialogHeader>
            <p className="text-sm text-zinc-600">{t('deleteRepairConfirm')}</p>
            {deletingRepair && <p className="text-sm font-mono bg-zinc-50 p-2 border">{deletingRepair.description} — &euro;{(deletingRepair.amount || 0).toFixed(2)}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteRepairDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={handleDeleteRepair} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="repair-confirm-delete">{t('delete')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== EDIT REPAIR DISTRIBUTION DIALOG (Phase 279) ====== */}
        <EditRepairDistributionDialog
          open={editDistOpen}
          onOpenChange={setEditDistOpen}
          buildingId={buildingId}
          repair={editDistRepair}
          apartments={apartments}
          onSaved={() => { fetchRepairs(); fetchApartments?.(); }}
        />

        {/* ====== REPAIR PAYMENT PICKER DIALOG (opens from Payments tab "Add Payment" button) ====== */}
        <Dialog
          open={repPaymentPickerOpen}
          onOpenChange={(open) => {
            setRepPaymentPickerOpen(open);
            if (open) {
              // Always fetch fresh repairs when the picker opens so newly-created
              // repairs (and updated remaining balances) appear without a manual reload.
              fetchRepairs();
            }
          }}
        >
          <DialogContent className="sm:max-w-md rounded-none" data-testid="rep-pay-picker-dialog">
            <DialogHeader>
              <DialogTitle>{t('addPayment')} · {t('repairs')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-xs text-zinc-500">{t('selectRepairToPay')}</p>
              <Select value={repPaymentPickerRepairId || '_none'} onValueChange={(v) => setRepPaymentPickerRepairId(v === '_none' ? '' : v)}>
                <SelectTrigger className="rounded-none" data-testid="rep-pay-picker-select"><SelectValue placeholder={t('selectRepair')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{t('selectRepair')}</SelectItem>
                  {(() => {
                    // Show ALL active repairs with a remaining balance > 0, regardless
                    // of payment_type. A brand-new repair will have no fund_payments
                    // yet, so its `remaining` equals its `amount` (or is undefined if
                    // the list hasn't been refreshed yet — we treat undefined as
                    // "unknown, show it" to avoid hiding legitimate entries).
                    const options = (repairsList || []).filter(r => {
                      const status = (r.status || 'active');
                      if (status !== 'active') return false;
                      if (r.remaining === undefined || r.remaining === null) return (r.amount || 0) > 0;
                      return r.remaining > 0;
                    });
                    if (options.length === 0) {
                      return <SelectItem value="_empty" disabled>{t('noRepairs')}</SelectItem>;
                    }
                    return options.map(r => {
                      const remaining = (r.remaining !== undefined && r.remaining !== null) ? r.remaining : (r.amount || 0);
                      return (
                        <SelectItem key={r.repair_id} value={r.repair_id}>
                          {r.description} — €{remaining.toFixed(2)}
                        </SelectItem>
                      );
                    });
                  })()}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-400 leading-snug">{t('repairPaymentPickerHint')}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRepPaymentPickerOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button
                onClick={() => {
                  if (!repPaymentPickerRepairId) { toast.error(t('selectRepair')); return; }
                  const rep = repairsList.find(r => r.repair_id === repPaymentPickerRepairId);
                  if (!rep) return;
                  setRepPaymentPickerOpen(false);
                  setRepPaymentPickerRepairId('');
                  openRepairPaymentDialog(rep);
                }}
                className="bg-zinc-900 rounded-none"
                data-testid="rep-pay-picker-continue"
              >{t('continue')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== REPAIR FUND PAYMENT DIALOG ====== */}
        <Dialog open={repairPaymentDialogOpen} onOpenChange={(open) => { setRepairPaymentDialogOpen(open); if (!open) { setEditingRepairPayment(null); setActivePaymentRepair(null); } }}>
          <DialogContent className="sm:max-w-md rounded-none" data-testid="repair-payment-dialog">
            <DialogHeader>
              <DialogTitle>{editingRepairPayment ? t('editPayment') : t('addPayment')}</DialogTitle>
              {activePaymentRepair && (() => {
                const total = activePaymentRepair.amount || 0;
                const alreadyPaid = (activePaymentRepair.fund_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                const excludeCurrent = editingRepairPayment ? (editingRepairPayment.amount || 0) : 0;
                const remaining = Math.max(0, total - (alreadyPaid - excludeCurrent));
                return (
                  <DialogDescription className="text-xs text-zinc-500">
                    <span className="block">{activePaymentRepair.description}</span>
                    <span className="block mt-1 font-mono">{t('total')}: €{total.toFixed(2)} · {t('remaining')}: <span className="text-amber-700 font-semibold" data-testid="repair-payment-remaining">€{remaining.toFixed(2)}</span></span>
                  </DialogDescription>
                );
              })()}
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('date')} *</Label>
                  <DatePickerDMY value={repairPaymentForm.date} onChange={v => setRepairPaymentForm(f => ({ ...f, date: v }))} className="mt-1 font-mono" data-testid="repair-payment-date" />
                </div>
                <div>
                  <Label>{t('amount')} (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={repairPaymentForm.amount}
                    onChange={e => setRepairPaymentForm(f => ({ ...f, amount: e.target.value }))}
                    className="mt-1 rounded-none font-mono"
                    data-testid="repair-payment-amount"
                  />
                </div>
              </div>
              <div>
                <Label>{t('paymentSource')} *</Label>
                <p className="text-[10px] text-zinc-500 mt-0.5" data-testid="repair-payment-source-hint">{t('buildingFundsOnlyHint')}</p>
                <div className="grid grid-cols-2 gap-2 mt-1" data-testid="repair-payment-source-group">
                  <label className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${repairPaymentForm.payment_source === 'cash' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="repair-payment-source-cash">
                    <input type="radio" name="repair_payment_source" checked={repairPaymentForm.payment_source === 'cash'} onChange={() => setRepairPaymentForm(f => ({ ...f, payment_source: 'cash' }))} className="accent-zinc-900" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold">{t('cashReceiptsBuilding')}</p>
                      <p className="text-[10px] font-mono leading-tight"><span className={(sourceBalances.cash?.available || 0) < 0 ? 'text-red-500' : 'text-zinc-500'}>€{(sourceBalances.cash?.available || 0).toFixed(2)}</span> <span className="text-zinc-400">{t('available')}</span></p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${repairPaymentForm.payment_source === 'bank' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="repair-payment-source-bank">
                    <input type="radio" name="repair_payment_source" checked={repairPaymentForm.payment_source === 'bank'} onChange={() => setRepairPaymentForm(f => ({ ...f, payment_source: 'bank' }))} className="accent-zinc-900" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold">{t('bankReceiptsBuilding')}</p>
                      <p className="text-[10px] font-mono leading-tight"><span className={(sourceBalances.bank?.available || 0) < 0 ? 'text-red-500' : 'text-zinc-500'}>€{(sourceBalances.bank?.available || 0).toFixed(2)}</span> <span className="text-zinc-400">{t('available')}</span></p>
                    </div>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('expenseCategory')} *</Label>
                  <Select value={repairPaymentForm.category || '_none'} onValueChange={(val) => setRepairPaymentForm(f => ({ ...f, category: val === '_none' ? '' : val }))}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="repair-payment-category-select">
                      <SelectValue placeholder={t('expenseCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">{t('expenseCategory')}</SelectItem>
                      <SelectGroup>
                        <SelectLabel>{t('monthlyExpenses')}</SelectLabel>
                        {MONTHLY_CATEGORIES.map(c => (
                          <SelectItem key={c} value={c}>{getCatLabel(c, t, customCategories)}</SelectItem>
                        ))}
                        {customCategories
                          .filter(c => !MONTHLY_CATEGORIES.includes(c.category_code) && !CAPITAL_CATEGORIES.includes(c.category_code) && (c.group === 'monthly' || (/^2/).test(c.category_code)))
                          .map(c => (
                            <SelectItem key={c.category_code} value={c.category_code}>{c.name}</SelectItem>
                          ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>{t('capitalExpenses')}</SelectLabel>
                        {CAPITAL_CATEGORIES.map(c => (
                          <SelectItem key={c} value={c}>{getCatLabel(c, t, customCategories)}</SelectItem>
                        ))}
                        {customCategories
                          .filter(c => !MONTHLY_CATEGORIES.includes(c.category_code) && !CAPITAL_CATEGORIES.includes(c.category_code) && (c.group === 'capital' || (/^3/).test(c.category_code)))
                          .map(c => (
                            <SelectItem key={c.category_code} value={c.category_code}>{c.name}</SelectItem>
                          ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {repairPaymentForm.category && (() => {
                    const code = repairPaymentForm.category;
                    const isCapital = CAPITAL_CATEGORIES.includes(code) || (/^3/).test(code);
                    return (
                      <p className="text-[10px] mt-1" data-testid="repair-payment-type-indicator">
                        <span className="text-zinc-400 uppercase tracking-wider">{t('type')}:</span>
                        <span className={`ml-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase ${isCapital ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'bg-zinc-100 text-zinc-700 border border-zinc-200'}`}>
                          {isCapital ? t('capital') : t('monthly')}
                        </span>
                        <span className="ml-1 text-zinc-400 font-mono">· {code}</span>
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <Label>{t('vendor')} *</Label>
                  <Select value={repairPaymentForm.vendor_id || '_none'} onValueChange={(val) => setRepairPaymentForm(f => ({ ...f, vendor_id: val === '_none' ? '' : val }))}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="repair-payment-vendor-select">
                      <SelectValue placeholder={t('selectVendor')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">{t('selectVendor')}</SelectItem>
                      {vendorList.map(v => (
                        <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer py-1 px-2 border border-zinc-200 bg-amber-50/30" data-testid="repair-payment-reserve-label">
                <input
                  type="checkbox"
                  checked={!!repairPaymentForm.paid_from_reserve_fund}
                  onChange={e => setRepairPaymentForm(f => ({ ...f, paid_from_reserve_fund: e.target.checked }))}
                  className="accent-amber-600 w-4 h-4"
                  data-testid="repair-payment-reserve-checkbox"
                />
                <div>
                  <p className="text-xs font-semibold text-amber-800">{t('deductFromReserveFund')}</p>
                  <p className="text-[10px] text-amber-600">{t('reserveFundAvailable')}: €{(sourceBalances?.reserve_fund?.available || 0).toFixed(2)}</p>
                </div>
              </label>
              <div>
                <Label>{t('notes')}</Label>
                <Input
                  value={repairPaymentForm.notes}
                  onChange={e => setRepairPaymentForm(f => ({ ...f, notes: e.target.value }))}
                  className="mt-1 rounded-none"
                  placeholder={t('optionalNotes')}
                  data-testid="repair-payment-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRepairPaymentDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={handleSaveRepairPayment} className="bg-zinc-950 rounded-none" data-testid="repair-payment-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* ====== SEND EMAIL DIALOG (shared) ====== */}
        <SendEmailDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          pdfUrl={emailDialogProps.pdfUrl}
          filename={emailDialogProps.filename}
          defaultSubject={emailDialogProps.defaultSubject}
          defaultEmail={emailDialogProps.defaultEmail}
          buildingId={buildingId}
        />

        {/* ====== TASK DETAILS DIALOG (Phase 71) ====== */}
        <Dialog open={taskDetailsOpen} onOpenChange={setTaskDetailsOpen}>
          <DialogContent className="sm:max-w-lg rounded-none max-h-[85vh] overflow-y-auto" data-testid="task-details-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span>{t('task')}</span>
                {taskDetailsData?.status === 'completed' && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="task-details-completed-badge">{t('completed')}</span>
                )}
                {taskDetailsData?.is_important && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 bg-red-50 text-red-700 border border-red-200">{t('important')}</span>
                )}
              </DialogTitle>
            </DialogHeader>
            {taskDetailsData ? (
              <div className="space-y-4">
                {/* Meta block */}
                <div className="bg-zinc-50 border border-zinc-200 p-3 text-xs space-y-1">
                  <div><span className="text-zinc-400">{t('building')}:</span> <span className="font-semibold text-zinc-800">{building?.name || ''}</span></div>
                  <div><span className="text-zinc-400">{t('scope')}:</span>{' '}
                    {taskDetailsData.type === 'apartment' ? (
                      <span className="font-semibold text-blue-700">{t('apartment')}{taskDetailsData.apartment_number ? ` #${taskDetailsData.apartment_number}` : ''}</span>
                    ) : <span className="font-semibold text-zinc-800">{t('building')}</span>}
                  </div>
                  {taskDetailsData.start_date && <div><span className="text-zinc-400">{t('startDate')}:</span> <span className="font-mono">{formatDateDMY(taskDetailsData.start_date)}</span></div>}
                  {taskDetailsData.reminder_date && <div><span className="text-zinc-400">{t('reminderDate')}:</span> <span className="font-mono">{formatDateDMY(taskDetailsData.reminder_date)}</span></div>}
                  {taskDetailsData.notes && <div><span className="text-zinc-400">{t('notes')}:</span> <span className="text-zinc-700">{taskDetailsData.notes}</span></div>}
                </div>

                {/* History timeline */}
                <div>
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">{t('history')}</h4>
                  {(taskDetailsData.history || []).length === 0 ? (
                    <p className="text-xs text-zinc-400 italic" data-testid="task-history-empty">{t('noHistoryYet')}</p>
                  ) : (
                    <ul className="space-y-2 border-l-2 border-zinc-200 pl-3" data-testid="task-history-list">
                      {(taskDetailsData.history || []).map(h => (
                        <li key={h.entry_id} className="relative" data-testid={`task-history-${h.entry_id}`}>
                          <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 border-white ${h.type === 'status_change' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                          <div className="text-xs">
                            <div className="text-zinc-800">{h.note}</div>
                            <div className="text-[10px] text-zinc-400 mt-0.5">
                              <span>{h.author_name || 'System'}</span>
                              <span className="mx-1">·</span>
                              <span className="font-mono">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Add note (hidden when completed) */}
                {taskDetailsData.status !== 'completed' && (
                  <form onSubmit={handleAddTaskNote} className="space-y-2 border-t border-zinc-200 pt-3">
                    <Label className="text-xs">{t('addNote')}</Label>
                    <textarea
                      value={taskNoteDraft}
                      onChange={(e) => setTaskNoteDraft(e.target.value)}
                      rows={2}
                      className="w-full border border-zinc-300 rounded-none px-3 py-2 text-sm resize-none"
                      placeholder={t('addNotePlaceholder')}
                      data-testid="task-add-note-input"
                    />
                    <div className="flex justify-end">
                      <Button type="submit" disabled={!taskNoteDraft.trim()} className="bg-zinc-950 text-white rounded-none" data-testid="task-add-note-submit">{t('addNote')}</Button>
                    </div>
                  </form>
                )}

                {/* Phase 598 — Status controls (4 buckets) + follow-up
                    date editor. Mirrors a real maintenance/task tracker
                    instead of a binary done/not-done. */}
                <div className="border-t border-zinc-200 pt-3 space-y-3" data-testid="task-status-controls">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('status') || 'Status'}</Label>
                    <div className="mt-1.5 grid grid-cols-4 gap-1.5" role="tablist">
                      {[
                        { key: 'pending',     label: t('statusPending')    || 'Pending',     tone: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
                        { key: 'in_progress', label: t('statusInProgress') || 'In Progress', tone: 'bg-blue-50 text-blue-700 border-blue-300' },
                        { key: 'waiting',     label: t('statusWaiting')    || 'Waiting',     tone: 'bg-amber-50 text-amber-700 border-amber-300' },
                        { key: 'completed',   label: t('statusCompleted')  || 'Completed',   tone: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
                      ].map(s => {
                        const isActive = (taskDetailsData.status || 'pending') === s.key;
                        return (
                          <button
                            key={s.key}
                            type="button"
                            onClick={async () => {
                              if (isActive) return;
                              try {
                                await axios.put(`${API}/tasks/${taskDetailsData.task_id}`, { status: s.key });
                                toast.success(t('savedSuccess'));
                                reloadTaskDetails();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || t('errorOccurred'));
                              }
                            }}
                            className={`text-[11px] px-2 py-1 border transition-colors ${isActive ? s.tone + ' font-semibold' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}
                            data-testid={`task-status-${s.key}`}
                          >{s.label}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('followUpDate') || 'Follow-up date'}</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <input
                        type="date"
                        value={taskDetailsData.reminder_date || ''}
                        onChange={async (e) => {
                          const v = e.target.value || null;
                          // Optimistic local update so the picker doesn't flicker.
                          setTaskDetailsData(prev => ({ ...prev, reminder_date: v }));
                          try {
                            await axios.put(`${API}/tasks/${taskDetailsData.task_id}`, { reminder_date: v });
                            reloadTaskDetails();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || t('errorOccurred'));
                          }
                        }}
                        className="border border-zinc-300 rounded-none px-2 py-1 text-xs font-mono"
                        data-testid="task-reminder-date-input"
                      />
                      {taskDetailsData.reminder_date && (
                        <button
                          type="button"
                          onClick={async () => {
                            setTaskDetailsData(prev => ({ ...prev, reminder_date: null }));
                            try {
                              await axios.put(`${API}/tasks/${taskDetailsData.task_id}`, { reminder_date: null });
                              reloadTaskDetails();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || t('errorOccurred'));
                            }
                          }}
                          className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-zinc-900 px-1"
                          data-testid="task-reminder-date-clear"
                        >{t('clear') || 'Clear'}</button>
                      )}
                    </div>
                  </div>
                </div>

                <DialogFooter className="border-t border-zinc-200 pt-3">
                  {taskDetailsData.status !== 'completed' ? (
                    <Button type="button" onClick={handleMarkTaskCompleted} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none" data-testid="task-mark-completed">{t('markAsCompleted')}</Button>
                  ) : (
                    <span className="text-xs text-zinc-400 italic">{t('taskCompletedReadOnly')}</span>
                  )}
                  <Button type="button" variant="outline" onClick={() => setTaskDetailsOpen(false)} className="rounded-none" data-testid="task-details-close">{t('close')}</Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="text-center py-6 text-zinc-400 text-sm">{t('loading')}...</div>
            )}
            {taskDetailsLoading && <div className="text-[10px] text-zinc-400">{t('loading')}...</div>}
          </DialogContent>
        </Dialog>

        {/* ====== ADD TASK DIALOG (Phase 65 – keeps building context) ====== */}
        <Dialog open={addTaskDialogOpen} onOpenChange={setAddTaskDialogOpen}>
          <DialogContent className="sm:max-w-md rounded-none" data-testid="add-task-dialog">
            <DialogHeader>
              <DialogTitle>{t('addTask')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateBuildingTask} className="space-y-3">
              <div className="text-xs text-zinc-500 px-3 py-2 bg-zinc-50 border border-zinc-200" data-testid="add-task-building-label">
                {t('building')}: <span className="font-semibold text-zinc-800">{building?.name || ''}</span>
              </div>
              {/* Task scope selector (Phase 67) */}
              <div>
                <Label>{t('scope')} *</Label>
                <div className="mt-1 inline-flex border border-zinc-300 w-full" role="tablist" data-testid="add-task-type-tabs">
                  <button
                    type="button"
                    onClick={() => setTaskForm(v => ({ ...v, type: 'building' }))}
                    className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${taskForm.type === 'building' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                    data-testid="add-task-type-building"
                  >{t('building')}</button>
                  <button
                    type="button"
                    onClick={() => setTaskForm(v => ({ ...v, type: 'apartment' }))}
                    className={`flex-1 px-4 py-2 text-sm font-medium transition-colors border-l border-zinc-300 ${taskForm.type === 'apartment' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                    data-testid="add-task-type-apartment"
                  >{t('apartment')}</button>
                </div>
              </div>
              {taskForm.type === 'apartment' && (
                <div data-testid="add-task-apt-block">
                  <Label>{t('apartmentNumber')} *</Label>
                  <Select value={taskForm.apartment_number} onValueChange={(v) => setTaskForm(f => ({ ...f, apartment_number: v }))}>
                    <SelectTrigger className="mt-1 rounded-none h-9" data-testid="add-task-apt-select">
                      <SelectValue placeholder={t('apartmentNumber')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(apartments || []).map(a => (
                        <SelectItem key={a.apartment_id} value={a.apartment_number}>
                          #{a.apartment_number} — {a.owner_name || '-'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>{t('startDate')} *</Label>
                <Input
                  type="date"
                  value={taskForm.start_date}
                  onChange={(e) => setTaskForm(v => ({ ...v, start_date: e.target.value }))}
                  className="mt-1 rounded-none h-9"
                  required
                  data-testid="add-task-start-date"
                />
              </div>
              <div>
                <Label>{t('reminderDate')} <span className="text-zinc-400 text-[10px]">({t('optional')})</span></Label>
                <Input
                  type="date"
                  value={taskForm.reminder_date}
                  onChange={(e) => setTaskForm(v => ({ ...v, reminder_date: e.target.value }))}
                  className="mt-1 rounded-none h-9"
                  data-testid="add-task-reminder-date"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={taskForm.is_important}
                  onChange={(e) => setTaskForm(v => ({ ...v, is_important: e.target.checked }))}
                  data-testid="add-task-important"
                />
                <span>{t('important')}</span>
              </label>
              <div>
                <Label>{t('notes')}</Label>
                <textarea
                  value={taskForm.notes}
                  onChange={(e) => setTaskForm(v => ({ ...v, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full border border-zinc-300 rounded-none px-3 py-2 text-sm resize-none"
                  placeholder={t('notes')}
                  data-testid="add-task-notes"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddTaskDialogOpen(false)} className="rounded-none" data-testid="add-task-cancel">{t('cancel')}</Button>
                <Button type="submit" disabled={taskSaving} className="bg-zinc-950 text-white rounded-none" data-testid="add-task-save">{taskSaving ? '...' : t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Phase 148 — Plan Create / Rename / Duplicate dialog. */}
        <Dialog open={planDialogMode !== null} onOpenChange={(open) => { if (!open) setPlanDialogMode(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="plan-dialog">
            <DialogHeader>
              <DialogTitle>
                {planDialogMode === 'new' && t('newPlan')}
                {planDialogMode === 'duplicate' && t('duplicatePlan')}
                {planDialogMode === 'rename' && t('renamePlan')}
              </DialogTitle>
              <DialogDescription>
                {planDialogMode === 'duplicate' && t('duplicatePlanDesc')}
                {planDialogMode === 'new' && t('newPlanDesc')}
                {planDialogMode === 'rename' && t('renamePlanDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('planName')} *</Label>
                <Input value={planDialogName} onChange={(e) => setPlanDialogName(e.target.value)} autoFocus className="rounded-none mt-1" data-testid="plan-dialog-name" placeholder={t('planNamePlaceholder')} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitPlanDialog(); } }} />
              </div>
              {planDialogMode === 'duplicate' && (
                <div className="border border-zinc-200 p-3 bg-zinc-50">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('duplicateChoice')}</Label>
                  <div className="mt-2 space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer" data-testid="plan-dup-keep-rounded">
                      <input type="radio" name="keepRounded" checked={planDialogKeepRounded} onChange={() => setPlanDialogKeepRounded(true)} className="mt-0.5" />
                      <span className="text-xs">
                        <strong>{t('duplicateKeepRounded')}</strong>
                        <span className="block text-zinc-500 mt-0.5">{t('duplicateKeepRoundedDesc')}</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer" data-testid="plan-dup-reset-rounded">
                      <input type="radio" name="keepRounded" checked={!planDialogKeepRounded} onChange={() => setPlanDialogKeepRounded(false)} className="mt-0.5" />
                      <span className="text-xs">
                        <strong>{t('duplicateResetRounded')}</strong>
                        <span className="block text-zinc-500 mt-0.5">{t('duplicateResetRoundedDesc')}</span>
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPlanDialogMode(null)} className="rounded-none" data-testid="plan-dialog-cancel">{t('cancel')}</Button>
              <Button onClick={submitPlanDialog} className="rounded-none bg-zinc-950" data-testid="plan-dialog-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 169 — Activate Plan dialog: collects the required start_date. */}
        <Dialog open={planActivateDialogOpen} onOpenChange={(open) => { if (!open) { setPlanActivateDialogOpen(false); setPlanActivateTarget(null); } }}>
          <DialogContent className="sm:max-w-md rounded-none" data-testid="plan-activate-dialog">
            <DialogHeader>
              <DialogTitle>{t('activatePlan')}</DialogTitle>
              <DialogDescription>{t('activatePlanDesc')}</DialogDescription>
            </DialogHeader>
            {planActivateTarget && (
              <div className="space-y-3 py-2">
                <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm">
                  <span className="text-zinc-500">{t('plan')}: </span>
                  <span className="font-semibold">{planActivateTarget.name}</span>
                </div>
                {(() => {
                  const prev = plansList.find(p => p.is_active && p.plan_id !== planActivateTarget.plan_id);
                  if (!prev) return null;
                  return (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="plan-activate-prev-info">
                      {t('previousPlanWillClose').replace('{name}', prev.name).replace('{date}', planActivateStartDate ? formatDateDMY(new Date(new Date(planActivateStartDate).getTime() - 86400000).toISOString().slice(0, 10)) : '—')}
                    </div>
                  );
                })()}
                <div>
                  <Label>{t('startDate')} *</Label>
                  <Input
                    type="date"
                    value={planActivateStartDate}
                    onChange={(e) => setPlanActivateStartDate(e.target.value)}
                    className="rounded-none mt-1 font-mono"
                    data-testid="plan-activate-start-date"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPlanActivateDialogOpen(false); setPlanActivateTarget(null); }} className="rounded-none" data-testid="plan-activate-cancel">{t('cancel')}</Button>
              <Button onClick={confirmActivatePlan} className="rounded-none bg-emerald-700 hover:bg-emerald-800" data-testid="plan-activate-confirm">{t('activate')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 169 — Edit Plan Dates dialog (admin manual override with overlap check). */}
        <Dialog open={planDatesDialogOpen} onOpenChange={(open) => { if (!open) { setPlanDatesDialogOpen(false); setPlanDatesTarget(null); } }}>
          <DialogContent className="sm:max-w-md rounded-none" data-testid="plan-dates-dialog">
            <DialogHeader>
              <DialogTitle>{t('editPlanDates')}</DialogTitle>
              <DialogDescription>{t('editPlanDatesDesc')}</DialogDescription>
            </DialogHeader>
            {planDatesTarget && (
              <div className="space-y-3 py-2">
                <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm">
                  <span className="text-zinc-500">{t('plan')}: </span>
                  <span className="font-semibold">{planDatesTarget.name}</span>
                </div>
                <div>
                  <Label>{t('startDate')}</Label>
                  <Input type="date" value={planDatesStart} onChange={(e) => setPlanDatesStart(e.target.value)} className="rounded-none mt-1 font-mono" data-testid="plan-dates-start" />
                </div>
                <div>
                  <Label>{t('endDate')} <span className="text-zinc-400 text-xs">({t('optional')})</span></Label>
                  <Input type="date" value={planDatesEnd} onChange={(e) => setPlanDatesEnd(e.target.value)} className="rounded-none mt-1 font-mono" data-testid="plan-dates-end" />
                  <p className="text-[10px] text-zinc-400 mt-1">{t('leaveEmptyForOngoing')}</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPlanDatesDialogOpen(false); setPlanDatesTarget(null); }} className="rounded-none" data-testid="plan-dates-cancel">{t('cancel')}</Button>
              <Button onClick={saveEditPlanDates} className="rounded-none bg-zinc-950" data-testid="plan-dates-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Phase 139 — Annual Expenses audit history modal. Read-only timeline of all amount changes. */}
        <Dialog open={annualHistoryOpen} onOpenChange={setAnnualHistoryOpen}>
          <DialogContent className="sm:max-w-2xl" data-testid="annual-history-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClockClockwise size={18} />
                <span>{t('editHistory')}</span>
                <span className="text-sm font-normal text-zinc-500">— {annualHistoryCategory.label} ({annualYear})</span>
              </DialogTitle>
              <DialogDescription>{t('editHistoryDesc')}</DialogDescription>
            </DialogHeader>
            {annualHistoryLoading ? (
              <div className="py-10 text-center text-zinc-400 text-sm" data-testid="annual-history-loading">{t('loading')}…</div>
            ) : annualHistoryRows.length === 0 ? (
              <div className="py-10 text-center text-zinc-400 text-sm border border-dashed border-zinc-200" data-testid="annual-history-empty">
                {t('noHistoryYet')}
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto border border-zinc-200">
                <table className="excel-table" data-testid="annual-history-table">
                  <thead>
                    <tr>
                      <th className="w-40">{t('changedAt')}</th>
                      <th className="text-right">{t('previousAmount')} (&euro;)</th>
                      <th className="text-right">{t('newAmount')} (&euro;)</th>
                      <th className="text-right w-24">{t('delta')} (&euro;)</th>
                      <th>{t('changedBy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {annualHistoryRows.map(r => {
                      const delta = (r.new_amount || 0) - (r.previous_amount || 0);
                      const deltaStr = `${delta > 0 ? '+' : ''}${delta.toFixed(2)}`;
                      const cls = delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : 'text-zinc-500';
                      let when = r.changed_at || '';
                      try { when = new Date(r.changed_at).toLocaleString(language === 'el' ? 'el-GR' : 'en-GB'); } catch { /* keep ISO */ }
                      return (
                        <tr key={r.history_id} data-testid={`annual-history-row-${r.history_id}`}>
                          <td className="font-mono text-xs">{when}</td>
                          <td className="text-right font-mono text-zinc-600">&euro;{(r.previous_amount || 0).toFixed(2)}</td>
                          <td className="text-right font-mono font-semibold">&euro;{(r.new_amount || 0).toFixed(2)}</td>
                          <td className={`text-right font-mono font-semibold ${cls}`}>&euro;{deltaStr}</td>
                          <td className="text-xs text-zinc-600">{r.changed_by?.name || r.changed_by?.email || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAnnualHistoryOpen(false)} className="rounded-none" data-testid="annual-history-close">{t('close')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Phase 735.35 — Outstanding balances popup. Uses the SAME
            calculation as the Overview card so the total in the footer
            always equals the Overview total, row-by-row transparency. */}
        <Dialog open={outstandingModalOpen} onOpenChange={setOutstandingModalOpen}>
          <DialogContent className="sm:max-w-3xl" data-testid="outstanding-balances-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span>{t('totalOutstanding')}</span>
                <span className="text-sm font-normal text-zinc-500">— {building?.name || ''}</span>
              </DialogTitle>
              <DialogDescription>
                {t('outstandingDesc') || 'All apartments with their invoiced / paid / remaining balance. Negative values indicate credit balances (overpaid).'}
              </DialogDescription>
            </DialogHeader>
            {outstandingLoading || !outstandingData ? (
              <div className="py-10 text-center text-zinc-400 text-sm" data-testid="outstanding-loading">{t('loading')}…</div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto border border-zinc-200">
                <table className="excel-table" data-testid="outstanding-balances-table">
                  <thead>
                    <tr>
                      <th className="w-24">{t('apartment')}</th>
                      <th>{t('ownerName') || t('owner') || 'Owner'}</th>
                      <th className="text-right w-24">{t('invoiced') || 'Invoiced'} (&euro;)</th>
                      <th className="text-right w-24">{t('paid')} (&euro;)</th>
                      <th className="text-right w-28">{t('outstanding') || 'Outstanding'} (&euro;)</th>
                      <th className="w-28 text-right">{t('actions') || 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstandingData.apartments.map(r => (
                      <tr key={r.apartment_id} data-testid={`outstanding-row-${r.apartment_id}`} className={r.outstanding > 0 ? 'bg-red-50/40' : r.outstanding < 0 ? 'bg-emerald-50/40' : ''}>
                        <td className="font-mono text-sm font-semibold">{r.apartment_number}</td>
                        <td className="text-sm">{r.owner_name || '—'}</td>
                        <td className="text-right font-mono text-xs text-zinc-600">&euro;{r.total_invoiced.toFixed(2)}</td>
                        <td className="text-right font-mono text-xs text-zinc-600">&euro;{r.total_paid.toFixed(2)}</td>
                        <td className={`text-right font-mono font-semibold ${r.outstanding > 0 ? 'text-red-700' : r.outstanding < 0 ? 'text-emerald-700' : 'text-zinc-500'}`}>&euro;{r.outstanding.toFixed(2)}</td>
                        <td className="text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              setOutstandingModalOpen(false);
                              navigate(`/apartments/${r.apartment_id}`);
                            }}
                            className="text-[10px] font-semibold text-blue-800 border border-blue-300 bg-blue-50 px-2 py-0.5 hover:bg-blue-100 transition-colors"
                            data-testid={`view-statement-${r.apartment_id}`}
                          >
                            {t('viewStatement') || 'View Statement'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-zinc-100 font-bold">
                      <td colSpan={4} className="text-right text-xs uppercase tracking-wider text-zinc-700">{t('total')}</td>
                      <td className={`text-right font-mono ${outstandingData.total_outstanding > 0 ? 'text-red-700' : outstandingData.total_outstanding < 0 ? 'text-emerald-700' : 'text-zinc-700'}`} data-testid="outstanding-total">&euro;{outstandingData.total_outstanding.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOutstandingModalOpen(false)} className="rounded-none" data-testid="outstanding-close">{t('close')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
